/**
 * Svenska, typad mot en.ts. Byråns uppdragsgivare är en klient (aldrig kund); klientens egna kunder är kunder
 * (design 12, bilaga B). Granskas av en svensktalande compliancekollega före lansering.
 */

import type { MessageKey } from './en'

export const sv: Record<MessageKey, string> = {
  'app.name': 'Olivia',
  'app.tagline': 'utläggningsavdelningens arbetsyta',
  'app.fictional': 'fiktiv',

  'common.loading': 'Laddar…',
  'common.save': 'Spara',
  'common.saved': 'Sparat.',
  'common.cancel': 'Avbryt',
  'common.close': 'Stäng',
  'common.back': 'Tillbaka',
  'common.none': 'Inga',
  'common.reason': 'Skäl',
  'common.yes': 'Ja',
  'common.no': 'Nej',
  'common.error': 'Något gick fel: {message}',
  'common.unreachable': 'Det gick inte att nå Olivias server. Kontrollera att den är i gång och försök igen.',
  'common.notFound.title': 'Hittades inte',
  'common.notFound.body': 'Här finns ingenting, eller så har du ingen åtkomst. Båda ser likadana ut, med avsikt.',
  'common.showingOf': 'visar {n} av {total}',
  'later.title': 'Inte byggt ännu',
  'later.body': 'Den här delen av Olivia är inte byggd ännu. Till dess visar sidan ingenting, hellre än något påhittat.',
  'later.badge': 'senare',
  'later.group': 'Kommer senare ({n})',
  'common.retry': 'Försök igen',


  'errors.INTERNAL': 'Något gick fel. Försök igen och säg till på byrån om det fortsätter.',
  'errors.UNAVAILABLE': 'Olivia kunde inte kontrollera det just nu. Försök igen om en stund.',
  'errors.SIGN_IN': 'Logga in först.',
  'errors.SESSION_ENDED': 'Din session har avslutats. Logga in igen.',
  'errors.SIGN_IN_INPUT': 'Ange din e-postadress och ditt lösenord.',
  'errors.SIGN_IN_FAILED': 'E-postadressen och lösenordet stämmer inte med något konto.',
  'errors.LOCKED': 'För många misslyckade inloggningar till kontot från ditt nätverk. Försök igen om 15 minuter.',
  'errors.TOO_MANY': 'För många försök från den här adressen. Försök igen senare.',
  'errors.SAME_ORIGIN': 'Begäran avvisades eftersom den inte kom från den här webbplatsen.',
  'errors.FORBIDDEN': 'Du får inte göra det här.',
  'errors.NOT_FOUND': 'Hittades inte.',
  'errors.CONFLICT': 'Det här finns redan.',
  'errors.INVALID': 'Det är inte giltigt.',
  'errors.READ_ONLY': 'Portföljen kan bara läsas. Öppna en klient för att ändra något.',
  'errors.LINK_INVALID': 'Länken är inte giltig, eller så har den gått ut eller redan använts.',
  'errors.PASSWORD_MISSING': 'Ange ett lösenord.',
  'errors.PASSWORD_SHORT': 'Välj ett lösenord på minst 12 tecken.',
  'errors.PASSWORD_LONG': 'Välj ett lösenord på högst 72 byte (ungefär 72 bokstäver).',
  'errors.DAILY_SPEND_CAP': 'Dagens AI-budget är förbrukad, eller så räcker det som är kvar inte. Den nollställs vid midnatt UTC.',
  'errors.MONTHLY_SPEND_CAP': 'Klientens AI-budget för månaden är förbrukad, eller så räcker det som är kvar inte.',

  'shell.openMenu': 'Öppna menyn',
  'shell.closeMenu': 'Stäng menyn',
  'shell.mainNav': 'Huvudmeny',
  'shell.mode.label': 'Arbete eller Expert',
  'shell.mode.work': 'Arbete',
  'shell.mode.expert': 'Expertläge',
  'shell.language': 'Språk',
  'shell.signedInAs': 'Inloggad som {name}',
  'shell.signOut': 'Logga ut',
  'shell.about': 'Om Olivia',
  'shell.privacy': 'Integritetsmeddelande',
  'shell.method': 'Så avgör Olivia',
  'shell.skip': 'Hoppa till sidan',

  'switcher.label': 'Klient',
  'switcher.choose': 'Välj klient',
  'switcher.search': 'Sök klienter…',
  'switcher.all': 'Alla klienter (portföljen)',
  'switcher.recent': 'Senaste',
  'switcher.allOfYours': 'Alla dina {n} klienter',
  'switcher.allOfYours.one': 'Din {n} klient',
  'switcher.none': 'Du är inte medlem i någon klient ännu.',
  'switcher.noMatch': 'Ingen klient matchar.',

  'nav.section.waiting': 'Väntar på dig',
  'nav.section.clients': 'Dina klienter',
  'nav.section.firm': 'Byrån',
  'nav.section.client': 'Den här klienten',
  'nav.section.registers': 'Register',
  'nav.section.records': 'Underlag',
  'nav.section.settings': 'Profil och behörigheter',

  'nav.myWork': 'Mitt arbete, alla klienter',
  'nav.myWork.answers': 'Vad väntar på mig hos alla klienter?',
  'nav.portfolio': 'Portfölj',
  'nav.portfolio.answers': 'Var börjar jag i dag?',
  'nav.cycleAll': 'Årshjul, alla klienter',
  'nav.cycleAll.answers': 'Vad förfaller hos mina klienter?',
  'nav.changesAll': 'Regelförändringar',
  'nav.changesAll.answers': 'Vilka nya regler berör vilka klienter?',
  'nav.deliverablesAll': 'Leveranser att granska',
  'nav.deliverablesAll.answers': 'Vilka rapporter väntar på min granskning?',
  'nav.people': 'Medarbetare',
  'nav.people.answers': 'Vem arbetar här, och med vilka roller?',
  'nav.clients': 'Klienter',
  'nav.clients.answers': 'Vilka klienter har byrån, och vem är klientansvarig?',
  'nav.library': 'Bibliotek',
  'nav.library.answers': 'Vilka mallar och tjänstetyper använder byrån?',
  'nav.settings': 'Inställningar',
  'nav.settings.answers': 'Hur är den här installationen inställd?',
  'nav.spend': 'AI-kostnader',
  'nav.spend.answers': 'Vad kostar AI-användningen för byrån?',
  'nav.about': 'Om',
  'nav.about.answers': 'Vad är Olivia, och vem har gjort den?',
  'nav.method': 'Så avgör Olivia',
  'nav.method.answers': 'Efter vilka regler avgör Olivia, och hur kan jag kontrollera dem?',

  'nav.work.week': 'Din vecka och månad',
  'nav.work.week.answers': 'Vad är viktigt för klienten just nu?',
  'nav.work.happened': 'Vad som hänt',
  'nav.work.happened.answers': 'Vad har ändrats här på sistone?',
  'nav.work.forYou': 'För dig',
  'nav.work.forYou.answers': 'Vad betyder det för mitt arbete?',
  'nav.work.forClient': 'För klienten',
  'nav.work.forClient.answers': 'Vad betyder det för klienten?',
  'nav.work.compared': 'Jämfört med i dag',
  'nav.work.compared.answers': 'Hur skiljer sig nya regler från dem som gäller nu?',
  'nav.work.deadlines': 'Tidsfrister',
  'nav.work.deadlines.answers': 'Vad förfaller, och när?',
  'nav.work.todo': 'Att göra',
  'nav.work.todo.answers': 'Vad behöver vi göra?',

  'nav.overview': 'Översikt',
  'nav.overview.answers': 'Hur går det för klienten?',
  'nav.services': 'Tjänster',
  'nav.services.answers': 'Vad gör byrån åt klienten, och vem gör vad?',
  'nav.cycle': 'Årshjul',
  'nav.cycle.answers': 'Hur ser klientens år ut?',
  'nav.changes': 'Regelförändringar',
  'nav.changes.answers': 'Vilka nya regler arbetar klienten med?',
  'nav.actions': 'Åtgärder',
  'nav.actions.answers': 'Vilka iakttagelser är öppna, och vem äger dem?',
  'nav.moreRegisters': 'Fler register',
  'nav.moreRegisters.answers': 'Incidenter, nyckelriskindikatorer, styrdokument, utbildning och resten.',
  'nav.risk': 'Riskbedömning',
  'nav.risk.answers': 'Vilka risker har klienten, och hur ligger de mot riskaptiten?',
  'nav.data': 'Datainflöde',
  'nav.data.answers': 'Vilka uppgifter har klienten skickat, och är de införda?',
  'nav.documents': 'Dokument',
  'nav.documents.answers': 'Vilka filer hör till klienten?',
  'nav.deliverables': 'Leveranser',
  'nav.deliverables.answers': 'Vilka rapporter har gått, eller ska gå, till klienten?',
  'nav.aiDrafts': 'AI-utkast',
  'nav.aiDrafts.answers': 'Vilka utkast har en modell hjälpt till med?',
  'nav.profile': 'Klientprofil',
  'nav.profile.answers': 'Vem är klienten, och vad omfattar verksamheten?',
  'nav.members': 'Medlemmar och AI',
  'nav.members.answers': 'Vem får se klienten, och får dess uppgifter gå till en modell?',
  'nav.customerData': 'Kunduppgifter (underlag)',
  'nav.customerData.answers': 'På vilken grund får byrån hålla klientens kunduppgifter?',
  'nav.audit': 'Händelselogg',
  'nav.audit.answers': 'Vem gjorde vad här, och håller loggen?',

  'role.lead': 'klientansvarig',
  'role.reviewer': 'granskare',
  'role.analyst': 'handläggare',
  'role.reader': 'läsare',
  'role.customer_data': 'behörighet till kunduppgifter',
  'firmRole.firm_admin': 'byråadministratör',
  'firmRole.regulatory_desk': 'regelbevakning',
  'firmRole.partner': 'partner (delägare)',
  'firmRole.consultant': 'konsult',
  'firmRole.auditor': 'byråns granskare',
  'status.onboarding': 'under uppstart',
  'status.active': 'aktiv',
  'status.paused': 'pausad',
  'status.offboarding': 'under avveckling',
  'status.closed': 'avslutad',
  'status.purged': 'raderad',

  'signIn.title': 'Logga in',
  'signIn.email': 'E-post',
  'signIn.password': 'Lösenord',
  'signIn.submit': 'Logga in',
  'signIn.busy': 'Loggar in…',
  'signIn.invited': 'Inbjuden? Öppna länken du fick.',
  'signIn.forgot': 'Glömt lösenordet? Fråga en byråadministratör. En återställning kräver två administratörer, och du får veta vilka.',
  'signIn.picture': 'Olivia, utläggningsavdelningens guide',

  'invite.title': 'Du är inbjuden till Olivia',
  'invite.by': 'Inbjuden av {name}.',
  'invite.firmRoles': 'Roller på byrån: {roles}.',
  'invite.grants': 'Hos klienter:',
  'invite.grant': '{role} hos {client}',
  'invite.expires': 'Länken gäller till {date}.',
  'invite.name': 'Ditt namn',
  'invite.password': 'Lösenord',
  'invite.passwordHint': 'minst 12 tecken',
  'invite.terms': 'Jag godkänner villkoren och integritetsmeddelandet',
  'invite.create': 'Skapa mitt konto',
  'invite.resetTitle': 'Välj ett nytt lösenord',
  'invite.resetFor': 'För {email}.',
  'invite.resetSubmit': 'Spara mitt lösenord',
  'invite.noToken': 'Den här sidan öppnas från en inbjudnings- eller återställningslänk.',
  'invite.checking': 'Kontrollerar länken…',

  'resetNotice.title': 'Ditt lösenord har återställts',
  'resetNotice.body': 'Ditt lösenord återställdes {date} på begäran av {requester} och bekräftades av {confirmer}. Alla dina tidigare sessioner har avslutats. Om du inte bad om det, säg till på byrån.',
  'resetNotice.host': 'Ditt lösenord återställdes {date} av driftansvarig. Alla dina tidigare sessioner har avslutats. Om du inte bad om det, säg till på byrån.',
  'resetNotice.ack': 'Jag har läst detta',

  'portfolio.title': 'Portfölj',
  'portfolio.summary': '{n} klienter som du arbetar med',
  'portfolio.summary.one': '{n} klient som du arbetar med',
  'portfolio.q1': 'Vad väntar på dig',
  'portfolio.q2': 'Vad har ändrats',
  'portfolio.q3': 'Går arbetet enligt plan',
  'portfolio.q4': 'Vad Olivia innehåller',
  'portfolio.q4.clients': '{n} klienter där du är medlem.',
  'portfolio.q4.clients.one': '{n} klient där du är medlem.',
  'portfolio.q4.spend': 'Din AI-kostnad i dag: {usd}.',
  'portfolio.q4.spendFirm': 'Byråns AI-kostnad i dag: {usd} (bara summor, aldrig per klient).',
  'portfolio.index': 'Var allt finns',
  'portfolio.noClients': 'Du är inte medlem i någon klient ännu. Klientens ansvarige, eller en partner som skapar klienten, lägger till dig.',

  'client.youHere': 'Du här: {roles}',
  'client.work.title': 'Din vecka och månad',
  'client.work.q1': 'Vad som hänt',
  'client.work.q2': 'Vad det betyder för dig',
  'client.work.q3': 'Vad det betyder för klienten',
  'client.work.q4': 'Jämfört med det som gäller i dag',
  'client.work.q5': 'Tidsfrister',
  'client.work.q6': 'Vad vi behöver göra',

  'members.title': 'Medlemmar och AI',
  'members.list': 'Medlemmar',
  'members.person': 'Person',
  'members.role': 'Roll',
  'members.grantedBy': 'tilldelad av {name}',
  'members.choosePerson': 'Välj en person',
  'members.submit': 'Tilldela rollen',
  'members.revoke': 'Återkalla',
  'members.revokeReason': 'Varför återkallas rollen?',
  'members.leadOnly': 'Bara klientens ansvarige tilldelar och återkallar roller här, och aldrig åt sig själv.',
  'members.invite.title': 'Bjud in någon ny till klienten',
  'members.invite.note': 'Klientansvarig bjuder in till sin egen klient, och en byråadministratör eller en annan klientansvarig för klienten bekräftar inbjudan: länken visas en gång, för den som bekräftar, att lämna över.',
  'members.customerDataNote': 'Behörigheten till kunduppgifter kräver ett bekräftat underlag för kunduppgifter hos klienten, och bara klientansvarig kan ge den.',

  'ai.title': 'AI för klientuppgifter',
  'ai.off': 'Av: klientens uppgifter går inte till någon modell.',
  'ai.on': 'På: klientens uppgifter går till Claude via Anthropics API.',
  'ai.decided': 'Beslutat av {name} den {date}.',
  'ai.basis': 'Grund (till exempel avtalet och punkten som tillåter det)',
  'ai.reviewOn': 'Ses över senast',
  'ai.reviewPassed': 'Datumet för översyn ({date}) har passerat: inga uppgifter om klienten går till AI förrän en klientansvarig har sett över tillåtelsen och satt ett nytt datum.',
  'ai.cap': 'Månadstak i amerikanska dollar (valfritt)',
  'ai.switchOn': 'Slå på',
  'ai.switchOff': 'Slå av',
  'ai.ceilingOff': 'Installationen skickar inga klientuppgifter till någon modell, så det går inte att slå på här.',
  'ai.leadOnly': 'Bara klientens ansvarige beslutar om detta.',
  'ai.month': 'Den här månaden: {usd}.',
  'ai.contact': 'Godkänt av, hos klienten',
  'ai.contactNone': 'Välj en kontaktperson',
  'ai.document': 'Dokumentet med godkännandet',
  'ai.documentNone': 'Välj ett dokument',
  'ai.location': 'Plats för behandlingen',
  'ai.transfer': 'Grund för överföringen',
  'ai.waiting': 'Väntar på att en andra medlem i klientens team bekräftar. Till dess går inga klientuppgifter till AI.',
  'ai.confirm': 'Bekräfta',
  'ai.confirmed': 'Bekräftat av {name} den {date}.',
  'ai.authorised': 'Godkänt av {name} hos klienten ({document}). Behandlas i: {location}. Grund för överföringen: {transfer}.',
  'ai.secondMember': 'En andra medlem i klientens team, en klientansvarig eller granskare som inte fattade beslutet, bekräftar det. Varje senare ändring väntar på en ny bekräftelse.',
  'spend.month': 'Månad: {month} (UTC)',
  'spend.yourTitle': 'Dina kostnader',
  'spend.youToday': 'Du i dag: {usd}',
  'spend.youMonth': 'Du den här månaden: {usd}',
  'spend.yourCap': 'Ditt dagliga tak: {usd}',
  'spend.firmTitle': 'Byrån',
  'spend.firmToday': 'Byrån i dag: {usd}',
  'spend.firmMonth': 'Byrån den här månaden: {usd}',
  'spend.byEngine': 'Den här månaden per motor: Anthropic {anthropic} · OpenRouter {openrouter} (bara offentlig text)',
  'spend.firmCap': 'Byråns dagliga tak: {usd}',
  'spend.firmOnly': 'Byråns summor visas bara för delägare och byråadministratörer, aldrig per klient.',
  'ai.monthOfCap': 'Den här månaden: {usd} av {cap}.',
  'ai.customer': 'Kunduppgifter kräver dessutom installationens eget tak, klientens bekräftade underlag och behörigheten till kunduppgifter. Den här installationen skickar dem ingenstans.',

  'audit.title': 'Händelselogg',
  'audit.verified': 'Kedjan håller.',
  'audit.broken': 'Kedjan bryts vid position {at}.',
  'audit.verifyNow': 'Be bakgrundstjänsten kontrollera nu',
  'audit.queued': 'Lagt i kö hos bakgrundstjänsten.',
  'audit.alreadyQueued': 'Redan i kö den här timmen.',
  'audit.at': 'När',
  'audit.actor': 'Vem',
  'audit.action': 'Vad',
  'audit.record': 'Post',
  'audit.systemActor': 'systemet',
  'audit.empty': 'Inget är loggat ännu.',
  'audit.latest': 'De senaste {n} händelserna.',

  'people.title': 'Medarbetare',
  'people.name': 'Namn',
  'people.email': 'E-post',
  'people.firmRoles': 'Roller på byrån',
  'people.clients': 'Klienter (roller)',
  'people.disabled': 'avstängd',
  'people.requestReset': 'Begär återställning av lösenord',
  'people.resetNote': 'En återställning måste bekräftas av en andra byråadministratör. Då avslutas alla personens sessioner, och personen får veta det vid nästa inloggning.',
  'people.resetRequested': 'Begärd. Den väntar på en andra administratör.',

  'invites.title': 'Bjud in en person',
  'invites.email': 'E-post',
  'invites.name': 'Namn (personen kan ändra det)',
  'invites.firmRoles': 'Roller på byrån',
  'invites.grants': 'Roller hos klienter',
  'invites.addGrant': 'Lägg till en klientroll',
  'invites.removeGrant': 'Ta bort',
  'invites.client': 'Klient',
  'invites.submit': 'Skapa inbjudan',
  'invites.link': 'Lämna själv över länken till personen. Den visas en gång och gäller till {date}:',
  'invites.waits': 'Varje inbjudan väntar på en andra person innan någon länk finns: en andra byråadministratör eller, för enbart klientroller, en klientansvarig för var och en av klienterna. Länken visas en gång, för den som bekräftar.',
  'invites.pending': 'Väntande',
  'invites.none': 'Inget väntar.',
  'invites.kind.invite': 'Inbjudan',
  'invites.kind.reset': 'Återställning av lösenord',
  'invites.kind.grant': 'Klientroller för en befintlig person',
  'invites.askedBy': 'begärd av {name}',
  'invites.forYou': 'Väntar på din bekräftelse',
  'invites.forOther': 'Väntar på en andra person',
  'invites.forNamed': 'Väntar på att {names} bekräftar den',
  'invites.forNobody': 'Ingen annan får bekräfta den ännu: byrån behöver en andra administratör, eller klienten en annan klientansvarig.',
  'invites.linkGiven': 'Länk utfärdad',
  'invites.confirm': 'Bekräfta',
  'invites.refuse': 'Avslå',
  'invites.refuseReason': 'Varför avslås den?',
  'invites.confirmedLink': 'Bekräftad. Lämna själv över länken till personen; den visas en gång:',
  'invites.confirmedGrant': 'Bekräftad. Rollerna är tilldelade.',

  'clients.title': 'Klienter',
  'clients.note': 'Byråadministratörer ser den här listan. En klients innehåll kräver medlemskap, och dess kunduppgifter ytterligare en behörighet.',
  'clients.code': 'Kod',
  'clients.name': 'Namn',
  'clients.status': 'Status',
  'clients.leads': 'Klientansvarig',
  'clients.members': 'Medlemmar',
  'clients.ai': 'AI för klientuppgifter',
  'clients.customerData': 'Kunduppgifter',
  'clients.aiOn': 'på',
  'clients.aiOff': 'av',
  'clients.basisLive': 'underlag gäller',
  'clients.noBasis': 'inget',
  'clients.youMember': 'du är medlem',
  'clients.new': 'Ny klient',
  'clients.fictional': 'Fiktiv (demonstrationsdata)',
  'clients.firstLead': 'Första klientansvarig',
  'clients.create': 'Skapa klienten',
  'clients.created': 'Skapad.',
  'clients.partnerNote': 'En partner som skapar en klient blir dess första klientansvarig. En byråadministratör utser en annan person.',

  'settings.title': 'Inställningar',
  'settings.firmName': 'Byråns namn',
  'settings.orgNumber': 'Organisationsnummer',
  'settings.accent': 'Profilfärg (#RRGGBB)',
  'settings.language': 'Standardspråk',
  'settings.disclaimer': 'Ansvarsfriskrivning i exporter',
  'settings.installation': 'Installationen, inställd av driftansvarig',
  'settings.clientEngines': 'Klientuppgifter får gå till',
  'settings.customerEngines': 'Kunduppgifter får gå till',
  'settings.idle': 'Sessioner avslutas efter {n} minuter utan aktivitet och efter {h} timmar totalt.',
  'settings.noModel': 'ingen modell',
  'settings.claude': 'Claude (Anthropics API)',
  'settings.adminOnly': 'Bara en byråadministratör ändrar detta.',

  'about.title': 'Om Olivia',
  'about.body': 'Olivia är arbetsytan för en avdelning som sköter utlagda funktioner för regelefterlevnad, riskhantering och förebyggande av ekonomisk brottslighet.',
  'about.createdBy': 'Skapad av Daniel Bardun.',
  'about.version': 'Version {version}',
  'about.builtOn': 'Byggd på idéer och kod från ANTON, Horizon, ARIS, Gap Assessor och ALEXANDER (varje kopierad fil anger sitt ursprung).',
  'about.developed': 'Utvecklad av Daniel Bardun enligt ANTON- och Alexander-ramverken och deras kunskapsupplägg.',
  'about.licensedUnder': 'Licensierad under',
  'about.licenceName': 'Europeiska unionens licens för fri programvara (EUPL) v1.2',
  'about.fonts': 'Typsnitt: Instrument Sans och Instrument Serif (SIL Open Font License).',
  'about.method': 'Så avgör Olivia: varje regel, med ett genomräknat exempel som testerna kör genom koden.',
  'about.supporting.title': 'Övriga stödjande licenser',
  'about.supporting.lucide': 'Ikonerna kommer från Lucide',
  'about.supporting.isc': 'ISC-licens',
  'about.supporting.version': 'Version {version}',
  'about.supporting.commit': 'Commit {commit}',
  'about.supporting.text': 'Licenstext',

  // ── Metodsidan (IP2b) ──────────────────────────────────────────────────────────────────────────────────────
  'method.title': 'Så avgör Olivia',
  'method.intro': 'Kod räknar fram varje nivå, klass, prioritet, grupp och datum i Olivia med reglerna nedan; en person beslutar, och en modell föreslår bara. Varje regel har ett kort med ett genomräknat exempel. Testerna kör varje exempel genom koden och jämför resultatet, så ett kort kan inte glida ifrån det som körs.',
  'method.contents': 'På den här sidan',
  'method.rules': 'Regler',
  'method.area.engine': 'Mognadsmotorn',
  'method.area.business': 'Verksamhetsprofilen',
  'method.area.pictures': 'Värmekartan och vår roll',
  'method.area.findings': 'Iakttagelser och planen',
  'method.card.decides': 'Vad den avgör',
  'method.card.formula': 'Hur',
  'method.card.inputs': 'Indata',
  'method.card.example': 'Genomräknat exempel',
  'method.card.exampleRun': 'Testerna kör det här exemplet genom koden och jämför resultatet.',
  'method.card.exampleData': 'Exemplets data',
  'method.card.case': 'Fall {n}',
  'method.card.input': 'Det som går in',
  'method.card.output': 'Det som koden ger',
  'method.card.sources': 'Källor',
  'method.card.code': 'Kod',
  'method.card.sql': 'Dess motsvarighet i databasen: {fn}',
  'method.card.file': 'Regelfilen',
  'method.card.fileLine': '{path}, version {version}, SHA-256 {sha}',
  'method.card.showFile': 'Visa regelfilen',
  'method.card.status.baseline': 'Olivias förslag: kan användas i byråns interna arbete, ännu inte antaget av en regelbevakning.',
  'method.card.status.other': 'Filens status: {status}',
  'method.card.noFile': 'Regeln har ingen egen fil: dess värden finns i koden.',
  'method.prompts': 'Instruktioner till modeller',
  'method.prompts.none': 'Olivia skickar ingen instruktion till någon modell i den här versionen. När den gör det blir varje instruktion en fil i kodförrådet, som visas här med sin SHA-256 och används för en verklig klient först när två personer på byrån har antagit den.',
  'method.prompts.files': 'Filer med instruktioner i den här versionen: {list}',
  'method.never': 'Det här gör en modell aldrig här',
  'method.never.1': 'En modell avgör aldrig ett tal: nivåer, klasser, prioriteter, slutdatum och räckvidd kommer från de här reglerna eller från en namngiven person.',
  'method.never.2': 'En modell ser aldrig mer än en klient i samma instruktion.',
  'method.never.3': 'Klientdata går bara till Claude, via Anthropics API, och bara när både installationen och klientens arbetsyta tillåter det.',
  'method.never.4': 'En publik modell (GLM, via OpenRouter) ser bara offentliga uppgifter: inget som sägs inom en klient når den.',
  'method.never.5': 'En modells förslag är aldrig ett beslut: en person godtar eller avvisar det, med ett kontrollerat citat där det vilar på ett dokument.',
  'method.later': 'Regler som ännu saknar kort',
  'method.later.body': 'Koden tillämpar också de här reglerna. Deras kort är inte skrivna ännu; till dess är koden som anges bredvid var och en den plats där regeln finns.',
  'method.computed': 'Så räknades detta',
  'method.heatCell': 'Varför den här rutan?',

  'privacy.title': 'Integritetsmeddelande',
  'privacy.draft': 'UTKAST, ännu inte granskat av en jurist.',
  'privacy.who.h': 'Vem som ansvarar',
  'privacy.who': '[[BYRÅNS NAMN]] driver den här installationen av Olivia. För klientuppgifter är byrån personuppgiftsbiträde åt varje klient, som är personuppgiftsansvarig, enligt ett personuppgiftsbiträdesavtal. För medarbetarnas konton är byrån personuppgiftsansvarig.',
  'privacy.what.h': 'Vad som lagras',
  'privacy.what': 'Medarbetarkonton (namn, e-post, inloggningar); för varje klient dess profil, register, dokument och sammanställda uppgifter. Uppgifter om en klients egna kunder lagras bara där klienten har dokumenterat en rättslig grund och ett ändamål för det, bara för de kunder som byråns arbete berört och bara de uppgifter som arbetet kräver; identifierande uppgifter krypteras med en nyckel som är klientens egen, och varje läsning av dem loggas. Inget som avslöjar en rapport om misstanke lagras. Dokument som laddas upp för en klient kan ändå innehålla personuppgifter om dess kunder eller anställda, även om varje uppladdning anger om de gör det; det är uppladdarens uppgift.',
  'privacy.where.h': 'Var uppgifterna finns',
  'privacy.where': '[[DRIFTMILJÖ]], inom EU. Säkerhetskopior: [[PLATS FÖR SÄKERHETSKOPIOR]].',
  'privacy.ai.h': 'AI',
  'privacy.ai': 'En klients uppgifter, även dess dokument, går till Anthropic (Claude, via dess API) bara där klienten har tillåtit det; uppgifter om klientens kunder bara där klientens dokumenterade underlag dessutom tillåter det. Bara offentligt regelmaterial, aldrig något om en klient, går till OpenRouter, och då bara till Inceptron utan lagring av uppgifter och utan datainsamling.',
  'privacy.retention.h': 'Lagringstid',
  'privacy.retention': 'Uppgifter om en klients kunder: [[TID, förslag 24 månader efter att arbetet avslutats]], därefter raderas de. Övriga klientuppgifter: [[TID]] efter att klientrelationen upphört, därefter raderas de. Medarbetarkonton: [[TID]].',
  'privacy.rights.h': 'Dina rättigheter',
  'privacy.rights': 'Om du är kund hos någon av våra klienter: vänd dig till den klienten, som besvarar din begäran; vi hjälper klienten. I övrigt: [[KONTAKT]].',

  // ── O1 screens (generated from one table, so both languages keep the same keys) ─────────────────────────
  'common.noCustomerNames': 'Ange inte klientens kunder här. En kund hör hemma bland uppgifterna på kundnivå, där de skyddas.',
  'common.remove': 'Ta bort',
  'client.fictionalNote': 'En fiktiv klient, endast för demonstration.',
  'errors.CUSTOMER_LEVEL_NOT_YET': 'Dokument med uppgifter på kundnivå tas inte emot ännu: registren och skydden för dem kommer senare. Uppladdningen avvisas och inget av den sparas.',
  'errors.TEMPLATE_NOT_APPLICABLE': 'Bara en grundmall eller en antagen mall kan tillämpas; ett utkast väntar på regelbevakningen.',
  'errors.PERFORMER_NEEDED': 'Ange kontaktpersonen hos klienten som utför ”{title}”.',
  'errors.FILE_TOO_LARGE': 'Filen är för stor: en fil får vara högst {mb} MB.',
  'errors.FILE_MISSING': 'Den sparade filen saknas eller stämmer inte längre med sin post. Säg till på byrån.',
  'switcher.overdue': '{n} försenade',
  'switcher.overdue.one': '{n} försenad',
  'switcher.failed': 'Dina klienter kunde inte listas just nu.',
  'nav.counter.needsYou': '{n} väntar på dig',
  'nav.counter.actionsOpen': '{n} öppna åtgärder',
  'nav.counter.actionsOpen.one': '{n} öppen åtgärd',
  'nav.counter.cycleOverdue': '{n} försenade årshjulspunkter',
  'nav.counter.cycleOverdue.one': '{n} försenad årshjulspunkt',
  'state.overdue': 'försenad',
  'state.unowned': 'saknar ansvarig',
  'state.inReview': 'under granskning',
  'state.inProgress': 'pågår',
  'state.inPreparation': 'förberedelse',
  'state.due': 'planerad',
  'state.done': 'klar',
  'state.skipped': 'överhoppad',
  'state.cancelled': 'struken',
  'state.waiting': 'väntar på klienten',
  'state.performerLeft': 'utföraren har slutat',
  'state.waitingOnLeft': 'väntar på någon som har slutat',
  'contact.hasLeft': '{name} (har slutat)',
  'rule.yearly': 'årligen',
  'rule.everyNYears': 'med {n} års mellanrum',
  'rule.twiceYearly': 'två gånger om året',
  'rule.quarterly': 'kvartalsvis',
  'rule.monthly': 'månadsvis',
  'rule.everyNMonths': 'med {n} månaders mellanrum',
  'rule.timesYear': '{n} gånger om året',
  'rule.weekly': 'veckovis',
  'rule.daily': 'dagligen',
  'rule.other': 'enligt egen regel',
  'rule.lastWorkingDay': 'sista vardagen',
  'rule.nthWorkingDay': 'vardag {n}',
  'rule.onDate': '{day} {month}',
  'rule.dayOfMonth': 'den {day} i månaden',
  'anchor.at': 'vid {anchor}',
  'anchor.after': '{n} dagar efter {anchor}',
  'anchor.after.one': '{n} dag efter {anchor}',
  'anchor.before': '{n} dagar före {anchor}',
  'anchor.before.one': '{n} dag före {anchor}',
  'anchor.financial_year_end': 'räkenskapsårets slut',
  'anchor.agm': 'årsstämman',
  'anchor.licence_date': 'tillståndsdagen',
  'anchor.service_start': 'tjänstens start',
  'anchor.calendar': 'kalendern',
  'roll.none': 'ligger kvar på sitt datum',
  'roll.previous_business_day': 'flyttas till vardagen före',
  'roll.next_business_day': 'flyttas till vardagen efter',
  'when.title': 'När den infaller',
  'when.kind': 'När',
  'when.kind.fixed': 'På ett datum varje år',
  'when.kind.anchored': 'Några dagar före eller efter ett datum hos klienten',
  'when.kind.month_day': 'På en dag i valda månader',
  'when.kind.weekday': 'På en vardag i valda månader',
  'when.day': 'Dag',
  'when.month': 'Månad',
  'when.days': 'Dagar',
  'when.side': 'Före eller efter',
  'when.before': 'före',
  'when.on': 'samma dag',
  'when.after': 'efter',
  'when.anchor': 'Klientens datum',
  'when.dayOfMonth': 'Dag i månaden (1–28)',
  'when.dayOfMonthHint': 'För slutet av en månad, välj i stället dess sista vardag.',
  'when.position': 'Vilken vardag',
  'when.position.nth': 'vardag {n} i månaden',
  'when.position.last': 'månadens sista vardag',
  'when.months': 'Månader',
  'when.reads': 'Lyder: {rule}.',
  'when.firstDates': 'Första datum: {dates}',
  'yearEnd.title': 'Räkenskapsårets slut',
  'yearEnd.assumedShort': 'Datumen utgår från räkenskapsårets slut {date}, som ingen har bekräftat.',
  'yearEnd.assumedLine': '{date}: antaget, inte bekräftat',
  'yearEnd.confirmedLine': '{date}: bekräftat av {name} den {on}',
  'yearEnd.confirm': 'Bekräfta {date} som klientens räkenskapsårsslut',
  'yearEnd.confirmedTick': 'Bekräftat: detta är klientens räkenskapsårsslut (enligt bolagsordningen eller årsredovisningen)',
  'yearEnd.seriesAssumed': 'Datumen utgår från räkenskapsårets slut {date}, som ingen har bekräftat.',
  'yearEnd.itemAssumed': 'Datumet utgår från klientens räkenskapsårsslut, som ingen har bekräftat.',
  'yearEnd.confirmOnProfile': 'Bekräfta det i profilen',
  'yearEnd.changeOnProfile': 'Ändra det i profilen',
  'yearEnd.templateQuestion': 'Klientens räkenskapsårsslut antas vara {date}; ingen har bekräftat det. {n} av de valda punkterna nedan dateras utifrån det.',
  'yearEnd.templateQuestion.one': 'Klientens räkenskapsårsslut antas vara {date}; ingen har bekräftat det. {n} av de valda punkterna nedan dateras utifrån det.',
  'yearEnd.applyAssumed': 'Lägg in datumen utifrån det antagna räkenskapsårsslutet ({date}); de visar att det inte är bekräftat',
  'yearEnd.homeLine': 'Räkenskapsårets slut ({date}) är antaget, inte bekräftat, och datum i årshjulet utgår från det.',
  'cadence.regulation': 'Regelverk',
  'cadence.market_practice': 'Marknadspraxis (offentlig källa)',
  'cadence.proposal': 'Olivias förslag',
  'cadence.advisense_stated': 'Marknadspraxis (offentlig källa)',
  'cadence.firm_practice': 'Olivias förslag',
  'cadence.mark.regulation': 'R',
  'cadence.mark.market_practice': 'M',
  'cadence.mark.proposal': 'F',
  'cadence.mark.advisense_stated': 'M',
  'cadence.mark.firm_practice': 'F',
  'cadence.custom': 'egen aktivitet',
  'cadence.legend': 'Varifrån intervallet kommer: R regelverk · M marknadspraxis (offentlig källa) · F Olivias förslag, tills er regelbevakning antar mallen. Källan anges på punkten.',
  'provenance.duty': 'Skyldighet',
  'provenance.noDuty': 'Ingen lag anges för den här punkten.',
  'provenance.requirement': 'krav {code}',
  'provenance.cadence': 'Intervall',
  'provenance.page': 'källsidan, läst {date}',
  'provenance.day': 'Dag',
  'provenance.day.proposal': 'Olivias förslag; klientansvarig flyttar den för den här klienten',
  'provenance.day.regulation': 'Bestäms av regeln själv',
  'provenance.notAdopted': 'Ännu inte antaget av er regelbevakning.',
  'provenance.adoptedBy': 'Antaget av {name}, {date}.',
  'provenance.more': 'Varifrån den kommer',
  'provenance.englishOnly': 'Den här versionen av mallen har texten bara på engelska.',
  'line.first': 'första linjen',
  'line.second': 'andra linjen',
  'line.third': 'tredje linjen',
  'line.management': 'ledning',
  'line.support': 'stöd',
  'responsibility.firm': 'Byrån utför',
  'responsibility.client_firm_reviews': 'Klienten utför, byrån granskar',
  'responsibility.client_tracked': 'Klienten utför, följs endast',
  'delivery.outsourced': 'utlagd',
  'delivery.co_sourced': 'co-sourcing',
  'delivery.interim': 'interimslösning',
  'delivery.on_demand': 'vid behov',
  'presence.home': 'hemland',
  'presence.subsidiary': 'dotterbolag',
  'presence.branch': 'filial',
  'presence.cross_border': 'gränsöverskridande',
  'contactRole.board_chair': 'styrelseordförande',
  'contactRole.ceo': 'VD',
  'contactRole.board_member': 'styrelseledamot',
  'contactRole.aml_compliance_manager': 'regelefterlevnadschef, ledamot av ledningsorganet (AMLR art. 11.1)',
  'contactRole.appointed_aml_officer': 'särskilt utsedd befattningshavare, regelefterlevnadsansvarig (AMLR art. 11.2)',
  'contactRole.compliance_officer': 'complianceansvarig (compliancefunktionen)',
  'contactRole.risk_officer': 'riskansvarig',
  'contactRole.internal_audit_head': 'internrevisionschef',
  'contactRole.dpo': 'dataskyddsombud',
  'contactRole.security_officer': 'informationssäkerhetsansvarig',
  'contactRole.cfo': 'ekonomichef',
  'contactRole.data_owner': 'dataägare',
  'contactRole.other': 'annan roll',
  'activity.annual_plan': 'årsplan',
  'activity.annual_report': 'årsrapport',
  'activity.bwra_review': 'översyn av allmän riskbedömning',
  'activity.risk_assessment_update': 'uppdatering av riskbedömning',
  'activity.policy_review': 'översyn av styrdokument',
  'activity.procedure_review': 'översyn av rutiner',
  'activity.board_report': 'rapport till styrelsen',
  'activity.management_report': 'rapport till ledningen',
  'activity.monitoring_activity': 'övervakning och kontroller',
  'activity.kyc_refresh_sample': 'stickprov kundkännedom',
  'activity.screening_review': 'granskning av screening',
  'activity.sanctions_review': 'översyn av sanktioner',
  'activity.training_board': 'utbildning för styrelsen',
  'activity.training_staff': 'utbildning för personalen',
  'activity.training_plan': 'utbildningsplan',
  'activity.kri_report': 'rapport om riskindikatorer',
  'activity.regulatory_return': 'rapportering till myndighet',
  'activity.regulatory_monitoring_report': 'omvärldsbevakningsrapport',
  'activity.independent_audit': 'oberoende granskning',
  'activity.appetite_review': 'översyn av riskaptit',
  'activity.atlas_cycle': 'riskbedömningscykel',
  'activity.third_party_follow_up': 'uppföljning av tredje parter',
  'activity.mock_inspection': 'fiktivt tillsynsbesök',
  'activity.capital_planning': 'kapitalplanering',
  'activity.model_validation': 'modellvalidering',
  'activity.board_papers': 'styrelsehandlingar',
  'activity.board_minutes': 'styrelseprotokoll',
  'activity.audit_engagement': 'revisionsgranskning',
  'activity.quality_assessment': 'kvalitetsbedömning',
  'activity.privacy_review': 'dataskyddsgranskning',
  'activity.security_review': 'säkerhetsgranskning',
  'activity.other': 'övrigt',
  'table.occurrence': 'årshjulspunkter',
  'table.series': 'återkommande aktiviteter',
  'table.step': 'checklistor',
  'table.action': 'åtgärder',
  'table.document': 'dokument',
  'table.documentLink': 'dokumentkopplingar',
  'table.comment': 'kommentarer',
  'table.profile': 'klientprofil',
  'table.institutionType': 'institutstyper',
  'table.jurisdiction': 'jurisdiktioner',
  'table.contact': 'kontakter',
  'table.service': 'tjänster',
  'table.basis': 'underlag för kunduppgifter',
  'table.aiAllowance': 'AI-medgivande',
  'table.member': 'medlemmar',
  'severity.low': 'låg',
  'severity.medium': 'medel',
  'severity.high': 'hög',
  'severity.critical': 'kritisk',
  'priority.P1': 'P1',
  'priority.P2': 'P2',
  'priority.P3': 'P3',
  'priority.P4': 'P4',
  'heat.cell.A': 'på eller över målnivå',
  'heat.cell.B': '1 nivå under målnivå',
  'heat.cell.C': '2 nivåer under målnivå',
  'heat.cell.D': '3 eller fler nivåer under målnivå',
  'heat.cell.not_determined': 'kan inte fastställas: underlag behövs',
  'heat.cell.not_assessed': 'bedöms inte: inget av dess arbetsmoment ingår i analysen',
  'heat.cell.not_applicable': 'inte tillämplig för klienten',
  'heat.marker.breach': 'möjlig överträdelse',
  'heat.marker.partly': 'delvis uppfyllt',
  'heat.marker.capped': 'begränsad: nivå 4 kräver en oberoende bedömning',
  'source.monitoring': 'övervakning',
  'source.cycle_review': 'granskning i årshjulet',
  'source.gap_assessment': 'gapanalys',
  'source.internal_audit': 'internrevision',
  'source.external_audit': 'extern revision',
  'source.supervisor': 'tillsynsmyndighet',
  'source.mock_inspection': 'fiktivt tillsynsbesök',
  'source.incident': 'incident',
  'source.regulatory_change': 'regelförändring',
  'source.risk_assessment': 'riskbedömning',
  'source.third_party_review': 'granskning av tredje part',
  'source.product_assessment': 'produktbedömning',
  'source.other': 'annat',
  'actionStatus.open': 'öppen',
  'actionStatus.reported_done': 'klar enligt klienten',
  'actionStatus.in_progress': 'pågår',
  'actionStatus.remediated': 'åtgärdad',
  'actionStatus.verified': 'verifierad',
  'actionStatus.closed': 'stängd',
  'actionStatus.cancelled': 'avbruten',
  'moved.due_on': 'förfallodag',
  'moved.status': 'läge',
  'moved.owner_person_id': 'ägare',
  'moved.owner_contact_id': 'ägare hos klienten',
  'moved.severity': 'allvarlighetsgrad',
  'docKind.evidence': 'underlag',
  'docKind.summary': 'sammanfattning',
  'docKind.policy': 'styrdokument',
  'docKind.report': 'rapport',
  'docKind.board_paper': 'styrelsehandling',
  'docKind.contract': 'avtal',
  'docKind.instruction': 'instruktion',
  'docKind.dpia': 'konsekvensbedömning avseende dataskydd',
  'docKind.correspondence': 'korrespondens',
  'docKind.other': 'övrigt',
  'linkPurpose.evidence': 'underlag',
  'linkPurpose.source': 'källa',
  'linkPurpose.output': 'resultat',
  'linkPurpose.attachment': 'bilaga',
  'subject.occurrence': 'årshjulspunkt (CY-…)',
  'subject.action': 'åtgärd (ACT-…)',
  'subject.series': 'återkommande aktivitet (SER-…)',
  'subject.service': 'tjänst (SRV-…)',
  'subject.basis': 'underlag för kunduppgifter (CDB-…)',
  'subject.profile': 'klientprofil',
  'purpose.sample_check': 'stickprovskontroller',
  'purpose.kyc_review': 'granskning av kundkännedom',
  'purpose.screening_review': 'granskning av träffar',
  'purpose.alert_review': 'granskning av larm',
  'purpose.subject_request': 'begäran från registrerade',
  'category.identity': 'identitet',
  'category.kyc_cdd': 'kundkännedom (KYC/CDD)',
  'category.screening': 'screeningträffar',
  'category.transaction': 'transaktionsutdrag',
  'field.customer_ref': 'kundreferens',
  'field.kind': 'fysisk eller juridisk person',
  'field.name': 'namn',
  'field.national_id': 'person- eller organisationsnummer',
  'field.date_of_birth': 'födelsedatum',
  'field.address': 'adress',
  'field.nationality': 'medborgarskap',
  'field.id_document': 'id-handlingens nummer',
  'field.risk_class': 'riskklass',
  'field.pep': 'PEP-markering',
  'field.purpose_nature': 'affärsförbindelsens syfte och art',
  'field.beneficial_owners': 'verkliga huvudmän',
  'field.source_of_funds': 'medlens eller förmögenhetens ursprung',
  'field.cdd_dates': 'datum för kundkännedom',
  'field.next_review_on': 'nästa översyn',
  'field.checks': 'gjorda kontroller',
  'field.exceptions': 'avvikelser',
  'field.note': 'anteckning',
  'field.list_kind': 'lista',
  'field.hit_detail': 'träffens detaljer',
  'field.disposition': 'bedömning',
  'field.offence_stage': 'skede (anklagelse till dom)',
  'field.reasoning': 'motivering',
  'field.date': 'datum',
  'field.amount': 'belopp',
  'field.currency': 'valuta',
  'field.counterparty_name': 'motpartens namn',
  'field.counterparty_account': 'motpartens konto',
  'field.country': 'land',
  'needs.review': 'Granska',
  'needs.prepare': 'Förbered',
  'needs.verify': 'Verifiera',
  'needs.act': 'Åtgärda',
  'needs.confirm_basis': 'Bekräfta ett underlag',
  'needs.follow_up': 'Följ upp',
  'needs.close': 'Stäng',
  'needs.contact_left': 'Kontakt har slutat',
  'needs.follow_up_left': 'Ingen följer upp',
  'needs.returned': 'Skickad tillbaka',
  'needs.returnedBy': 'skickad tillbaka av {name} den {date}: {note}',
  'needs.ownedBy': 'ägs av {name} hos klienten',
  'needs.verifiedBy': 'verifierad av {name}',
  'needs.contactLeft': 'har slutat och nämns fortfarande; ange vem som tar över',
  'needs.followUpLeft': '{name} arbetar inte längre med klienten; ange vem som följer upp',
  'needs.followUpNobody': 'ingen är utsedd att följa upp; ange vem som gör det',
  'needs.nameSuccessor': 'Kontakter',
  'needs.confirm_skip': 'Bekräfta överhoppning',
  'needs.confirm_request': 'Bekräfta',
  'needs.skipAskedBy': 'överhoppning begärd av {name}: {reason}',
  'needs.requestAskedBy': '{kind}, begärd av {name} den {date}',
  'needs.toConfirm': 'Att bekräfta',
  'needs.due': 'senast {date}',
  'needs.preparedBy': 'förberedd av {name}',
  'needs.remediatedBy': 'åtgärdad av {name}',
  'needs.recordedBy': 'registrerat av {name}',
  'help.review': 'Kan granska',
  'help.verify': 'Kan verifiera',
  'help.take': 'Ingen utsedd',
  'help.close': 'Kan stänga',
  'help.follow_up_left': 'Ingen följer upp',
  'portfolio.allWork': 'Allt ditt arbete →',
  'portfolio.q1.none': 'Ingenting väntar på dig hos någon av dina klienter.',
  'portfolio.q1.showing': 'visar {n} av {total}, försenade först',
  'portfolio.q1.counts': 'Räknat: granskningar du får göra, punkter du förbereder när förberedelsen har börjat, åtgärder du får verifiera, dina åtgärder med frist inom 30 dagar och underlag för kunduppgifter som väntar på en andra person.',
  'portfolio.q2.changes': 'Ändringar av personer',
  'portfolio.q2.actions': 'Nya åtgärder',
  'portfolio.q2.done': 'Klara årshjulspunkter',
  'portfolio.q2.documents': 'Nya dokument',
  'portfolio.q2.note': 'Läst ur varje klients egen händelselogg; det som arbetsprocessen skapade på egen hand räknas inte. Incidenter och överskridna indikatorer, och nya regler som berör varje klient, kommer senare.',
  'portfolio.q3.items': 'Årshjulspunkter',
  'portfolio.q3.highOpen': 'Höga eller kritiska öppna',
  'portfolio.q3.actionsOverdue': 'Försenade åtgärder',
  'portfolio.q3.ofOpen': '{n} av {total}',
  'portfolio.q3.since': 'äldsta sedan {date}',
  'portfolio.q3.note': 'Årshjulspunkter med frist inom perioden, plus alla försenade. Försenad, saknar ansvarig och väntar räknas fram ur posterna varje gång och lagras aldrig; en försenad punkt som väntar på klienten räknas som båda. Väntar på klienten räknar både årshjulspunkter och åtgärder som väntar, och kan därför vara fler än årshjulet visar.',
  'portfolio.q3.policies': 'Styrdokument som passerat sin översyn kommer med registret över styrdokument.',
  'portfolio.q4.records': 'Aktiva återkommande aktiviteter: {series} · öppna årshjulspunkter: {items} · öppna åtgärder: {actions} · dokument: {documents}.',
  'portfolio.q4.templates': 'Mallar: {baseline} förslag från Olivia som inte är antagna, {adopted} antagna av er regelbevakning · tjänstetyper: {kinds}.',
  'portfolio.q4.worker': 'Arbetsprocessen sågs senast {at}.',
  'portfolio.q4.noWorker': 'Arbetsprocessen har inte setts: inget nytt dateras förrän den körs.',
  'portfolio.q4.chains': 'Händelseloggar som håller: {verified} av {checked} kontrollerade, av {of} klienter (senast {at}).',
  'portfolio.q4.chainsNone': 'Ingen händelselogg har kontrollerats ännu (av {of} klienter); arbetsprocessen kontrollerar varje natt.',
  'portfolio.q4.corpus': 'Regelsamlingen, dess bedömningar och vad som berör varje klient.',
  'portfolio.q4.radar': 'Radarn och hur färska dess källor är.',
  'cycleAll.subtitle': 'Alla dina {n} klienter',
  'cycleAll.subtitle.one': 'Din {n} klient',
  'cycleAll.allClients': 'Alla klienter',
  'cycleAll.function': 'Funktion',
  'cycleAll.allFunctions': 'Alla funktioner',
  'cycleAll.laterViews': 'Årsvyn över alla klienter och personvyn (beläggning per vecka) kommer senare.',
  'home.window': 'Period',
  'home.deadlineWindow': 'Hur långt fram',
  'home.last7': 'Senaste 7 dagarna',
  'home.last30': 'Senaste 30 dagarna',
  'home.days': '{n} dagar',
  'home.all': 'Allt',
  'home.more': 'Mer',
  'home.owner': 'ägare: {name}',
  'home.ownerContact': '{name} (kontakt hos klienten)',
  'home.forYou.phase': 'Vad nya regler och radarträffar betyder för dina arbetsfunktioner kommer med Radarn, som inte är byggd ännu.',
  'home.forClient.phase': 'Träffbedömning, beredskap och hälsoläge, framräknade med regler och aldrig med AI, kommer med regelsamlingen, som inte är byggd ännu.',
  'home.compared.phase': 'Hur kommande regler skiljer sig från det som gäller i dag kommer med regelsamlingen, som inte är byggd ännu.',
  'home.happened.register': 'I registren',
  'home.happened.none': 'Inga ändringar av personer under perioden.',
  'home.happened.changes': '{n} ändringar av personer:',
  'home.happened.changes.one': '{n} ändring av personer:',
  'home.happened.byPeople': 'Läst ur klientens händelselogg; det som arbetsprocessen daterade på egen hand räknas inte.',
  'home.happened.audit': 'Hela händelseloggen →',
  'home.happened.outside': 'Nya regler och radarträffar som berör klienten.',
  'home.happened.data': 'Data som klienten skickat, och om den förts in.',
  'home.deadlines.cycle': 'Årshjulet',
  'home.deadlines.actions': 'Åtgärder',
  'home.deadlines.noCycle': 'Ingen öppen årshjulspunkt har frist inom {n} dagar, och ingen är försenad.',
  'home.deadlines.noActions': 'Ingen öppen åtgärd har frist inom {n} dagar.',
  'home.deadlines.regulatory': 'Regulatoriska datum som berör klienten, med sin precision.',
  'home.todo.yours': 'Ditt',
  'home.todo.yoursNone': 'Inget här är ditt att göra just nu.',
  'home.todo.couldHelp': 'Du kan hjälpa till',
  'home.todo.couldHelpNone': 'Inget här väntar på en andra person som du kan vara.',
  'home.todo.otherThan': 'kräver någon annan än {name}',
  'home.todo.waiting': 'Väntar på klienten',
  'home.todo.waitingNone': 'Inget väntar på klienten.',
  'home.todo.requests': 'Registret över begäranden till klienten kommer senare.',
  'home.todo.absences': 'Det som saknas',
  'home.todo.absencesNone': 'Inget har passerat sin frist, och varje tjänst har sitt årshjul.',
  'home.todo.lapsed': 'passerad',
  'home.todo.contactLeft': 'Kontakt har slutat',
  'home.todo.contactLeftLine': '{name} har slutat och nämns fortfarande på {n} ställen',
  'home.todo.contactLeftLine.one': '{name} har slutat och nämns fortfarande på {n} ställe',
  'home.todo.yearEndAssumed': 'Räkenskapsårsslut antaget',
  'home.todo.serviceWithoutCycle': '{service}: inga årshjulspunkter ännu',
  'overview.onboarding': 'Klienten håller fortfarande på att sättas upp.',
  'overview.continueSetUp': 'Fortsätt uppsättningen →',
  'overview.outsideAppetite': 'Utanför riskaptiten',
  'overview.outsideAppetite.phase': 'Hotvägar och kvarvarande risk kommer med riskbedömningen, som inte är byggd ännu.',
  'overview.overdue': 'Försenat',
  'overview.overdueNone': 'Inget är försenat.',
  'overview.overdueCounts': 'årshjulspunkter: {cycle} · åtgärder: {actions}',
  'overview.openCycle': 'Öppna årshjulet',
  'overview.openActions': 'Öppna åtgärderna',
  'overview.nextThree': 'De tre närmaste sakerna',
  'overview.nextNone': 'Ingen öppen årshjulspunkt är daterad ännu.',
  'overview.health': 'Hälsoläge',
  'overview.health.phase': 'Hälsoraden (styrning, riskbedömning, övervakning, utbildning med mera) kommer med de övriga registren.',
  'overview.services': 'Tjänster',
  'overview.servicesAll': 'Tjänster och ansvarsfördelning →',
  'overview.noServices': 'Ingen tjänst är registrerad ännu, så inget årshjul löper.',
  'overview.serviceNext': 'Nästa: {title}, {date}',
  'overview.serviceNoItems': 'Ingen öppen årshjulspunkt.',
  'overview.serviceOverdue': '{n} försenade',
  'overview.serviceOverdue.one': '{n} försenad',
  'overview.waiting': 'Väntar på klienten',
  'overview.waitingLine': '{n} punkter sedan {date} ({who})',
  'overview.waitingLine.one': '{n} punkt sedan {date} ({who})',
  'overview.more': 'Vidare',
  'overview.upcoming': '{n} årshjulspunkter de närmaste 90 dagarna',
  'overview.upcoming.one': '{n} årshjulspunkt de närmaste 90 dagarna',
  'overview.recentChanges': 'Senaste ändringar',
  'overview.drift': 'Öppna avvikelsesignaler',
  'cycle.addSeries': 'Lägg till en återkommande aktivitet',
  'cycle.applyTemplate': 'Tillämpa en mall',
  'cycle.template': 'Mall: {name} (version {version})',
  'cycle.noTemplate': 'Ingen mall är tillämpad på klienten ännu.',
  'cycle.leadOnly': 'Bara klientansvarig ändrar klientens årshjul.',
  'cycle.view.label': 'Vy',
  'cycle.view.list': 'Lista',
  'cycle.view.month': 'Månad',
  'cycle.view.year': 'År',
  'cycle.filter.window': 'Frist inom',
  'cycle.filter.windowAll': 'Alla daterade punkter',
  'cycle.filter.state': 'Läge',
  'cycle.filter.state.open': 'Öppna',
  'cycle.filter.state.overdue': 'Försenade',
  'cycle.filter.state.unowned': 'Saknar ansvarig',
  'cycle.filter.state.waiting': 'Väntar på klienten',
  'cycle.filter.state.closed': 'Klara eller överhoppade',
  'cycle.filter.state.all': 'Alla',
  'cycle.filter.person': 'Person',
  'cycle.filter.everyone': 'Alla',
  'cycle.filter.me': 'Jag',
  'cycle.filter.service': 'Tjänst',
  'cycle.filter.allServices': 'Alla tjänster',
  'cycle.counts': 'Försenade: {overdue} · saknar ansvarig: {unowned} · väntar på klienten: {waiting} ·',
  'cycle.countsOfShown': '(räknat bland de visade)',
  'cycle.empty': 'Ingen årshjulspunkt motsvarar den här vyn.',
  'cycle.noSeries': 'Inga återkommande aktiviteter ännu: tillämpa en mall eller lägg till en aktivitet.',
  'cycle.overdueFirst': 'Försenade ({n}), visas först',
  'cycle.week': 'Vecka {week} ({from} – {to})',
  'cycle.thisWeek': 'denna vecka',
  'cycle.col.due': 'Frist',
  'cycle.col.client': 'Klient',
  'cycle.col.code': 'Kod',
  'cycle.col.item': 'Punkt',
  'cycle.col.who': 'Vem gör det',
  'cycle.col.state': 'Läge',
  'cycle.col.series': 'Aktivitet',
  'cycle.col.source': 'Källa',
  'cycle.col.next': 'Nästa',
  'cycle.nobodyNamed': 'ingen utsedd',
  'cycle.who.tracked': 'klienten ({performer}), följs endast',
  'cycle.who.clientReviews': 'klienten ({performer}) · {reviewer} granskar',
  'cycle.waitingSince': 'sedan {date} ({who})',
  'cycle.movedFrom': 'flyttad från {date}',
  'cycle.noneNext': 'inget planerat',
  'cycle.prevMonth': 'Föregående månad',
  'cycle.nextMonth': 'Nästa månad',
  'cycle.inMonth': '{n} punkter denna månad',
  'cycle.inMonth.one': '{n} punkt denna månad',
  'cycle.markerLegend': 'Markeringar',
  'cycle.markerLegendNote': 'Läget står också utskrivet på varje punkt.',
  'occurrence.due': 'frist {date} · förberedelse från {from}',
  'occurrence.series': 'Återkommande aktivitet',
  'occurrence.service': 'Tjänst',
  'occurrence.kind': 'Slag',
  'occurrence.who': 'Vem gör det',
  'occurrence.responsibility': 'Vem utför',
  'occurrence.cadence': 'Varifrån intervallet kommer',
  'occurrence.review': 'Granskning',
  'occurrence.reviewRequired': 'En andra person granskar',
  'occurrence.reviewNone': 'Ingen granskning behövs',
  'occurrence.evidence': 'Underlag',
  'occurrence.evidenceRequired': 'Underlag måste kopplas innan den är klar',
  'occurrence.evidenceNone': 'Inget underlag krävs',
  'occurrence.byOn': '{name}, {date}',
  'occurrence.skippedReason': 'Överhoppad eftersom',
  'occurrence.acts': 'Vad som kan göras nu',
  'occurrence.start': 'Påbörja',
  'occurrence.started': 'Påbörjad.',
  'occurrence.prepare': 'Markera som förberedd',
  'occurrence.recordDelivery': 'Registrera vad klienten levererat',
  'occurrence.prepareNote': 'Anteckning (valfri)',
  'occurrence.prepared': 'Förberedd. Den väntar nu på granskning av någon annan.',
  'occurrence.done': 'Markera som klar',
  'occurrence.doneTracked': 'Markera som klar (följs endast)',
  'occurrence.doneNote': 'Klar.',
  'occurrence.approve': 'Godkänn',
  'occurrence.reviewNoteOptional': 'Granskningsanteckning (valfri)',
  'occurrence.approved': 'Godkänd och klar.',
  'occurrence.sendBack': 'Skicka tillbaka',
  'occurrence.sendBackNote': 'Varför skickas den tillbaka?',
  'occurrence.sentBack': 'Tillbakaskickad till förberedelse.',
  'occurrence.withdraw': 'Dra tillbaka min förberedelse',
  'occurrence.withdrawn': 'Tillbakadragen.',
  'occurrence.skip': 'Hoppa över',
  'occurrence.findings': 'Iakttagelser från den här punkten',
  'occurrence.findingsNone': 'Ingen iakttagelse har registrerats från den här punkten.',
  'occurrence.raiseFinding': 'Registrera en iakttagelse',
  'skip.ask': 'Begär att hoppa över',
  'skip.basis': 'På vilken grund',
  'skip.basis.client': 'Klienten har godkänt det',
  'skip.basis.board': 'Det rapporteras till styrelsen',
  'skip.agreedBy': 'Vem hos klienten som godkände',
  'skip.agreedOn': 'När',
  'skip.boardReport': 'I vilken rapport till styrelsen',
  'skip.boardReportHint': 'Till exempel: den årliga compliancerapporten 2026',
  'skip.twoPeople': 'Den här punkten kräver granskning eller underlag, så att hoppa över den kräver två personer: en annan granskare än du bekräftar det.',
  'skip.askSubmit': 'Begär att den hoppas över',
  'skip.asked': 'Begärt. En annan granskare än du bekräftar eller avböjer det.',
  'skip.confirm': 'Bekräfta att den hoppas över',
  'skip.withdraw': 'Dra tillbaka eller avböj',
  'skip.withdrawNote': 'Anteckning (frivillig)',
  'skip.withdrawn': 'Begäran att hoppa över är tillbakadragen.',
  'skip.basisLine.board': 'rapporteras till styrelsen i {report}',
  'skip.basisLine.client': 'godkänt av {name} hos klienten den {date}',
  'skip.askedLine': 'begärt av {name} den {when}: {reason} ({basis})',
  'skip.factAsked': 'Begärt att hoppa över, väntar på en granskare',
  'skip.factSkipped': 'Begärt att hoppa över',
  'skip.factConfirmed': 'Bekräftat av',
  'occurrence.move': 'Flytta den här punkten',
  'occurrence.moveTo': 'Ny frist',
  'occurrence.moveReason': 'Varför den flyttas',
  'occurrence.moveNote': 'Bara den här punkten flyttas; aktiviteten behåller sin regel. Vem som flyttade den, när och varför sparas med den och i historiken.',
  'occurrence.moveUntil': 'Som längst till {date}: en punkt flyttas inom sin egen period, före nästa punkt i aktiviteten, och aldrig förbi planeringshorisonten eller tjänstens slut.',
  'occurrence.moveSubmit': 'Flytta den',
  'occurrence.movedDone': 'Flyttad.',
  'occurrence.movedFact': 'Flyttad',
  'occurrence.movedLine': 'från {date}, av {name} den {when}: {reason}',
  'occurrence.movedBackLine': 'tillbaka till regelns datum ({date}), av {name} den {when}: {reason}',
  'occurrence.skipReason': 'Varför hoppas den över?',
  'occurrence.skipped': 'Överhoppad.',
  'occurrence.youPrepared': 'Du förberedde den. En annan granskare än du granskar den.',
  'occurrence.awaitingReview': 'Väntar på granskning av en annan granskare än {name}.',
  'occurrence.notThePreparer': 'Du får granska: du är inte den som förberedde ({name}).',
  'occurrence.needsEvidence': 'Punkten behöver kopplat underlag innan den kan bli klar.',
  'occurrence.readerOnly': 'Du läser hos klienten; du agerar inte här.',
  'occurrence.databaseDecides': 'Vem som agerade registreras av databasen utifrån din session; samma person förbereder och granskar aldrig samma punkt.',
  'occurrence.waitingTitle': 'Väntar på klienten',
  'occurrence.assignments': 'Vem som förväntas agera',
  'occurrence.preparer': 'Förberedare',
  'occurrence.reviewer': 'Granskare',
  'occurrence.assignNote': 'En tilldelning anger vem som ska agera; vem som faktiskt agerade registreras när det sker. En granskare behöver granskarrollen här.',
  'occurrence.checklist': 'Checklista',
  'occurrence.checklistNone': 'Inga rader i checklistan.',
  'occurrence.stepAdd': 'Ny rad',
  'occurrence.stepSubmit': 'Lägg till',
  'occurrence.stepRemove': 'Ta bort',
  'occurrence.stepDoneBy': 'bockad av {name}',
  'occurrence.firstLineNote': 'Arbete i första linjen: tills registren för uppgifter på kundnivå är byggda är underlaget en sammanfattning av stickprovet (antal per riskklass, avvikelser per slag), aldrig kundakterna, och den som laddar upp anger att det inte innehåller uppgifter på kundnivå.',
  'series.status': 'Status',
  'series.status.active': 'aktiv',
  'series.status.paused': 'pausad',
  'series.status.retired': 'avslutad',
  'series.rule': 'Hur ofta',
  'series.from': 'Från',
  'series.roll': 'På en helg',
  'series.prepDays': 'Dagar för förberedelse',
  'series.hours': 'Uppskattade timmar',
  'series.hoursHint': 'En uppskattning för planering, aldrig en tidrapport.',
  'series.noEstimate': 'ingen uppskattning',
  'series.basis': 'Varför den finns',
  'series.next': 'Nästa',
  'series.lastDone': 'Senast klar',
  'series.notYet': 'inte ännu',
  'series.ruleRaw': 'Regel (RFC 5545)',
  'series.ruleHint': 'Till exempel FREQ=YEARLY;BYMONTH=11;BYMONTHDAY=30. En ändrad regel stryker bara planerade framtida punkter, med skälet.',
  'series.expertRule': 'Ändra regeln (expert)',
  'series.change': 'Ändra ansvarsfördelningen',
  'series.saved': 'Sparat. {n} planerade punkter justerades; arbetsprocessen daterar resten.',
  'series.saved.one': 'Sparat. {n} planerad punkt justerades; arbetsprocessen daterar resten.',
  'series.performer': 'Kontakt hos klienten som utför',
  'series.recorder': 'Medlem som registrerar leveransen',
  'series.appliesFromNext': 'En ändrad ansvarsfördelning gäller från nästa planerade punkt; påbörjade punkter behåller sin.',
  'series.pause': 'Pausa',
  'series.resume': 'Återuppta',
  'series.retire': 'Avsluta',
  'series.upcoming': 'Dess nästa punkter',
  'series.noneUpcoming': 'Ingen punkt i den här aktiviteten är daterad.',
  'series.chooseService': 'Välj en tjänst',
  'series.title': 'Rubrik',
  'series.line': 'Linje',
  'series.ruleReads': 'Regeln lyder: {rule}.',
  'series.create': 'Lägg till aktiviteten',
  'series.whenTitle': 'När den infaller',
  'series.whenNote': 'Ett nytt datum gäller de planerade punkterna från i dag; påbörjade punkter behåller sitt. Vill du flytta en enda punkt, öppna den.',
  'series.whenSave': 'Spara datumet',
  'series.whenNoPlain': 'Den här aktivitetens regel har ingen enkel form. Den kan ändras under expertvalet nedan.',
  'series.followCalendar': 'Följ kalendern i stället för {anchor}: en regel som skrivs här följer inte längre det datumet',
  'series.ruleSave': 'Spara regeln',
  'series.daySetForClient': 'dagen satt för den här klienten',
  'series.expertRuleNew': 'Skriv regeln själv (expert)',
  'template.label.baseline': 'Olivias förslag, ännu inte antaget av er regelbevakning',
  'template.label.adopted': 'Antagen av regelbevakningen',
  'template.label.adoptedBy': 'Antagen av {name}',
  'template.label.draft': 'Utkast: kan inte tillämpas förrän regelbevakningen bekräftat det',
  'template.label.retired': 'Avvecklad',
  'template.intro': 'En mall skapar återkommande aktiviteter bara för de tjänster klienten har. Punkter som erbjuds utan bock hålls utanför om du inte bockar för dem. Varje punkt anger vem som gör den och varifrån intervallet kommer.',
  'template.none': 'Ingen mall finns i biblioteket.',
  'template.choose': 'Mall',
  'template.otherType': 'för en annan klienttyp',
  'template.noneFits': 'Ingen mall passar klientens institutstyp och storlek; kontrollera profilen först.',
  'template.notApplicable': 'Mallen är ett utkast och kan inte tillämpas.',
  'template.preparerAll': 'Förberedare för alla punkter nedan',
  'template.reviewerAll': 'Granskare för alla punkter nedan',
  'template.unownedNote': 'En punkt utan förberedare visas som att den saknar ansvarig när fristen är inom 30 dagar.',
  'template.include': 'Tillämpa',
  'template.includeItem': 'Tillämpa ”{item}”',
  'template.firstDates': 'Intervall och första datum',
  'template.appliedAs': 'tillämpad som serie {code}',
  'template.notOffered': 'erbjuds inte: tjänsten saknas',
  'template.offeredUnticked': 'erbjuds utan bock',
  'template.sourceNote': 'Källhänvisning',
  'template.performerDefault': 'tjänstens ansvariga kontakt',
  'template.defaultFor': 'Bibliotekets förval för leveransformen ”{model}”.',
  'template.clientAct': 'Klientens eget beslut oavsett leveransform: klienten utför det, och byrån för in det med underlag.',
  'template.byModel': '{model}: {who}',
  'template.apply': 'Tillämpa de förbockade punkterna',
  'template.changeDate': 'Ett annat datum för den här klienten',
  'template.dateChanged': 'Datumet ändrat för den här klienten',
  'template.dateFor': 'Datumet för ”{item}”',
  'template.dateBack': 'Tillbaka till förslaget',
  'template.applied': 'Tillämpat: skapade aktiviteter {created}, utelämnade {left}, redan tillämpade {already}. Inom kort lägger bakgrundsprocessen ut daterade punkter för de kommande 18 månaderna.',
  'services.intro': 'Vad byrån gör för klienten, hur, och vem hos klienten som behåller ansvaret. Tjänsterna avgör vilka mallpunkter som gäller och vilka kort hemsidorna visar.',
  'services.none': 'Ingen tjänst är registrerad för klienten ännu.',
  'services.service': 'Tjänst',
  'services.how': 'Hur',
  'services.responsible': 'Ansvarig hos klienten',
  'services.firmLead': 'Ansvarig på byrån',
  'services.series': 'Aktiva aktiviteter',
  'services.trackedOnly': 'följs endast',
  'services.trackedOnlyNote': 'En tjänst som bara följs: arbetet görs i andra verktyg, och dess datum och dokument hålls här.',
  'services.since': 'sedan {date}',
  'services.endedOn': 'avslutad {date}',
  'services.ended': 'avslutad',
  'services.noOwner': 'ej angiven',
  'services.responsibleLeft': 'Den ansvariga personen har slutat',
  'services.responsibleMissing': 'Ingen hos klienten är angiven som ansvarig',
  'services.line': 'från {from} · ansvarig på byrån {lead} · ansvarig hos klienten: {owner}',
  'services.kind': 'Tjänstetyp',
  'services.chooseKind': 'Välj en tjänstetyp',
  'services.delivery': 'Leveransform',
  'services.lineDefault': 'Typens standard: {line}',
  'services.lineFromKind': 'Enligt tjänstetypen',
  'services.startOn': 'Börjar',
  'services.description': 'Beskrivning',
  'services.contractRef': 'Avtalsreferens',
  'services.interimRole': 'Titel hos klienten (interim)',
  'services.independenceNote': 'Notering om oberoende',
  'services.add': 'Lägg till tjänsten',
  'services.addOpen': 'Lägg till en tjänst',
  'services.added': 'Tjänsten är tillagd.',
  'services.leadOnly': 'Bara klientansvarig registrerar klientens tjänster.',
  'services.edit': 'Ändra',
  'services.period': 'Period',
  'services.commercial': 'Kommersiell grund och planerade timmar',
  'services.whoDoesWhat': 'Ansvarsfördelning',
  'services.retained.title': 'Stannar hos klienten',
  'services.retained.lead': 'Vem som än gör resten av arbetet kan klienten inte lägga ut de här uppgifterna. Besluten om enskilda kunder stannar i klientens eget ärendearbete.',
  'services.retained.inCycle': 'I klientens årshjul som dess egna beslut, som byrån för in med underlag:',
  'services.retained.missing': 'Ännu inte i klientens årshjul: {items}. En klientansvarig lägger till dem genom att tillämpa en mall som har dem.',
  'services.retained.none': 'De godkännanden som återkommer varje år finns inte i klientens årshjul.',
  'services.retained.source': 'Källa',
  'services.cadence': 'Intervall',
  'services.noSeries': 'Ingen aktivitet löper för tjänsten ännu.',
  'services.notApplied': 'Inte tillämpat från mallen: {items}',
  'services.effort': 'Uppskattat: {hours} timmar i månaden i snitt det kommande året (punkter utan uppskattning: {missing}). Uppskattningar, inte rapporterad tid.',
  'services.effortNone': 'Inga uppskattade timmar registrerade (punkter det kommande året som saknar en: {missing}).',
  'services.whoNote': 'Varje rad där klienten utför anger kontaktpersonen som utför. En ändring gäller från nästa planerade punkt och registreras i händelseloggen.',
  'services.end': 'Avsluta tjänsten',
  'services.endNote': 'När en tjänst avslutas pausas dess aktiviteter med skälet; inget raderas.',
  'services.endOn': 'Slutar',
  'services.endedPaused': 'Avslutad. {n} aktiviteter är pausade.',
  'services.endedPaused.one': 'Avslutad. {n} aktivitet är pausad.',
  'services.openCycle': 'Dess punkter i årshjulet',
  'services.exportLater': 'Export av ansvarsfördelning: senare',
  'independence.person': '{name} är utsedd i {lines} hos klienten ({services}).',
  'independence.firm': 'Byrån sköter internrevisionen ({audit}) och även arbete i första eller andra linjen ({others}) hos klienten: registrera en notering om oberoende på internrevisionstjänsten.',
  'independence.firmNoted': 'Byrån sköter internrevisionen ({audit}) och även arbete i första eller andra linjen ({others}) hos klienten; en notering om oberoende finns.',
  'independence.neverRefuses': 'En varning, aldrig ett stopp.',
  'profile.edit': 'Ändra profilen',
  'profile.notYet': 'Ingen profil är registrerad ännu.',
  'profile.identity': 'Identitet',
  'profile.legalName': 'Firma',
  'profile.orgNumber': 'Organisationsnummer',
  'profile.lei': 'LEI-kod',
  'profile.leiHint': '20 tecken',
  'profile.perimeter': 'Verksamhetsområde',
  'profile.institutionTypes': 'Institutstyper',
  'profile.addType': 'Lägg till en typ',
  'profile.chooseType': 'Välj en typ',
  'profile.removeType': 'Ta bort typen',
  'profile.typeNeeded': 'Minst en institutstyp behövs.',
  'profile.sizeBand': 'Storleksklass',
  'profile.notRecorded': 'ej registrerat',
  'profile.supervisors': 'Tillsynsmyndigheter',
  'profile.commaSeparated': 'åtskilda med kommatecken',
  'profile.jurisdictions': 'Jurisdiktioner',
  'profile.jurisdiction': 'Jurisdiktion',
  'profile.chooseJurisdiction': 'Välj en jurisdiktion',
  'profile.presence': 'Närvaro',
  'profile.addJurisdiction': 'Lägg till en jurisdiktion',
  'profile.crossBorderNote': 'Gränsöverskridande verksamhet bevakas men tillämpas inte.',
  'profile.oneHome': 'Bara en jurisdiktion kan vara hemland.',
  'profile.jurisdictionWatched': '{code} (gränsöverskridande, bevakas)',
  'profile.noPerimeter': 'Inget verksamhetsområde registrerat ännu',
  'profile.licences': 'Tillstånd',
  'profile.licenceKind': 'Tillstånd',
  'profile.licenceSince': 'Sedan',
  'profile.addLicence': 'Lägg till ett tillstånd',
  'profile.anchors': 'Hållpunkter',
  'profile.fyeMonth': 'Räkenskapsåret slutar: månad',
  'profile.fyeDay': 'och dag',
  'profile.agmMonth': 'Månad för årsstämma',
  'profile.licenceDate': 'Tillståndsdag',
  'profile.anchorsNote': 'Punkter som utgår från dessa datum följer med: en ändring här daterar om deras planerade punkter.',
  'profile.anchorsLine': 'räkenskapsårets slut {fye} · årsstämma månad {agm} · tillstånd {licence}',
  'profile.business': 'Verksamhet',
  'profile.employees': 'Anställda',
  'profile.customers': 'Kunder (antalsintervall)',
  'profile.description': 'Beskrivning',
  'profile.products': 'Produkter',
  'profile.channels': 'Kanaler',
  'profile.keySystems': 'Viktiga system',
  'profile.featuresLater': 'Vad verksamheten gör i termer av träffbedömning (betalningar, distansregistrering och liknande) registreras med regelsamlingen, som inte är byggd ännu.',
  'profile.savedReresolved': 'Sparat. {n} aktiviteter följde med de nya datumen; arbetsprocessen daterar om deras planerade punkter.',
  'profile.savedReresolved.one': 'Sparat. {n} aktivitet följde med de nya datumen; arbetsprocessen daterar om dess planerade punkter.',
  'profile.leadOnly': 'Bara klientansvarig ändrar klientens profil.',
  'profile.servicesLink': 'Tjänster och ansvarsfördelning →',
  'profile.history': 'Historik för profil, tjänster och kontakter',
  'contacts.title': 'Kontakter hos klienten',
  'contacts.none': 'Ingen kontakt är registrerad ännu.',
  'contacts.name': 'Namn',
  'contacts.titleField': 'Titel',
  'contacts.role': 'Roll hos klienten',
  'contacts.reach': 'E-post och telefon',
  'contacts.email': 'E-post',
  'contacts.phone': 'Telefon',
  'contacts.board': 'i styrelsen',
  'contacts.retired': 'inte längre kontakt',
  'contacts.stillNamed': 'nämns fortfarande på {n} ställen',
  'contacts.stillNamed.one': 'nämns fortfarande på {n} ställe',
  'contacts.whereNamed': 'Var, och vem som tar över',
  'contacts.stillNamedIntro': '{name} är inte längre kontakt och nämns fortfarande här:',
  'contacts.staysAsRecorded': 'Dessa visar vad som hände och står kvar som de är:',
  'contacts.successor': 'Vem som tar över',
  'contacts.nameSuccessor': 'Ange vem som tar över',
  'contacts.leadNamesSuccessor': 'En klientansvarig anger vem som tar över.',
  'contacts.successorNamed': 'Sparat: den som tar över är angiven.',
  'contacts.named.service': 'ansvarig för tjänsten',
  'contacts.named.series': 'utför den återkommande aktiviteten',
  'contacts.named.action': 'äger åtgärden',
  'contacts.named.occurrence': 'utför den påbörjade punkten',
  'contacts.named.waiting': 'byrån väntar på personen för',
  'contacts.named.basis': 'gav instruktionen för grunden',
  'contacts.add': 'Lägg till en kontakt',
  'contacts.edit': 'Ändra',
  'contacts.retire': 'Inte längre kontakt',
  'contacts.minimal': 'Håll det till vad arbetet kräver: namn, roll och ett sätt att nå personen.',
  'actions.title': 'Åtgärder och iakttagelser',
  'actions.new': 'Ny åtgärd',
  'actions.create': 'Registrera åtgärden',
  'actions.titleField': 'Rubrik',
  'actions.detail': 'Detaljer',
  'actions.source': 'Källa',
  'actions.sourceRef': 'Källreferens',
  'actions.sourceOccurrence': 'Årshjulspunkt den kom från',
  'actions.sourceOccurrenceHint': 'En kod som CY-2026-041, valfritt',
  'actions.severity': 'Allvarlighetsgrad',
  'actions.severityBasis': 'Grund för allvarlighetsgraden',
  'actions.severityBasisHint': 'En regel eller ett skäl som en läsare kan kontrollera, till exempel ”byråns regel: en kontrollbrist för högriskkunder är hög”.',
  'actions.owner': 'Ägare',
  'actions.ownerMembers': 'Medlemmar på byrån',
  'actions.ownerContacts': 'Kontakter hos klienten',
  'actions.due': 'Frist',
  'actions.noDue': 'ingen frist',
  'actions.status': 'Läge',
  'actions.filter.status': 'Visa',
  'actions.filter.status.open': 'Öppna',
  'actions.filter.status.closed': 'Stängda eller avbrutna',
  'actions.filter.status.all': 'Alla',
  'actions.filter.status.to_close': 'Verifierade, att stänga',
  'actions.filter.overdueOnly': 'Bara försenade',
  'actions.showing': 'visar {n} av {total} · i registret: öppna {open}, verifierade att stänga {toClose}, stängda {closed}',
  'actions.followedUpBy': 'följs upp av {name}',
  'actions.followUpLeft': '{name}, som inte längre arbetar med klienten',
  'actions.awaitingClose': 'verifierad; en klientansvarig eller granskare stänger den',
  'actions.followUp': 'Följs upp på byrån av',
  'actions.followUpHint': 'Medlemmen som påminner klienten och ser den under Vad väntar på dig.',
  'actions.followUpMe': 'Jag (den som registrerar den)',
  'actions.verificationRefused': 'Verifieringen avvisad',
  'actions.filter.all': 'Alla',
  'actions.moved': 'Flyttade',
  'actions.movedAny': 'Alla',
  'actions.movedDays': 'de senaste {n} dagarna',
  'actions.movedNote': '”Flyttad” betyder att frist, läge, ägare eller allvarlighetsgrad ändrats under perioden, läst ur händelseloggen. Andra ändringar räknas inte.',
  'actions.movedCol': 'Vad som flyttats',
  'actions.none': 'Ingen åtgärd motsvarar urvalet.',
  'actions.noneMoved': 'Ingen åtgärd har flyttats de senaste {n} dagarna.',
  'actions.awaitingVerification': 'väntar på verifiering',
  'actions.pipeline': 'Var åtgärden står',
  'actions.created': 'Registrerad',
  'actions.remediated': 'Åtgärdad',
  'actions.verified': 'Verifierad',
  'actions.cancelledReason': 'Avbruten eftersom',
  'actions.edit': 'Ändra',
  'actions.start': 'Påbörja',
  'actions.started': 'Påbörjad.',
  'actions.remediate': 'Registrera åtgärdandet',
  'actions.remediationNote': 'Vad gjordes?',
  'actions.remediatedNote': 'Registrerat. En annan granskare än du verifierar det.',
  'actions.verify': 'Verifiera',
  'actions.verificationNote': 'Verifieringsanteckning (valfri)',
  'actions.verifiedNote': 'Verifierad.',
  'actions.refuse': 'Inte verifierad',
  'actions.refuseNote': 'Vad saknas fortfarande?',
  'actions.refusedNote': 'Tillbakaskickad för vidare arbete.',
  'actions.close': 'Stäng',
  'actions.closedNote': 'Stängd.',
  'actions.cancel': 'Avbryt åtgärden',
  'actions.cancelReason': 'Varför avbryts den?',
  'actions.cancelledNote': 'Avbruten.',
  'actions.youRemediated': 'Du registrerade åtgärdandet; en annan granskare än du verifierar det.',
  'actions.reviewerVerifies': 'En medlem med granskarrollen verifierar det.',
  'documents.upload': 'Ladda upp en fil',
  'documents.uploading': 'Laddar upp…',
  'documents.file': 'Fil',
  'documents.titleField': 'Rubrik',
  'documents.kind': 'Slag',
  'documents.statement': 'Vad filen innehåller',
  'documents.statementNone': 'Filen innehåller inga uppgifter på kundnivå (inga uppgifter om klientens egna kunder, en och en).',
  'documents.statementCustomer': 'Filen innehåller uppgifter på kundnivå.',
  'documents.statementNote': 'Uppgiften är din som laddar upp; Olivia kan inte kontrollera den, och säger det.',
  'documents.firstLine': 'Underlag för arbete i första linjen måste vara en sammanfattning utan uppgifter på kundnivå.',
  'documents.thisClientOnly': 'Filerna sparas bara för den här klienten; inget delas med andra klienter.',
  'documents.none': 'Inget dokument är sparat för klienten ännu.',
  'documents.noneOfKind': 'Inget dokument av det här slaget.',
  'documents.linkedTo': 'Kopplat till',
  'documents.uploaded': 'Uppladdat',
  'documents.notLinked': 'inte kopplat',
  'documents.versionOf': 'ny version av {code}',
  'documents.retired': 'avvecklat',
  'documents.statedNone': 'innehåller inga uppgifter på kundnivå, enligt {name} vid uppladdningen',
  'documents.statedBy': 'enligt {name} vid uppladdningen, {date}',
  'documents.sha256': 'Fingeravtryck (SHA-256)',
  'documents.download': 'Ladda ner',
  'documents.newVersion': 'Ladda upp en ny version',
  'documents.retire': 'Avveckla',
  'documents.links': 'Var det används',
  'documents.linkedBy': 'kopplat av {name}, {date}',
  'documents.linkSubject': 'Koppla till',
  'documents.linkCode': 'Kod',
  'documents.linkCodeHint': 'till exempel CY-2026-041',
  'documents.purpose': 'Som',
  'documents.link': 'Koppla',
  'documents.linked': 'Kopplat.',
  'documents.evidence': 'Underlag och filer',
  'documents.noneLinked': 'Inget dokument är kopplat ännu.',
  'documents.unlink': 'Ta bort kopplingen',
  'documents.uploadHere': 'Ladda upp en fil här',
  'documents.linkExisting': 'Koppla ett dokument som klienten redan har',
  'documents.chooseDocument': 'Dokument',
  'documents.choose': 'Välj ett dokument',
  'bytes.b': '{n} byte',
  'bytes.kb': '{n} kB',
  'bytes.mb': '{n} MB',
  'comments.title': 'Kommentarer',
  'comments.none': 'Inga kommentarer.',
  'comments.retracted': 'Tillbakadragen av skribenten.',
  'comments.retract': 'Dra tillbaka',
  'comments.add': 'Kommentar',
  'comments.submit': 'Lägg till kommentaren',
  'history.title': 'Historik',
  'history.none': 'Inget registrerat ännu.',
  'waiting.since': 'sedan {date}: {who}, {what}',
  'waiting.clear': 'Klienten har svarat',
  'waiting.markOpen': 'Markera som väntar på klienten…',
  'waiting.mark': 'Markera som väntande',
  'waiting.contact': 'Tillfrågad kontakt',
  'waiting.chooseContact': 'Välj en kontakt',
  'waiting.what': 'Vad som efterfrågades',
  'waiting.askedOn': 'Frågat den (i dag om tomt)',
  'waiting.requestsLater': 'Väntan stoppar inte klockan: en försenad punkt förblir försenad. Senare kommer väntan att kunna peka på en begäran i registret över begäranden.',
  'basis.title': 'Kunduppgifter (underlag)',
  'basis.nothingStored': 'Inga uppgifter på kundnivå sparas i Olivia ännu: registren som håller dem, med fältkryptering, loggad läsning och lagringstider, kommer senare. Det som registreras här är klientens underlag för dem.',
  'basis.youHold': 'Du har här: {roles}; {customer}.',
  'basis.withRole': 'inklusive behörigheten till kunduppgifter',
  'basis.withoutRole': 'inte behörigheten till kunduppgifter',
  'basis.none': 'Inget underlag är registrerat för klienten. Utan ett bekräftat underlag kan inga uppgifter på kundnivå hållas för den.',
  'basis.forService': 'för tjänst {service}',
  'basis.state.live': 'gällande',
  'basis.state.awaiting': 'väntar på bekräftelse av en andra person',
  'basis.state.retired': 'avvecklat',
  'basis.state.notYetEffective': 'bekräftat, gäller ännu inte',
  'basis.state.reviewPassed': 'översynsdatum passerat: inget nytt får hållas med stöd av det',
  'basis.purposes': 'Ändamål',
  'basis.categories': 'Kategorier och fält',
  'basis.notCovered': 'Omfattas inte',
  'basis.suspicionNotAvailable': 'innehåll som rör misstankar (inte tillgängligt)',
  'basis.legalBasis': 'Rättslig grund',
  'basis.legalBasisHint': 'Som klienten, den personuppgiftsansvarige, anger den.',
  'basis.asTheClientStates': '”{text}” (som klienten anger den)',
  'basis.purposeStatement': 'Ändamål i ord',
  'basis.instruction': 'Instruktion',
  'basis.instructionHint': 'Klausulen i personuppgiftsbiträdesavtalet eller den skriftliga instruktionen, sparad som dokument först.',
  'basis.instructionFrom': '{doc} från {who}',
  'basis.dpia': 'Konsekvensbedömning',
  'basis.dpiaDocument': 'Klientens konsekvensbedömning (ett dokument)',
  'basis.dpiaNone': 'ingen: ange klientens skäl nedan',
  'basis.dpiaNote': 'Klientens angivna skäl till att ingen konsekvensbedömning behövs',
  'basis.retention': 'Lagringstid',
  'basis.retentionLine': '{category}: {months} månader efter att arbetet avslutats',
  'basis.retentionFor': 'Lagringstid för {category} (månader)',
  'basis.retentionHint': '1 till 60 månader efter att arbetet avslutats; förslag 24.',
  'basis.ai': 'AI',
  'basis.aiAllowed': 'får gå till Claude, enligt de fyra villkoren nedan',
  'basis.aiNotAllowed': 'inte tillåtet',
  'basis.aiCheckbox': 'Klienten tillåter att uppgifter på kundnivå enligt detta underlag går till Claude (Anthropics API)',
  'basis.period': 'Gäller',
  'basis.periodLine': 'från {from}, ses över senast {review}',
  'basis.recorded': 'Registrerat',
  'basis.confirmedBy': 'bekräftat av {name}, {date}',
  'basis.retired': 'Avvecklat',
  'basis.termsFixed': 'Villkoren ligger fast från det att de registreras; en ändring är ett nytt underlag.',
  'basis.confirm': 'Bekräfta underlaget',
  'basis.confirmed': 'Bekräftat. Underlaget gäller från sitt startdatum.',
  'basis.youRecorded': 'Du registrerade det; en andra medlem (klientansvarig eller granskare) bekräftar det.',
  'basis.retire': 'Avveckla',
  'basis.record': 'Registrera ett underlag',
  'basis.recordIntro': 'Registrera vad klienten har instruerat och på vilken grund. Bara de fält varje uppgift kräver; inget på kundnivå sparas innan en andra person bekräftat det.',
  'basis.recordSubmit': 'Registrera underlaget',
  'basis.recordedNote': 'Registrerat. Det gäller inte förrän en andra medlem bekräftat det.',
  'basis.secondPerson': 'Att registrera kräver klientansvarig; att bekräfta kräver en andra medlem som är klientansvarig eller granskare.',
  'basis.leadRecords': 'Klientansvarig registrerar ett underlag; en andra medlem bekräftar det.',
  'basis.service': 'Tjänst det gäller',
  'basis.controller': 'Vem hos klienten som gav instruktionen',
  'basis.fields': 'Fält per kategori och ändamål',
  'basis.fieldsNote': 'Bocka bara för det som varje uppgift kräver. Ett stickprov som behöver referensen, riskklassen och kontrollerna håller aldrig ett namn.',
  'basis.notAvailableNote': 'Innehåll som rör misstankar och särskilda kategorier av personuppgifter har inga fält alls.',
  'basis.effectiveFrom': 'Gäller från',
  'basis.reviewOn': 'Ses över senast',
  'basis.reviewHint': 'Högst ett år efter att det börjat gälla.',
  'basis.needsDocument': 'Ladda först upp instruktionen (och konsekvensbedömningen, om det finns en) som dokument.',
  'basis.holders': 'Vem som har behörighet till kunduppgifter',
  'basis.noHolders': 'Ingen har den.',
  'basis.roleRule': 'Medlemskap räcker inte: behörigheten till kunduppgifter är en andra behörighet, som bara klientansvarig ger, aldrig till sig själv, och bara medan ett bekräftat underlag gäller.',
  'basis.grantTo': 'Ge behörigheten till',
  'basis.grant': 'Ge behörigheten',
  'basis.grantNeedsLive': 'Behörigheten kan ges när ett underlag är bekräftat och gäller.',
  'basis.whereDataGoes': 'Vart klientens uppgifter får gå',
  'basis.class.public': 'Offentligt material',
  'basis.class.publicGoes': 'Bara offentligt regulatoriskt material, via den låsta OpenRouter-vägen eller Claude.',
  'basis.class.client': 'Klientuppgifter',
  'basis.class.clientClaude': 'Till Claude via Anthropics API (tillåtet av {name}).',
  'basis.class.clientNone': 'Till ingen modell: klientens AI-medgivande är avslaget, eller så skickar installationen inga klientuppgifter.',
  'basis.class.customer': 'Uppgifter på kundnivå',
  'basis.class.customerClaude': 'Får gå till Claude om personen också har behörighet till kunduppgifter.',
  'basis.class.customerNone': 'Till ingen modell.',
  'basis.class.conditions': 'Installationens tak: {ceiling} · klientens AI-medgivande: {allowance} · ett gällande underlag tillåter AI: {basis} · du har behörigheten: {role}',
  'basis.condYes': 'ja',
  'basis.condNo': 'nej',
  'basis.class.suspicion': 'Innehåll som rör misstankar',
  'basis.class.suspicionNever': 'Hålls inte i Olivia, och skickas aldrig till någon modell.',
  'onboarding.title': 'Uppsättning av klienten',
  'onboarding.steps': 'Stegen i uppsättningen',
  'onboarding.phase.setUp': 'Sätt upp',
  'onboarding.phase.start': 'Starta',
  'onboarding.step.client': 'Klient',
  'onboarding.step.perimeter': 'Verksamhetsområde',
  'onboarding.step.services': 'Tjänster',
  'onboarding.step.cycle': 'Årshjul',
  'onboarding.step.members': 'Medlemmar',
  'onboarding.step.customer': 'Kunduppgifter',
  'onboarding.step.review': 'Granska och starta',
  'onboarding.back': 'Tillbaka',
  'onboarding.next': 'Nästa',
  'onboarding.leadOnly': 'Bara klientansvarig sätter upp klienten; du kan läsa varje steg.',
  'onboarding.alreadyStarted': 'Klienten är redan startad ({status}); stegen finns kvar att se tillbaka på.',
  'onboarding.perimeterIntro': 'Vem klienten är och var den verkar. Verksamhetsområdet avgör vilka mallar som passar, och senare vilka regler som berör den.',
  'onboarding.servicesIntro': 'Registrera kontakterna först, sedan varje tjänst som byrån sköter åt klienten, med vem hos klienten som behåller ansvaret.',
  'onboarding.cycleIntro': 'Tillämpa mallen för klienttypen: bara punkterna för de valda tjänsterna erbjuds, var och en med vem som gör den och varifrån intervallet kommer.',
  'onboarding.cycleNeedsServices': 'Registrera en tjänst först: en mall tillämpas bara på de tjänster klienten har.',
  'onboarding.membersIntro': 'Granskat arbete kräver två personer: ge en andra medlem en arbetande roll, och granskarrollen till den som granskar.',
  'onboarding.customerQuestion': 'Kommer byrån att hålla uppgifter på kundnivå för klienten (till exempel akterna i ett stickprov)?',
  'onboarding.customerNo': 'Nej, inte nu',
  'onboarding.customerYes': 'Ja: registrera underlaget',
  'onboarding.customerRecorded': 'Registrerat: {codes}. En andra medlem bekräftar det på sidan',
  'onboarding.customerNote': 'Klientansvarig registrerar underlaget och en andra medlem bekräftar det; inget på kundnivå sparas före det, och i den här versionen inget alls.',
  'onboarding.done': 'klart',
  'onboarding.open': 'återstår',
  'onboarding.notNow': 'inte nu',
  'onboarding.reviewNote': 'När klienten startas blir den aktiv. Steg som återstår kan göras klart senare från klientens egna sidor.',
  'onboarding.start': 'Starta klienten',
  'onboarding.startNeeds': 'En profil med institutstyp och minst en tjänst behövs först.',
  'onboarding.toClient': 'Till klienten',
  'library.subtitle': 'Byråns mallar, tjänstetyper och arbetsfunktioner, inlästa från biblioteksfilerna och låsta med sina fingeravtryck.',
  'library.templates': 'Årshjulsmallar',
  'library.noTemplates': 'Ingen mall är inläst. Kör migreringarna, som läser in biblioteket.',
  'library.template': 'Mall',
  'library.forTypes': 'För',
  'library.anyType': 'alla klienttyper',
  'library.status': 'Status',
  'library.items': 'Punkter',
  'library.deskNote': 'Olivias föreslagna mallar kan tillämpas från första dagen. En medlem i regelbevakningen antar en mall efter att ha kontrollerat varje hänvisning mot de konsoliderade texterna; fram till dess är varje intervall och dag i den Olivias förslag.',
  'library.serviceKinds': 'Tjänstetyper',
  'library.functions': 'Arbetsfunktioner',
  'library.what': 'Vad byrån gör',
  'library.workFunctions': 'Arbetsfunktioner',
  'library.oliviaCode': 'tillagd av Olivia',
  'library.functionsNote': 'Vad en person gör, inte vad den får göra: funktioner ger inga behörigheter.',
  'library.description': 'Beskrivning',
  'library.baselineDecision': 'Varför den får tillämpas före antagandet',
  'library.adopted': 'Antagen',
  'library.retired': 'Avvecklad',
  'library.sha': 'Fingeravtryck (SHA-256)',
  'library.adopt': 'Anta för byrån',
  'library.adoptedNote': 'Antagen. Ditt namn registreras med den.',
  'library.retire': 'Avveckla',
  'library.deskOnly': 'En medlem i regelbevakningen antar eller avvecklar mallar.',
  'people.subtitle': 'Byråroller styr byråns egna saker; en klients innehåll kräver medlemskap hos den klienten.',
  'people.manage': 'Hantera',
  'people.grantRole': 'Ge byrårollen',
  'people.chooseRole': 'Välj en byråroll',
  'people.revokeRole': 'Återkalla',
  'people.notYourself': 'Ingen ändrar sina egna byråroller.',
  'people.clientRoles': 'Roller hos en klient',
  'people.requestGrant': 'Begär rollen',
  'people.grantNote': 'Inget ges förrän en andra byråadministratör, eller klientens ansvarige, bekräftat det.',
  'people.grantRequested': 'Begärt. Det väntar på en andra person.',
  'people.disable': 'Stäng av kontot',
  'people.enable': 'Slå på kontot',
  'clients.none': 'Ingen klient finns ännu.',
  'clients.notMember': 'du är inte medlem: bara namn, status och antal',
  'clients.markReal': 'Rätta: inte fiktiv',
  'clients.markFictional': 'Rätta: fiktiv',
  'clients.correctionReason': 'Varför märkningen rättas (sparas i byråns händelselogg)',
  'clients.adminOnly': 'Byråadministratörer och byråns granskare ser listan över klienter. Dina egna klienter finns i klientväljaren.',
  'clients.createRole': 'En partner eller en byråadministratör skapar en klient.',
  'clients.codeHint': 'Små bokstäver, siffror och bindestreck; det blir adressen och ändras aldrig.',
  'clients.createdOther': 'Skapad som {code}. Den första klientansvarige fortsätter uppsättningen inne hos klienten.',
  'members.granted': 'Rollen är given.',
  'members.needsSecond': 'Granskat arbete kräver en andra person här: en ensam arbetande medlem kan inte granska sitt eget arbete.',
  'ai.customerCeilingOn': 'Uppgifter på kundnivå får gå till Claude bara om dessutom klientens bekräftade underlag tillåter det och personen har behörighet till kunduppgifter.',
  'invites.grantNote': 'Klientroller kan bekräftas av en klientansvarig för var och en av klienterna; byråroller bara av en andra byråadministratör.',
  'state.paused': 'aktiviteten pausad',
  'cycle.monthCut': 'Visar {n} av månadens {total} punkter. Snäva in filtren för att se resten.',
  'cycle.yearCut': 'Visar {n} av de {total} punkterna under de tolv månaderna. Välj en tjänst för att se resten.',
  'series.reviewStays': 'När en aktivitet kräver granskning eller underlag fortsätter den att göra det: ingen stänger ensam av den andra granskningen. Vill du ändra vad aktiviteten är, avsluta den med ett skäl och lägg till en ny.',
  'people.adminRequested': 'Begärt. Rollen ges först när en andra byråadministratör bekräftar den.',
  'invites.kind.adminGrant': 'Rollen byråadministratör för en befintlig person',

  // ── The audit trail in words, and the notes on a cycle item (expert review O-C) ─────────────────────────────────
  'auditAction.insert': 'Lade till',
  'auditAction.update': 'Ändrade',
  'auditAction.delete': 'Tog bort',
  'auditAction.other': 'Registrerade en ändring',
  'auditAction.job': 'Schemalagt arbete av systemet',
  'auditAction.action.create': 'Registrerade en åtgärd',
  'auditAction.action.update': 'Ändrade en åtgärd',
  'auditAction.action.start': 'Började arbeta med en åtgärd',
  'auditAction.action.remediate': 'Registrerade att åtgärden är genomförd',
  'auditAction.action.verify': 'Verifierade genomförandet',
  'auditAction.action.verification_refused': 'Godtog inte genomförandet',
  'auditAction.action.close': 'Stängde en åtgärd',
  'auditAction.action.cancel': 'Avbröt en åtgärd',
  'auditAction.action.waiting_on_client': 'Markerade som väntande på klienten',
  'auditAction.action.waiting_cleared': 'Registrerade att klienten har svarat',
  'auditAction.ai_allowance.decide': 'Beslutade om AI-medgivandet',
  'auditAction.ai_allowance.confirm': 'Bekräftade AI-medgivandet',
  'auditAction.basis.record': 'Registrerade ett underlag för kunduppgifter',
  'auditAction.basis.confirm': 'Bekräftade ett underlag för kunduppgifter',
  'auditAction.basis.retire': 'Avvecklade ett underlag för kunduppgifter',
  'auditAction.basis.replace': 'Ersatte ett underlag för kunduppgifter',
  'auditAction.comment.add': 'Lade till en kommentar',
  'auditAction.comment.retract': 'Drog tillbaka en kommentar',
  'auditAction.contact.add': 'Lade till en kontakt',
  'auditAction.contact.update': 'Ändrade en kontakt',
  'auditAction.contact.retire': 'Markerade en kontakt som inte längre kontakt',
  'auditAction.cycle.add_series': 'Lade till en återkommande aktivitet',
  'auditAction.cycle.apply_template': 'Tillämpade en mall',
  'auditAction.cycle.change_series': 'Ändrade en återkommande aktivitet',
  'auditAction.cycle.start': 'Började med en årshjulspunkt',
  'auditAction.cycle.prepare': 'Förberedde en årshjulspunkt',
  'auditAction.cycle.review_approve': 'Godkände granskningen',
  'auditAction.cycle.review_send_back': 'Skickade tillbaka punkten',
  'auditAction.cycle.withdraw': 'Drog tillbaka förberedelsen',
  'auditAction.cycle.done': 'Markerade en årshjulspunkt som klar',
  'auditAction.cycle.skip': 'Hoppade över en årshjulspunkt',
  'auditAction.cycle.assign': 'Fördelade en årshjulspunkt',
  'auditAction.cycle.waiting_on_client': 'Markerade som väntande på klienten',
  'auditAction.cycle.waiting_cleared': 'Registrerade att klienten har svarat',
  'auditAction.cycle.step_add': 'Lade till ett steg i checklistan',
  'auditAction.cycle.step_tick': 'Bockade av ett steg i checklistan',
  'auditAction.cycle.step_untick': 'Tog bort bocken för ett steg i checklistan',
  'auditAction.cycle.step_remove': 'Tog bort ett steg ur checklistan',
  'auditAction.cycle.move': 'Flyttade en årshjulspunkt',
  'auditAction.cycle.skip_request': 'Begärde att en årshjulspunkt hoppas över',
  'auditAction.cycle.skip_confirm': 'Bekräftade att en årshjulspunkt hoppas över',
  'auditAction.cycle.skip_withdraw': 'Drog tillbaka eller avböjde en begäran att hoppa över',
  'auditAction.document.upload': 'Laddade upp ett dokument',
  'auditAction.document.update': 'Ändrade ett dokuments rubrik eller typ',
  'auditAction.document.retire': 'Avvecklade ett dokument',
  'auditAction.document.link': 'Kopplade ett dokument',
  'auditAction.document.unlink': 'Tog bort en dokumentkoppling',
  'auditAction.profile.update': 'Ändrade klientprofilen',
  'auditAction.profile.confirm_year_end': 'Bekräftade räkenskapsårets slut',
  'auditAction.contact.replace': 'Angav vem som tar över efter en kontakt',
  'auditAction.service.add': 'Lade till en tjänst',
  'auditAction.service.update': 'Ändrade en tjänst',
  'auditAction.service.end': 'Avslutade en tjänst',
  'auditAction.workspace.create': 'Skapade klienten',
  'auditAction.workspace.status': 'Ändrade klientens status',
  'auditAction.workspace.fictional': 'Rättade om klienten är fiktiv',
  'auditAction.workspace_member.grant': 'Gav en roll hos klienten',
  'auditAction.workspace_member.revoke': 'Återkallade en roll hos klienten',
  'auditAction.invite.create': 'Bjöd in en person',
  'auditAction.invite.confirm': 'Bekräftade en inbjudan',
  'auditAction.invite.accept': 'Tog emot en inbjudan',
  'auditAction.invite.revoke': 'Drog tillbaka en inbjudan',
  'auditAction.grant.request': 'Begärde att en roll ges',
  'auditAction.firm_role.grant': 'Gav en roll på byrån',
  'auditAction.firm_role.revoke': 'Återkallade en roll på byrån',
  'auditAction.firm_admin.request': 'Begärde att en byråadministratör utses',
  'auditAction.person.disable': 'Stängde av ett konto',
  'auditAction.person.enable': 'Aktiverade ett konto igen',
  'auditAction.reset.request': 'Begärde återställning av ett lösenord',
  'auditAction.reset.accept': 'Valde ett nytt lösenord',
  'auditAction.reset.host': 'Återställde ett lösenord på servern',
  'auditAction.reset.notice_ack': 'Läste meddelandet om återställt lösenord',
  'auditAction.admin.create': 'Skapade den första administratören',
  'auditAction.session.open': 'Loggade in',
  'auditAction.session.sign_out': 'Loggade ut',
  'auditAction.session.end': 'En session avslutades',
  'auditAction.session.touch': 'En session användes',
  'auditAction.session.sweep': 'Avslutade inaktiva sessioner',
  'auditAction.settings.update': 'Ändrade byråns inställningar',
  'auditAction.installation.sync': 'Läste in installationens inställningar',
  'auditAction.library.sync': 'Läste in byråns bibliotek',
  'auditAction.template.adopt': 'Antog en mall',
  'auditAction.template.retire': 'Avvecklade en mall',
  'auditAction.spend.reserve': 'Reserverade AI-budget',
  'auditAction.spend.settle': 'Avräknade kostnaden för ett AI-anrop',
  'auditAction.spend.release': 'Släppte oanvänd AI-budget',
  'auditAction.spend.sweep': 'Släppte inaktuella AI-reservationer',
  'auditAction.job.enqueue': 'Bad systemet om ett arbete',
  'auditAction.job.wheel.materialise': 'Skapade de planerade årshjulspunkterna',
  'auditAction.job.wheel.daily': 'Uppdaterade årshjulet för dagen',
  'auditAction.job.audit.verify': 'Kontrollerade händelseloggen',
  'auditAction.job.audit.verify_firm': 'Kontrollerade byråns händelselogg',
  'auditAction.job.session.sweep': 'Avslutade inaktiva sessioner',
  'auditAction.job.spend.sweep': 'Släppte inaktuella AI-reservationer',
  'auditAction.seed.workspace': 'Satte upp demonstrationen',
  'auditAction.seed.client': 'Satte upp en demonstrationsklient',
  'auditAction.seed.people': 'Satte upp demonstrationens personer',
  'auditAction.seed.activity': 'Registrerade demonstrationens arbete',
  'auditAction.seed.baseline': 'Registrerade dokumenten till demonstrationens nulägesanalys',
  'record.occurrence': 'Årshjulspunkt',
  'record.series': 'Återkommande aktivitet',
  'record.action': 'Åtgärd',
  'record.service': 'Tjänst',
  'record.document': 'Dokument',
  'record.basis': 'Underlag för kunduppgifter',
  'record.profile': 'Klientprofil',
  'record.contact': 'Kontakt',
  'record.institutionType': 'Institutstyp',
  'record.jurisdiction': 'Jurisdiktion',
  'record.step': 'Steg i checklistan',
  'record.documentLink': 'Dokumentkoppling',
  'record.comment': 'Kommentar',
  'record.aiAllowance': 'AI-medgivande',
  'record.aiCall': 'AI-anrop',
  'record.member': 'Medlem',
  'record.spend': 'AI-kostnad',
  'record.other': 'Post',
  'record.on': 'på {record}',
  'audit.changes': 'Vad som ändrades',
  'audit.unknownPerson': 'en person som inte finns med här',
  'audit.unknownRecord': 'en post som inte finns med här',
  'audit.structuredChanged': 'ändrad (se posten)',
  'audit.roundEnded': '{act} av {name}: {note}',
  'audit.roundEndedNoNote': '{act} av {name}',
  'audit.since': 'sedan {date}',
  'auditField.status': 'läge',
  'auditField.title': 'rubrik',
  'auditField.createdBy': 'skapad av',
  'auditField.dueOn': 'förfallodag',
  'auditField.startsOn': 'förberedelsen börjar',
  'auditField.responsibility': 'vem som utför den',
  'auditField.performer': 'utförs hos klienten av',
  'auditField.reviewRequired': 'kräver granskning',
  'auditField.evidenceRequired': 'kräver underlag',
  'auditField.preparer': 'tilldelad att förbereda',
  'auditField.reviewer': 'tilldelad att granska',
  'auditField.preparedBy': 'förberedd av',
  'auditField.preparedAt': 'förberedd den',
  'auditField.reviewedBy': 'granskad av',
  'auditField.reviewedAt': 'granskad den',
  'auditField.prepareNote': 'förberedarens anteckning',
  'auditField.reviewNote': 'granskarens anteckning',
  'auditField.noteBeforeSplit': 'anteckning (vem som skrev den registrerades inte)',
  'auditField.earlierRounds': 'tidigare omgångar',
  'auditField.doneAt': 'klar den',
  'auditField.skippedBy': 'överhoppad av',
  'auditField.skippedAt': 'överhoppad den',
  'auditField.skippedReason': 'skäl att hoppa över',
  'auditField.movedFrom': 'regelns datum',
  'auditField.movedBy': 'flyttad av',
  'auditField.movedAt': 'flyttad den',
  'auditField.movedReason': 'varför den flyttades',
  'auditField.skipRequestedBy': 'överhoppning begärd av',
  'auditField.skipRequestedAt': 'överhoppning begärd den',
  'auditField.skipRequestReason': 'varför den ska hoppas över',
  'auditField.skipAgreedContact': 'godkänt hos klienten av',
  'auditField.skipAgreedOn': 'godkänt den',
  'auditField.skipBoardReport': 'rapporteras till styrelsen i',
  'auditField.yearEndConfirmedBy': 'räkenskapsårsslut bekräftat av',
  'auditField.yearEndConfirmedAt': 'räkenskapsårsslut bekräftat den',
  'auditField.cancelledBy': 'avbruten av',
  'auditField.cancelledAt': 'avbruten den',
  'auditField.cancelledReason': 'skäl att avbryta',
  'auditField.waitingOn': 'väntar på',
  'auditField.waitingAskedOn': 'tillfrågad den',
  'auditField.waitingFor': 'vad som efterfrågades',
  'auditField.waitingClearedAt': 'klienten svarade den',
  'auditField.activityKind': 'typ av aktivitet',
  'auditField.function': 'funktion',
  'auditField.line': 'försvarslinje',
  'auditField.anchor': 'utgår från',
  'auditField.offsetDays': 'dagar från utgångsdatumet',
  'auditField.rule': 'när den infaller',
  'auditField.starts': 'börjar',
  'auditField.ends': 'slutar',
  'auditField.roll': 'vid helg',
  'auditField.prepDays': 'dagar för förberedelse',
  'auditField.reviewDays': 'dagar för granskning',
  'auditField.service': 'tjänst',
  'auditField.estimatedHours': 'uppskattade timmar',
  'auditField.basis': 'varför den finns',
  'auditField.cadenceSource': 'varifrån intervallet kommer',
  'auditField.sourceNote': 'källanteckning',
  'auditField.statusReason': 'skäl',
  'auditField.step': 'steg',
  'auditField.tickedBy': 'avbockad av',
  'auditField.tickedAt': 'avbockad den',
  'auditField.removedBy': 'borttagen av',
  'auditField.removedAt': 'borttagen den',
  'auditField.removedReason': 'skäl att ta bort',
  'auditField.detail': 'beskrivning',
  'auditField.sourceKind': 'källa',
  'auditField.sourceRef': 'källhänvisning',
  'auditField.sourceDocument': 'källdokument',
  'auditField.sourceOccurrence': 'årshjulspunkt',
  'auditField.severity': 'allvarlighetsgrad',
  'auditField.severityBasis': 'grund för allvarlighetsgraden',
  'auditField.owner': 'ägare',
  'auditField.ownerContact': 'ägare hos klienten',
  'auditField.followUp': 'följs upp av',
  'auditField.remediatedBy': 'genomförd av',
  'auditField.remediatedAt': 'genomförd den',
  'auditField.remediationNote': 'vad som gjordes',
  'auditField.verifiedBy': 'verifierad av',
  'auditField.verifiedAt': 'verifierad den',
  'auditField.verificationNote': 'anteckning om verifieringen',
  'auditField.closedBy': 'stängd av',
  'auditField.closedAt': 'stängd den',
  'auditField.serviceKind': 'typ av tjänst',
  'auditField.deliveryModel': 'leveransform',
  'auditField.description': 'beskrivning',
  'auditField.endedReason': 'skäl att avsluta',
  'auditField.contractRef': 'avtalsreferens',
  'auditField.lead': 'klientansvarig',
  'auditField.clientOwner': 'ansvarig hos klienten',
  'auditField.interimRole': 'roll hos klienten',
  'auditField.independenceNote': 'anteckning om oberoende',
  'auditField.documentKind': 'dokumenttyp',
  'auditField.size': 'storlek',
  'auditField.originalName': 'filnamn',
  'auditField.versionOf': 'tidigare version',
  'auditField.customerContent': 'uppgifter på kundnivå',
  'auditField.uploadedBy': 'uppladdat av',
  'auditField.uploadedAt': 'uppladdat den',
  'auditField.retiredBy': 'avvecklad av',
  'auditField.retiredAt': 'avvecklad den',
  'auditField.retiredReason': 'skäl att avveckla',
  'auditField.document': 'dokument',
  'auditField.linkPurpose': 'kopplat som',
  'auditField.comment': 'kommentar',
  'auditField.author': 'skribent',
  'auditField.retractedAt': 'tillbakadragen den',
  'auditField.legalName': 'registrerat namn',
  'auditField.organisationNumber': 'organisationsnummer',
  'auditField.lei': 'LEI-kod',
  'auditField.sizeBand': 'storleksklass',
  'auditField.supervisors': 'tillsynsmyndigheter',
  'auditField.licences': 'tillstånd',
  'auditField.financialYearEnd': 'räkenskapsårets slut',
  'auditField.agmMonth': 'månad för årsstämman',
  'auditField.licenceDate': 'tillståndsdatum',
  'auditField.employeesBand': 'anställda',
  'auditField.customersBand': 'kunder',
  'auditField.products': 'produkter',
  'auditField.channels': 'kanaler',
  'auditField.keySystems': 'viktiga system',
  'auditField.flags': 'kännetecken',
  'auditField.name': 'namn',
  'auditField.jobTitle': 'befattning',
  'auditField.roleAtClient': 'roll hos klienten',
  'auditField.email': 'e-post',
  'auditField.phone': 'telefon',
  'auditField.isBoard': 'i styrelsen',
  'auditField.contactRetiredBy': 'markerad som inte längre kontakt av',
  'auditField.contactRetiredAt': 'inte längre kontakt från',
  'auditField.institutionType': 'institutstyp',
  'auditField.addedBy': 'tillagd av',
  'auditField.addedAt': 'tillagd den',
  'auditField.jurisdiction': 'jurisdiktion',
  'auditField.presence': 'närvaro',
  'auditField.purposes': 'ändamål',
  'auditField.categories': 'kategorier',
  'auditField.fields': 'fält',
  'auditField.legalBasis': 'rättslig grund',
  'auditField.purposeStatement': 'beskrivning av ändamålet',
  'auditField.controllerContact': 'personuppgiftsansvarigs kontakt',
  'auditField.instructionDocument': 'instruktion',
  'auditField.dpiaDocument': 'konsekvensbedömning',
  'auditField.dpiaNote': 'anteckning om konsekvensbedömningen',
  'auditField.retention': 'lagringstid',
  'auditField.aiAllowed': 'AI tillåten',
  'auditField.recordedBy': 'registrerat av',
  'auditField.recordedAt': 'registrerat den',
  'auditField.confirmedBy': 'bekräftat av',
  'auditField.authorisedBy': 'godkänt av, hos klienten',
  'auditField.processingLocation': 'plats för behandlingen',
  'auditField.transferBasis': 'grund för överföringen',
  'auditField.confirmedAt': 'bekräftat den',
  'auditField.effectiveFrom': 'gäller från',
  'auditField.reviewOn': 'ses över senast',
  'auditField.engines': 'tjänster som får ta emot klientuppgifter',
  'auditField.allowanceBasis': 'grund för medgivandet',
  'auditField.monthlyCap': 'tak per månad (USD)',
  'auditField.decidedBy': 'beslutat av',
  'auditField.decidedAt': 'beslutat den',
  'auditField.forPerson': 'för',
  'auditField.purpose': 'syfte',
  'auditField.engine': 'AI-tjänst',
  'auditField.model': 'modell',
  'auditField.cost': 'kostnad (USD)',
  'auditField.reserved': 'reserverat (USD)',
  'auditField.settledAt': 'avräknat den',
  'auditField.callStatus': 'utfall',
  'auditField.refusal': 'avslag',
  'auditField.person': 'person',
  'auditField.role': 'roll',
  'auditField.grantedBy': 'given av',
  'auditField.grantedVia': 'given genom',
  'auditField.grantedAt': 'given den',
  'auditField.revokedBy': 'återkallad av',
  'auditField.revokedAt': 'återkallad den',
  'auditField.revokeReason': 'skäl att återkalla',
  'occurrenceStatus.planned': 'planerad',
  'occurrenceStatus.in_progress': 'pågår',
  'occurrenceStatus.prepared': 'förberedd',
  'occurrenceStatus.done': 'klar',
  'occurrenceStatus.skipped': 'överhoppad',
  'occurrenceStatus.cancelled': 'struken',
  'customerContent.none': 'inga',
  'customerContent.customer_level': 'innehåller uppgifter på kundnivå',
  'grantedVia.person': 'beslut av en klientansvarig',
  'grantedVia.workspace_create': 'att klienten skapades',
  'grantedVia.invite': 'en inbjudan',
  'grantedVia.grant_request': 'en administratörs begäran, bekräftad av en andra person',
  'grantedVia.seed': 'demonstrationen',
  'occurrence.notesTitle': 'Förberedelse och granskning',
  'occurrence.notesNone': 'Inte förberedd ännu.',
  'occurrence.notePrepared': 'Förberedd av {name}, {date}',
  'occurrence.noteSentBack': 'Skickad tillbaka av {name}, {date}',
  'occurrence.noteWithdrawn': 'Tillbakadragen av {name}, {date}',
  'occurrence.noteApproved': 'Godkänd av {name}, {date}',
  'occurrence.noteDone': 'Markerad som klar av {name}, {date}',
  'occurrence.noteNoText': 'Ingen anteckning.',
  'occurrence.noteBeforeSplit': 'En anteckning från innan Olivia registrerade vem som skrev varje anteckning: vem som skrev den är inte känt.',
  'occurrence.act.sent_back': 'Skickad tillbaka',
  'occurrence.act.withdrawn': 'Tillbakadragen',

  // ── Refusals in the reader's language (expert review O-D) ───────────────────────────────────────────────────────
  'errors.UNREACHABLE': 'Det gick inte att nå Olivias server. Kontrollera anslutningen och försök igen om en stund.',
  'errors.subject.rule': 'Regeln',
  'errors.subject.item': '”{title}”',
  'errors.subject.series': 'Aktiviteten {code}',
  'errors.TEXT_REQUIRED': 'Fyll i texten som efterfrågas.',
  'errors.TEXT_TOO_SHORT': 'Skriv minst {min} tecken.',
  'errors.TEXT_TOO_LONG': 'Skriv högst {max} tecken.',
  'errors.REASON_REQUIRED': 'Ange ett skäl.',
  'errors.UPLOAD_UNREADABLE': 'Uppladdningen kunde inte läsas. Försök igen och säg till på byrån om det fortsätter.',
  'errors.FIRM_ROLE_NEEDED': 'Det här kräver rollen {roles} på byrån.',
  'errors.CLIENT_ROLE_NEEDED': 'Det här kräver rollen {roles} hos klienten.',
  'errors.WORKING_ROLE_NEEDED': 'Det här kräver en arbetande roll hos klienten: klientansvarig, granskare eller handläggare.',
  'errors.ASSIGN_NEEDS_LEAD': 'Bara en klientansvarig fördelar en årshjulspunkt till någon annan; du kan själv ta en punkt att förbereda.',
  'errors.LEAD_GRANTS_ROLES': 'Bara en klientansvarig hos klienten ger dess roller.',
  'errors.LEAD_REVOKES_ROLES': 'Bara en klientansvarig hos klienten eller en byråadministratör återkallar dess roller.',
  'errors.FOUR_EYES_REVIEW': 'Du förberedde punkten, så någon annan granskar den.',
  'errors.FOUR_EYES_VERIFY': 'Du registrerade genomförandet, så någon annan verifierar det.',
  'errors.FOUR_EYES_BASIS': 'Du registrerade underlaget, så en andra medlem bekräftar det.',
  'errors.REVIEW_NEEDED': 'Punkten måste granskas av en andra person innan den är klar.',
  'errors.OWN_ROLES': 'Ingen ger eller återkallar sina egna roller.',
  'errors.OWN_ACCOUNT': 'Du kan inte stänga av ditt eget konto.',
  'errors.OWN_RESET': 'Be en annan administratör att återställa ditt lösenord.',
  'errors.CANNOT_CONFIRM': 'Du kan inte bekräfta begäran: det gör en andra person.',
  'errors.CANNOT_WITHDRAW': 'Du kan inte dra tillbaka begäran.',
  'errors.ADMIN_NEEDS_SECOND': 'En byråadministratör utses bara när en andra administratör bekräftar det. Begär det i stället.',
  'errors.INVITE_NEEDS_SECOND': 'En roll från en inbjudan eller en begäran ges först när en andra person har bekräftat den.',
  'errors.CYCLE_ITEM_STATE': 'En årshjulspunkt kan inte gå från {from} till {to}.',
  'errors.CYCLE_ITEM_CLOSED': 'Punkten är stängd och förblir stängd.',
  'errors.ITEM_HAS_NO_REVIEW': 'Punkten har ingen granskning.',
  'errors.SEND_BACK_REASON_REQUIRED': 'Ange varför punkten skickas tillbaka.',
  'errors.EVIDENCE_NEEDED': 'Koppla underlaget innan punkten markeras som klar.',
  'errors.LAST_EVIDENCE': 'Det här är det enda underlaget för en stängd punkt, så det ligger kvar.',
  'errors.FIRST_LINE_EVIDENCE': 'Underlag för arbete i första linjen måste vara ett dokument som angetts sakna uppgifter på kundnivå.',
  'errors.DOCUMENT_RETIRED': 'Ett avvecklat dokument kan inte kopplas.',
  'errors.SERIES_REASON_REQUIRED': 'Att pausa eller avsluta en återkommande aktivitet kräver ett skäl.',
  'errors.SERIES_KEEPS_REVIEW': 'En aktivitet som kräver granskning eller underlag fortsätter att göra det. Vill du ändra vad den är, avsluta den med ett skäl och lägg till en ny.',
  'errors.YEAR_END_NOT_CONFIRMED': 'Klientens räkenskapsårsslut ({yearEnd}) är antaget, inte bekräftat, och datum du valt utgår från det. Bekräfta det, eller kryssa i att datumen får utgå från det antagna.',
  'errors.YEAR_END_CHANGED': 'Räkenskapsårets slut har ändrats under tiden, till {yearEnd}. Läs det igen innan du bekräftar det.',
  'errors.PROFILE_NEEDED': 'Registrera klientens profil först.',
  'errors.RULE_DROPS_ANCHOR': 'Den här aktiviteten följer {anchor}. En regel som skrivs för hand följer i stället kalendern: kryssa i ”Följ kalendern i stället” för att säga det.',
  'errors.CYCLE_ITEM_SAME_DATE': 'Punkten har redan den fristen.',
  'errors.CYCLE_ITEM_OVERDUE_NOT_MOVED': 'En försenad punkt flyttas inte: den förblir försenad tills den är klar.',
  'errors.CYCLE_ITEM_MOVE_PAST': 'En punkt flyttas till i dag eller ett senare datum.',
  'errors.CYCLE_ITEM_AFTER_SERVICE_END': 'En punkt har aldrig sin frist efter att dess tjänst har upphört.',
  'errors.CYCLE_ITEM_MOVE_PAST_NEXT': 'En punkt flyttas till ett datum före nästa punkt i aktiviteten, som regeln ger den {date}.',
  'errors.CYCLE_ITEM_MOVE_PAST_HORIZON': 'En punkt flyttas som längst till {date}, planeringshorisontens slut.',
  'errors.FOLLOW_UP_NEEDED': 'En åtgärd som en kontakt hos klienten äger anger vilken medlem på byrån som följer upp den.',
  'errors.CONTACT_REPLACES_ITSELF': 'Ange en annan kontakt som tar över.',
  'errors.SERVICE_RESPONSIBLE_NEEDED': 'Innan klienten startas, ange vem hos klienten som är ansvarig för {services}.',
  'errors.SKIP_NEEDS_SECOND': 'Den här punkten kräver granskning eller underlag, så att hoppa över den kräver två personer: en klientansvarig begär det och en granskare som inte är samma person bekräftar.',
  'errors.CLIENT_ACT_STAYS_CLIENTS': 'Det här är klientens eget beslut oavsett leveransform: klienten utför det, och byrån för in det.',
  'errors.FOUR_EYES_SKIP': 'Du begärde att punkten hoppas över, så någon annan bekräftar det.',
  'errors.SKIP_REQUEST_PENDING': 'En begäran att hoppa över punkten väntar redan.',
  'errors.SOURCE_ITEM_NOT_FOUND': 'Ingen årshjulspunkt hos den här klienten har koden {code}.',
  'errors.SERIES_SERVICE_ENDED': 'Tjänsten för aktiviteten har avslutats, så aktiviteten förblir pausad.',
  'errors.SERIES_RETIRED': 'Aktiviteten är avslutad och förblir avslutad.',
  'errors.SERVICE_ENDED': 'Tjänsten har avslutats; en ny aktivitet kräver en pågående tjänst.',
  'errors.TEMPLATE_STATE': 'En mall går från utkast eller grundmall till antagen, eller till avvecklad.',
  'errors.TEMPLATE_RETIRED': 'Mallen är avvecklad och förblir det.',
  'errors.TEMPLATE_ITEM_NOT_OFFERED': '”{title}” hör till en tjänst som klienten inte har.',
  'errors.WHEEL_ANCHOR_MISSING': '{what}: inget datum är registrerat för {anchor}, så regeln kan inte placeras. Registrera datumet först.',
  'errors.WHEEL_ANCHOR_NOT_YEARLY': '{what}: en regel som utgår från ett datum i klientens år kan bara vara årlig, eller med några års mellanrum.',
  'errors.WHEEL_CALENDAR_OFFSET': '{what}: en regel i kalendern tar inga dagar före eller efter.',
  'errors.WHEEL_LEAP_DAY': '{what}: ett datum den 29 februari kan inte återkomma varje år. Välj en annan dag.',
  'errors.WHEEL_TOO_OFTEN': '{what}: regeln infaller för ofta för ett årshjul.',
  'errors.WHEEL_BAD_DATE': '{what}: ett av datumen finns inte.',
  'errors.WHEEL_RULE_UNREADABLE': '{what}: årshjulet kan inte läsa regeln.',
  'errors.ACTION_ONE_OWNER': 'En åtgärd har en ägare: en person på byrån eller en kontakt hos klienten, inte båda.',
  'errors.ACTION_STATE': 'En åtgärd kan inte gå från {from} till {to}.',
  'errors.ACTION_CLOSED': 'Åtgärden är stängd eller avbruten och förblir det.',
  'errors.REMEDIATION_NOTE_REQUIRED': 'Ange vad som gjordes för att åtgärda.',
  'errors.VERIFICATION_REASON_REQUIRED': 'Ange varför genomförandet inte godtas.',
  'errors.SEVERITY_BASIS_REQUIRED': 'En ny allvarlighetsgrad kräver sin grund.',
  'errors.ASSIGNEE_NOT_MEMBER': 'Den som tilldelas måste vara en arbetande medlem hos klienten.',
  'errors.ASSIGNEE_NEEDS_ROLE': 'Den som tilldelas måste vara medlem hos klienten med rollen {role}.',
  'errors.CONTACT_NOT_CURRENT': 'Kontakten måste vara en nuvarande kontakt hos klienten.',
  'errors.CONTACT_RETIRED': 'Personen är inte längre kontakt.',
  'errors.LAST_LEAD': 'En klient behåller minst en klientansvarig.',
  'errors.CLIENT_CLOSED': 'Klienten är stängd.',
  'errors.CLIENT_STATE': 'En klient kan inte gå från {from} till {to} här.',
  'errors.SERVICE_OVERLAP': 'Klienten har redan en tjänst av det slaget under den perioden.',
  'errors.INTERIM_ROLE_NEEDED': 'Ett interimsuppdrag anger vilken roll konsulten har hos klienten.',
  'errors.SERVICE_LINE_NEEDED': 'Ange försvarslinjen för tjänsten.',
  'errors.SERVICE_KIND_RETIRED': 'Den typen av tjänst är avvecklad i byråns bibliotek.',
  'errors.SERVICE_ENDED_ALREADY': 'Tjänsten är redan avslutad.',
  'errors.CUSTOMER_ROLE_NEEDS_BASIS': 'Behörigheten till kunduppgifter kräver ett gällande underlag för klientens kunduppgifter.',
  'errors.CUSTOMER_ROLE_BY_LEAD': 'Behörigheten till kunduppgifter ges av en klientansvarig, aldrig genom en inbjudan eller en administratörs begäran.',
  'errors.ACCOUNT_OFF': 'Kontot är avstängt.',
  'errors.PARTNER_FIRST_LEAD': 'Bara en partner blir första klientansvarig för en klient den själv skapar; ange en annan person.',
  'errors.FIRST_LEAD_NEEDED': 'En ny klient behöver sin första klientansvarige.',
  'errors.EMAIL_INVALID': 'Ange en giltig e-postadress.',
  'errors.EMAIL_TAKEN': 'Det finns redan ett konto med den e-postadressen. Ge kontot roller i stället för att bjuda in det.',
  'errors.INVITE_NO_ROLE': 'En inbjudan ger minst en roll.',
  'errors.GRANT_NOT_ALLOWED': 'Du får inte ge roller hos den klienten.',
  'errors.GRANT_TWICE': 'En roll står med två gånger.',
  'errors.ALREADY_ADMIN': 'Personen är redan byråadministratör.',
  'errors.ADMIN_REQUEST_WAITING': 'En begäran om att göra personen till byråadministratör väntar redan.',
  'errors.SSO_NO_PASSWORD': 'Ett konto som loggar in med enkel inloggning har inget lösenord att återställa.',
  'errors.INVITE_NO_LONGER_VALID': 'Inbjudan eller begäran gäller inte längre: personerna eller klienten den bygger på har ändrats.',
  'errors.TERMS_NOT_ACCEPTED': 'Godkänn villkoren och integritetsmeddelandet först.',
  'errors.BASIS_DECIDED_ALREADY': 'Underlaget är redan bekräftat eller avvecklat.',
  'errors.BASIS_RETIRED_ALREADY': 'Underlaget är redan avvecklat.',
  'errors.BASIS_SERVICE_ENDED': 'Ett underlag hör till en pågående tjänst.',
  'errors.BASIS_DOCUMENTS_RETIRED': 'Instruktionen och konsekvensbedömningen måste vara gällande dokument.',
  'errors.BASIS_FIELDS_NEEDED': 'Ange vilka fält underlaget tillåter för {category}.',
  'errors.BASIS_PURPOSE_FIELDS_NEEDED': 'Ange fälten för {category} vid {purpose}.',
  'errors.BASIS_RETENTION_NEEDED': 'Ange lagringstiden för {category} i hela månader, 1 till 60.',
  'errors.DOCUMENT_STORED_ALREADY': 'Filen finns redan sparad som {code}. Koppla det dokumentet i stället.',
  'errors.DOCUMENT_RETIRED_ALREADY': 'Dokumentet är redan avvecklat.',
  'errors.FILE_EXECUTABLE': 'Program och skript sparas inte. Ladda upp ett dokument i stället.',
  'errors.FILE_SCRIPT': 'Skriptfiler sparas inte.',
  'errors.FILE_SCRIPTED_PAGE': 'Webbsidor med skript sparas inte. Spara sidan som pdf och ladda upp den.',
  'errors.COMMENT_NOT_YOURS': 'Bara skribenten kan dra tillbaka en kommentar.',
  'errors.AI_ENGINE_NOT_ALLOWED': 'Installationen skickar inte klientuppgifter till {engines}.',

  // ── Onboarding and the current state (docs/ONBOARDING-CURRENT-STATE.md) ──────────────────────────────────────
  'documents.textForQuotes': 'Text för citat',
  'textStatus.waiting': 'väntar på att systemet läser texten',
  'textStatus.extracted': 'texten är läst: {chars} tecken, {pages} sidor',
  'textStatus.not_checkable': 'ingen text att kontrollera (en inskannad sida eller en bild: ingen textigenkänning görs)',
  'textStatus.failed': 'texten kunde inte läsas',
  // IP4b: decks, transcripts and e-mails
  'textStatus.slides': 'texten är läst: {slides} bilder, varav {notes} med talaranteckningar',
  'textStatus.cues': 'texten är läst: {cues} tidsmarkerade avsnitt, {speakers} talare',
  'textStatus.email': 'texten är läst: ett e-postmeddelande med {attachments} bilagor',
  'textStatus.emailOne': 'texten är läst: ett e-postmeddelande med 1 bilaga',
  'quoteFind.title': 'Hitta ett citat i texten',
  'quoteFind.field': 'Ord ur dokumentet',
  'quoteFind.hint': 'Systemet letar efter exakt de här orden i texten som lästs ur filen. Inget sparas.',
  'quoteFind.check': 'Leta efter orden',
  'quoteFind.foundAt': 'Hittat {where}.',
  'quoteFind.foundInText': 'Hittat i texten.',
  'quoteFind.not_found': 'Finns inte i texten som lästs ur filen.',
  'quoteFind.not_checkable': 'Filen har ingen text som kan kontrolleras.',
  'locator.page': 'på sidan {page}',
  'locator.sheet': 'på blad {page}',
  'locator.slide': 'på bild {slide}',
  'locator.notes': 'i talaranteckningarna till bild {slide}',
  'locator.cue': 'vid {time}',
  'locator.cueSpeaker': 'vid {time}, sagt av {speaker}',
  'locator.headers': 'i e-postmeddelandets huvudrader',
  'locator.body': 'i e-postmeddelandets text',
  'attachments.title': 'Bilagor i e-postmeddelandet',
  'attachments.note': 'En bilaga blir ett eget dokument först när du sparar den. Till dess listas den bara här.',
  'attachments.none': 'E-postmeddelandet har inga bilagor.',
  'attachments.unnamed': 'bilaga {n} (utan namn)',
  'attachments.inline': 'visas inne i e-postmeddelandet',
  'attachments.storedAs': 'sparad som {code}',
  'attachments.store': 'Spara som eget dokument',
  'attachments.stored': 'Bilagan är sparad som ett eget dokument.',
  'documents.takenFrom': 'Hämtad ur',
  'documents.fromAttachment': 'bilaga {n} till {code}',
  'transcript.labelsFound': 'Filen namnger {n} talare. Välj vem var och en är: inget väljs åt dig.',
  'transcript.unmapped': 'Välj en deltagare för varje talare, eller ta bort raden.',
  'transcript.removeSpeaker': 'Ta bort',
  'errors.ATTACHMENT_NOT_READ': 'Systemet har inte läst e-postmeddelandet än, så dess bilagor listas inte. Försök igen om en stund.',
  'auditAction.document.store_attachment': 'Sparade en bilaga ur ett e-postmeddelande som dokument',
  'auditField.fromEmail': 'hämtad ur e-postmeddelandet',
  'auditField.fromAttachment': 'bilagans nummer',
  'errors.QUOTE_NOT_FOUND': 'Citatet finns inte i {document}. Kopiera orden exakt som de står där.',
  'errors.QUOTE_TOO_LONG': 'Ett citat får vara högst {max} tecken.',
  'auditAction.job.document.extract': 'Läste texten i ett dokument',
  'auditField.extractor': 'läst med',
  'auditField.textStatus': 'text',
  'auditField.textReason': 'varför text saknas',
  'auditField.chars': 'antal tecken',
  'auditField.extractedAt': 'texten lästes',
  'textState.extracted': 'läst',
  'textState.not_checkable': 'kan inte kontrolleras',
  'textState.failed': 'kunde inte läsas',
  'auditAction.document.extract': 'Bad systemet läsa texten i ett dokument',
  'record.role': 'Roll',
  'record.startCheck': 'Startkontroll',
  'record.checkItem': 'Punkt i en startkontroll',
  'record.inherited': 'Övertaget läge',
  'record.documentText': 'Text i ett dokument',
  'auditField.reason': 'skäl',
  'auditField.roleKind': 'roll',
  'auditField.roleMode': 'hur byrån tar rollen',
  'auditField.firmPerson': 'byråns medarbetare',
  'auditField.clientHolder': 'innehavare hos klienten',
  'auditField.delegatedTasks': 'uppgifter som byrån utför',
  'auditField.roleBasis': 'rättslig grund',
  'auditField.roleBasisNote': 'anteckning om grunden',
  'auditField.roleBasisDocument': 'dokument bakom grunden',
  'auditField.appointmentDocument': 'utnämningsbeslut',
  'auditField.notifiedOn': 'anmält den',
  'auditField.notificationDocument': 'anmälan',
  'auditField.evidenceDocument': 'underlag',
  'auditField.exceptionReason': 'skäl för undantaget',
  'auditField.exceptionRequestedBy': 'undantag begärt av',
  'auditField.exceptionRequestedAt': 'undantag begärt den',
  'auditField.exceptionConfirmedBy': 'undantag bekräftat av',
  'auditField.exceptionConfirmedAt': 'undantag bekräftat den',
  'auditField.locator': 'var i dokumentet',
  'auditField.quote': 'citat',
  'auditField.quoteCheck': 'kontroll av citatet',
  'auditField.asOf': 'per',
  'auditField.openOrders': 'öppna förelägganden',
  'auditField.openOrdersSource': 'källa till de öppna föreläggandena',
  'auditField.openOrdersNote': 'anteckning om föreläggandena',
  'auditField.alertsUnhandled': 'ohanterade larm',
  'auditField.alertsUnhandledSource': 'källa till antalet larm',
  'auditField.alertsOldestDays': 'äldsta larmet, i dagar',
  'auditField.alertsOldestDaysSource': 'källa till larmens ålder',
  'auditField.reviewsOverdue': 'försenade kunduppföljningar',
  'auditField.reviewsOverdueSource': 'källa till de försenade uppföljningarna',
  'auditField.customersNotScreened': 'kunder som inte kontrollerats mot listor',
  'auditField.customersNotScreenedSource': 'källa till antalet ej kontrollerade',
  'auditField.customersWithoutRiskClass': 'kunder utan riskklass',
  'auditField.customersWithoutRiskClassSource': 'källa till antalet utan riskklass',
  'auditField.openAuditFindings': 'öppna revisionsiakttagelser',
  'auditField.openAuditFindingsSource': 'källa till revisionsiakttagelserna',
  'auditField.auditFindingsDocument': 'dokument med revisionsiakttagelser',
  'auditField.knownBreaches': 'kända överträdelser',
  'auditField.knownBreachesSource': 'källa till överträdelserna',
  'auditField.knownBreachesNote': 'anteckning om överträdelserna',
  'auditField.acknowledgedBy': 'bekräftat av',
  'auditField.acknowledgementDocument': 'bekräftelse',
  'auditField.acknowledgedOn': 'bekräftat den',
  'auditField.acknowledgementRecordedBy': 'bekräftelsen registrerad av',
  'auditField.acknowledgementRecordedAt': 'bekräftelsen registrerad den',
  'roleMode.supported': 'stöttad (klienten innehar den)',
  'roleMode.held': 'innehas av byrån',
  'roleMode.interim': 'upprätthålls tillfälligt av byrån',
  'checkState.open': 'öppen',
  'checkState.done': 'klar',
  'checkState.not_applicable': 'inte tillämplig',
  'checkState.exception': 'undantag',
  'itemState.unchecked': 'inte kontrollerad',
  'itemState.found': 'finns',
  'itemState.missing': 'saknas',
  'itemState.not_applicable': 'inte tillämplig',
  'quoteCheck.found': 'finns i dokumentet',
  'quoteCheck.not_checkable': 'kan inte kontrolleras (ingen text)',
  'countSource.client_report': 'klientens uppgift',
  'countSource.system_extract': 'ett systemuttag',
  'countSource.estimate': 'en uppskattning',
  'auditAction.mandate.sync_checks': 'Skapade startkontrollerna',
  'auditAction.role.add': 'Registrerade en roll',
  'auditAction.role.update': 'Ändrade en roll',
  'auditAction.role.confirm_basis': 'Bekräftade den rättsliga grunden för en roll',
  'auditAction.role.retire': 'Avslutade en roll',
  'auditAction.mandate_check.done': 'Registrerade en startkontroll som klar',
  'auditAction.mandate_check.not_applicable': 'Registrerade en startkontroll som inte tillämplig',
  'auditAction.mandate_check.exception_request': 'Begärde undantag från en startkontroll',
  'auditAction.mandate_check.exception_confirm': 'Bekräftade undantag från en startkontroll',
  'auditAction.mandate_check.exception_withdraw': 'Drog tillbaka eller avböjde ett undantag',
  'auditAction.mandate_check.item': 'Registrerade en punkt i en startkontroll',
  'auditAction.inherited.record': 'Registrerade det övertagna läget',
  'auditAction.inherited.acknowledge': 'Registrerade klientens bekräftelse av det övertagna läget',
  'auditAction.library.refs_checked': 'Registrerade att hänvisningarna i en biblioteksfil kontrollerats',
  'auditAction.library.adopt': 'Antog en biblioteksfil',
  'errors.FOUR_EYES_ADOPTION': 'Du kontrollerade hänvisningarna i den här filen, så en annan medlem av regelbevakningen antar den.',
  'errors.LIBRARY_REFS_CHECKED_ALREADY': 'Hänvisningarna i den här versionen är redan registrerade som kontrollerade.',
  'errors.LIBRARY_REFS_NOT_CHECKED': 'Registrera att hänvisningarna kontrollerats innan filen antas.',
  'errors.DOCUMENT_NOT_USABLE': 'Ange ett aktuellt dokument hos den här klienten som inte innehåller uppgifter på kundnivå.',
  'errors.ROLE_NOT_FOR_SERVICE': 'Den rollen hör inte till en tjänst av det här slaget.',
  'errors.HOME_JURISDICTION_NEEDED': 'Registrera klientens hemland i profilen först: klientens roller och dess arbetsmoment beror på det.',
  'errors.MAP_NOT_FOR_JURISDICTION': 'Inga arbetsmoment är ännu skrivna för {jurisdiction}, så analysen kan inte planeras. Planen visar vilka kartor som finns för vilka jurisdiktioner.',
  'errors.BASELINE_RECORD_UNVERIFIABLE': 'Den låsta analysen kan inte kontrolleras mot sin ögonblicksbild, så dess bild visas inte.',
  'errors.MAP_JURISDICTION_UNSTATED': 'Den här kartan över arbetsmoment anger ingen jurisdiktion, så den ingår bara i en analys som redan planerats på den.',
  'errors.PRACTICE_ALREADY_IN_SCOPE': 'Arbetsmomentet ingår redan i analysen.',
  'errors.ROLE_OUTSIDE_SERVICE': 'En roll börjar inom tjänstens period.',
  'errors.ROLE_RULE_MISSING': 'Ingen regel anger om en leverantör får ta den här rollen i {jurisdiction} den {date}. Regelbevakningen registrerar en regel först.',
  'errors.ROLE_NOT_HOLDABLE_HERE': 'En leverantör får inte ta den här rollen i {jurisdiction} från den {date} på det valda sättet: {mode}. Regeln och dess källa visas vid rollen.',
  'errors.ROLE_BASIS_NEEDED': 'Regeln tillåter det här sättet att ta rollen ({mode}) bara på en grund som den godtar. Välj grunden och förklara varför den gäller.',
  'errors.ROLE_BASIS_NOT_NEEDED': 'Rollen behöver ingen grund här.',
  'errors.ROLE_RETIRED': 'Rollen är avslutad, och den förblir avslutad.',
  'errors.FOUR_EYES_ROLE_BASIS': 'Du registrerade grunden, så en annan granskare bekräftar den.',
  'errors.TASK_NOT_OUTSOURCEABLE': 'Uppgiften {task} får inte läggas på byrån enligt den regel som gäller.',
  'errors.CHECK_DECIDED': 'Startkontrollen är avgjord, och den förblir det.',
  'errors.CHECK_EVIDENCE_NEEDED': 'En startkontroll blir klar med sitt underlag.',
  'errors.CHECK_ITEMS_OPEN': 'Varje punkt i kontrollen ska finnas eller vara inte tillämplig innan kontrollen är klar.',
  'errors.CHECK_CLOSES_WITH_ACKNOWLEDGEMENT': 'Den här kontrollen stängs med klientens bekräftelse av det övertagna läget.',
  'errors.INHERITED_ALWAYS_APPLIES': 'Det övertagna läget gäller alltid: det bekräftas, eller så startar klienten med ett undantag.',
  'errors.EXCEPTION_NOT_ASKED': 'Ett undantag bekräftas först när en klientansvarig har begärt det.',
  'errors.FOUR_EYES_EXCEPTION': 'Du begärde undantaget, så en annan granskare bekräftar det.',
  'errors.EXCEPTION_ASKED_ALREADY': 'Ett undantag väntar redan på den här kontrollen.',
  'errors.INHERITED_ACKNOWLEDGED': 'Ett bekräftat övertaget läge ändras aldrig.',
  'errors.INHERITED_ACKNOWLEDGED_BY_OWNER': 'Det övertagna läget bekräftas av den person hos klienten som ansvarar för tjänsten.',
  'errors.DATE_IN_FUTURE': 'Datumet är i dag eller tidigare.',
  'errors.MANDATE_CHECK_OPEN': 'Det finns öppna startkontroller. Klienten startar när var och en är klar, inte tillämplig eller undantagen av två personer.',
  'errors.ROLE_NO_BASIS_TO_CONFIRM': 'Rollen har ingen grund som väntar på bekräftelse.',
  'errors.NO_EXCEPTION_REQUEST': 'Inget undantag väntar på den här kontrollen.',
  'errors.QUOTE_NEEDS_DOCUMENT': 'Ett citat anger dokumentet det är hämtat ur.',
  'errors.INHERITED_NOT_RECORDED': 'Registrera det övertagna läget först.',
  'nav.mandate': 'Uppdrag',
  'nav.mandate.answers': 'Vilka roller tar byrån för klienten, och vad måste finnas på plats innan den startar?',
  'mandate.title': 'Uppdrag',
  'mandate.library.proposal': '{what}: Olivias förslag, ännu inte antaget av er regelbevakning.',
  'mandate.library.adopted': '{what}: antaget av {name} den {date}.',
  'mandate.noHome': 'Registrera klientens hemland i profilen: rollernas regler beror på det.',
  'mandate.noServices': 'Inga tjänster ännu. Registrera tjänsterna först: uppdraget anges per tjänst.',
  'mandate.from': 'Från',
  'mandate.lead': 'Klientansvarig på byrån',
  'mandate.responsible': 'Ansvarig hos klienten',
  'mandate.noResponsible': 'ingen utsedd ännu',
  'mandate.roles': 'Roller',
  'mandate.noRoleKinds': 'Ingen roll är definierad för den här typen av tjänst.',
  'mandate.appointedBy': 'Utses av',
  'mandate.allowed.never': 'inte tillåtet',
  'mandate.allowed.with_basis': 'bara på en registrerad grund som en granskare bekräftar',
  'mandate.allowed.yes': 'tillåtet',
  'mandate.ruleNone': 'Ingen regel anger i dag om en leverantör får ta den här rollen i {jurisdiction}; regelbevakningen registrerar en regel först.',
  'mandate.ruleNext': 'Från den {date} gäller en annan regel:',
  'mandate.sourcePage': 'texten, läst den {date}',
  'mandate.tasksFirm': 'Uppgifter som byrån utför',
  'mandate.tasksStay': 'Stannar hos klienten',
  'mandate.oversight': 'Klientens tillsyn över byrån',
  'mandate.notice': 'Anmälan till myndigheten när rollen har utsetts',
  'mandate.clientActs': 'Klientens egna beslut',
  'mandate.clientActs.count': '{n} av {total} i årscykeln',
  'mandate.clientAct.inCycle': 'i årscykeln ({code})',
  'mandate.clientAct.notInCycle': 'inte i årscykeln',
  'mandate.clientActs.apply': 'Tillämpa klienttypens mall',
  'mandate.addRole': 'Registrera rollen',
  'mandate.form.modeNever': '{mode} (inte tillåtet här)',
  'mandate.form.nobody': 'ingen',
  'mandate.form.basis': 'Grund som regeln godtar',
  'mandate.form.basisNote': 'Varför grunden gäller',
  'mandate.form.basisNeedsReviewer': 'En granskare som inte registrerade grunden bekräftar den innan rollen räknas.',
  'mandate.role.since': 'från den {date}, {jurisdiction}',
  'mandate.role.basisConfirmed': 'bekräftad av {name}',
  'mandate.role.confirmBasis': 'Bekräfta grunden',
  'mandate.role.retire': 'Avsluta rollen',
  'mandate.role.appointment': 'Utnämningsbeslut (dokument)',
  'mandate.role.recordAppointment': 'Registrera utnämningen',
  'mandate.role.recordNotice': 'Registrera anmälan',
  'roleState.basis_not_confirmed': 'grunden ännu inte bekräftad av en granskare',
  'roleState.appointment_not_recorded': 'utnämningen inte registrerad',
  'roleState.notice_not_recorded': 'anmälan inte registrerad',
  'roleState.rule_changes': 'regeln ändras den {date}',
  'roleState.must_end_or_become_supported': 'regeln tillåter inte detta sedan den {date}: avsluta rollen eller registrera den som stöttad',
  'roleState.tasks_rule_changes': 'från den {date} räknar regeln inte längre upp: {tasks}',
  'mandate.checks': 'Startkontroller',
  'mandate.checks.make': 'Skapa startkontrollerna',
  'mandate.checks.notMade': '{n} startkontroller som tjänsterna kräver är inte skapade ännu.',
  'mandate.checks.none': 'Ingen startkontroll gäller för den här tjänsten.',
  'mandate.checks.onDemand': 'En tjänst vid behov kräver inga startkontroller.',
  'mandate.check.beforeStart': 'före start',
  'mandate.check.after': 'efter start',
  'mandate.check.from': 'gäller från den {date}',
  'mandate.check.actor.firm': 'byrån kontrollerar',
  'mandate.check.actor.client_act': 'klientens egen åtgärd',
  'mandate.check.closesWith': 'Stängs med',
  'mandate.check.doneBy': 'Klar med {code}, av {name} den {date}',
  'mandate.check.notApplicableWhy': 'Inte tillämplig: {reason}',
  'mandate.check.exceptionAsked': 'Undantag begärt av {name}: {reason}',
  'mandate.check.exceptionConfirmed': 'Bekräftat av {name}.',
  'mandate.check.items': '{found} av {total} finns',
  'mandate.check.missing': 'saknas eller inte kontrollerade: {items}',
  'mandate.check.showItems': 'Visa punkterna',
  'mandate.check.inheritedNote': 'Den här kontrollen stängs när klienten bekräftar det övertagna läget.',
  'mandate.check.done': 'Registrera som klar',
  'mandate.check.notApplicable': 'Inte tillämplig',
  'mandate.check.askException': 'Begär undantag',
  'mandate.check.confirmException': 'Bekräfta undantaget',
  'mandate.check.withdrawException': 'Dra tillbaka eller avböj undantaget',
  'mandate.check.exceptionNote': 'En klientansvarig begär undantag med skälet; en granskare som inte är den klientansvarige bekräftar det.',
  'mandate.item.record': 'Registrera punkten',
  'mandate.item.result': 'Resultat',
  'mandate.item.locator': 'Var (punkt eller sida)',
  'mandate.item.quote': 'Citat',
  'mandate.item.quoteHint': 'Högst 300 tecken, kopierade ur dokumentet: citatet kontrolleras mot dokumentets text.',
  'mandate.item.recordedBy': 'registrerad av {name}',
  'inherited.title': 'Övertaget läge',
  'inherited.intro': 'Det byrån tar över, i en kort sammanställning som den ansvarige hos klienten bekräftar med ett undertecknat brev eller protokoll.',
  'inherited.noServices': 'Ingen tjänst driver en funktion åt klienten, så det finns inget läge att ta över.',
  'inherited.none': 'Inte registrerat ännu.',
  'inherited.line': '{code} · det byrån tar över, per den {date}',
  'inherited.count.open_orders': 'Öppna förelägganden från tillsynsmyndigheten',
  'inherited.count.alerts_unhandled': 'Ohanterade larm',
  'inherited.count.alerts_oldest_days': 'Äldsta ohanterade larmet, i dagar',
  'inherited.count.reviews_overdue': 'Försenade kunduppföljningar',
  'inherited.count.customers_not_screened': 'Kunder som inte kontrollerats mot listor',
  'inherited.count.customers_without_risk_class': 'Kunder utan riskklass',
  'inherited.count.open_audit_findings': 'Öppna revisionsiakttagelser',
  'inherited.count.known_breaches': 'Kända överträdelser',
  'inherited.unknown': 'klienten kunde inte svara',
  'inherited.source': 'Källa till siffran',
  'inherited.valueHint': 'Lämna en siffra tom när klienten inte kunde svara: den visas som okänd, aldrig som noll.',
  'inherited.ordersNote': 'Föreläggandena, kort',
  'inherited.breachesNote': 'Överträdelserna, kort',
  'inherited.record': 'Registrera läget',
  'inherited.edit': 'Ändra läget',
  'inherited.save': 'Spara läget',
  'inherited.acknowledge': 'Registrera bekräftelsen',
  'inherited.ackHint': 'Den person hos klienten som ansvarar för tjänsten.',
  'inherited.ackDocument': 'Undertecknat brev eller protokoll',
  'inherited.acknowledged': 'bekräftat av {name} den {date} ({code})',
  'inherited.notAcknowledged': 'inte bekräftat ännu',
  'inherited.backlogHint': 'Varje balans över noll erbjuder en åtgärd. Inget skapas av sig självt: åtgärden finns först när du skickar in den.',
  'inherited.raiseAction': 'Skapa en åtgärd för: {what} ({n})',
  'inherited.actionTitle': 'Arbeta av den övertagna balansen: {what} ({n})',
  'onboarding.step.mandate': 'Uppdrag',
  'onboarding.step.inherited': 'Övertaget läge',
  'onboarding.mandateIntro': 'För varje tjänst: rollerna som byrån tar, med den regel som gäller i klientens land; klientens egna beslut; och startkontrollerna.',
  'onboarding.inheritedIntro': 'Det byrån tar över när den startar, bekräftat av klienten.',
  'onboarding.startChecksOpen': 'Startkontroller som fortfarande är öppna: {n}. Klienten startar när var och en är klar, inte tillämplig eller undantagen av två personer.',
  'onboarding.startChecksNone': 'Ingen startkontroll är öppen.',
  'library.files': 'Regelfiler',
  'library.file.roles': 'Roller och deras regler per land',
  'library.file.checks': 'Startkontroller',
  'library.noFiles': 'Ingen regelfil är inläst.',
  'library.refsChecked': 'Hänvisningarna kontrollerade av {name} den {date}',
  'library.refsNotChecked': 'Hänvisningarna är inte kontrollerade ännu',
  'library.adoptedFile': 'Antagen av {name} den {date}',
  'library.notAdoptedFile': 'Olivias förslag, ännu inte antaget av er regelbevakning',
  'library.recordRefsChecked': 'Registrera att hänvisningarna kontrollerats',
  'library.adoptFile': 'Anta den här versionen',
  'library.adoptOther': 'Du kontrollerade hänvisningarna: en annan medlem av regelbevakningen antar den här versionen.',
  'library.filesNote': 'Två personer på regelbevakningen: en kontrollerar hänvisningarna mot de konsoliderade texterna, en annan antar den versionen.',
  'people.roles': 'Roller för klienter',
  'people.rolesAbove': '{n}, över byråns gräns på {max}',
  'people.rolesNote': 'Roller som innehas, upprätthålls eller stöttas för klienter, räknat över de klienter du kan se.',
  'settings.roleCapacity': 'Varna när en person tar fler roller för klienter än',
  'settings.roleCapacityHint': 'En varning på sidan med personer, aldrig ett hinder.',
  'services.mandateLink': 'Roller, startkontroller och övertaget läge',
  'nav.business': 'Verksamhet',
  'nav.business.answers': 'Vad gör klienten, i vilken omfattning, och hur exponerad är den?',
  'onboarding.step.business': 'Verksamhet',
  'onboarding.businessIntro': 'Klientens verksamhet i siffror: vad den erbjuder, i vilken omfattning, var, genom vilka system och modeller. Siffror får saknas och visas då som saknade.',
  'library.file.business': 'Fälten i verksamhetsprofilen',
  'library.file.inherent': 'Regeln för inneboende risk',
  'library.file.perimeter': 'Regeln för tillståndets omfattning',
  'record.figure': 'Siffra',
  'record.exposure': 'Exponering',
  'record.feature': 'Inslag i verksamheten',
  'record.system': 'System',
  'record.model': 'Modell',
  'business.title': 'Verksamhetsprofil',
  'business.aggregatesOnly': 'Bara siffror och antal: inget här namnger eller identifierar en kund.',
  'business.features': 'Vad klienten erbjuder',
  'business.noFeatures': 'Inget inslag registrerat ännu.',
  'business.featureAdd': 'Lägg till ett inslag',
  'business.featureRemove': 'Erbjuds inte längre',
  'business.perimeter': 'Inom tillståndet?',
  'business.perimeterNone': 'Inget inslag utanför det som klientens institutstyper har tillstånd för.',
  'business.perimeterQuestion': 'möjligen utanför tillståndet',
  'business.perimeterNote': 'En fråga för en person att undersöka, aldrig en iakttagelse: regeln avgör ingenting.',
  'business.figures': 'Siffror',
  'business.figuresIntro': 'Varje siffra registreras av en medarbetare och bekräftas av en annan. En siffra som regeln för inneboende risk läser räknas först när den är bekräftad.',
  'business.readByRule': 'läses av regeln för inneboende risk',
  'business.notRecorded': 'inte registrerad',
  'business.periodYear': 'för året till och med den {date}',
  'business.periodPoint': 'per den {date}',
  'business.periodEndYear': 'Sista dagen av de tolv månaderna',
  'business.periodEndPoint': 'Räknat den',
  'business.valueIn.count': 'Antal',
  'business.valueIn.eur': 'Belopp i euro',
  'business.valueIn.pct': 'Procent (0 till 100)',
  'business.valueIn.fte': 'Heltidstjänster',
  'business.unit.eur': '{value} EUR',
  'business.unit.pct': '{value} %',
  'business.unit.fte': '{value} heltidstjänster',
  'business.recordFigure': 'Registrera siffran',
  'business.figureConfirmNote': 'En annan medarbetare bekräftar siffran; om den ändras senare upphör bekräftelsen.',
  'business.confirmedBy': 'bekräftad av {name} den {date}',
  'business.toConfirm': 'registrerad av {name}, ska bekräftas av en annan medarbetare',
  'business.confirm': 'Bekräfta',
  'business.retire': 'Gäller inte längre',
  'business.inherent': 'Inneboende risk per område för penningtvätt',
  'business.inherentIntro': 'Beräknad av regeln för inneboende risk utifrån de bekräftade siffrorna och inslagen, per den {date}. Olivias förslag; en person avgör vilken nivå som gäller när nulägesanalysen planeras.',
  'business.band': 'nivå {band}',
  'business.notRated': 'inte bedömd: nivå {band} antas',
  'business.bandReason': 'Nivå {band}:',
  'business.baseBand': 'Inget villkor i regeln höjer det här området.',
  'business.missing': 'Saknas: {fields}',
  'business.missingWhy.not_recorded': 'inte registrerad',
  'business.missingWhy.not_confirmed': 'inte bekräftad',
  'business.missingWhy.no_common_period': 'inte för samma dag som siffran den jämförs med',
  'business.inherentNote': 'Ett område som inte är bedömt behandlas som nivå 3 för mål och prioritet, aldrig lägre.',
  'business.exposures': 'Exponeringar',
  'business.noExposures': 'Ingen exponering registrerad ännu.',
  'business.addExposure': 'Registrera en exponering',
  'business.exposureCode': 'Kod',
  'business.exposureCodeHint.country': 'Två bokstäver, till exempel SE',
  'business.exposureCodeHint.currency': 'Tre bokstäver, till exempel EUR',
  'business.exposureCodeHint.channel': 'En kort kod, till exempel ombud',
  'business.exposureCodeHint.counterparty_type': 'En kort kod, till exempel handlare',
  'business.systems': 'Viktiga system',
  'business.noSystems': 'Inget system registrerat ännu.',
  'business.addSystem': 'Registrera ett system',
  'business.vendor': 'från {vendor}',
  'business.inHouse': 'egenutvecklat',
  'business.vendorHint': 'Lämna tomt om det är egenutvecklat.',
  'business.models': 'Modeller mot ekonomisk brottslighet',
  'business.modelsIntro': 'Klientens logik för övervakning, kontroll mot listor och riskklassning, registrerad oavsett om klienten kallar den en modell.',
  'business.noModels': 'Ingen modell registrerad ännu.',
  'business.addModel': 'Registrera en modell',
  'business.editModel': 'Ändra',
  'business.saveModel': 'Spara modellen',
  'business.lastValidated': 'senast validerad den {date}',
  'business.neverValidated': 'aldrig validerad',
  'business.lastChanged': 'väsentligt ändrad den {date}',
  'business.vendorDefaults': 'leverantörens standardinställningar bedömda: {state}',
  'business.lastValidatedHint': 'Lämna tomt om den aldrig har validerats.',
  'business.modelConfirmNote': 'En annan medarbetare bekräftar modellen; om den ändras senare upphör bekräftelsen.',
  'exposureKind.country': 'land',
  'exposureKind.channel': 'kanal',
  'exposureKind.counterparty_type': 'typ av motpart',
  'exposureKind.currency': 'valuta',
  'exposureMeasure.customers': 'kunder',
  'exposureMeasure.volume_share': 'andel av antalet transaktioner',
  'exposureMeasure.value_share': 'andel av transaktionsvärdet',
  'systemKind.core': 'kärnsystem',
  'systemKind.onboarding': 'kundintag',
  'systemKind.screening': 'kontroll mot listor',
  'systemKind.monitoring': 'transaktionsövervakning',
  'systemKind.case_management': 'ärendehantering',
  'systemKind.reporting': 'rapportering',
  'systemKind.other': 'annat',
  'modelKind.transaction_monitoring': 'transaktionsövervakning',
  'modelKind.screening_customer': 'kontroll av kunder mot listor',
  'modelKind.screening_payment': 'kontroll av betalningar mot listor',
  'modelKind.customer_risk_rating': 'riskklassning av kunder',
  'modelKind.fraud': 'bedrägeriupptäckt',
  'modelKind.adverse_media': 'negativ publicitet',
  'modelKind.ai_tool': 'AI-verktyg',
  'modelKind.other': 'annat',
  'vendorDefaults.yes': 'ja',
  'vendorDefaults.no': 'nej',
  'vendorDefaults.unknown': 'okänt',
  'modelState.validation_due': 'aldrig validerad: en validering behövs',
  'modelState.changed_since_validation': 'ändrad sedan den senaste valideringen',
  'modelState.not_confirmed': 'inte bekräftad',
  'modelState.vendor_defaults_not_assessed': 'leverantörens standardinställningar inte bedömda',
  'auditField.businessField': 'fält',
  'auditField.periodEnd': 'periodens slut',
  'auditField.value': 'värde',
  'auditField.countSource': 'källa',
  'auditField.note': 'anteckning',
  'auditField.exposureKind': 'typ av exponering',
  'auditField.exposureMeasure': 'mått',
  'auditField.exposureBasis': 'hur det räknades',
  'auditField.feature': 'inslag',
  'auditField.setBy': 'angivet av',
  'auditField.setAt': 'angivet den',
  'auditField.systemKind': 'typ av system',
  'auditField.vendor': 'leverantör',
  'auditField.modelKind': 'typ av modell',
  'auditField.versionLabel': 'version',
  'auditField.system': 'system',
  'auditField.inUseSince': 'används sedan',
  'auditField.lastValidatedOn': 'senast validerad den',
  'auditField.lastValidationRef': 'valideringsrapport',
  'auditField.lastSignificantChangeOn': 'senaste väsentliga ändring den',
  'auditField.changeNote': 'vad som ändrades',
  'auditField.openFindings': 'öppna iakttagelser',
  'auditField.openFindingsSource': 'källa till de öppna iakttagelserna',
  'auditField.vendorDefaultsAssessed': 'leverantörens standardinställningar bedömda',
  'auditAction.business.figure': 'Registrerade en siffra i verksamhetsprofilen',
  'auditAction.business.exposure': 'Registrerade en exponering',
  'auditAction.business.confirm': 'Bekräftade en uppgift i verksamhetsprofilen',
  'auditAction.business.retire': 'Registrerade att en uppgift i verksamhetsprofilen inte längre gäller',
  'auditAction.feature.set': 'Registrerade ett inslag i verksamheten',
  'auditAction.feature.retire': 'Registrerade att ett inslag inte längre erbjuds',
  'auditAction.system.add': 'Registrerade ett system',
  'auditAction.system.update': 'Ändrade ett system',
  'auditAction.model.add': 'Registrerade en modell',
  'auditAction.model.update': 'Ändrade en modell',
  'errors.FOUR_EYES_BUSINESS': 'Du registrerade detta, så en annan medarbetare bekräftar det.',
  'errors.BUSINESS_CONFIRMED_ALREADY': 'Detta är redan bekräftat; en ändring gör att bekräftelsen upphör.',
  'errors.BUSINESS_RETIRED': 'Uppgiften gäller inte längre, och det förblir så. Registrera den på nytt vid behov.',
  'errors.BUSINESS_FIELD_UNKNOWN': 'Verksamhetsprofilen har inget sådant fält.',
  'errors.FIGURE_WHOLE_NUMBER': 'Ett antal är ett heltal.',
  'errors.FIGURE_SHARE_TOO_HIGH': 'En andel är högst 100 procent.',
  'errors.MODEL_DATES_ORDER': 'En modell valideras eller ändras efter att den tagits i bruk.',
  'errors.SYSTEM_NOT_CURRENT': 'Ange ett aktuellt system hos den här klienten.',
  'errors.FEATURE_SET_ALREADY': 'Klienten erbjuder redan detta.',
  'library.file.mapAml': 'Arbetsmoment mot penningtvätt för betalningsinstitut och institut för elektroniska pengar',
  'library.file.mapGovernance': 'Arbetsmoment för styrning för betalningsinstitut och institut för elektroniska pengar',
  'library.file.requests': 'Katalog för dokumentbegäran',
  'library.file.guides': 'Intervjuguider',
  'library.file.maturity': 'Regel för mognadsnivåer',
  'library.file.duty': 'Regel för bedömning av lagkrav',
  'library.file.instruments': 'Förteckning över rättsakter',
  'library.pin': '{name}, version {version}',
  'library.maps': 'Arbetsmoment för nulägesanalysen',
  'library.noMaps': 'Ingen karta över arbetsmoment är inläst.',
  'library.mapsCounts': 'Med {requests} punkter i katalogen för dokumentbegäran och {questions} intervjufrågor som pekar på arbetsmomenten.',
  'library.mapLine': '{practices} arbetsmoment, varav {shall} rättsliga skyldigheter och {key} nyckelkontroller · version {version}',
  'library.showPractices': 'Visa arbetsmomenten',
  'library.keyControl': 'nyckelkontroll',
  'library.weight.shall': 'rättslig skyldighet',
  'library.weight.good_practice': 'god praxis',
  'library.refFrom': 'från den {date}',
  'library.refUntil': 'till och med den {date}',
  'nav.currentState': 'Nuläge',
  'nav.currentState.answers': 'Hur moget är klientens regelefterlevnadsarbete i dag, och vad samlas in för att visa det?',
  'onboarding.step.currentState': 'Nuläge',
  'onboarding.currentStateIntro': 'Planera nulägesanalysen: omfattningen utifrån tjänsterna, per vilket datum, vem som tar fram och vem som granskar, utkastet till begäran och intervjuerna. Den hindrar inte starten.',
  'onboarding.currentStateLead': 'En klientansvarig planerar nulägesanalysen.',
  'record.assessment': 'Nulägesanalys',
  'analysis.notFound': 'Det finns ingen analys {code} för den här klienten.',
  'analysis.notFound.list': 'Alla analyser för den här klienten',
  'record.scope': 'Arbetsmoment i omfattningen',
  'record.band': 'Nivå av inneboende risk',
  'record.request': 'Dokumentbegäran',
  'record.requestLine': 'Rad i en begäran',
  'record.inquiry': 'Intervju eller genomgång',
  'record.participant': 'Deltagare',
  'record.statement': 'Uttalande',
  'record.statementSource': 'Konfidentiell källa',
  'record.statementLink': 'Uttalande om ett arbetsmoment',
  'record.inconsistency': 'Motsägelse',
  'assessment.title': 'Nuläge',
  'assessment.intro': 'Nulägesanalysen: planerad utifrån klientens tjänster, insamlad genom dokument och intervjuer. Nivåerna beräknas senare, utifrån underlaget, enligt regel.',
  'assessment.list': 'Analyser',
  'assessment.none': 'Ingen analys är planerad ännu.',
  'assessment.asOfLine': 'per den {date} · {n} arbetsmoment i omfattningen',
  'assessment.plan': 'Planera en nulägesanalys',
  'assessment.create': 'Skapa planen',
  'assessment.windowHint': 'De månader före datumet som underlaget får avse.',
  'assessment.reviewerHint': 'Bara granskare som inte är klientansvariga för någon av klientens tjänster och inte har någon av dess roller erbjuds.',
  'assessment.reviewersNotOffered': 'Erbjuds inte som granskare, eftersom de är klientansvariga för en tjänst eller har en roll här: {names}.',
  'assessment.scopePreview': 'I omfattningen utifrån klientens tjänster',
  'assessment.mapPractices': '{n} av {total} arbetsmoment ({domains})',
  'assessment.mapNotServed': 'utanför byråns tjänster för den här klienten',
  'assessment.mapNotForTypes': 'inte för den här klientens institutstyper',
  'assessment.mapWrittenFor': 'skrivna för {written}',
  'assessment.mapNotForJurisdiction': 'skrivna för {written}, inte för klientens hemland ({home})',
  'assessment.noHomeJurisdiction': 'Registrera klientens hemland i profilen först: varje karta är skriven för de jurisdiktioner den anger.',
  'assessment.bandsPreview': 'Inneboende risk per område för penningtvätt:',
  'assessment.notRatedShort': '{band} antas',
  'assessment.requestPreview': 'Utkast till begäran: {core} centrala punkter ikryssade, {supporting} kompletterande punkter erbjuds.',
  'assessment.gather': 'Börja samla in',
  'assessment.refresh': 'Uppdatera omfattningen',
  'assessment.withdraw': 'Återkalla analysen',
  'assessment.bands': 'Inneboende risk',
  'assessment.bandsIntro': 'Så som regeln för inneboende risk beräknade den vid planeringen, utifrån den bekräftade verksamhetsprofilen. En person kan ändra en nivå med ett skäl; en granskare bekräftar den nivå som gäller.',
  'assessment.bandsNote': 'Används bara för standardmålen och prioriteringen av brister; en nivå sänker aldrig en prioritet under det som nivå 3 ger.',
  'assessment.overridden': 'ändrad från {from} av {name}: {reason}',
  'assessment.bandConfirmed': 'bekräftad av {name}',
  'assessment.bandToConfirm': 'en granskare bekräftar den nivå som gäller',
  'assessment.bandMissing': '{n} siffror saknas eller är inte bekräftade',
  'assessment.override': 'Ändra nivån',
  'assessment.confirmBand': 'Bekräfta nivån',
  'assessment.scope': 'Omfattning',
  'assessment.scopeCount': '{n} av {total} arbetsmoment i omfattningen',
  'assessment.scopeDecided': '{name}: {reason}',
  'assessment.scopeConfirmed': 'bekräftat av {name}',
  'assessment.scopeToConfirm': 'en granskare bekräftar detta för en rättslig skyldighet',
  'assessment.takeOut': 'Ta bort ur omfattningen',
  'assessment.putBack': 'Ta tillbaka i omfattningen',
  'assessment.confirmScope': 'Bekräfta',
  'assessment.notApplying': '{n} arbetsmoment gäller inte den här klienten',
  'assessment.notApplying.one': '{n} arbetsmoment gäller inte den här klienten',
  'assessment.notOnSimplified': 'inte för en entitet med den förenklade ramen i DORA art. 16',
  'assessment.onlyForTypes': 'endast för: {types}',
  'assessment.doesNotApply': 'bara med {what}',
  'assessment.requests': 'Dokumentbegäran',
  'assessment.noRequests': 'Ingen begäran ännu.',
  'assessment.requestLine': '{received} av {lines} mottagna · skickad {date}',
  'assessment.newRequest': 'Ny begäran',
  'assessment.interviewPlan': 'Intervjuplan',
  'assessment.interviewRole': '{role}: {n} frågor',
  'assessment.inquiries': 'Intervjuer, genomgångar och workshoppar',
  'assessment.noInquiries': 'Inga ännu.',
  'assessment.statementsCount': '{n} uttalanden',
  'assessment.newInquiry': 'Ny intervju eller genomgång',
  'assessment.inconsistencies': 'Motsägelser',
  'assessment.disposedBy': 'hanterad av {name}: {disposition}',
  'assessment.dispose': 'Hantera den',
  'appliesIf.modelAny': 'en modell i inventeringen',
  'appliesIf.modelVendor': 'en leverantörs modell i inventeringen',
  'request.send': 'Registrera som skickad',
  'request.close': 'Stäng begäran',
  'request.checklist': 'Ladda ned checklistan (Excel)',
  'request.letter': 'Ladda ned brevet (Word)',
  'request.lines': 'Det som begärs',
  'request.addItem': 'Lägg till från katalogen',
  'request.addOwn': 'Lägg till en egen rad',
  'request.attach': 'Koppla det mottagna dokumentet',
  'request.markReceived': 'Markera som mottagen',
  'request.withdrawLine': 'Återkalla raden',
  'request.noCustomers': 'Begär inga kundnamn, kundförteckningar eller uppgifter om misstankar: det räcker med antal.',
  'requestTier.core': 'central',
  'requestTier.supporting': 'kompletterande',
  'inquiry.record': 'Registrera som genomförd',
  'inquiry.close': 'Stäng',
  'inquiry.notesNote': 'Anteckningarna är ett dokument hos klienten utan uppgifter på kundnivå. Deltagarna kan bekräfta dem eller skicka rättelser.',
  'inquiry.participants': 'Deltagare',
  'inquiry.noParticipants': 'Ingen deltagare ännu.',
  'inquiry.participant': 'Deltagare',
  'inquiry.member': 'medarbetare på byrån',
  'inquiry.addParticipant': 'Lägg till deltagaren',
  'inquiry.questions': 'Frågor ur guiden',
  'inquiry.noQuestions': 'Ingen fråga vald.',
  'inquiry.chooseQuestions': 'Välj frågorna',
  'inquiry.nearSuspicion': 'nära rapporter om misstankar',
  'inquiry.confirmWith': 'Bekräfta med: {what}',
  'inquiry.statements': 'Uttalanden',
  'inquiry.noStatements': 'Inget uttalande ännu.',
  'inquiry.confidentialSource': 'En konfidentiell källa',
  'inquiry.sourceShownToYou': '(visas för dig som intervjuare eller granskare; andra ser bara rollen: {role})',
  'inquiry.retired': 'Återkallat: {reason}',
  'inquiry.retireStatement': 'Återkalla uttalandet',
  'inquiry.noQuestion': 'Ingen särskild fråga',
  'inquiry.speaker': 'Vem som sade det',
  'inquiry.roleHint': 'I leveranser anges uttalanden bara med roll.',
  'inquiry.speaksTo': 'Arbetsmoment som det gäller',
  'inquiry.confidentialTick': 'Konfidentiell källa: bara intervjuaren och granskaren ser vem som sade det.',
  'inquiry.noSuspicionTick': 'Jag bekräftar att uttalandet inte avser någon enskild bedömning av eller rapport om misstanke.',
  'inquiry.noAllegationTick': 'Jag bekräftar att uttalandet inte innehåller något påstående om missförhållanden mot en namngiven person; ett sådant påstående hör hemma i klientens visselblåsarkanal.',
  'inquiry.recordStatement': 'Registrera uttalandet',
  'assessmentKind.baseline': 'nulägesanalys',
  'assessmentKind.reassessment': 'förnyad bedömning',
  'assessmentKind.triggered': 'händelsestyrd bedömning',
  'assessmentKind.correction': 'rättelse',
  'assessmentStatus.planning': 'planeras',
  'assessmentStatus.gathering': 'insamling',
  'assessmentStatus.assessing': 'bedöms',
  'assessmentStatus.in_review': 'granskas',
  'assessmentStatus.frozen': 'låst',
  'assessmentStatus.withdrawn': 'återkallad',
  'scope.in_scope': 'i omfattningen',
  'scope.not_applicable': 'inte tillämplig',
  'scope.not_assessed': 'bedöms inte',
  'riskSource.business_profile': 'verksamhetsprofilen',
  'riskSource.atlas': 'riskatlasen',
  'requestStatus.draft': 'utkast',
  'requestStatus.sent': 'skickad',
  'requestStatus.closed': 'stängd',
  'lineStatus.asked': 'begärd',
  'lineStatus.received': 'mottagen',
  'lineStatus.not_held': 'finns inte',
  'lineStatus.not_applicable': 'inte tillämplig',
  'lineStatus.withdrawn': 'återkallad',
  'inquiryKind.interview': 'intervju',
  'inquiryKind.walkthrough': 'genomgång',
  'inquiryKind.workshop': 'workshop',
  'inquiryKind.questionnaire': 'enkät',
  'inquiryKind.validation_meeting': 'avstämningsmöte',
  'inquiryStatus.draft': 'planerad',
  'inquiryStatus.recorded': 'registrerad',
  'inquiryStatus.closed': 'stängd',
  'participantConfirmation.none': 'inte bekräftad ännu',
  'participantConfirmation.confirmed': 'bekräftad av deltagarna',
  'participantConfirmation.corrections_received': 'rättelser mottagna',
  'statementKind.claim': 'påstående',
  'statementKind.confirms_document': 'bekräftar ett dokument',
  'statementKind.contradicts_document': 'motsäger ett dokument',
  'statementKind.self_rating': 'självskattning',
  'statementKind.management_response': 'ledningens svar',
  'statementKind.root_cause': 'grundorsak',
  'inconsistencyState.open': 'öppen',
  'inconsistencyState.resolved_by_fact': 'löst genom ett faktum',
  'inconsistencyState.disposed': 'hanterad',
  'auditField.assessmentKind': 'typ av bedömning',
  'auditField.windowMonths': 'period i månader',
  'auditField.gatheringFrom': 'insamling från',
  'auditField.cycleItem': 'punkt i årscykeln',
  'auditField.withdrawnReason': 'skäl för återkallelse',
  'auditField.scope': 'omfattning',
  'auditField.scopeReason': 'skäl',
  'auditField.domain': 'område',
  'auditField.computedBand': 'beräknad nivå',
  'auditField.computedReasons': 'skälen för den beräknade nivån',
  'auditField.ruleVersion': 'regelversion',
  'auditField.overrideBand': 'nivå satt av en person',
  'auditField.overrideReason': 'skäl för nivån',
  'auditField.overrideBy': 'nivån satt av',
  'auditField.overrideAt': 'nivån satt den',
  'auditField.bandSource': 'källa till nivån',
  'auditField.assessment': 'analys',
  'auditField.sentTo': 'skickad till',
  'auditField.sentOn': 'skickad den',
  'auditField.sentBy': 'registrerad som skickad av',
  'auditField.lineNo': 'rad',
  'auditField.requestItem': 'katalogpunkt',
  'auditField.why': 'varför',
  'auditField.askedOn': 'begärd den',
  'auditField.answeredNote': 'vad klienten svarade',
  'auditField.inquiryKind': 'typ',
  'auditField.heldOn': 'genomförd den',
  'auditField.interviewer': 'intervjuare',
  'auditField.secondListener': 'bisittare',
  'auditField.guideQuestions': 'frågor ur guiden',
  'auditField.notesDocument': 'anteckningar',
  'auditField.sharedOn': 'anteckningarna delade med deltagarna den',
  'auditField.participantConfirmation': 'deltagarnas bekräftelse',
  'auditField.confirmationDocument': 'bekräftelse',
  'auditField.participantContact': 'deltagare hos klienten',
  'auditField.participantPerson': 'deltagare på byrån',
  'auditField.roleTitle': 'roll',
  'auditField.question': 'fråga',
  'auditField.speakerContact': 'talare hos klienten',
  'auditField.speakerPerson': 'talare på byrån',
  'auditField.speakerRole': 'talarens roll',
  'auditField.statementText': 'uttalande',
  'auditField.statementKind': 'typ av uttalande',
  'auditField.selfRating': 'självskattad nivå',
  'auditField.confidentialSource': 'konfidentiell källa',
  'auditField.noSuspicionConfirmed': 'avser ingen misstanke, bekräftat',
  'auditField.noAllegationConfirmed': 'innehåller inget påstående om missförhållanden, bekräftat',
  'auditField.linkedBy': 'kopplad av',
  'auditField.linkedAt': 'kopplad den',
  'auditField.disposition': 'hantering',
  'auditField.disposedBy': 'hanterad av',
  'auditField.disposedAt': 'hanterad den',
  'auditAction.assessment.plan': 'Planerade en nulägesanalys',
  'auditAction.assessment.update': 'Ändrade planen för en analys',
  'auditAction.assessment.refresh': 'Uppdaterade omfattningen av en analys',
  'auditAction.assessment.gather': 'Började samla in för en analys',
  'auditAction.assessment.withdraw': 'Återkallade en analys',
  'auditAction.assessment.scope': 'Ändrade omfattningen för ett arbetsmoment',
  'auditAction.assessment.scope_confirm': 'Bekräftade ett beslut om omfattning',
  'auditAction.assessment.band_override': 'Satte en nivå av inneboende risk',
  'auditAction.assessment.band_confirm': 'Bekräftade en nivå av inneboende risk',
  'auditAction.request.create': 'Skapade en dokumentbegäran',
  'auditAction.request.update': 'Ändrade en dokumentbegäran',
  'auditAction.request.line_add': 'Lade till en rad i en begäran',
  'auditAction.request.line': 'Registrerade svaret på en rad',
  'auditAction.request.line_document': 'Kopplade ett mottaget dokument till en rad',
  'auditAction.request.send': 'Registrerade en begäran som skickad',
  'auditAction.request.close': 'Stängde en begäran',
  'auditAction.inquiry.create': 'Planerade en intervju eller genomgång',
  'auditAction.inquiry.update': 'Ändrade en intervju eller genomgång',
  'auditAction.inquiry.participant': 'Lade till en deltagare',
  'auditAction.inquiry.record': 'Registrerade en intervju som genomförd',
  'auditAction.inquiry.close': 'Stängde en intervju',
  'auditAction.inquiry.statement': 'Registrerade ett uttalande',
  'auditAction.inquiry.statement_retire': 'Återkallade ett uttalande',
  'auditAction.inconsistency.dispose': 'Hanterade en motsägelse',
  'errors.ASSESSMENT_PEOPLE_NEEDED': 'Ange vem som tar fram och vem som granskar innan insamlingen börjar.',
  'errors.ASSESSMENT_DATES_FIXED': 'Datumet och perioden bestäms medan analysen planeras.',
  // The engine's records (stage 1 of the engine).
  'record.evidence': 'Underlag',
  'record.fact': 'Konstaterande',
  'record.factEvidence': 'Underlag för ett konstaterande',
  'record.element': 'Obligatoriskt inslag',
  'record.sampleTest': 'Stickprovstest',
  'record.kri': 'KRI',
  'record.kriReading': 'KRI-avläsning',
  'record.practiceLink': 'Koppling till ett arbetsmoment',
  'record.escalation': 'Eskalering',
  'record.target': 'Målnivå',
  'record.duty': 'Bedömning av lagkrav',
  'auditAction.assessment.assess': 'Började bedöma en analys',
  'auditAction.assessment.submit': 'Skickade en analys till granskning',
  'auditAction.assessment.send_back': 'Skickade tillbaka en analys till den som förbereder den',
  // The freeze (033; addendum §8.13, §14 "Review and freeze")
  'auditAction.assessment.freeze': 'Låste en analys',
  'auditAction.job.baseline.recompute': 'Räknade om de låsta analyserna från deras ögonblicksbilder',
  'record.snapshot': 'Ögonblicksbild',
  'record.result': 'Låst resultat',
  'auditField.snapshotKind': 'typ av ögonblicksbild',
  'snapshotKind.current_state': 'nulägesanalys',
  'auditField.levelKind': 'resultat för',
  'levelKind.practice': 'ett arbetsmoment',
  'levelKind.capability': 'en förmåga',
  'levelKind.function': 'en funktion',
  'auditField.subject': 'avser',
  'auditField.exportKind': 'vad som exporterades',
  'auditField.exportedBy': 'exporterat av',
  'auditField.exportedAt': 'exporterat',
  'exportKind.request_letter': 'begäran som brev',
  'exportKind.request_checklist': 'begäran som checklista',
  'exportKind.missing_letter': 'det vi fortfarande behöver, som brev',
  'exportKind.picture': 'organisationen på en sida',
  'auditField.computedLevel': 'beräknad nivå',
  'auditField.finalLevel': 'slutlig nivå',
  'auditField.capReason': 'begränsad eftersom',
  'capReason.self_assessed': 'bedömd av den som utför funktionen',
  'capReason.unknown': 'en högre nivå kunde inte fastställas',
  'auditField.performedBy': 'utförs av',
  'auditField.gap': 'avstånd till målnivå',
  'auditField.priority': 'prioritet',
  'auditField.quickWin': 'snabb åtgärd',
  'auditField.capOnly': 'avstånd som bara beror på taket för den som utför funktionen',
  'auditField.wave': 'etapp i färdplanen',
  'auditField.resultState': 'läge',
  'resultState.assessed': 'bedömd',
  'resultState.not_applicable': 'ej tillämpligt',
  'resultState.not_assessed': 'ej bedömd',
  'resultState.not_determined': 'kan inte fastställas',
  'auditField.atOrAbove': 'på eller över målnivå',
  'auditField.total': 'av',
  'auditField.notDeterminedCount': 'kan inte fastställas',
  'errors.FREEZE_NOT_IN_REVIEW': 'En analys låses när den är under granskning.',
  'errors.FOUR_EYES_FREEZE': 'Du skickade analysen för granskning, så en annan granskare låser den.',
  'errors.FREEZE_NOT_INDEPENDENT': 'Du leder eller har en roll i en tjänst som ingår, så en annan granskare låser den.',
  'errors.FREEZE_NOT_REVIEWED': 'Inte ännu: {n} arbetsmoment har inte markerats som granskade av en oberoende granskare som inte själv registrerat deras konstateranden.',
  'errors.FREEZE_NOT_REVIEWED.one': 'Inte ännu: 1 arbetsmoment har inte markerats som granskat av en oberoende granskare som inte själv registrerat dess konstateranden.',
  'errors.FREEZE_OVERRIDE_UNCONFIRMED': 'Inte ännu: {n} justeringar väntar på bekräftelse.',
  'errors.FREEZE_OVERRIDE_UNCONFIRMED.one': 'Inte ännu: 1 justering väntar på bekräftelse.',
  'errors.FREEZE_SCOPE_UNCONFIRMED': 'Inte ännu: {n} beslut att lämna ett lagkrav utanför väntar på bekräftelse.',
  'errors.FREEZE_SCOPE_UNCONFIRMED.one': 'Inte ännu: 1 beslut att lämna ett lagkrav utanför väntar på bekräftelse.',
  'errors.FREEZE_TARGET_UNCONFIRMED': 'Inte ännu: {n} målnivåer som avviker från standard väntar på bekräftelse.',
  'errors.FREEZE_TARGET_UNCONFIRMED.one': 'Inte ännu: 1 målnivå som avviker från standard väntar på bekräftelse.',
  'errors.FREEZE_BAND_UNCONFIRMED': 'Inte ännu: {n} nivåer av inneboende risk väntar på bekräftelse.',
  'errors.FREEZE_BAND_UNCONFIRMED.one': 'Inte ännu: 1 nivå av inneboende risk väntar på bekräftelse.',
  'errors.FREEZE_EVIDENCE_UNREVIEWED': 'Inte ännu: {n} underlagsposter väntar på att någon annan granskar dem.',
  'errors.FREEZE_EVIDENCE_UNREVIEWED.one': 'Inte ännu: 1 underlagspost väntar på att någon annan granskar den.',
  'errors.FREEZE_QUOTE_NOT_FOUND': 'Inte ännu: {n} citat finns inte längre i sina dokument.',
  'errors.FREEZE_QUOTE_NOT_FOUND.one': 'Inte ännu: 1 citat finns inte längre i sitt dokument.',
  'errors.FREEZE_INCONSISTENCY_OPEN': 'Inte ännu: {n} motstridigheter är öppna.',
  'errors.FREEZE_INCONSISTENCY_OPEN.one': 'Inte ännu: 1 motstridighet är öppen.',
  'errors.FREEZE_DUTY_UNCONFIRMED': 'Inte ännu: {n} bedömningar av lagkrav är inte beslutade och bekräftade.',
  'errors.FREEZE_DUTY_UNCONFIRMED.one': 'Inte ännu: 1 bedömning av lagkrav är inte beslutad och bekräftad.',
  'errors.FREEZE_NOTICE_UNSENT': 'Inte ännu: {n} eskaleringsmeddelanden är inte registrerade som skickade.',
  'errors.FREEZE_NOTICE_UNSENT.one': 'Inte ännu: 1 eskaleringsmeddelande är inte registrerat som skickat.',
  'freeze.title': 'Granskning och låsning',
  'freeze.before': 'Innan analysen låses',
  'freeze.blocker.not_reviewed': 'Arbetsmoment i omfattningen som har markerats som granskade',
  'freeze.blocker.override': 'Bekräftade justeringar',
  'freeze.blocker.scope': 'Arbetsmoment med lagkrav som lämnats utanför omfattningen, bekräftade',
  'freeze.blocker.target': 'Målnivåer som avviker från standard, bekräftade',
  'freeze.blocker.band': 'Bekräftade nivåer av inneboende risk',
  'freeze.blocker.evidence': 'Underlagsposter som analysen vilar på, granskade av en annan medlem',
  'freeze.blocker.quotes': 'Citat som fortfarande finns i sina dokument',
  'freeze.blocker.inconsistencies': 'Lösta motstridigheter',
  'freeze.blocker.duty': 'Bedömningar av lagkrav som är beslutade och bekräftade',
  'freeze.blocker.notices': 'Eskaleringsmeddelanden registrerade som skickade',
  'freeze.waiting': '{n} väntar',
  'freeze.line': '{label}: {done} av {total}',
  'freeze.done': 'klart',
  'freeze.freeze': 'Lås',
  'freeze.frozenNow': 'Analysen är låst.',
  'freeze.never': 'En låst analys ändras aldrig; en rättelse görs som en ny analys.',
  'freeze.whyNot.submitted': 'Du skickade analysen för granskning, så en annan granskare låser den.',
  'freeze.whyNot.not_independent': 'Du leder eller har en roll i en tjänst som ingår, så en annan granskare låser den.',
  'freeze.whyNot.reviewer': 'En granskare för den här klienten låser analysen.',
  'freeze.whyNot.waits': 'Låsningen öppnas när ingenting ovan väntar.',
  'freeze.internalOnly': 'Endast för internt bruk',
  'freeze.internalOnlyWarning': 'Några biblioteksfiler som analysen bygger på är ännu inte antagna av regelbevakningen: {files}. Analysen låses för internt bruk, och dess rapport kan inte publiceras förrän de är antagna.',
  'freeze.allAdopted': 'Alla biblioteksfiler som analysen bygger på är antagna av regelbevakningen.',
  'freeze.frozenTitle': 'Låst',
  'freeze.snapshot': 'Ögonblicksbild',
  'freeze.snapshotLine': '{code} · SHA-256 {hash}…',
  'freeze.matches': 'Omräknat från ögonblicksbilden: resultaten stämmer.',
  'freeze.mismatch': 'Omräknat från ögonblicksbilden: {n} resultat stämmer inte.',
  'freeze.mismatch.one': 'Omräknat från ögonblicksbilden: 1 resultat stämmer inte.',
  'freeze.hashBroken': 'Ögonblicksbilden stämmer inte längre med den kontrollsumma som sparades med den.',
  'freeze.filesMissing': 'Biblioteksfiler som ögonblicksbilden hänvisar till saknas: {files}.',
  'engine.frozenNotice': 'Låst: detta är resultaten som sparades vid låsningen, omräknade enligt regel från dess ögonblicksbild. Ingen siffra här sätts av en AI-modell.',
  'auditAction.evidence.add': 'Registrerade underlag',
  'auditAction.evidence.review': 'Granskade underlag',
  'auditAction.evidence.retire': 'Avregistrerade underlag',
  'auditAction.fact.record': 'Registrerade ett konstaterande',
  'auditAction.fact.escalate': 'Registrerade en saknad grundläggande kontroll och eskalerade den',
  'auditAction.fact.confirm': 'Bekräftade ett konstaterande',
  'auditAction.fact.retire': 'Drog tillbaka ett konstaterande',
  'auditAction.element.record': 'Registrerade ett obligatoriskt inslag',
  'auditAction.practice.override': 'Justerade en beräknad nivå',
  'auditAction.practice.override_confirm': 'Bekräftade en justerad nivå',
  'auditAction.practice.review': 'Markerade ett arbetsmoment som granskat',
  'auditAction.sample.plan': 'Planerade ett stickprovstest',
  'auditAction.sample.results': 'Registrerade resultatet av ett stickprovstest',
  'auditAction.sample.review': 'Granskade ett stickprovstest',
  'auditAction.sample.conclude': 'Drog en slutsats av ett stickprovstest',
  'auditAction.sample.conclusion_confirm': 'Bekräftade slutsatsen av ett stickprovstest',
  'auditAction.kri.create': 'Lade till en KRI',
  'auditAction.kri.reading': 'Registrerade en KRI-avläsning',
  'auditAction.kri.reading_confirm': 'Bekräftade en KRI-avläsning',
  'auditAction.practice_link.add': 'Kopplade en post till ett arbetsmoment',
  'auditAction.practice_link.remove': 'Tog bort en koppling till ett arbetsmoment',
  'auditAction.escalation.notice': 'Registrerade att ett eskaleringsmeddelande har skickats',
  'auditAction.target.set': 'Angav en målnivå',
  'auditAction.target.confirm': 'Bekräftade en målnivå',
  'auditAction.duty.propose': 'Uppdaterade förslagen om skyldigheter',
  'auditAction.duty.decide': 'Beslutade en bedömning av lagkrav',
  'auditAction.duty.confirm': 'Bekräftade en bedömning av lagkrav',
  'errors.ASSESSMENT_STATUS_NEEDED': 'Det här kan bara göras när analysen har en annan status.',
  'errors.ASSESSMENT_STATE': 'Analysen kan inte gå till den statusen härifrån.',
  'errors.SUBMIT_PRACTICES_OPEN': 'Varje arbetsmoment i omfattningen behöver sitt konstaterande om att det utförs innan granskningen: {practices}.',
  'errors.SUBMIT_PREPARER_ONLY': 'Den som förbereder analysen skickar den till granskning.',
  'errors.OVERRIDE_NOT_ASSESSED': 'En nivå justeras bara för ett bedömt arbetsmoment, aldrig för ett som inte kan fastställas, inte bedöms eller inte är tillämpligt.',
  'errors.OVERRIDE_OUT_OF_RANGE': 'En nivå ligger mellan 0 och 4.',
  'errors.OVERRIDE_ABOVE_CAP': 'Byrån utför det här arbetsmomentet, så ingen justering lyfter det över nivå {cap}: nivå 4 kräver en oberoende bedömning.',
  'errors.SAMPLE_ALL_NOT_POPULATION': 'Att testa alla betyder att hela populationen testas: storleken är {population}.',
  'errors.EVIDENCE_SOURCE_KIND': 'Bara ett protokoll eller en logg, ett dokument eller en KRI-serie kan komma från ett systemuttag.',
  'errors.NOT_INDEPENDENT_OF_SERVICE': 'Du är ansvarig för, eller har en roll i, den tjänst som omfattar det här arbetsmomentet, så någon annan gör det här.',
  'errors.FOUR_EYES_PRACTICE_REVIEW': 'Du har registrerat konstateranden eller inslag för det här arbetsmomentet, så en annan granskare markerar det som granskat.',
  'errors.PRACTICE_REVIEWED_ALREADY': 'Det här arbetsmomentet är redan markerat som granskat.',
  'errors.PRACTICE_REVIEW_OUT_OF_SCOPE': 'Bara ett arbetsmoment i omfattningen markeras som granskat.',
  'errors.FOUR_EYES_OVERRIDE': 'Du justerade den här nivån, så en annan granskare bekräftar den.',
  'errors.OVERRIDE_NOTHING_TO_CONFIRM': 'Bara en justerad nivå väntar på bekräftelse.',
  'errors.KRI_RETIRED': 'Den här KRI:n är avslutad och förblir det.',
  'errors.READING_CONFIRMED_ALREADY': 'Den här avläsningen är redan bekräftad.',
  'errors.FOUR_EYES_READING': 'Du registrerade den här avläsningen, så en annan medlem bekräftar den.',
  'errors.PRACTICE_UNKNOWN': 'Biblioteket har inget arbetsmoment {practice}.',
  'errors.SAMPLE_STATE': 'Ett stickprovstest går från planerat till testat till granskat.',
  'errors.SAMPLE_PLAN_FIXED': 'Planen för ett stickprovstest ligger fast när resultatet är registrerat.',
  'errors.SAMPLE_RESULTS_RECORDED': 'Resultatet av det här stickprovstestet är redan registrerat.',
  'errors.FOUR_EYES_SAMPLE': 'Du registrerade resultatet, så en annan granskare granskar testet.',
  'errors.SAMPLE_RULE_CONCLUDED': 'Regeln har dragit slutsatsen av det här testet, så ingen person skriver den.',
  'errors.SAMPLE_NOTHING_TO_CONFIRM': 'Bara en persons slutsats väntar på bekräftelse.',
  'errors.FOUR_EYES_SAMPLE_CONCLUSION': 'Du skrev den här slutsatsen, så en annan granskare bekräftar den.',
  'errors.SAMPLE_METHOD_NEEDED': 'En storlek ur en tabell anger urvalsmetoden.',
  'errors.SAMPLE_NO_ROW': 'Metoden anger ingen storlek för den här kontrollen och populationen; ange storleken med ett skäl.',
  'errors.SAMPLE_SIZE_OUT_OF_RANGE': 'Välj en storlek från {min} till {max}, som metoden tillåter, med ett skäl.',
  'errors.SAMPLE_SIZE_NEEDED': 'Ange storleken, med ett skäl.',
  'errors.EVIDENCE_RETIRED': 'Det här underlaget är avregistrerat och förblir det.',
  'errors.EVIDENCE_IN_USE': 'Ett konstaterande eller ett inslag vilar på det här underlaget, så det tas inte bort.',
  'errors.EVIDENCE_REVIEWED_ALREADY': 'Det här underlaget är redan granskat.',
  'errors.FOUR_EYES_EVIDENCE': 'Du hämtade in det här underlaget, så en annan medlem granskar det.',
  'errors.EVIDENCE_REVIEWED_FIXED': 'Granskat underlag ändras aldrig; ta bort det och registrera ett nytt.',
  'errors.CUSTOMER_LEVEL_NOT_FOR_ASSESSMENT': 'Ett dokument med uppgifter på kundnivå är inte underlag för analysen.',
  'errors.EVIDENCE_STATEMENT_NOT_HERE': 'Ett uttalande är underlag för sin egen analys, så länge det gäller.',
  'errors.EVIDENCE_SELF_RATING_KIND': 'En självskattning är underlag av typen självskattning, och bara den.',
  'errors.EVIDENCE_CYCLE_NOT_DONE': 'En punkt i årshjulet blir underlag när den är klar.',
  'errors.EVIDENCE_NOT_HERE': 'Underlag kopplas inom sin egen analys.',
  'errors.FACT_PRACTICE_OUT_OF_SCOPE': 'Konstateranden registreras för ett arbetsmoment i omfattningen.',
  'errors.FACT_CONFIRMED_ALREADY': 'Det här konstaterandet är redan bekräftat.',
  'errors.FOUR_EYES_FACT': 'Du registrerade det här konstaterandet, så en annan medlem bekräftar det.',
  'errors.ELEMENT_UNKNOWN': 'Det här arbetsmomentet har inget inslag {element}.',
  'errors.ELEMENT_NEEDS_QUOTE': 'Ett inslag som finns anger sitt underlag, citatet och var det står.',
  'errors.QUOTE_NOT_CHECKABLE': 'Texten i {document} kan inte läsas, så citatet kan inte kontrolleras.',
  'errors.FACT_NEEDS_EVIDENCE': 'Ett ja eller ett delvis kräver underlag av en klass som räcker för det.',
  'errors.ESCALATION_NEEDED': 'En saknad grundläggande kontroll eskaleras i samma steg: ange åtgärdens ansvarige, dess förfallodag och vem som får brevet.',
  'errors.ESCALATION_NOT_HERE': 'Bara ett nej på en grundläggande kontroll som arbetsmomentet eskalerar öppnar en eskalering.',
  'errors.ESCALATION_ACTION_NEEDED': 'En eskalering skapar en P1-åtgärd med ansvarig och förfallodag.',
  'errors.NOTICE_SENT_ALREADY': 'Eskaleringsmeddelandet är redan registrerat som skickat.',
  'errors.NOTICE_DATE_RANGE': 'Ett eskaleringsmeddelande skickas efter eskaleringen och senast i dag.',
  'errors.NOTICE_LETTER_NEEDED': 'Ett eskaleringsmeddelande registreras som skickat tillsammans med brevet som skickades.',
  'errors.TARGET_NOTHING_TO_CONFIRM': 'Bara en målnivå som avviker från standardnivån väntar på bekräftelse.',
  'errors.FOUR_EYES_TARGET': 'Du angav den här målnivån, så en annan granskare bekräftar den.',
  'errors.DUTY_NOTHING_TO_CONFIRM': 'Bara en beslutad slutsats väntar på bekräftelse.',
  'errors.FOUR_EYES_DUTY': 'Du beslutade den här slutsatsen, så en annan granskare bekräftar den.',
  'errors.DUTY_NOT_IN_FORCE': 'En skyldighet bedöms enligt en regel som gäller per bedömningsdagen; en kommande regel visas som beredskap.',
  'auditField.submittedBy': 'skickad till granskning av',
  'auditField.submittedAt': 'skickad till granskning den',
  'auditField.frozenBy': 'låst av',
  'auditField.frozenAt': 'låst den',
  'auditField.internalOnly': 'endast för internt bruk',
  'auditField.levelOverride': 'nivån justerad med',
  'auditField.levelOverrideReason': 'skäl för att justera nivån',
  'auditField.levelOverrideBy': 'nivån justerad av',
  'auditField.levelOverrideAt': 'nivån justerad den',
  'auditField.unit': 'enhet',
  'auditField.kriDirection': 'sämre när',
  'auditField.warningAt': 'varning vid',
  'auditField.breachAt': 'överskridande vid',
  'auditField.frequency': 'frekvens',
  'auditField.kriPosition': 'i förhållande till gränsvärdena',
  'auditField.readingSource': 'källa till avläsningen',
  'auditField.practice': 'arbetsmoment',
  'auditField.populationNote': 'vad som testades',
  'auditField.populationCount': 'population',
  'auditField.periodFrom': 'period från',
  'auditField.periodTo': 'period till',
  'auditField.controlFrequency': 'kontrollens frekvens',
  'auditField.controlNature': 'kontrollens art',
  'auditField.samplingMethod': 'urvalsmetod',
  'auditField.samplingMethodVersion': 'metodens version',
  'auditField.sizeSource': 'storlek från',
  'auditField.sizeReason': 'skäl för storleken',
  'auditField.selection': 'urval',
  'auditField.sampleSize': 'stickprovets storlek',
  'auditField.tolerableExceptions': 'tolererbara avvikelser',
  'auditField.itemsTested': 'testade poster',
  'auditField.itemsNotTested': 'otestade poster',
  'auditField.exceptions': 'avvikelser',
  'auditField.exceptionKinds': 'typer av avvikelser',
  'auditField.methodAdopted': 'metoden antagen',
  'auditField.ruleConclusion': 'slutsats enligt regel',
  'auditField.ruleReason': 'varför regeln inte drog någon slutsats',
  'auditField.personConclusion': 'en persons slutsats',
  'auditField.personReason': 'skäl för personens slutsats',
  'auditField.concludedBy': 'slutsats dragen av',
  'auditField.concludedAt': 'slutsats dragen den',
  'auditField.resultDocument': 'resultat',
  'auditField.testedBy': 'testat av',
  'auditField.testedAt': 'testat den',
  'auditField.evidenceKind': 'typ av underlag',
  'auditField.datedOn': 'daterat',
  'auditField.coversFrom': 'avser från',
  'auditField.coversTo': 'avser till',
  'auditField.evidenceSource': 'hur det hämtades in',
  'auditField.preparedForAssessment': 'framtaget för analysen',
  'auditField.original': 'original',
  'auditField.approvalBody': 'godkänt av',
  'auditField.approvalRef': 'beslutsreferens',
  'auditField.approvedOn': 'godkänt den',
  'auditField.validUntil': 'giltigt till',
  'auditField.testerKind': 'testat av (typ)',
  'auditField.testerPerson': 'testare på byrån',
  'auditField.testerContact': 'testare hos klienten',
  'auditField.receiptDocument': 'kvitto',
  'auditField.obtainedBy': 'inhämtat av',
  'auditField.obtainedAt': 'inhämtat den',
  'auditField.fact': 'konstaterande',
  'auditField.factValue': 'värde',
  'auditField.checkedAt': 'kontrollerat den',
  'auditField.element': 'inslag',
  'auditField.elementState': 'läge',
  'auditField.noticeTo': 'meddelande till',
  'auditField.noticeState': 'meddelande',
  'auditField.noticeSentOn': 'meddelandet skickat den',
  'auditField.noticeDocument': 'brev',
  'auditField.sentAt': 'registrerat som skickat den',
  'auditField.capability': 'förmåga',
  'auditField.targetLevel': 'målnivå',
  'auditField.targetReason': 'skäl för målnivån',
  'auditField.reference': 'hänvisning',
  'auditField.proposedValue': 'regelns förslag',
  'auditField.proposedReasons': 'skälen för förslaget',
  'auditField.proposedAt': 'föreslaget den',
  'auditField.dutyValue': 'slutsats',
  'source.current_state': 'nulägesanalys',
  'source.escalation': 'eskalering',
  'kriDirection.higher_is_worse': 'högre',
  'kriDirection.lower_is_worse': 'lägre',
  'kriFrequency.monthly': 'månadsvis',
  'kriFrequency.quarterly': 'kvartalsvis',
  'kriFrequency.yearly': 'årligen',
  'kriPosition.within': 'inom gränsvärdena',
  'kriPosition.warning': 'på varningsnivå',
  'kriPosition.breach': 'över gränsvärdet',
  'kriPosition.no_threshold': 'inget gränsvärde angivet',
  'readingSource.manual': 'registrerad av en medarbetare',
  'controlFrequency.annual': 'årlig',
  'controlFrequency.semiannual': 'halvårsvis',
  'controlFrequency.quarterly': 'kvartalsvis',
  'controlFrequency.monthly': 'månadsvis',
  'controlFrequency.semimonthly': 'två gånger i månaden',
  'controlFrequency.weekly': 'veckovis',
  'controlFrequency.daily': 'daglig',
  'controlFrequency.multiple_daily': 'flera gånger om dagen',
  'controlFrequency.event_driven': 'vid varje händelse',
  'controlFrequency.continuous': 'löpande',
  'controlNature.manual': 'manuell',
  'controlNature.it_dependent_manual': 'manuell, beroende av ett system',
  'controlNature.automated': 'automatisk',
  'sizeSource.table': 'urvalsmetoden',
  'sizeSource.person': 'en person, med ett skäl',
  'selection.random_seeded': 'slumpmässigt, med registrerat slumpfrö',
  'selection.systematic': 'systematiskt',
  'selection.judgemental': 'efter bedömning',
  'selection.all': 'varje post',
  'sampleStatus.planned': 'planerat',
  'sampleStatus.tested': 'testat',
  'sampleStatus.reviewed': 'granskat',
  'testConclusion.effective': 'effektiv',
  'testConclusion.not_effective': 'inte effektiv',
  'testConclusion.not_concluded': 'ingen slutsats',
  'notConcluded.no_adopted_method': 'ingen urvalsmetod antagen',
  'notConcluded.person_size': 'en storlek som en person angav',
  'notConcluded.no_acceptable_number': 'inget godtaget antal avvikelser',
  'notConcluded.judgemental': 'ett urval efter bedömning',
  'evidenceKind.document': 'dokument',
  'evidenceKind.record': 'protokoll eller logg',
  'evidenceKind.approval': 'godkännande',
  'evidenceKind.statement': 'uttalande',
  'evidenceKind.walkthrough': 'genomgång',
  'evidenceKind.sample_test': 'stickprovstest',
  'evidenceKind.kri_series': 'KRI-serie',
  'evidenceKind.prior_report': 'tidigare rapport',
  'evidenceKind.valentin_report': 'Valentin-rapport',
  'evidenceKind.self_rating': 'självskattning',
  'evidenceKind.cycle_evidence': 'byråns egen punkt i årscykeln',
  'evidenceSource.client_prepared': 'framtaget av klienten',
  'evidenceSource.obtained_directly': 'inhämtat direkt',
  'evidenceSource.independent_third_party': 'från en oberoende tredje part',
  'evidenceSource.system_extract': 'systemuttag',
  'approvalBody.management_body': 'ledningsorganet',
  'approvalBody.compliance_manager': 'den särskilt utsedda befattningshavaren',
  'approvalBody.compliance_officer': 'den centralt funktionsansvariga',
  'approvalBody.none': 'inget organ angivet',
  'testerKind.client_internal_audit': 'klientens internrevision',
  'testerKind.client_second_line': 'klientens andra linje',
  'testerKind.client_first_line': 'klientens första linje',
  'testerKind.external_party': 'en extern part',
  'testerKind.supervisor': 'tillsynsmyndigheten',
  'testerKind.valentin': 'Valentin',
  'testerKind.firm_member': 'en medarbetare på byrån',
  'fact.performed': 'utförs',
  'fact.designed': 'utformad',
  'fact.approved': 'godkänd',
  'fact.current': 'aktuell',
  'fact.owner': 'ägare utsedd',
  'fact.operating': 'fungerar över perioden',
  'fact.sample': 'stickprov effektivt',
  'fact.tested': 'oberoende testad',
  'fact.measured': 'mäts',
  'fact.within_threshold': 'inom sitt gränsvärde',
  'fact.reported': 'rapporterad till styrelsen',
  'factValue.yes': 'ja',
  'factValue.partial': 'delvis',
  'factValue.no': 'nej',
  'factValue.unknown': 'okänt',
  'elementState.present': 'finns',
  'elementState.absent': 'saknas',
  'elementState.not_applicable': 'inte tillämpligt',
  'noticeState.to_send': 'att skicka',
  'noticeState.sent': 'skickat',
  'dutyValue.met': 'uppfyllt',
  'dutyValue.partly_met': 'delvis uppfyllt',
  'dutyValue.not_met': 'inte uppfyllt',
  'dutyValue.not_determined': 'kan inte avgöras',
  'errors.ASSESSMENT_AS_OF_RANGE': 'En analys avser ett datum inom det kommande året.',
  'errors.REVIEWER_NOT_INDEPENDENT': 'Granskaren får inte vara klientansvarig för någon av klientens tjänster eller ha någon av dess roller under de tolv månaderna före datumet.',
  'errors.ASSESSMENT_CLOSED': 'En återkallad eller fastställd analys ändras aldrig.',
  'errors.ASSESSMENT_NOT_OPEN': 'Den här analysen är inte öppen för planering eller insamling.',
  'errors.SCOPE_NOTHING_TO_CONFIRM': 'Bara ett arbetsmoment som tagits bort ur omfattningen väntar på bekräftelse.',
  'errors.FOUR_EYES_SCOPE': 'Du fattade beslutet om omfattningen, så en annan granskare bekräftar det.',
  'errors.BAND_CONFIRMED_ALREADY': 'Den nivå som gäller är redan bekräftad.',
  'errors.FOUR_EYES_BAND': 'Du satte nivån, så en annan granskare bekräftar den.',
  'errors.BANDS_REFRESH_PLANNING': 'De beräknade nivåerna uppdateras medan analysen planeras.',
  'errors.REQUEST_CLOSED': 'En stängd begäran förblir stängd.',
  'errors.REQUEST_CONTACT_NEEDED': 'En begäran skickas till en kontaktperson hos klienten: välj en först.',
  'errors.REQUEST_LINES_NEEDED': 'En begäran skickas med minst en rad.',
  'errors.REQUEST_ITEM_UNKNOWN': 'Katalogen har ingen sådan punkt.',
  'errors.LINE_DOCUMENT_NEEDED': 'Koppla först det mottagna dokumentet till raden.',
  'errors.REQUEST_ANSWER_NEEDED': 'Ange vad klienten svarade.',
  'errors.INQUIRY_CLOSED': 'En stängd intervju förblir stängd.',
  'errors.INQUIRY_NOT_HELD': 'En intervju registreras när den har genomförts.',
  'errors.INQUIRY_NOTES_NEEDED': 'Registrera anteckningarna, eller skälet till att den inte genomfördes.',
  'errors.INQUIRY_DATE_RANGE': 'En intervju hålls inom det kommande året.',
  'errors.INQUIRY_SHARED_DATE': 'Anteckningar delas efter intervjun och senast i dag.',
  'errors.STATEMENT_QUESTION_NOT_IN_GUIDE': 'Ett uttalande besvarar en av intervjuns frågor.',
  'errors.NO_SUSPICION_TICK_NEEDED': 'Bekräfta att uttalandet inte avser någon enskild misstanke eller rapport.',
  'errors.SPEAKER_NOT_PARTICIPANT': 'Talaren är en deltagare i intervjun: lägg till personen först.',
  'errors.STATEMENT_RETIRED': 'Ett återkallat uttalande kopplas inte.',
  'errors.DISPOSITION_NEEDED': 'Ange hur motsägelsen hanteras.',
  'errors.INCONSISTENCY_CLOSED': 'En avslutad motsägelse förblir avslutad.',
  'errors.SELF_RATING_LEVEL_NEEDED': 'En självskattning anger sin nivå, 0 till 4.',
  'sampling.title': 'Urvalsmetoder',
  'sampling.intro': 'Sätten att bestämma storleken på och utvärdera ett test av en kontroll. Ingen metod gäller förrän en medlem av regelbevakningen har registrerat att källan är kontrollerad och en annan har antagit den. Tills en metod är antagen når inget stickprovstest en slutsats enligt regel: en person skriver slutsatsen med ett skäl, och en granskare bekräftar den.',
  'sampling.shared': 'Posterna är Valentins, ord för ord, så att ett stickprovstest läses likadant i båda produkterna; där en text säger vad Valentin gör beskriver den Valentin.',
  'sampling.none': 'Ingen urvalsmetod är inläst.',
  'sampling.method.table': 'Tabell',
  'sampling.method.formula': 'Beräknad formel',
  'sampling.method.rule': 'Regel',
  'sampling.method.reference': 'Text att läsa',
  'sampling.selection.statistical': 'statistiskt urval',
  'sampling.selection.nonstatistical': 'icke-statistiskt urval',
  'sampling.example': 'Exempel',
  'sampling.readOnly': 'Bestämmer inget stickprov',
  'sampling.canSize': 'Antagen: kan bestämma stickprov',
  'sampling.notAdopted': 'Inte antagen: bestämmer inget ännu',
  'sampling.formulaSizes': 'Vid {confidence} konfidensnivå och {rate} godtagbar avvikelseprocent, beräknat:',
  'sampling.formulaSize': '{n} poster, {a} avvikelser godtas',
  'sampling.sourceChecked': 'Källan kontrollerad av {name} den {date}',
  'sampling.sourceNotChecked': 'Källan är inte kontrollerad ännu',
  'sampling.notAdoptedYet': 'inte antagen ännu',
  'sampling.showDetails': 'Visa källan och raderna',
  'sampling.recordSourceChecked': 'Registrera att källan kontrollerats',
  'sampling.adopt': 'Anta den här metoden',
  'sampling.adoptOther': 'Du kontrollerade källan: en annan medlem av regelbevakningen antar metoden.',
  'sampling.source': 'Källa',
  'sampling.edition': 'Utgåva',
  'sampling.readOn': 'Läst den',
  'sampling.page': 'Sida',
  'sampling.licence': 'Licens',
  'sampling.licence.public_domain': 'fri från upphovsrätt',
  'sampling.licence.cc_by_4_0': 'återanvänd enligt CC BY 4.0',
  'sampling.licence.eu_law': 'EU-rätt',
  'sampling.licence.copyrighted_cite_only': 'upphovsrättsskyddad: hänvisad till, inte kopierad',
  'sampling.licence.firm_internal': 'byråns egen',
  'sampling.secondStage': 'Andra steg',
  'sampling.evaluation': 'Slutsats',
  'sampling.rule.deviations_le_acceptable': 'enligt regel: inom de godtagna avvikelserna, effektiv',
  'sampling.rule.person': 'av en person',
  'sampling.reviewDue': 'Ses över senast',
  'sampling.file': 'Fil',
  'sampling.rows': 'Stickprovsstorlekar',
  'sampling.row': 'Gäller',
  'sampling.size': 'Storlek',
  'sampling.accepted': 'Godtagna avvikelser',
  'sampling.rowSource': 'Var det står',
  'sampling.itgc': 'Bara så länge de generella IT-kontrollerna är effektiva',
  'sampling.size.items': '{n} poster',
  'sampling.size.then': ', sedan ',
  'sampling.size.range': '{min} till {max} poster, valt med ett skäl',
  'sampling.size.all': 'varje post',
  'sampling.size.percent': 'minst {rate} av posterna, och minst {min}',
  'sampling.accepted.person': 'anges inte: en person drar slutsatsen',
  'sampling.caveats': 'Innan metoden antas',
  'sampling.verification': 'Kontrollerat',
  'sampling.own': 'Byråns egen tabell',
  'sampling.ownNote': 'Ladda ned mallen, ersätt varje platshållare med byråns metodik och lägg till den i biblioteket som en fil. Den kontrolleras och antas sedan av två medlemmar av regelbevakningen, som metoderna ovan; biblioteket avvisar mallen så länge en platshållare finns kvar.',
  'sampling.ownDownload': 'Ladda ned mallen (JSON)',
  'request.overdue': 'Försenad',
  // The current-state analysis on screen (stage 2 of the engine).
  'level.0': 'Saknas',
  'level.1': 'Utförs, ad hoc',
  'level.2': 'Fastställd',
  'level.3': 'Fungerar i praktiken',
  'level.4': 'Prövad och styrd',
  'performer.client': 'klienten',
  'performer.firm': 'byrån',
  'performer.firm_reviews': 'klienten, med byrån som granskar',
  'elementStatus.ok': 'finns, citatet hittat',
  'elementStatus.absent': 'saknas',
  'elementStatus.not_applicable': 'inte tillämpligt',
  'elementStatus.unverified': 'finns, men ännu inte visat i granskat underlag',
  'elementStatus.unchecked': 'inte registrerat',
  'quoteCheck.not_found': 'finns inte i dokumentet',
  'engine.gate.met': 'uppfyllt',
  'engine.gate.not_met': 'inte uppfyllt',
  'engine.gate.unknown': 'okänt',
  'engine.readiness.met_already': 'uppfyllt redan',
  'engine.readiness.not_yet': 'inte ännu',
  'engine.wave.now': 'nu (0–3 månader)',
  'engine.wave.mid': '3–6 månader',
  'engine.wave.later': '6–18 månader',
  'engine.title': 'Nulägesanalys',
  'engine.nav.label': 'Analysens delar',
  'engine.nav.plan': 'Plan och insamling',
  'engine.nav.analysis': 'Resultat och avstånd',
  'engine.nav.evidence': 'Underlag och KRI',
  'engine.nav.tests': 'Stickprovstest',
  'engine.linksGathering': 'Underlag kan registreras under insamlingen. Nivåerna beräknas när en ansvarig startar bedömningen.',
  'engine.linksAssessing': 'Här registreras och granskas konstateranden, nivåer, avstånd och bedömningar av lagkrav.',
  'engine.provisional': 'Preliminärt: beräknat enligt regel utifrån det som är registrerat nu ({rule}). Inget sparas förrän analysen låses, och ingen siffra här sätts av en AI-modell.',
  'engine.count.withFacts': 'Arbetsmoment med konstaterandet "utförs"',
  'engine.count.of': '{n} av {total}',
  'engine.count.notDetermined': 'Kan inte fastställas',
  'engine.count.notAssessed': 'Bedöms inte',
  'engine.count.notApplicable': 'Inte tillämpliga',
  'engine.count.byProvider': 'Utförs av byrån (bedömda av den som utför funktionen, högst nivå 3)',
  'engine.count.duty': 'Bedömningar av gällande lagkrav',
  'engine.count.dutyLine': '{total} föreslagna · {decided} beslutade · {confirmed} bekräftade',
  'engine.count.evidence': 'Underlagsposter',
  'engine.count.evidenceLine': 'aktuella: {total} · granskade av en andra person: {reviewed} av {total}',
  'engine.rounds': 'Tidigare omgångar som återsänts',
  'engine.round': 'Omgång {n}: skickad för granskning av {submitter}, återsänd av {returner} den {date}: {reason}',
  'engine.assess': 'Starta bedömningen',
  'engine.submit': 'Skicka för granskning',
  'engine.submitNeeds': '{n} arbetsmoment i omfattningen behöver fortfarande konstaterandet "utförs" före granskningen: {practices}.',
  'engine.submitByPreparer': 'Den som förbereder analysen skickar den till granskning.',
  'engine.submitNeeds.one': '1 arbetsmoment i omfattningen behöver fortfarande konstaterandet "utförs" före granskningen: {practices}.',
  'engine.sendBack': 'Återsänd till den som förbereder',
  'engine.sendBackReason': 'Varför den återsänds',
  'engine.headline': 'Var klienten står',
  'engine.headlineIntro': 'Förmågor på eller över målnivå, räknade enligt regel. En nivå räknas aldrig som ett genomsnitt.',
  'engine.functionLine': '{at} av {total} förmågor på eller över målnivå · {nd} kan inte fastställas',
  'engine.noFunctions': 'Ingen förmåga i omfattningen ännu.',
  'engine.capabilities': 'Förmågor och målnivåer',
  'engine.capabilitiesIntro': 'En förmågas nivå är den lägsta nivån bland dess arbetsmoment med lagkrav. En annan målnivå än regelns förval kräver en motivering och en granskare som inte har satt den.',
  'engine.col.level': 'Nivå',
  'engine.col.gap': 'Avstånd till målnivå',
  'engine.col.priority': 'Prioritet',
  'engine.col.review': 'Granskning',
  'engine.col.item': 'Post',
  'engine.col.dates': 'Datum',
  'engine.col.class': 'Underlagsklass',
  'engine.levelChip': 'Nivå {n} · {name}',
  'engine.notDetermined': 'Kan inte fastställas',
  'engine.missing': 'saknas: {practices}',
  'engine.goodPractice': '{n} av {total} poster med god praxis på eller över denna nivå',
  'engine.targetDefault': '{n} (regelns förval)',
  'engine.targetSet': '{n} (satt av en person)',
  'engine.targetBy': '{name}: {reason}',
  'engine.targetRuleDefault': 'Regelns förval',
  'engine.setTarget': 'Ändra målnivån',
  'engine.confirmTarget': 'Bekräfta målnivån',
  'engine.confirmedBy': 'bekräftat av {name}',
  'engine.toConfirm': 'väntar på en andra persons bekräftelse',
  'engine.notConfirmed': 'inte bekräftat av en andra person innan analysen stängdes',
  'engine.practices': 'Arbetsmoment',
  'engine.byProvider': 'Bedömd av den som utför funktionen',
  'engine.movedUp': 'beräknad {n}, justerad en nivå upp',
  'engine.movedDown': 'beräknad {n}, justerad en nivå ner',
  'engine.cappedByUnknown': 'stannar vid ett okänt',
  'engine.overrideToConfirm': 'justerad nivå väntar på en granskare',
  'engine.gapLine': '{gap} under målnivå {target}',
  'engine.priority': '{p}',
  'engine.quickWin': 'Snabb åtgärd',
  'engine.escalated': 'Eskalerat',
  'engine.reviewedBy': 'granskat av {name}',
  'engine.notReviewed': 'inte granskat ännu',
  'engine.gaps': 'Avstånd och prioriteringar',
  'engine.gapsIntro': 'Avstånd, prioritet, snabb åtgärd och tidsplan beräknas enligt regel. Förfallodag och allvarlighetsgrad för varje åtgärd som tas fram ur ett avstånd bestäms av en person.',
  'engine.gapsClient': 'Avstånd till målnivå',
  'engine.noGaps': 'Inget arbetsmoment ligger under sin målnivå.',
  'engine.needsIndependent': 'Nivå 4 kräver en oberoende bedömning',
  'engine.capOnlyIntro': 'Byrån utför dessa arbetsmoment, så dess egen bedömning stannar vid nivå 3. De behöver granskas av någon som är oberoende av byrån, till exempel klientens internrevision; det är ingen svaghet hos klienten.',
  'engine.capOnlyNote': 'Det enda avståndet är begränsningen för den som utför funktionen: en granskning av någon som är oberoende av byrån stänger det.',
  'engine.toObtain': 'Kan inte fastställas: underlag att ta in',
  'engine.claimedNotEvidenced': 'påstått, inte styrkt',
  'engine.notEvidenced': 'inte styrkt',
  'engine.followUpAsked': '{item}: begärt i dokumentbegäran {request}, rad {line} ({status})',
  'engine.followUpNotAsked': '{item}: inte begärt ännu',
  'engine.followUpNone': 'Ingen post i katalogen gäller det här arbetsmomentet; begär underlaget med en egen rad.',
  'engine.escalations': 'Eskaleringar',
  'engine.escalationsIntro': 'En saknad grundläggande kontroll eskaleras i samma steg som registrerar den, med en åtgärd med prioritet P1. Meddelandet till klienten ska skickas senast en arbetsdag senare.',
  'engine.escalationFact': '{fact}: nej',
  'engine.noticeSent': 'meddelande skickat {date} (brev {doc})',
  'engine.noticeDue': 'meddelandet ska skickas senast {date}',
  'engine.noticeOverdue': 'meddelandet är försenat sedan {date}',
  'engine.escalationLine': 'Öppnad {opened} av {by} · meddelande till {to}',
  'engine.recordNotice': 'Registrera meddelandet som skickat',
  'engine.keyControl': 'nyckelkontroll',
  'engine.refToday': 'I dag: {ref}',
  'engine.refComing': 'Från {date}: {ref}',
  'engine.performer': 'Utförs av',
  'engine.service': 'Tjänst',
  'engine.gates': 'Vad nivån bygger på',
  'engine.gatesIntro': 'Varje nivå kräver att alla lägre nivåer är uppfyllda. Ett okänt hålls isär från ett nej.',
  'engine.gatesNotDetermined': 'Att arbetsmomentet utförs är inte styrkt ännu, så ingen nivå anges.',
  'engine.gatesNotAssessed': 'Detta arbetsmoment bedöms inte i denna analys.',
  'engine.gateLevel': 'Nivå {n}:',
  'engine.gate.fact': '{fact}: {value}',
  'engine.gate.element': 'inslag {element}: {state}',
  'engine.gate.actionOpen': 'öppen kritisk åtgärd {code}',
  'engine.gate.actionOverdue': 'försenad åtgärd {code}',
  'engine.facts': 'Konstateranden',
  'engine.factsIntro': 'Ett ja eller ett delvis kräver underlag av en klass som tillåter det; ett nej kan bygga på en anteckning. Ett konstaterande ersätts, det redigeras aldrig, och en medlem som inte registrerade det bekräftar det.',
  'engine.factNotApplicable': 'gäller inte detta arbetsmoment:',
  'engine.recordedAs': 'registrerat som {value}',
  'engine.notRecorded': 'inte registrerat',
  'engine.derived': 'härlett',
  'engine.periods': 'dokumentation för {n} av {total} perioder',
  'engine.periodsMissing': 'dokumentation för {n} av {total} perioder; saknas: {missing}',
  'engine.elementsPresent': '{n} av {total} inslag finns',
  'engine.withinLine': '{n} av {total} mätvärden inom gränsvärdet',
  'engine.recordedBy': 'registrerat av {name} den {date}',
  'engine.evidenceNotReviewed': 'inte granskat ännu',
  'engine.confirmFact': 'Bekräfta',
  'engine.confirmOther': 'du registrerade det, så en annan medlem bekräftar det',
  'engine.confirmNotYou': 'väntar på en medlem utanför denna tjänst',
  'engine.withdrawFact': 'Dra tillbaka konstaterandet',
  'engine.recordFact': 'Registrera ett konstaterande',
  'engine.recordAndEscalate': 'Registrera och eskalera',
  'engine.factYesNeeds': 'Länka underlag av en klass som tillåter det, med citatet och var det står.',
  'engine.factNoNeeds': 'Ett nej eller ett okänt kan bygga på en anteckning.',
  'engine.factNote': 'Anteckning',
  'engine.replaceReason': 'Varför det registrerade konstaterandet ersätts',
  'engine.replaceHint': 'Det är registrerat som {value} nu; det konstaterandet sparas i historiken.',
  'engine.evidenceLinks': 'Underlag',
  'engine.evidenceItem': 'Underlagspost',
  'engine.quote': 'Citat (kontrolleras mot dokumentet)',
  'engine.locator': 'Var det står (sida, avsnitt, rad)',
  'engine.addLink': 'Länka en post till',
  'engine.escalationForm': 'Eskaleras direkt',
  'engine.escalationExplain': 'Detta arbetsmoment eskalerar ett nej på detta konstaterande: åtgärden med prioritet P1 och eskaleringen öppnas i samma steg, och den ansvariga registrerar sedan meddelandet till klienten.',
  'engine.actionTitle': 'Åtgärden',
  'engine.escalationBasis': 'Grund för allvarlighetsgraden',
  'engine.escalationBasisDefault': 'P1 enligt regeln maturity.v1: {practice}, {fact}: nej, eskaleras direkt',
  'engine.actionOwner': 'Vem som äger åtgärden',
  'engine.ownerMembers': 'Byråns medlemmar',
  'engine.ownerContacts': 'Kontakter hos klienten',
  'engine.elements': 'Obligatoriska inslag',
  'engine.elementsIntro': 'Ett inslag finns bara med ett citat som hittats i granskat underlag.',
  'engine.requiredAt': 'krävs från nivå {n}',
  'engine.recordElement': 'Registrera detta inslag',
  'engine.duty': 'Efterlevnad av reglerna',
  'engine.dutyIntro': 'Regeln föreslår en bedömning för varje gällande regel; en person beslutar den, och en granskare utanför tjänsten bekräftar den. Detta är byråns rådgivande bedömning, inte ett rättsligt avgörande.',
  'engine.duty.inForce': 'Regler som gäller på bedömningsdagen',
  'engine.duty.noneInForce': 'Ingen regel för detta arbetsmoment gäller på bedömningsdagen.',
  'engine.duty.proposed': 'Föreslaget enligt regeln:',
  'engine.duty.decided': 'Beslutat av {name}:',
  'engine.duty.toConfirm': 'väntar på en granskare utanför denna tjänst',
  'engine.duty.decide': 'Besluta bedömningen',
  'engine.duty.confirm': 'Bekräfta bedömningen',
  'engine.duty.reasonNeeded': 'Motivering (krävs: beslutet skiljer sig från förslaget)',
  'engine.duty.reasonOptional': 'Motivering (frivillig)',
  'engine.duty.reasonMissing': 'Ange varför beslutet skiljer sig från regelns förslag.',
  'engine.duty.coming': 'Kommande regler: endast beredskap',
  'engine.duty.comingIntro': 'En regel som börjar gälla senare bedöms aldrig. Här visas om klienten redan uppfyller den.',
  'engine.duty.comingFrom': 'Från {date}',
  'engine.override': 'Justering av nivån',
  'engine.overrideIntro': 'En person kan justera den beräknade nivån ett steg, med en motivering; en granskare som inte föreslog den bekräftar den.',
  'engine.overrideCapped': 'En person kan justera den beräknade nivån ett steg, med en motivering, och en granskare bekräftar den. Den lyfter aldrig nivån över det den som utför funktionen får bedöma (nivå 3).',
  'engine.overrideUp': 'Justerad en nivå upp: {reason}',
  'engine.overrideDown': 'Justerad en nivå ner: {reason}',
  'engine.overrideConfirmed': 'bekräftad',
  'engine.noOverride': 'Den beräknade nivån gäller.',
  'engine.moveLevel': 'Justera nivån ett steg',
  'engine.removeOverride': 'Ta bort justeringen',
  'engine.confirmOverride': 'Bekräfta justeringen',
  'engine.overrideStep': 'Riktning',
  'engine.oneUp': 'En nivå upp',
  'engine.oneDown': 'En nivå ner',
  'engine.reviewMark': 'Granskningsmarkering',
  'engine.reviewedOn': 'granskat av {name} den {date}',
  'engine.reviewNotYet': 'Inte markerat som granskat ännu. En granskare som inte har registrerat några av dess konstateranden eller inslag, och som inte har någon roll i dess tjänst, markerar det.',
  'engine.reviewNotYouRecorded': 'Inte markerat som granskat ännu. Du har registrerat konstateranden eller inslag här, så en annan granskare markerar det.',
  'engine.reviewNotYouService': 'Inte markerat som granskat ännu. Du är ansvarig för, eller har en roll i, tjänsten som omfattar detta arbetsmoment, så en annan granskare markerar det.',
  'engine.markReviewed': 'Markera som granskat',
  'engine.noTestsHere': 'Inget stickprovstest för detta arbetsmoment.',
  'engine.countsOnly': 'Endast antal, inte omprövat',
  'engine.toTests': 'Till stickprovstesterna',
  'engine.history': 'Tidigare konstateranden',
  'engine.historyLine': '{fact}: {value}, registrerat av {by}, tillbakadraget {withdrawn}: {reason}',
  'engine.evidence': 'Underlagsposter',
  'engine.evidenceIntro': 'En medlem registrerar en post med dess uppgifter; en annan medlem granskar den, och först då räknas den. Dess klass beräknas utifrån uppgifterna och väljs aldrig.',
  'engine.noEvidence': 'Ingen underlagspost registrerad ännu.',
  'engine.preparedForAnalysis': 'upprättat för denna analys',
  'engine.copy': 'en kopia',
  'engine.approvalLine': 'godkänt av {body}, {ref}, den {date}',
  'engine.testerLine': 'testat av {kind}',
  'engine.retired': 'avregistrerad: {reason}',
  'engine.covers': 'avser {from} till {to}',
  'engine.classOnlyCorroborated': 'räknas bara när det bekräftas av en annan källa',
  'engine.classNote': 'Klasser: E1 testat, E2 dokumentation från den löpande verksamheten, E3 ett godkänt styrdokument. Ett uttalande eller ett dokument som upprättats för analysen är E4 bara när en post i klass E1–E3 stöder samma konstaterande, annars E5, som aldrig höjer en nivå.',
  'engine.obtainedBy': 'inhämtat av {name} den {date}',
  'engine.reviewEvidence': 'Granska',
  'engine.reviewOther': 'du inhämtade den, så en annan medlem granskar den',
  'engine.retireEvidence': 'Avregistrera posten',
  'engine.addEvidence': 'Registrera en underlagspost',
  'engine.document': 'Dokument',
  'engine.statement': 'Uttalande',
  'engine.confidentialSource': 'en konfidentiell källa',
  'engine.occurrence': 'Byråns punkt i årshjulet (kod)',
  'engine.occurrenceHint': 'En utförd punkt i årshjulet, till exempel CY-2026-041.',
  'engine.approval': 'Godkännande',
  'engine.notApproved': 'inget godkännande',
  'engine.testerUnknown': 'inte angivet',
  'engine.valentinNote': 'En rapport från Valentin räknas ännu inte som ett oberoende test: Olivia kan inte kontrollera dess kvitto förrän Olivia skickar egna överlämningsbegäranden.',
  'engine.kris': 'Nyckelriskindikatorer (KRI)',
  'engine.krisIntro': 'Klientens KRI och deras mätvärden. Varje mätvärde jämförs med gränsvärdena enligt regel och bekräftas av en medlem som inte registrerade det.',
  'engine.noKris': 'Ingen KRI registrerad för denna klient.',
  'engine.kriLine': 'sämre när {direction} · varning vid {warning} · överskridande vid {breach} {unit} · {frequency}',
  'engine.noReadings': 'Inga mätvärden ännu.',
  'engine.readingConfirmed': 'bekräftat',
  'engine.confirmReading': 'Bekräfta mätvärdet',
  'engine.readingOther': 'du registrerade det, så en annan medlem bekräftar det',
  'engine.addReading': 'Registrera ett mätvärde',
  'engine.periodEnd': 'Periodens slut',
  'engine.value': 'Värde',
  'engine.addKri': 'Lägg till en KRI',
  'engine.kriName': 'Namn',
  'engine.kriPractices': 'Arbetsmoment den mäter',
  'engine.kriPracticesHint': 'Välj ett eller flera; håll ned Ctrl eller Skift för att välja flera.',
  'engine.testsIntro': 'Till dess att uppgifter på kundnivå kan hållas registrerar ett test endast antal, och granskaren kan inte ompröva det. Varje utvald post som inte testats räknas som en avvikelse.',
  'engine.noTests': 'Inget stickprovstest planerat ännu.',
  'engine.noTestsClosed': 'Inget stickprovstest planerades i den här analysen.',
  'engine.testPlanLine': 'population {population} · {from} till {to} · {frequency}, {nature}',
  'engine.testSizeLine': 'storlek {size}, från {source} · {selection} · tolererbara avvikelser: {tolerable}',
  'engine.testMethod': 'urvalsmetod: {name}, version {v}',
  'engine.testMethodUnnamed': 'en urvalsmetod som inte längre finns i biblioteket',
  'engine.testCounts': 'testade: {tested} av {size} · inte testade: {notTested} · undantag: {exceptions} · avvikelser: {deviations}',
  'engine.testedBy': 'testat av {name}',
  'engine.ruleConclusion': 'Enligt regel:',
  'engine.personConclusion': 'Av {name}:',
  'engine.recordResults': 'Registrera resultatet',
  'engine.reviewTest': 'Granska testet',
  'engine.concludeTest': 'Skriv en slutsats',
  'engine.confirmConclusion': 'Bekräfta slutsatsen',
  'engine.selectionPositions': 'Poster att testa, efter plats i populationen: {positions}',
  'engine.selectionNone': 'Urval {selection}: posterna väljs utan slumptal.',
  'engine.kindsHint': 'Skilj olika slag åt med kommatecken.',
  'engine.untestedCount': 'En utvald post som inte testades räknas som en avvikelse.',
  'engine.planTest': 'Planera ett stickprovstest',
  'engine.planTestIntro': 'Storleken kommer från urvalsmetoden enligt regel, eller från en person med en motivering. Inget test når en slutsats enligt regel förrän dess metod har antagits av regelbevakningen.',
  'engine.methodHint': 'En metod som ännu inte antagits ger testets storlek, men ingen slutsats nås enligt regel.',
  'engine.sizeFromMethod': 'Lämna tomt om inte metoden anger ett intervall; välj då inom det, med en motivering.',
  'engine.sizeByPerson': 'Den storlek du väljer, med motiveringen nedan.',
  'engine.tolerableHint': 'För en nyckelkontroll för ett lagkrav godtar regeln ingen avvikelse, oavsett vad som anges.',
  'engine.seedNote': 'Databasen drar slumptalet för ett slumpmässigt urval när testet planeras, så att posterna inte kan styras av resultatet.',
  // The current-state report and its checkpoint (034; addendum §6 step J, §11.1; main 7.7)
  'engine.nav.report': 'Rapport',
  'report.title': 'Rapport över nuläget',
  'report.linkHint': 'framställd från denna ögonblicksbild, på engelska eller svenska',
  'report.notFrozen': 'Rapporten tas fram när analysen är låst.',
  'report.standing': 'Kan rapporten publiceras?',
  'report.internalWhy': 'Bygger på innehåll som regelbevakningen ännu inte har antagit: rapporten kan användas inom byrån men kan inte publiceras.',
  'report.unadopted': 'Ännu inte antagna: {files}.',
  'report.publishable': 'Alla biblioteksfiler som analysen bygger på är antagna av regelbevakningen: en rapport kan publiceras när en granskare har godkänt den.',
  'report.prepareTitle': 'En ny rapport',
  'report.prepareIntro': 'Rapporten framställs enligt regel från den låsta ögonblicksbilden, som Word-fil och PDF. Varje siffra i den kommer från ögonblicksbilden; ingenting i den skrivs av en AI-modell.',
  'report.language': 'Språk',
  'report.prepare': 'Ta fram rapporten',
  'report.prepared': 'Rapporten är framtagen.',
  'report.layout': 'Utformning',
  'report.layout.standard': 'Standardutformning',
  'report.layout.gap_report': 'Gap-rapport',
  'report.layoutNote': 'Båda utformningarna framställs enligt regel från samma låsta ögonblicksbild. Gap-rapporten ställer upp analysen som en gap-analys och skriver inte ut någon annan skala än Olivias.',
  'report.gapLog.title': 'Gap-logg',
  'report.gapLog.intro': 'En Excel-arbetsbok med den låsta analysen, en rad per hänvisning för varje arbetsmoment, byggd enbart från ögonblicksbilden. Den är en arbetsfil för byrån: det som lämnar byrån är rapporten, efter sin granskningspunkt. Varje nedladdning registreras.',
  'report.gapLog.en': 'Gap-logg på engelska (XLSX)',
  'report.gapLog.sv': 'Gap-logg på svenska (XLSX)',
  'report.listTitle': 'Rapporter',
  'report.none': 'Ingen rapport har tagits fram ännu.',
  'report.files': 'Filer',
  'report.receipt': 'Godkännandekvitto',
  'report.filesNote': 'Det här är filerna som en granskare godkänner och som publiceras oförändrade.',
  'report.returned': 'Återsänd: {note}',
  'report.checkpoint': 'Granskningspunkt',
  'report.asked': 'Granskning begärd av {name} {date}',
  'report.expires': 'löper ut {date}',
  'report.decided': 'beslutad av {name} {date}',
  'report.bound': 'bunden till sha256 {hash}',
  'report.askReview': 'Begär granskning',
  'report.askedDone': 'Granskning begärd. Filerna framställdes på nytt med granskningspunkten i sidfoten.',
  'report.approve': 'Godkänn',
  'report.approved': 'Godkänd.',
  'report.sendBack': 'Återsänd med en kommentar…',
  'report.sendBackNote': 'Varför den återsänds',
  'report.sendBackSubmit': 'Återsänd',
  'report.sentBack': 'Återsänd.',
  'report.publish': 'Publicera',
  'report.published': 'Publicerad, med sitt godkännandekvitto.',
  'report.renderAgain': 'Framställ på nytt',
  'report.rendered': 'Framställd på nytt.',
  'report.renderExpires': 'Om rapporten framställs på nytt blir den ett utkast igen och granskningspunkten löper ut.',
  'report.whyNot.reviewer': 'En granskare för den här klienten beslutar vid granskningspunkten.',
  'report.whyNot.four_eyes': 'Du tog fram rapporten eller begärde granskningen, så en annan granskare beslutar.',
  'report.whyNot.expired': 'Granskningspunkten har löpt ut; begär granskning på nytt.',
  'report.whyNotPublish': 'Du tog fram rapporten eller begärde granskningen, så en annan granskare publicerar den.',
  'reportLanguage.en': 'Engelska',
  'reportLanguage.sv': 'Svenska',
  'deliverableStatus.draft': 'utkast',
  'deliverableStatus.in_review': 'under granskning',
  'deliverableStatus.approved': 'godkänd',
  'deliverableStatus.published': 'publicerad',
  'checkpointStatus.pending': 'väntar',
  'checkpointStatus.approved': 'godkänd',
  'checkpointStatus.rejected': 'återsänd',
  'checkpointStatus.expired': 'har löpt ut',
  'record.deliverable': 'Rapport',
  'record.checkpoint': 'Granskningspunkt',
  'record.export': 'Export',
  'auditField.reportLanguage': 'språk',
  'auditField.reportLayout': 'utformning',
  'auditField.renderedBy': 'framställd av',
  'auditField.renderedAt': 'framställd',
  'auditField.docxDocument': 'Word-fil',
  'auditField.pdfDocument': 'PDF-fil',
  'auditField.checkpoint': 'granskningspunkt',
  'auditField.returnedNote': 'kommentar vid återsändning',
  'auditField.publishedBy': 'publicerad av',
  'auditField.publishedAt': 'publicerad',
  'auditField.deliverable': 'rapport',
  'auditField.requestedBy': 'granskning begärd av',
  'auditField.requestedAt': 'granskning begärd',
  'auditField.decisionNote': 'kommentar till beslutet',
  'auditField.expiresAt': 'löper ut',
  'auditField.expiredAt': 'löpte ut',
  'auditAction.report.prepare': 'Tog fram en rapport över nuläget',
  'auditAction.export.request_letter': 'Exporterade en begäran som brev',
  'auditAction.export.request_checklist': 'Exporterade en begäran som checklista',
  'auditAction.export.missing_letter': 'Exporterade det vi fortfarande behöver som brev',
  'auditAction.export.picture': 'Exporterade organisationen på en sida',
  'auditAction.report.render': 'Framställde en rapport på nytt',
  'auditAction.report.request_review': 'Begärde granskning av en rapport',
  'auditAction.report.approve': 'Godkände en rapport vid granskningspunkten',
  'auditAction.report.reject': 'Återsände en rapport',
  'auditAction.report.publish': 'Publicerade en rapport',
  'auditAction.checkpoint.expire': 'En granskningspunkt löpte ut: filerna stämde inte längre',
  'errors.REPORT_NOT_FROZEN': 'En rapport tas fram från en låst analys.',
  'errors.DELIVERABLE_PUBLISHED': 'En publicerad rapport ändras aldrig; en rättelse är en ny rapport.',
  'errors.LIBRARY_NOT_ADOPTED': 'Bygger på innehåll som regelbevakningen ännu inte har antagit: rapporten är endast för internt bruk och publiceras inte.',
  'errors.REVIEW_NEEDS_DRAFT': 'Granskning begärs för en rapport som är ett utkast.',
  'errors.FOUR_EYES_CHECKPOINT': 'Du tog fram rapporten eller begärde granskningen, så en annan granskare beslutar.',
  'errors.CHECKPOINT_EXPIRED': 'Granskningspunkten har löpt ut; begär granskning på nytt.',
  'errors.REJECTION_NEEDS_NOTE': 'Ange varför rapporten återsänds.',
  'errors.FILES_CHANGED': 'De lagrade filerna stämmer inte längre med de godkända filerna; granskningspunkten har löpt ut. Begär granskning på nytt.',
  'errors.CHECKPOINT_DECIDED': 'Granskningspunkten är redan beslutad.',
  'errors.PUBLISH_NOT_APPROVED': 'En rapport publiceras när en granskare har godkänt den vid granskningspunkten.',
  'errors.FOUR_EYES_PUBLISH': 'Du tog fram rapporten eller begärde granskningen, så en annan granskare publicerar den.',
  'errors.REPORT_RECOMPUTE_MISMATCH': 'Den låsta analysen stämmer inte längre med sin ögonblicksbild, så ingen rapport tas fram från den.',
  'errors.CHECKPOINT_NOT_PENDING': 'Rapporten har ingen granskningspunkt som väntar på beslut.',
  // The hand-over and the roadmap tracker (onboarding addendum §11.2, §11.4, §14).
  'engine.nav.handover': 'Överlämning',
  'engine.nav.roadmap': 'Uppföljning av färdplanen',
  'handover.title': 'Överlämning till arbetet',
  'handover.notFrozen': 'Överlämningen börjar när analysen är låst.',
  'handover.byPerson': 'Inget här skapas av sig självt: varje åtgärd, serie och KRI skapas genom att en person gör det.',
  'handover.gaps': 'Avstånd: en åtgärd för varje',
  'handover.gapsIntro': 'En åtgärd för varje avstånd, ett avstånd i taget. Rubrik, ägare och förfallodag bestämmer du; allvarlighetsgraden föreslås utifrån avståndets prioritet, med sin grund, och du kan ändra båda.',
  'handover.capOnly': 'Nivå 4 kräver en oberoende bedömning',
  'handover.capOnlyTitle': 'Oberoende bedömning: {title}',
  'handover.notDetermined': 'Kan inte fastställas: underlag att hämta in',
  'handover.noGaps': 'Inget avstånd.',
  'handover.waveEnds': 'slutar {date}',
  'handover.raise': 'Skapa en åtgärd…',
  'handover.raiseSubmit': 'Skapa åtgärden',
  'handover.basisDefault': '{priority} enligt regeln maturity.v1',
  'handover.basisHint': 'Föreslagen utifrån avståndets prioritet; säg varför om du ändrar allvarlighetsgraden.',
  'handover.dueHint': 'Du bestämmer den; uppföljningen mäter också åtgärden mot slutet av dess etapp.',
  'handover.applied': 'Tillagd i årshjulet.',
  'handover.cycle': 'Återkommande moment som arbetsmomenten anger',
  'handover.cycleIntro': 'Punkter i årshjulet som genomför ett arbetsmoment med ett avstånd och som ännu inte har lagts till för klienten. En ansvarig lägger till dem; varje serie anger vilken analys den kommer från.',
  'handover.noCycle': 'Inget att lägga till.',
  'handover.forPractices': 'För {practices} · tjänst {service}',
  'handover.apply': 'Lägg till',
  'handover.leadOnly': 'En ansvarig för klienten lägger till dessa.',
  'handover.cycleApplied': 'Redan i årshjulet: {list}',
  'handover.cycleNotOffered': 'Erbjuds inte, eftersom klienten saknar en tjänst av det slaget: {list}',
  'handover.models': 'Modeller i modellförteckningen',
  'handover.modelsIntro': 'Varje modell valideras innan den tas i bruk och efter en väsentlig ändring, i Valentin eller av någon annan; arbetsmomentet för modellvalidering bedöms för varje post. En ansvarig lägger till en serie för omvalidering för varje modell.',
  'handover.noModels': 'Modellförteckningen innehåller ingen modell.',
  'handover.seriesApplied': 'Serie {code}',
  'handover.applyRevalidation': 'Lägg till serien för omvalidering',
  'handover.revalidationNoService': 'Klienten saknar en tjänst av det slag som serien kräver.',
  'handover.kris': 'KRI som arbetsmomenten föreslår',
  'handover.krisIntro': 'En KRI för varje arbetsmoment i omfattningen vars nivåer anger en. Den skapas i KRI-registret och kopplas till arbetsmomentet.',
  'handover.noKris': 'Inget att skapa.',
  'handover.createKri': 'Skapa KRI…',
  'handover.krisLinked': 'Redan kopplade: {list}',
  'handover.reassessment': 'Förnyad bedömning',
  'handover.reassessmentIntro': 'Den årliga förnyade bedömningen av nuläget, som standard den 30 september, en månad före utkastet till den allmänna riskbedömningen. En ansvarig kan välja en annan dag på serien.',
  'handover.noReassessment': 'Ingen tjänst i analysens omfattning har en punkt för förnyad bedömning i klientens mall.',
  'handover.applyReassessment': 'Lägg till serien för förnyad bedömning',
  'roadmap.title': 'Uppföljning av färdplanen',
  'roadmap.notFrozen': 'Färdplanen följs upp när analysen är låst.',
  'roadmap.summary': 'Hur långt färdplanen har kommit',
  'roadmap.since': 'Utgångsläge {code}, låst {date} · i dag {today}',
  'roadmap.withAction': 'Avstånd med en åtgärd: {n} av {of}',
  'roadmap.done': 'Verifierade åtgärder: {n} av {of}',
  'roadmap.overdueAction': 'Försenade mot åtgärdens förfallodag: {n} av {of} öppna åtgärder',
  'roadmap.overdueWave': 'Försenade mot sin etapp: {n} av {of} avstånd',
  'roadmap.quickWins': 'Snabba åtgärder klara: {n} av {of}',
  'roadmap.noAction': 'Avstånd utan åtgärd ännu: {n} av {of}',
  'roadmap.escalations': 'Eskaleringsmeddelanden skickade: {n} av {of} · deras åtgärder klara: {d} av {dof}',
  'roadmap.intro': 'Räknat från de låsta avstånden och åtgärderna som de står i dag. Ett avstånd vars åtgärd är verifierad står kvar tills nästa förnyade bedömning bekräftar nivån.',
  'roadmap.waveEnds': 'slutar {date}',
  'roadmap.waveEnded': 'avslutad',
  'roadmap.waveCounts': '{gaps} avstånd · med en åtgärd: {with} av {gaps} · klara: {done} av {gaps}',
  'roadmap.waveEmpty': 'Inget avstånd i denna etapp.',
  'roadmap.capOnly': 'Oberoende bedömning behövs',
  'roadmap.capOnlyCounts': 'Med en åtgärd: {with} av {of} · försenade mot sin etapp: {overdue} av {of}',
  'roadmap.noActionTitle': 'Avstånd utan åtgärd ännu',
  'roadmap.noneWithoutAction': 'Varje avstånd har en åtgärd.',
  'roadmap.col.practice': 'Arbetsmoment',
  'roadmap.col.action': 'Åtgärd',
  'roadmap.col.state': 'Läge',
  'roadmap.due': 'förfaller {date}',
  'roadmap.noActionYet': 'ingen åtgärd ännu',
  'roadmap.overdueActionChip': 'försenad',
  'roadmap.overdueWaveChip': 'efter etappens slut',
  'roadmap.dueAfterWave': 'förfaller efter etappens slut',
  'roadmap.verified': 'åtgärden är verifierad; nivån bekräftas vid nästa förnyade bedömning',
  'roadmap.kris': 'KRI sedan analysen låstes',
  'roadmap.krisCount': 'KRI med en avläsning sedan analysen låstes: {n} av {of}',
  'roadmap.noKris': 'Ingen KRI är kopplad till ett arbetsmoment i denna analys.',
  'roadmap.noReading': 'ingen bekräftad avläsning sedan analysen låstes',
  'roadmap.threshold': 'gränsvärde {value}',
  'standing.title': 'Var klienten står',
  'standing.byRule': 'beräknat enligt regel, utan AI',
  'standing.none': 'Ingen nulägesanalys är låst ännu.',
  'standing.baseline': 'Utgångsläge {code}, låst {date}',
  'standing.function': '{fn}: {n} av {of} förmågor på eller över målnivå',
  'standing.function.aml': 'AML',
  'standing.function.governance': 'Styrning',
  'standing.breaches': 'Möjliga överträdelser: {n} av {of} bedömningar mot gällande regler',
  'standing.escalated': 'Eskalerat: {n}, meddelanden skickade {s} av {of}',
  'standing.overdueWave': 'Försenade mot sin etapp: {n} av {of} avstånd',
  'kriKind.training_completion': 'Genomförd utbildning',
  'kriKind.customers_without_risk_profile': 'Kunder utan riskprofil',
  'kriKind.reviews_overdue': 'Försenade kunduppföljningar',
  'kriKind.alert_backlog': 'Larmbalans',
  'kriKind.alert_handling_days': 'Dagar för att hantera ett larm',
  'kriKind.detection_to_report_days': 'Dagar från upptäckt till rapport',
  'kriKind.screening_runs_missed': 'Missade screeningkörningar',
  'kriUnit.percent': 'procent',
  'kriUnit.days': 'dagar',
  'kriUnit.count': 'antal',
  'auditField.originRef': 'tillagd från',
  'auditAction.handover.gap_action': 'Skapade en åtgärd ur ett avstånd',
  'auditAction.handover.apply_cycle_item': 'Lade till ett återkommande moment från överlämningen',
  'auditAction.handover.apply_revalidation': 'Lade till en modells serie för omvalidering',
  'auditAction.handover.apply_reassessment': 'Lade till serien för förnyad bedömning',
  'activity.current_state_analysis': 'nulägesanalys',
  'activity.maturity_reassessment': 'förnyad bedömning av nuläget',
  'errors.HANDOVER_NOT_FROZEN': 'Överlämningen börjar när analysen är låst.',
  'errors.HANDOVER_NOT_PROPOSED': 'Överlämningen av analysen föreslår inte det, eller så är det redan gjort.',
  'errors.ACTION_NOT_FROM_GAP': 'En åtgärd från nuläget tas fram ur ett avstånd i en låst analys.',
  'errors.GAP_HAS_ACTION': 'Avståndet har redan en åtgärd; avbryt den först för att skapa en ny.',
  'errors.REVALIDATION_EXISTS': 'Modellen har redan sin serie för omvalidering.',
  'errors.REVALIDATION_MODEL_RETIRED': 'En serie för omvalidering gäller en modell hos klienten som används.',
  'errors.REVALIDATION_PER_MODEL': 'En serie för omvalidering läggs till för en modell i modellförteckningen, vid överlämningen av en låst analys.',
  'nav.discover': 'Lär känna klienten',
  'nav.discover.answers': 'Var står klienten i arbetet, och vad behöver vi fortfarande?',
  'library.file.missing': 'Regel för det som fortfarande behövs',
  'auditAction.missing.request_add': 'Lade till rader från det vi fortfarande behöver',
  'auditAction.missing.interview_add': 'Lade till en fråga från det vi fortfarande behöver i en intervju',
  'errors.INQUIRY_NOT_DRAFT': 'Frågor läggs till i en intervju innan den hålls.',
  'errors.QUESTION_UNKNOWN': 'Intervjuguiderna har ingen sådan fråga.',
  'discover.title': 'Lär känna klienten',
  'discover.intro': 'Ett ställe för hela arbetet, från att klienten accepteras till uppföljningen. Varje siffra är ett antal med sin nämnare.',
  'discover.unverifiable': 'Den låsta analysen kan inte kontrolleras mot sin ögonblicksbild, så det vi fortfarande behöver visas inte.',
  'discover.analysis': 'Nulägesanalys {code} · {status} · per {date}',
  'discover.noAnalysis': 'Ingen nulägesanalys är planerad ännu.',
  'discover.others': 'Andra analyser av den här klienten',
  'discover.ai.off': 'AI-förslag: inte påslaget för den här klienten',
  'discover.ai.on': 'AI för den här klienten: påslaget sedan {date} · denna månad {spent} av {cap}',
  'discover.ai.onNoCap': 'AI för den här klienten: påslaget sedan {date} · denna månad {spent}, utan månadstak',
  'discover.stage.accept': 'Acceptera',
  'discover.stage.plan': 'Planera',
  'discover.stage.gather': 'Samla in',
  'discover.stage.analyse': 'Analysera',
  'discover.stage.picture': 'Helhetsbild',
  'discover.stage.conclude': 'Slutsatser',
  'discover.stage.next': 'Nästa steg',
  'discover.stage.follow': 'Följ upp',
  'discover.state.not_started': 'inte påbörjat',
  'discover.state.in_progress': 'pågår',
  'discover.state.done': 'klart',
  'discover.state.waiting': 'väntar på att analysen låses',
  'discover.state.provisional': 'preliminärt',
  'discover.state.review': 'granskas',
  'discover.state.closed': 'stängt: analysen är låst',
  'discover.accept.checks': 'Startkontroller klara eller ej tillämpliga: {n} av {of} · bekräftade undantag: {e} · fortfarande öppna: {open}',
  'discover.plan.map': '{map}: {n} av {of} arbetsmoment i omfattningen · inte bedömda: {na}',
  'discover.plan.mapNa': '{map}: {n} av {of} arbetsmoment i omfattningen · inte bedömda: {na} · inte tillämpliga: {nap}',
  'discover.plan.bands': 'Bekräftade nivåer av inneboende risk: {n} av {of} (antagna, eftersom ingen regel kunde bedöma dem: {a})',
  'discover.plan.scope': 'Arbetsmoment som lämnats utanför, bekräftade av en granskare: {n} av {of}',
  'discover.plan.notUsed': 'Inte med i den här analysen: {map} ({why})',
  'discover.plan.why.not_served': 'ingen av våra tjänster omfattar den',
  'discover.plan.why.types': 'inte skriven för den här sortens klient',
  'discover.plan.why.jurisdiction': 'inte skriven för klientens hemland',
  'discover.plan.why.not_planned': 'inte med i den här analysen',
  'discover.gather.requests': 'Begäranden: {n} (skickade: {sent} · utkast: {draft})',
  'discover.gather.requestsClosed': 'Begäranden: {n} (skickade: {sent} · utkast: {draft} · stängda: {closed})',
  'discover.gather.lines': 'Besvarade rader: {n} av {of} (kärnrader: {c} av {cof}) · finns inte: {nh}',
  'discover.gather.overdue': 'Försenat: {n} begäranden, med {lines} rader som fortfarande väntar på svar',
  'discover.gather.overdue.one': 'Försenat: {n} begäran, med {lines} rader som fortfarande väntar på svar',
  'discover.gather.overdueNone': 'Försenat: inget',
  'discover.gather.documents': 'Dokument som tagits emot på raderna: {n}',
  'discover.gather.evidence': 'Underlag som granskats av en andra medlem: {n} av {of}',
  'discover.gather.interviews': 'Hållna intervjuer: {n} av {of} planerade',
  'discover.gather.statements': 'Uttalanden som bekräftats av deltagaren: {n} av {of}',
  'discover.analyse.withFacts': 'Arbetsmoment med fakta: {n} av {of} i omfattningen',
  'discover.analyse.states': 'Bedömda: {a} · inte fastställda: {nd} · inte bedömda: {nas} · inte tillämpliga: {nap} (av {of})',
  'discover.analyse.facts': 'Konstateranden bekräftade av en andra medlem: {n} av {of}',
  'discover.analyse.inconsistencies': 'Öppna motstridigheter: {n} av {of}',
  'discover.picture.provisional': 'Preliminärt: analysen är inte låst, så varje bild här kan fortfarande ändras.',
  'discover.picture.frozen': 'Utgångsläget så som det låstes den {date}.',
  'discover.picture.internal': 'Endast för internt bruk så länge biblioteket inte är antaget.',
  'discover.picture.organisation': 'Organisationen på en sida',
  'discover.conclude.status': 'Läge: {status}, skickad av {name}',
  'discover.conclude.statusPlain': 'Läge: {status}',
  'discover.conclude.validation': 'Hållna valideringsmöten: {n} av {of} planerade',
  'discover.conclude.reportDrafted': 'Rapport: upprättad',
  'discover.conclude.reportNone': 'Rapport: inte upprättad ännu',
  'discover.next.gaps': 'Avstånd: {n} (preliminärt)',
  'discover.next.gapsFrozen': 'Avstånd: {n}',
  'discover.next.afterFreeze': 'Åtgärder tas fram ur avstånden när analysen är låst.',
  'discover.follow.waits': 'Börjar när analysen är låst.',
  'discover.follow.roadmap': 'Avstånd med en åtgärd: {n} av {of} · försenade etapper: {o} av {oof}',
  'discover.later.title': 'Kommer senare ({n})',
  'discover.later.inbox': 'Inte byggt ännu: en inkorg för många filer på en gång.',
  'discover.later.proposed_statements': 'Inte byggt ännu: uttalanden som föreslås ur en utskrift.',
  'discover.later.ai_facts': 'Inte byggt ännu: fakta som föreslås ur dokumenten.',
  'discover.later.findings': 'Inte byggt ännu: iakttagelser och åtgärdsplanen.',
  'discover.later.suggestions': 'Inte byggt ännu: förslag på nästa steg för varje avstånd.',
  'discover.later.handoffs': 'Inte byggt ännu: överlämningar till de andra experterna.',
  'discover.later.reassessment': 'Inte byggt ännu: en omprövning mot detta utgångsläge.',
  'discover.missing.top': 'De första {n} av {total}',
  'discover.missing.all': 'Alla {total} poster',
  'discover.act.close_checks': 'Stäng de öppna startkontrollerna',
  'discover.act.plan_analysis': 'Planera nulägesanalysen',
  'discover.act.finish_plan': 'Gör klart planen och börja samla in',
  'discover.act.gather': 'Skicka begäran och håll intervjuerna',
  'discover.act.see_missing': 'Se det vi fortfarande behöver ({n})',
  'discover.act.record_facts': 'Registrera fakta',
  'discover.act.open_picture': 'Öppna organisationen på en sida',
  'discover.act.review_freeze': 'Granska och lås analysen',
  'discover.act.draft_report': 'Upprätta rapporten',
  'discover.act.hand_over': 'Lämna över avstånden till arbetet',
  'discover.act.open_roadmap': 'Öppna uppföljningen av färdplanen',
  'discover.act.none': 'inget att göra här just nu',
  'discover.act.lead_plans': 'Klientansvarig planerar analysen.',
  'discover.upload': 'Ladda upp dokument',
  'discover.planInterview': 'Planera en intervju',
  'discover.export': 'Exportera det vi fortfarande behöver (DOCX)',
  'discover.work.open': 'Öppna översikten',
  'missing.title': 'Det vi fortfarande behöver',
  'missing.intro': 'Framräknat av en regel, inte av en modell: för varje arbetsmoment i omfattningen, det analysen ännu inte vet på arbetsmomentets nästa nivå (aldrig bortom målet), och den post i katalogen som skulle besvara det, med en intervjufråga om arbetsmomentet. Inget läggs till i en begäran eller en intervju förrän du lägger till det.',
  'missing.provisional': 'Preliminärt: framräknat utifrån det som är registrerat nu.',
  'missing.frozen': 'Analysen är låst: listan visar det den inte visste när den låstes.',
  'missing.count': '{n} poster',
  'missing.count.one': '{n} post',
  'missing.empty': 'Inget: varje arbetsmoment i omfattningen är avgjort på nästa nivå, eller når sitt mål.',
  'missing.col.practice': 'Arbetsmoment',
  'missing.col.unknown': 'Fortfarande okänt',
  'missing.col.request': 'Besvaras genom en begäran',
  'missing.col.interview': 'En intervjufråga om arbetsmomentet',
  'missing.level': 'Nivå {n}',
  'missing.fact': '{what}: {why}',
  'missing.element': '{code} {words}: {why}',
  'missing.reason.not_recorded': 'inte registrerat',
  'missing.reason.recorded_unknown': 'registrerat som okänt',
  'missing.reason.claimed_not_evidenced': 'påstått, inte belagt',
  'missing.reason.derived_unknown': 'kan inte räknas fram ännu',
  'missing.reason.not_checked': 'inte kontrollerat',
  'missing.reason.not_quoted': 'inget citat hittat ännu',
  'missing.line': 'begärd i begäran {request}, rad {no} ({status})',
  'missing.lineNone': 'inte begärd ännu',
  'missing.noItem': 'Ingen post i katalogen besvarar detta: fråga manuellt.',
  'missing.noQuestion': 'Intervjuguiderna har ingen fråga om arbetsmomentet.',
  'missing.askedIn': 'med i intervju {inquiry} ({status})',
  'missing.askedNone': 'inte med i någon intervju ännu',
  'missing.settles': 'besvarar {n} poster i listan',
  'missing.settles.one': 'besvarar {n} post i listan',
  'missing.add.request': 'Lägg till i begäran',
  'missing.add.target': 'Lägg till rader i',
  'missing.add.new': 'en ny begäran',
  'missing.add.interview': 'Lägg till i intervjun',
  'missing.add.inquiry': 'Lägg till frågor i',
  'missing.add.noDraft': 'Planera en intervju först: frågor läggs till i en intervju innan den hålls.',
  'missing.add.done': 'Tillagt: {items}.',
  'missing.add.none': 'Inget lades till.',
  'missing.add.skipped': 'Inte tillagt: {items}.',
  'missing.add.questionDone': 'Frågan finns nu med i intervjun.',
  'missing.add.questionThere': 'Frågan fanns redan med i intervjun.',
  'missing.skipped.on_open_request': 'redan begärd i en öppen begäran',
  'missing.skipped.withdrawn_here': 'återkallad från den här begäran; lägg till den i en annan',
  'missing.newRequestTitle': '{title}: det vi fortfarande behöver',
  'missing.readOnly': 'Det är klientens arbetande medlemmar som lägger till, medan analysen är öppen.',
  'missing.export': 'Exportera som brev med begäran (DOCX)',
  'engine.nav.missing': 'Det vi fortfarande behöver',
  'picture.title': 'Organisationen på en sida',
  'picture.internal': 'Endast för internt bruk: för byrån, inte för klienten.',
  'picture.internalChip': 'internt',
  'picture.asOf': 'Uppgifter per {date}',
  'picture.section.business': 'Verksamheten i siffror',
  'picture.section.offers': 'Det klienten erbjuder',
  'picture.section.bands': 'Inneboende risk',
  'picture.section.who': 'Roller i uppdraget och vem som har dem',
  'picture.noRoles': 'Inga roller i uppdraget registrerade.',
  'picture.section.contacts': 'Personer hos klienten',
  'picture.section.systems': 'System och modeller',
  'picture.section.inherited': 'Det vi tog över',
  'picture.section.services': 'Våra tjänster',
  'picture.value': '{value} (till {date})',
  'picture.later': 'senare: {value} till {date}, inte bekräftad',
  'picture.confirmed': 'bekräftad',
  'picture.notConfirmed': 'inte bekräftad',
  'picture.noFigures': 'Ingen uppgift är registrerad till och med detta datum.',
  'picture.noneRecorded': 'Inget registrerat.',
  'picture.role': '{role}: {mode} · vår person {person}',
  'picture.roleHolder': '{role}: {mode} · vår person {person} · klientens innehavare {holder}',
  'picture.neverValidated': 'aldrig validerad',
  'picture.validatedOn': 'senast validerad {date}',
  'picture.inherited': '{service}: ohanterade larm {alerts} · försenade uppföljningar {reviews} · kunder som inte kontrollerats mot listor {screened} · öppna revisionsiakttagelser {findings}',
  'picture.unknownCount': 'okänt',
  'picture.acknowledged': 'bekräftat av klienten',
  'picture.notAcknowledged': 'ännu inte bekräftat av klienten',
  'picture.cycleItems': '{n} poster i årshjulet i år',
  'picture.cycleItems.one': '{n} post i årshjulet i år',
  'picture.export': 'Exportera (DOCX, internt)',
  'picture.fte': '{n} årsarbetare',
  'picture.eur': '{n} euro',
  'picture.pct': '{n} %',
  'engine.nav.heatmap': 'Värmekarta',
  'engine.nav.role': 'Vår roll',
  'heat.title': 'Värmekarta över förmågor',
  'heat.intro': 'Beräknad enligt regler utifrån det som är registrerat, aldrig av en modell och aldrig som ett genomsnitt: varje ruta är en förmågas nivå mot målnivån, med avståndet i ord. Välj en ruta för att se varför.',
  'heat.provisional': 'Preliminär: skrafferade rutor vilar på konstateranden eller bedömningar av lagkrav som ännu inte är bekräftade. Ännu inte bekräftat: {u} av {n} konstateranden, {d} av {m} bedömningar av lagkrav.',
  'heat.confirmedOnly': 'Inte låst: beräknad utifrån det som är registrerat nu, och varje konstaterande och bedömning av lagkrav som den vilar på är bekräftad.',
  'heat.frozen': 'Analysen är låst: rutorna är dess sparade resultat.',
  'heat.rule': 'Framtagen enligt regeln för värmekartan, version {heat}, utifrån mognadsregeln, version {maturity}: Olivias förslag, ännu inte antagna av regelbevakningen.',
  'heat.legend': 'Teckenförklaring',
  'heat.hatched': 'Skrafferad: preliminär',
  'heat.dashed': 'Streckad kant: en möjlig överträdelse',
  'heat.filter': 'Visa',
  'heat.filter.all': 'Alla förmågor',
  'heat.filter.legal': 'Endast lagkrav',
  'heat.filter.changed': 'Inte byggt ännu: förmågorna som ändrats sedan förra bedömningen.',
  'heat.filter.hidden': '{n} av {total} förmågor visas',
  'heat.view': 'Visning',
  'heat.view.grid': 'Rutnät',
  'heat.view.table': 'Tabell',
  'heat.view.radar': 'Radar och staplar',
  'heat.overlays': 'Lager: bara ramar och antal, aldrig ett värde',
  'heat.overlay.needed': 'Behövs fortfarande: antal per ruta',
  'heat.overlay.role': 'Vår roll: ram',
  'heat.needed': 'behövs fortfarande: {n}',
  'heat.assessedOf': 'bedömda: {n} av {of} arbetsmoment',
  'heat.roleOnCell': 'vår roll: {groups}',
  'heat.col.capability': 'Förmåga',
  'heat.col.cell': 'Ruta',
  'heat.col.words': 'I ord',
  'heat.radar.title': 'Radar: nivå och målnivå per förmåga',
  'heat.radar.note': 'Tabellen gäller: en radar ändrar form med axlarnas ordning, så den står bredvid tabellen, aldrig ensam. En förmåga som inte kan fastställas har ingen punkt.',
  'heat.radar.desc': '{name}: varje förmågas målnivå som en streckad linje och dess nivå som en punkt, på en skala från 0 i mitten till 4 vid kanten.',
  'heat.radar.few': 'En radar behöver minst tre förmågor: staplarna visar dem.',
  'heat.bars.title': 'Nivå och målnivå per förmåga',
  'heat.bar.level': 'nivå {n}',
  'heat.bar.target': 'målnivå {n}',
  'heat.bar.none': 'nivån kan inte fastställas',
  'heat.empty': 'Ingen förmåga i analysen ännu: planera omfattningen först.',
  'heat.why.title': 'Varför: {cap}',
  'heat.why.close': 'Stäng',
  'heat.why.practices': 'Arbetsmomenten som avgör rutan',
  'heat.why.pick': 'Välj ett arbetsmoment för att se dess nivå och varför.',
  'heat.why.level': 'Nivå {level}, målnivå {target}: avstånd {gap}',
  'heat.why.levelMet': 'Nivå {level}, målnivå {target}: på eller över målnivån',
  'heat.why.priority': 'prioritet {p}',
  'heat.why.notDetermined': 'Kan inte fastställas: det är ännu inte känt om det utförs.',
  'heat.why.notAssessed': 'Bedöms inte: {reason}',
  'heat.why.notApplicable': 'Inte tillämplig: {reason}',
  'heat.why.rule': 'Beräknad enligt mognadsregeln, version {version}',
  'heat.why.gate.met': 'Nivå {n} uppnådd',
  'heat.why.gate.not_met': 'Nivå {n} inte uppnådd',
  'heat.why.gate.unknown': 'Nivå {n} är ännu inte känd',
  'heat.why.facts': 'Konstateranden',
  'heat.why.noFacts': 'Inget konstaterande är registrerat.',
  'heat.why.fact': '{fact}: {value}',
  'heat.why.recorded': 'Registrerat av {name}',
  'heat.why.confirmed': 'bekräftat av {name}',
  'heat.why.unconfirmed': 'inte bekräftat av en andra medlem',
  'heat.why.quote': '”{quote}” ({locator}) · {check}',
  'heat.why.quoteNoPlace': '”{quote}” · {check}',
  'heat.quote.found': 'citatet hittat',
  'heat.quote.not_found': 'citatet hittades inte',
  'heat.quote.not_checkable': 'citatet kan inte kontrolleras',
  'heat.why.elements': 'Obligatoriska element',
  'heat.why.element': '{element}: {state}',
  'heat.element.present': 'finns',
  'heat.element.absent': 'saknas',
  'heat.element.not_applicable': 'inte tillämpligt',
  'heat.why.duty': 'Bedömningar av lagkrav som gäller',
  'heat.why.noDuty': 'Ingen bedömning av lagkrav som gäller.',
  'heat.why.proposed': 'föreslaget: {value}',
  'heat.why.decided': 'beslutat av {name}: {value}',
  'heat.why.notDecided': 'inte beslutat ännu',
  'heat.why.dutyConfirmed': 'bekräftat av {name}',
  'heat.why.dutyUnconfirmed': 'inte bekräftat ännu',
  'heat.why.role': 'Vår roll',
  'heat.why.notAGap': 'Inget avstånd, så det har ingen grupp här.',
  'heat.why.noGroup.not_assessed': 'Bedöms inte i analysen, så den ingår inte i någon grupp.',
  'heat.why.noGroup.not_determined': 'Kan inte fastställas, så den ingår inte i någon grupp förrän nivån är känd.',
  'heat.why.noGroup.not_applicable': 'Inte tillämplig för klienten, så den ingår inte i någon grupp.',
  'heat.why.next': 'Nästa steg',
  'heat.why.equal': 'Omräknad från ögonblicksbilden: lika med det sparade resultatet.',
  'heat.why.differs': 'Omräknad från ögonblicksbilden: skiljer sig från det sparade resultatet ({fields}).',
  'heat.why.live': 'Inte låst: nivån beräknas vid varje läsning utifrån det som är registrerat nu, så det finns inget sparat att jämföra med.',
  'heat.why.open': 'Öppna arbetsmomentet',
  'heat.field.final_level': 'slutlig nivå',
  'heat.field.computed_level': 'beräknad nivå',
  'heat.field.target_level': 'målnivå',
  'heat.field.gap': 'avstånd',
  'heat.field.priority': 'prioritet',
  'heat.field.wave': 'tidsplan',
  'heat.field.quick_win': 'snabb åtgärd',
  'heat.field.cap_only': 'avstånd bara från taket',
  'heat.field.cap_reason': 'begränsningen',
  'heat.field.performer': 'vem som utför det',
  'heat.field.state': 'läge',
  'heat.field.reasons': 'nivåkraven',
  'heat.field.evidence_ids': 'underlaget',
  'heat.field.missing': 'en sparad rad saknas',
  'heat.field.extra': 'en sparad rad för mycket',
  'heat.field.snapshot': 'ögonblicksbilden kan inte läsas igen',
  'role.title': 'Vår roll',
  'role.intro': 'Varje avstånd i analysen i exakt en grupp, avgjort enligt regel utifrån tjänsterna, vem som utför arbetsmomentet, de uppgifter klienten behåller och klientens egna åtgärder. Aldrig av en modell.',
  'role.count': '{group}: {n}',
  'role.none': 'Inget avstånd i den här gruppen.',
  'role.offers': 'Möjliga erbjudanden: {list}',
  'role.noOffer': 'Möjliga erbjudanden: inga, eftersom klienten har varje tjänst för den här kartan.',
  'role.proposal': 'Gör ett utkast till uppdragsförslag för bristerna utanför det klienten har köpt (modul)',
  'role.independence': 'Oberoende: {words}',
  'role.capOnly': 'Avstånd som bara beror på taket för självbedömning, i ingen grupp: nivå 4 kräver en oberoende bedömning, och det är inget avstånd hos klienten.',
  'role.day': 'Tjänsterna som de ser ut den {date}.',
  'role.rule': 'Framtagen enligt regeln för vår roll, version {version}: Olivias förslag, ännu inte antaget av regelbevakningen.',
  'role.noGaps': 'Inget avstånd i analysen ännu.',
  'role.levelLine': 'nivå {level}, målnivå {target}, prioritet {p}',
  'discover.picture.heatmap': 'Värmekarta över förmågor',
  'discover.picture.role': 'Vår roll',
  'library.file.heat': 'Regel för värmekartan',
  'library.file.role': 'Regel för vår roll',
  'heat.item.met': 'uppfyllt',
  'heat.item.not_met': 'inte uppfyllt',
  'heat.item.unknown': 'ännu inte känt',
  // IP3a: the regulation view, the regime screening, the calendar and the overlay of the changes on the heat map.
  'engine.nav.regulation': 'Per regelverk',
  'engine.nav.calendar': 'Framåt',
  'regulation.title': 'Per regelverk',
  'regulation.intro': 'Var klienten står enligt varje regelverk den valda dagen. En ruta räknar slutsatserna om lagkrav som gäller; en regel som ännu inte gäller visar beredskap, aldrig en överträdelse; ett direktiv som riktar sig till medlemsstaterna är en notering, aldrig en ruta.',
  'regulation.on': 'Per dag',
  'regulation.today': 'I dag, {date}',
  'regulation.otherDay': 'En annan dag',
  'regulation.caption': 'Områden per regelverk den {date}',
  'regulation.domain': 'Område',
  'regulation.empty': 'Inget arbetsmoment i analysen hänvisar till regelverket inom detta område',
  'regulation.legend': 'Teckenförklaring',
  'regulation.legend.A': 'alla slutsatser uppfyllda',
  'regulation.legend.C': 'den sämsta slutsatsen är delvis uppfyllt',
  'regulation.legend.D': 'ett lagkrav som inte uppfylls: en möjlig överträdelse',
  'regulation.legend.unknown': 'kan inte avgöras, eller ännu inte beslutat av en person',
  'regulation.legend.readiness': 'beredskap, eller gäller men ännu inte bedömt: aldrig en överträdelse',
  'regulation.legend.hatched': 'Skrafferad: en slutsats som ännu inte är beslutad eller ännu inte bekräftad av en andra person',
  'regulation.state.in_force': 'gäller',
  'regulation.state.coming': 'kommande: gäller från {date}',
  'regulation.state.end_expected': 'väntas gälla sista gången {date}, inte beslutat',
  'regulation.state.ended': 'gäller inte längre',
  'regulation.count.met': 'uppfyllt: {n}',
  'regulation.count.partly_met': 'delvis uppfyllt: {n}',
  'regulation.count.not_met': 'inte uppfyllt: {n}',
  'regulation.count.not_determined': 'kan inte avgöras: {n}',
  'regulation.count.undecided': 'väntar på beslut: {n}',
  'regulation.unconfirmed': '(beslutade, inte bekräftade: {n})',
  'regulation.pending': 'gäller från {date}: ännu inte bedömt; beredskap {ready} av {total}',
  'regulation.readiness': 'beredskap: redo {ready} · ännu inte {not_yet}',
  'regulation.applies.yes': 'gäller',
  'regulation.applies.no': 'gäller inte',
  'regulation.applies.not_determined': 'inte fastställt',
  'regulation.why': 'Skäl: {reason}',
  'regulation.parts': 'Berörda delar: {list}',
  'regulation.partsNone': 'Berörda delar: inga angivna',
  'regulation.evidence': 'Underlag: {list}',
  'regulation.evidenceNone': 'Underlag: inget',
  'regulation.lookedAfter': 'Ansvarar: {who}',
  'regulation.who.our_service': 'vår tjänst',
  'regulation.who.client': 'klienten',
  'regulation.who.other_firm': 'en annan byrå',
  'regulation.who.unknown': 'okänt',
  'regulation.note': 'Anteckning: {note}',
  'regulation.recorded': 'Registrerat av {name} den {date}',
  'regulation.notScreened': 'ännu inte genomgånget',
  'regulation.record': 'Spara genomgången',
  'regulation.change': 'Ändra genomgången',
  'regulation.cancel': 'Avbryt',
  'regulation.formTitle': 'Genomgång av ett regelverk',
  'regulation.form.applies': 'Gäller det för klienten?',
  'regulation.form.reason': 'Skäl',
  'regulation.form.parts': 'Berörda delar',
  'regulation.form.evidence': 'Underlag: dokumentkoder, åtskilda med kommatecken',
  'regulation.form.who': 'Vem som ansvarar',
  'regulation.form.note': 'Anteckning (frivillig)',
  'regulation.adopted': 'Regel för regelverk, version {v}: antagen av er regelbevakning.',
  'regulation.unadopted': 'Reglerna för vyn per regelverk, version {v}: Olivias förslag, inte antagna av er regelbevakning.',
  'regulation.noColumns': 'Inget lagkrav i analysen hänvisar till ett regelverk som har en kolumn.',
  'regulation.screenedHeading': 'Genomgånget, inte bedömt',
  'regulation.screenedIntro': 'Regelverk som når klienten och som ingen karta bedömer ännu: om vart och ett gäller, vilka delar som berörs, vilket underlag som finns och vem som ansvarar. En genomgång är en uppgift för en person att läsa; den blir aldrig en ruta, en nivå eller en slutsats.',
  'regulation.saved': 'Genomgången är sparad.',
  'regulation.noScreening': 'Inget regelverk att gå igenom når den här klienten.',
  'regulation.notesHeading': 'Vad som påverkar er',
  'regulation.notesIntro': 'Direktiv som riktar sig till medlemsstaterna och de rättsakter som dagens regler bygger på: noteringar, aldrig rutor, eftersom de inte ställer några egna krav på klienten.',
  'regulation.source': 'Källa: {citation}',
  'regulation.touches': 'Berör områdena: {list}',
  'regulation.readinessHeading': 'Beredskap inför regler som ännu inte gäller',
  'regulation.noReadiness': 'Ingen regel i analysen träder i kraft efter bedömningsdagen.',
  'regulation.readinessLine': '{regime} gäller från {date}: {ready} av {total} lagkrav uppfylls redan',
  'calendar.title': 'Det som är viktigt framöver',
  'calendar.intro': 'Daterade hållpunkter i de regelverk som når klienten, lagkrav i analysen som börjar eller slutar gälla, regeländringar för klientens roller och dess egna analyspunkter. Varje datum är så exakt som sin källa.',
  'calendar.from': 'Från {date}',
  'calendar.empty': 'Inget är daterat från denna dag.',
  'calendar.when': 'När',
  'calendar.what': 'Vad',
  'calendar.source': 'Källa',
  'calendar.starts': '{regime}: lagkrav i analysen börjar gälla',
  'calendar.stops': '{regime}: lagkrav i analysen gäller för sista gången',
  'calendar.role': 'En regel för rollen ”{role}” ändras',
  'calendar.expected': 'väntas, inte beslutat',
  'calendar.readiness': 'beredskap: {ready} av {total} uppfylls redan',
  'calendar.ends': '{n} lagkrav i analysen gäller för sista gången denna dag',
  'calendar.ends.one': '1 lagkrav i analysen gäller för sista gången denna dag',
  'calendar.source.roles': 'Reglerna för rollerna',
  'calendar.source.cycle': 'Klientens årshjul',
  'calendar.source.maps': 'Kartornas hänvisningar',
  'calendar.quarter': 'kvartal {q} {y}',
  'calendar.note': 'Varje datum är hämtat ur sin källa; inget tas fram av en modell.',
  'heat.overlay.changes': 'Ändringar den {date}',
  'heat.changes.cell': 'ändras {date}: {parts}',
  'heat.changes.starts': '{n} regler börjar gälla ({ready} av {n} uppfylls redan)',
  'heat.changes.starts.one': '1 regel börjar gälla ({ready} av 1 uppfylls redan)',
  'heat.changes.stops': '{n} slutar gälla',
  'heat.changes.stops.one': '1 slutar gälla',
  'discover.plan.screening': 'Genomgång av regelverk: {n} av {of} regelverk genomgångna',
  'discover.plan.screeningParts': '{state}, berörda delar: {n} av {of}',
  'discover.picture.regulation': 'Per regelverk',
  'discover.picture.calendar': 'Det som är viktigt framöver',
  'errors.SCREENING_REGIME_NOT_SCREENED': 'Bara ett regelverk som ingen karta bedömer ännu gås igenom.',
  'errors.SCREENING_REGIME_RATED': 'Analysen bedömer regelverket med en karta, så det gås inte igenom.',
  'errors.SCREENING_PART_UNKNOWN': 'Regelverket har ingen del ”{part}”.',
  'errors.SCREENING_NOT_APPLIES_PARTS': 'Ett regelverk som inte gäller har inga berörda delar och ingen som ansvarar för det.',
  'errors.SCREENING_DOCUMENT_UNKNOWN': 'Underlaget anger ett dokument som klienten inte har: {document}.',
  'errors.INVALID_DAY': 'En dag skrivs ÅÅÅÅ-MM-DD, och ett fönster slutar efter att det börjar.',
  'record.regimeScreening': 'Genomgång av regelverk',
  'auditAction.regime.screen': 'Registrerade genomgången av ett regelverk',
  'auditField.regime': 'regelverk',
  'auditField.screeningApplies': 'gäller',
  'auditField.screeningParts': 'berörda delar',
  'auditField.lookedAfterBy': 'ansvarar',
  'regimeName.se_aml': 'Svenska penningtvättsregler i dag',
  'regimeName.amlr': 'AMLR',
  'regimeName.dora': 'DORA',
  'regimeName.gdpr': 'dataskyddsförordningen',
  'regimeName.safeguarding': 'skydd av användarnas medel',
  'regimeName.complaints': 'klagomålshantering',
  'regimeName.incident_reporting': 'rapportering av incidenter och bedrägerier',
  'regimeName.se_psa': 'lagen om betaltjänster',
  'regimeName.se_emoney': 'lagen om elektroniska pengar',
  'engine.nav.affects': 'Det som påverkar oss',
  'discover.picture.affects': 'Det som påverkar oss',
  'discover.picture.summary': 'Sammanfattning: lär känna klienten (DOCX, internt, två sidor)',
  'affects.title': 'Det som påverkar oss',
  'affects.intro': 'Det som den här klientens arbete kräver av oss som leverantör: avtalets punkter enligt EBA/GL/2019/02 punkt 75, klientens årliga granskning av oss, regeländringar för de roller vi innehar eller stödjer, den kapacitet som den ansvariga anger, roller per person och det vi tog över.',
  'affects.items.notAdopted': 'Punkterna i punkt 75 är Olivias förslag, citerade ur EBA:s texter; er regelbevakning har ännu inte antagit dem.',
  'affects.summary.title': 'Sammanfattning: lär känna klienten',
  'affects.summary.note': 'Intern och preliminär: underlag för startsamtalet, inte till klienten. Varje siffra i den kontrolleras mot registren innan filen skapas.',
  'affects.summary.docx': 'Sammanfattning: lär känna klienten (DOCX, internt, två sidor)',
  'affects.summary.pdf': 'Sammanfattning: lär känna klienten (PDF)',
  'affects.agreement.title': 'Avtalet (EBA/GL/2019/02 punkt 75)',
  'affects.agreement.line': '{service} {name}: {found} av {of} punkter finns',
  'affects.agreement.missing': ' · saknas: {list}',
  'affects.agreement.na': ' · inte tillämplig: {list}',
  'affects.agreement.unchecked': ' · ännu inte kontrollerade: {n}',
  'affects.agreement.point': '{letter}) {short}',
  'affects.agreement.state.done': ' · kontrollen är klar',
  'affects.agreement.state.not_applicable': ' · kontrollen är inte tillämplig',
  'affects.agreement.state.exception': ' · startade på ett undantag',
  'affects.agreement.noCheck': '{service} {name}: startkontrollerna är inte gjorda ännu',
  'affects.agreement.none': 'Ingen av våra tjänster kräver den här kontrollen.',
  'affects.agreement.points': 'Punkterna en och en, med orden i punkt 75',
  'affects.agreement.record': 'Registrera punkterna på sidan om uppdraget',
  'affects.reviews.title': 'Klientens årliga granskning av oss',
  'affects.review.due': '{service} {name}: klientens granskning ({series}), nästa gång {date}',
  'affects.review.none': '{service} {name}: klientens granskning ({series}), inget kommande tillfälle',
  'affects.review.noSeries': '{service} {name}: ännu inte med i årshjulet',
  'affects.reviews.none': 'Ingen av våra utlagda funktioner kräver klientens årliga granskning.',
  'affects.changes.title': 'Regeländringar för de roller vi innehar eller stödjer',
  'affects.change.tasks': '{role}, stöds av {person} ({service}): från {date} tillåter regeln inte längre att vi utför {n} av de delegerade uppgifterna',
  'affects.change.held': '{role}, innehas av {person} ({service}): från {date} tillåter regeln inte längre att vi innehar den',
  'affects.change.interim': '{role}, upprätthålls tillfälligt av {person} ({service}): från {date} tillåter regeln inte längre att vi upprätthåller den tillfälligt',
  'affects.changes.none': 'Ingen kommande regel ändrar en roll som vi innehar eller stödjer.',
  'affects.capacity.title': 'Kapacitet, så som den ansvariga anger den',
  'affects.capacity.line': '”{statement}” Angiven av {by} den {date}.',
  'affects.capacity.none': 'Inte angiven ännu.',
  'affects.capacity.never': 'Olivia räknar inte fram någon kapacitet: den visar det den ansvariga anger, daterat och med namn.',
  'affects.capacity.state': 'Ange kapaciteten',
  'affects.capacity.label': 'Kapaciteten, med dina ord',
  'affects.capacity.hint': 'Till exempel vem som täcker arbetet det kommande kvartalet. Högst 500 tecken.',
  'affects.capacity.save': 'Registrera uppgiften',
  'affects.capacity.saved': 'Uppgiften är registrerad.',
  'affects.people.title': 'Roller per person',
  'affects.person': '{person}: roller här {here} · hos de klienter du kan se {visible}',
  'affects.personAbove': ' · över byråns tröskel på {max}',
  'affects.people.none': 'Ingen innehar, upprätthåller eller stödjer en roll här.',
  'affects.people.note': 'Roller som innehas, upprätthålls tillfälligt eller stöds, räknade hos de klienter du kan se, som på sidan om personer.',
  'affects.inherited.title': 'Det vi tog över',
  'affects.inherited.line': '{service} {name}, övertaget läge per {date}, {ack}: {list}',
  'affects.inherited.ack.yes': 'bekräftat',
  'affects.inherited.ack.no': 'ännu inte bekräftat',
  'affects.inherited.nothing': 'inget öppet',
  'affects.inherited.notKnown': 'inte känt: {list}',
  'affects.inherited.none': 'Inget övertaget läge registrerat.',
  'mandate.item.foundNeeds': 'En punkt som finns behöver var den står i dokumentet och ett citat ur det.',
  'errors.ITEM_FOUND_NEEDS_QUOTE': 'En punkt som finns behöver dokumentet, var den står i det och ett citat ur det.',
  'errors.ITEM_QUOTE_NOT_CHECKABLE': 'Citatet kan inte kontrolleras: {document} har ännu ingen text att läsa. Registrera punkten när texten är läst, eller citera ett dokument med text.',
  'errors.SUMMARY_INCONSISTENT': 'Sammanfattningen stämmer inte med registren den gjordes av, så ingen fil skapades. Försök igen; om det kvarstår, säg till er administratör.',
  'errors.SUMMARY_TOO_LONG': 'Sammanfattningen får inte plats på {max} sidor, så ingen fil skapades.',
  'record.capacityStatement': 'Uppgift om kapacitet',
  'record.drift': 'Villkor i inkorgen',
  'auditAction.capacity.state': 'Angav kapaciteten',
  'auditField.capacityStatement': 'kapaciteten, så som den angavs',
  'auditField.statedBy': 'angiven av',
  'auditField.statedAt': 'angiven',
  'exportKind.discover_summary': 'sammanfattningen: lär känna klienten',
  'auditAction.export.discover_summary': 'Exporterade sammanfattningen: lär känna klienten',
  'exportKind.gap_log': 'gap-loggen för en analys',
  'auditAction.export.gap_log': 'Exporterade gap-loggen för en analys',
  // IP4a: accepting the client, the interview consent record and transcripts by hand (redaction as an option).
  'onboarding.step.accept': 'Klientacceptans',
  'acceptance.title': 'Acceptera klienten',
  'acceptance.intro': 'Innan byrån tar sig an klienten besvarar en klientansvarig eller en partner checklistan och beslutar; en andra medlem med rollen klientansvarig, granskare eller partner bekräftar. Ett nej är också ett beslut.',
  'acceptance.notAdopted': 'Checklistan är Olivias förslag och ännu inte antagen av er regelbevakning.',
  'acceptance.current.accepted': '{code}: accepterad den {on} av {by}, bekräftad den {confirmedOn} av {confirmedBy}.',
  'acceptance.current.declined': '{code}: avböjd den {on} av {by}, bekräftad den {confirmedOn} av {confirmedBy}.',
  'acceptance.waiting.accepted': '{code}: accepterad av {by} den {on}, väntar på att en andra person bekräftar.',
  'acceptance.waiting.declined': '{code}: avböjd av {by} den {on}, väntar på att en andra person bekräftar.',
  'acceptance.review': 'Ses över senast den {date}.',
  'acceptance.reviewDue': 'dags att se över',
  'acceptance.reviewOverdue': 'översynen är försenad',
  'acceptance.none': 'Inget beslut om att acceptera klienten ännu.',
  'acceptance.blocked.ACCEPTANCE_OPEN': 'Klienten startar när ett beslut att acceptera den är bekräftat, eller på ett undantag som två personer registrerar.',
  'acceptance.blocked.CLIENT_DECLINED': 'Klienten avböjdes, så den startar inte.',
  'acceptance.waitingTitle': 'Beslutet som väntar på bekräftelse',
  'acceptance.reasonIs': 'Skäl: {reason}',
  'acceptance.sourceIs': 'källa: {source}',
  'acceptance.choose': 'Välj ett svar',
  'transcript.chooseParticipant': 'Välj en deltagare',
  'acceptance.notAnswered': 'inte besvarad',
  'acceptance.documents': 'Dokument: {list}',
  'acceptance.confirm': 'Bekräfta beslutet',
  'acceptance.withdraw': 'Dra tillbaka beslutet',
  'acceptance.confirmWho': 'En andra medlem med rollen klientansvarig, granskare eller partner bekräftar; den som beslutade gör det inte.',
  'acceptance.warningsTitle': 'Konflikter och kapacitet, så som byråns register visar dem',
  'acceptance.noWarnings': 'Ingen varning: ingen i två försvarslinjer, ingen internrevision vid sidan av vårt andra arbete, och ingen över byråns tröskel för roller.',
  'acceptance.serviceLead': 'leds av {name}',
  'acceptance.noLead': 'ingen ansvarig utsedd',
  'acceptance.formTitle': 'Checklistan och beslutet',
  'acceptance.formIntro': 'Besvara varje punkt. För att acceptera ska varje punkt vila på ett svar som en acceptans får vila på; för att avböja räcker skälet.',
  'acceptance.source': 'Var svaret kommer ifrån',
  'acceptance.sourceHint': 'Listan, registret eller arkivet som söktes, och när.',
  'acceptance.words': 'Med dina ord',
  'acceptance.capacityWords': 'Kapaciteten, med dina ord',
  'acceptance.capacityHint': 'Vem som gör arbetet, och från när. Olivia räknar inte fram några timmar.',
  'acceptance.why': 'Varför',
  'acceptance.note': 'Vad som hittades',
  'acceptance.refusedOption': 'En klient accepteras inte på det här svaret; den kan avböjas på det.',
  'acceptance.addDocument': 'Ett dokument som beslutet vilar på',
  'acceptance.attach': 'Lägg till dokumentet',
  'acceptance.decisionReason': 'Skälet till beslutet',
  'acceptance.reasonNeeded': 'Ange skälet, minst tre tecken.',
  'acceptance.missing': 'För att acceptera, besvara de här punkterna fullt ut: {list}',
  'acceptance.decide.accept': 'Acceptera klienten',
  'acceptance.decide.decline': 'Avböj klienten',
  'acceptance.secondPerson': 'Beslutet väntar på en andra persons bekräftelse innan klienten kan starta.',
  'acceptance.exceptionTitle': 'Start före acceptansen',
  'acceptance.exceptionIntro': 'En klientansvarig kan begära att klienten startar innan acceptansen är beslutad; en andra person bekräftar. En avböjd klient startar inte på något undantag.',
  'acceptance.exceptionAsked': 'Begärt av {by} den {on}, ännu inte bekräftat. Skäl: {reason}',
  'acceptance.exceptionConfirmed': 'Begärt av {by}, bekräftat av {confirmedBy}. Skäl: {reason}',
  'acceptance.askException': 'Begär start på undantag',
  'acceptance.confirmException': 'Bekräfta undantaget',
  'acceptance.historyTitle': 'Tidigare beslut',
  'acceptance.withdrawnOn': 'Tillbakadraget den {date} av {by}.',
  'acceptance.basisTitle': 'Varför punkterna ställs',
  'acceptanceDecision.accepted': 'accepterad',
  'acceptanceDecision.declined': 'avböjd',
  'discover.accept.accepted': 'Accepterad: {code} av {by} den {on}, bekräftad av {confirmedBy} · ses över senast den {review}',
  'discover.accept.declined': 'Avböjd: {code} av {by} den {on}, bekräftad av {confirmedBy}',
  'discover.accept.waiting': 'Väntar på bekräftelse: {code}, beslutat av {by} den {on}',
  'discover.accept.none': 'Inte accepterad ännu: inget beslut om att acceptera klienten',
  'discover.accept.exception': 'Inte accepterad ännu: startad på ett undantag som två personer bekräftat',
  'discover.accept.reviewDue': 'dags för översyn',
  'discover.accept.reviewOverdue': 'översynen är försenad',
  'discover.act.accept_client': 'Acceptera klienten',
  'discover.gather.consent': 'Deltagarnas samtycke: ja {yes} av {of} · nej {no} · återkallat {withdrawn} · inte registrerat {missing}',
  'discover.gather.transcripts': 'Utskrifter: {n} (samtycket återkallat: {withdrawn})',
  'consent.title': 'Samtycke',
  'consent.intro': 'Varje deltagare får information om vad som registreras och varför, och säger ja eller nej till verktyget som utskriften görs med.',
  'consent.noParticipants': 'Ingen deltagare ännu: lägg till deltagarna först.',
  'consent.line.none': 'inget samtycke registrerat',
  'consent.line.yes': 'ja till ”{tool}”',
  'consent.line.no': 'nej till ”{tool}”',
  'consent.line.notice': ' · information lämnad den {date}',
  'consent.line.withdrawn': 'samtycket återkallat den {date} (var ja till ”{tool}”)',
  'consent.record': 'Registrera samtycket',
  'consent.change': 'Ändra svaret',
  'consent.withdraw': 'Registrera ett återkallande',
  'consent.withdrawn': 'Återkallandet är registrerat: uttalandena ur utskriften räknas inte längre som underlag.',
  'consent.withdrawNote': 'En deltagare kan när som helst återkalla sitt samtycke. När utskriften är bifogad kan ett samtycke bara återkallas, inte ändras.',
  'consent.tool': 'Verktyget som utskriften görs med',
  'consent.toolHint': 'Till exempel ”skriven av intervjuaren” eller namnet på verktyget för utskrift.',
  'consent.answer': 'Deltagarens svar',
  'consent.noticeOn': 'Information lämnad den',
  'consent.noticeNeeded': 'En deltagare hos klienten får informationen innan samtycket registreras.',
  'consent.noticeDocument': 'Informationen som dokument (om det finns)',
  'consent.save': 'Spara samtycket',
  'consentAnswer.yes': 'ja',
  'consentAnswer.no': 'nej',
  'transcript.title': 'Utskrift',
  'transcript.noAudio': 'Inget ljud sparas: en utskrift är text, skriven här eller ur ett dokument.',
  'transcript.consentRule': 'En utskrift bifogas först när alla deltagare har sagt ja till dess verktyg.',
  'transcript.none': 'Ingen utskrift ännu.',
  'transcript.line': '{code} · verktyg: {tool} · talare: {speakers}',
  'transcript.attached': 'Bifogad av {by} den {on}.',
  'transcript.withdrawn': 'Samtycket till utskriften är återkallat: uttalandena ur den räknas inte längre som underlag.',
  'transcript.text.pending': 'texten läses in',
  'transcript.text.not_checkable': 'ingen text kunde läsas: citat kan inte kontrolleras',
  'transcript.text.failed': 'texten kunde inte läsas',
  'transcript.redactedCounts': 'Maskerat innan det sparades: {counts}',
  'transcript.nothingRedacted': 'inget',
  'redaction.count.name': 'namn {n}',
  'redaction.count.phone': 'telefonnummer {n}',
  'redaction.count.email': 'e-postadresser {n}',
  'redaction.count.id': 'id-nummer {n}',
  'redaction.count.account': 'konto- eller kortnummer {n}',
  'redaction.kind.name': 'ett namn',
  'redaction.kind.phone': 'ett telefonnummer',
  'redaction.kind.email': 'en e-postadress',
  'redaction.kind.id': 'ett id-nummer',
  'redaction.kind.account': 'ett konto- eller kortnummer',
  'transcript.how': 'Hur utskriften kommer in',
  'transcript.typed': 'Skriv eller klistra in utskriften',
  'transcript.toolHint': 'Samma verktyg som alla deltagare sagt ja till.',
  'transcript.speakers': 'Talare',
  'transcript.speakersHint': 'Koppla varje beteckning i utskriften (S1, ett namn, en initial) till en deltagare: det de sade tillskrivs deras roll.',
  'transcript.label': 'Talare i utskriften',
  'transcript.speakerIs': 'Deltagare',
  'transcript.addSpeaker': 'Lägg till en talare',
  'transcript.titleField': 'Rubrik',
  'transcript.text': 'Utskriften',
  'transcript.textHint': 'En rad per replik, var och en med talarens beteckning först.',
  'transcript.loadFile': 'Eller läs in den från en textfil',
  'transcript.redact': 'Maskera namn och nummer innan du sparar',
  'transcript.redactHint': 'Koden föreslår de namn, telefonnummer, e-postadresser, id- och kontonummer den ser; du avgör vart och ett. Den hittar inte ett förnamn ensamt eller ett namn med små bokstäver: läs texten själv.',
  'transcript.findSpans': 'Hitta namn och nummer',
  'transcript.noSpans': 'Koden hittade inget att maskera.',
  'transcript.notAName': 'inte en persons namn: behåll det',
  'transcript.replacedBy': 'ersätts med {word}',
  'transcript.phrase': 'Maskera ett annat ord eller uttryck',
  'transcript.addPhrase': 'Maskera det',
  'transcript.readYourself': 'Det här är ett förslag, aldrig ett klartecken: läs resultatet innan du sparar.',
  'transcript.preview': 'Det som sparas',
  'transcript.save': 'Spara utskriften',
  'transcript.saved': 'Utskriften är bifogad.',
  'transcript.statementFrom': 'ur utskriften',
  'transcript.statementWithdrawn': 'samtycket återkallat',
  'transcript.quoteIs': 'Citat: ”{quote}”',
  'transcript.quoteField': 'Ett citat ur utskriften (frivilligt)',
  'transcript.quoteHint': 'Kopiera orden exakt som de står i utskriften; de kontrolleras mot den.',
  'transcript.noLabel': 'Ingen beteckning',
  'docKind.transcript': 'utskrift',
  'errors.ACCEPTANCE_DECIDER_ROLE': 'En klientansvarig eller en partner beslutar om att acceptera en klient.',
  'errors.ACCEPTANCE_PENDING': 'Ett beslut om att acceptera klienten väntar redan på bekräftelse.',
  'errors.ACCEPTANCE_INCOMPLETE': 'Några punkter i checklistan är inte besvarade fullt ut för en acceptans.',
  'errors.ACCEPTANCE_DECIDED': 'Beslutet är redan bekräftat eller tillbakadraget.',
  'errors.ACCEPTANCE_CONFIRMER_ROLE': 'En klientansvarig, en granskare eller en partner bekräftar.',
  'errors.FOUR_EYES_ACCEPTANCE': 'Du fattade beslutet, så en andra person bekräftar det.',
  'errors.EXCEPTION_REQUESTER_ROLE': 'En klientansvarig begär ett undantag från acceptansen.',
  'errors.EXCEPTION_PENDING': 'Ett undantag från acceptansen är redan begärt.',
  'errors.EXCEPTION_DECIDED': 'Undantaget är redan bekräftat eller tillbakadraget.',
  'errors.CLIENT_DECLINED': 'Klienten avböjdes, så den startar inte, varken på undantag eller på annat sätt.',
  'errors.ACCEPTANCE_OPEN': 'Klienten är inte accepterad ännu: ett bekräftat beslut, eller ett undantag som två personer registrerar, kommer först.',
  'errors.CONSENT_MISSING': 'Alla deltagare säger ja till utskriftens verktyg innan den bifogas: {yes} av {of} har gjort det.{tools}',
  'errors.CONSENT_FIXED': 'När utskriften är bifogad kan ett samtycke bara återkallas, inte ändras.',
  'errors.CONSENT_NOT_YES': 'Bara ett ja kan återkallas.',
  'errors.CONSENT_WITHDRAWN': 'Samtycket till utskriften är återkallat: inget mer hämtas ur den.',
  'errors.NOTICE_NEEDED': 'En deltagare hos klienten får informationen innan samtycket registreras.',
  'errors.TRANSCRIPT_ATTACHED': 'Intervjun har redan sin utskrift.',
  'errors.TRANSCRIPT_NOT_FOR_QUESTIONNAIRE': 'En enkät har ingen utskrift.',
  'errors.SPEAKER_LABEL_UNKNOWN': 'Talarbeteckningen finns inte i utskriften.',
  'errors.NO_TRANSCRIPT': 'Intervjun har ingen utskrift att citera.',
  'errors.TRANSCRIPT_NOT_READ': 'Utskriftens text är inte inläst ännu. Försök igen om en stund.',
  'errors.TRANSCRIPT_NOT_CHECKABLE': 'Utskriften har ingen text som ett citat kan kontrolleras mot.',
  'errors.REDACTION_INCOMPLETE': 'Något som identifierar en person finns kvar i texten: hitta namn och nummer igen och avgör vart och ett.',
  'errors.DOCUMENT_EMPTY': 'Utskriften har ingen text.',
  'record.acceptance': 'Beslut om att acceptera klienten',
  'record.acceptanceException': 'Undantag från acceptansen',
  'record.consent': 'En deltagares samtycke',
  'auditAction.acceptance.accept': 'Beslutade att acceptera klienten',
  'auditAction.acceptance.decline': 'Beslutade att avböja klienten',
  'auditAction.acceptance.confirm': 'Bekräftade beslutet om att acceptera klienten',
  'auditAction.acceptance.withdraw': 'Drog tillbaka beslutet om att acceptera klienten',
  'auditAction.acceptance.exception': 'Begärde start före acceptansen',
  'auditAction.acceptance.exception_confirm': 'Bekräftade start före acceptansen',
  'auditAction.acceptance.exception_withdraw': 'Drog tillbaka start före acceptansen',
  'auditAction.consent.record': 'Registrerade en deltagares samtycke',
  'auditAction.consent.withdraw': 'Registrerade att ett samtycke återkallades',
  'auditAction.transcript.attach': 'Bifogade utskriften',
  'auditField.decision': 'beslut',
  'auditField.checklistVersion': 'checklistans version',
  'auditField.withdrawnBy': 'återkallat av',
  'auditField.withdrawnAt': 'återkallat',
  'auditField.consent': 'samtycke',
  'auditField.tool': 'verktyg',
  'auditField.noticeGivenOn': 'information lämnad',
  'auditField.transcriptDocument': 'utskrift',
  'auditField.transcriptTool': 'utskriftens verktyg',
  'auditField.transcriptRedaction': 'vad som maskerades',
  'auditField.transcriptAttachedBy': 'utskriften bifogad av',
  'auditField.transcriptAttachedAt': 'utskriften bifogad',
  'auditField.consentNoticeDocument': 'informationen som dokument',
  'auditField.speakerLabel': 'talare i utskriften',
  // The review round of wave 2 (findings on IP3a to IP4b).
  'consent.withdrawConfirm': 'Registrera återkallandet? Uttalandena ur utskriften slutar räknas som underlag. Det går inte att ångra.',
  'consent.withdrawHow': 'Hur återkallandet kom in (till exempel: med e-post till intervjuaren)',
  'consent.withdrawSubmit': 'Registrera återkallandet',
  'documents.removePick': 'Ta bort',
  'inquiry.chooseLevel': 'Välj en nivå',
  'inquiry.chooseSpeaker': 'Välj vem som sade det',
  'inquiry.confidentialNoTranscript': 'Ett uttalande från en konfidentiell källa hämtas inte ur utskriften: citatet och talaren skulle avslöja källan.',
  'inquiry.noParticipantAfterTranscript': 'Utskriften är bifogad, så ingen deltagare läggs till: en ny person har inte samtyckt till den.',
  'regulation.form.addEvidence': 'Lägg till',
  'transcript.chooseTool': 'Välj verktyget',
  'transcript.toolFromConsent': 'Verktyget som alla deltagare har sagt ja till.',
  'transcript.toolChoose': 'Deltagarna har sagt ja till fler än ett verktyg: alla deltagare måste ha sagt ja till det som väljs.',
  'transcript.file': 'Utskriftsfilen (WebVTT, SubRip, text, Word eller PDF)',
  'transcript.fileHint': 'Filen går bara till den här intervjun: den sparas när alla deltagare har sagt ja, aldrig innan.',
  'transcript.fromFile': 'Skicka filen från transkriberingsverktyget',
  'transcript.heldOn': 'Dagen då intervjun hölls',
  'transcript.heldOnHint': 'En utskrift hör till en intervju som har hållits.',
  'transcript.statement': 'Vad utskriften innehåller',
  'transcript.statementNone': 'Utskriften innehåller inga uppgifter på kundnivå (inga uppgifter om klientens egna kunder, en och en).',
  'calendar.endsExpected': '{n} lagkrav i analysen gäller för sista gången denna dag (väntas, inte beslutat)',
  'calendar.endsExpected.one': '1 lagkrav i analysen gäller för sista gången denna dag (väntas, inte beslutat)',
  'heat.changes.stopsExpected': '{n} väntas sluta gälla (inte beslutat)',
  'heat.changes.stopsExpected.one': '1 väntas sluta gälla (inte beslutat)',
  'heat.changes.stopsSomeExpected': '{n} slutar gälla ({e} av dem väntat, inte beslutat)',
  'affects.agreement.allUnchecked': '{service} {name}: de {of} punkterna är inte kontrollerade ännu',
  'affects.review.open': 'Öppna serien {series}',
  'acceptance.reviewTitle': 'Se över accepterandet',
  'acceptance.reviewIntro': 'Ett beslut gäller redan. Ett nytt beslut ersätter det när en andra person har bekräftat det.',
  'acceptance.declinedActive': 'Klienten avböjdes ({code}, bekräftat den {date}) medan arbetet pågår. Avsluta klientens tjänster, eller se över accepterandet igen.',
  'acceptance.declinedActiveServices': 'Gå till tjänsterna',
  'mediaType.text': 'textfil',
  'mediaType.csv': 'CSV-fil',
  'mediaType.markdown': 'Markdown-fil',
  'mediaType.pdf': 'PDF',
  'mediaType.xlsx': 'Excel-arbetsbok',
  'mediaType.docx': 'Word-dokument',
  'mediaType.pptx': 'PowerPoint-presentation',
  'mediaType.doc': 'Word-dokument (äldre format)',
  'mediaType.xls': 'Excel-arbetsbok (äldre format)',
  'mediaType.ppt': 'PowerPoint-presentation (äldre format)',
  'mediaType.eml': 'e-post',
  'mediaType.vtt': 'WebVTT-utskrift',
  'mediaType.srt': 'SubRip-utskrift',
  'mediaType.image': 'bild',
  'mediaType.unknown': 'fil av okänd typ',
  'mediaType.zip': 'zip-arkiv',
  'mediaType.other': 'fil ({type})',
  'textStatus.slides.one': 'texten är läst: 1 bild, {notes} med talaranteckningar',
  'errors.consentTools': ' De har sagt ja till: {tools}.',
  'errors.TRANSCRIPT_THROUGH_INQUIRY': 'En utskrift skickas till sin intervju, där alla deltagares samtycke kontrolleras först; den laddas inte upp som ett vanligt dokument.',
  'errors.PARTICIPANT_AFTER_TRANSCRIPT': 'Utskriften är redan bifogad, så ingen deltagare läggs till: personen har inte samtyckt till den.',
  'errors.TRANSCRIPT_NOT_HELD': 'Registrera dagen då intervjun hölls innan utskriften.',
  'errors.CONFIDENTIAL_NOT_FROM_TRANSCRIPT': 'Ett uttalande från en konfidentiell källa hämtas inte ur en utskrift: citatet och talaren skulle avslöja källan.',
  'errors.WITHDRAWAL_NOTE_NEEDED': 'Ange hur återkallandet kom in.',
  // IP5a: the findings, what matters most, the definition of done and the mitigation plan.
  'engine.nav.mitigation': 'Det viktigaste och planen',
  'plan.title': 'Det viktigaste och åtgärdsplanen',
  'plan.notFrozen': 'Iakttagelserna och åtgärdsplanen börjar när analysen är låst.',
  'plan.matters.title': 'Det viktigaste',
  'plan.matters.p1': 'Brister med prioritet P1: {n}',
  'plan.matters.breaches': 'Möjliga överträdelser av gällande regler: {n} av {of} slutsatser om gällande regler',
  'plan.matters.escalations': 'Gjorda eskaleringar: {n}',
  'plan.matters.escalationSent': 'Eskalering {code} · {practice} {title} · meddelandet skickat den {date}',
  'plan.matters.escalationUnsent': 'Eskalering {code} · {practice} {title} · meddelandet ännu inte skickat',
  'plan.board.title': 'Beslut vi behöver från styrelsen',
  'plan.board.none': 'Ingen brist i analysen kräver ett beslut av styrelsen.',
  'plan.board.ownerRole': 'ägarrollen är styrelsen',
  'plan.board.clientAct': 'klientens egen handling: {acts}',
  'plan.findings.missing': '{n} brister saknar ännu en iakttagelse. När iakttagelserna skrivs skrivs förhållande, kriterier och definition av klart enligt regel.',
  'plan.findings.missing.one': '1 brist saknar ännu en iakttagelse. När iakttagelserna skrivs skrivs förhållande, kriterier och definition av klart enligt regel.',
  'plan.findings.write': 'Skriv iakttagelserna',
  'plan.findings.written': 'Iakttagelserna är skrivna.',
  'plan.plan.title': 'Åtgärdsplan',
  'plan.plan.counts': '{gaps} brister hos klienten · åtgärder skapade för {with} av {gaps}',
  'plan.wave.now': 'Nu · vågen slutar den {date}',
  'plan.wave.mid': 'Inom sex månader · vågen slutar den {date}',
  'plan.wave.later': 'Inom arton månader · vågen slutar den {date}',
  'plan.wave.empty': 'Ingen brist i den här vågen.',
  'plan.noAction': 'Ingen åtgärd skapad ännu',
  'plan.raiseAction': 'Skapa en åtgärd i överlämningen',
  'plan.owner': 'ägare: {owner}',
  'plan.ownerClient': '{role} (klienten)',
  'plan.ownerNone': 'ingen ägare utsedd',
  'plan.due': 'senast {date}',
  'plan.dueAfterWave': 'senast {date}, efter vågens slut',
  'plan.quickWin': 'snabb vinst',
  'plan.doneMeans': 'Klart betyder',
  'plan.doneEdited': 'Ändrad av {name}, eftersom: {reason}',
  'plan.doneGenerated': 'Som regeln skrev den',
  'plan.proof': 'Underlag',
  'plan.proofNone': 'Ingen post i begärankatalogen visar detta; bestäm underlaget med klienten.',
  'plan.editDone': 'Ändra definitionen av klart…',
  'plan.editDone.en': 'På engelska',
  'plan.editDone.sv': 'På svenska',
  'plan.editDone.reason': 'Varför den ändras',
  'plan.editDone.save': 'Spara definitionen',
  'plan.editDone.hint': 'Ange ett skäl på minst 3 tecken och båda texterna.',
  'plan.editDone.saved': 'Definitionen av klart är ändrad.',
  'plan.editDone.keepsRule': 'Texten som regeln skrev, och underlaget, sparas bredvid dina ord.',
  'plan.cancel': 'Avbryt',
  'plan.findings.title': 'Iakttagelser',
  'plan.finding.line': 'Prioritet {priority} · nivå {level} av målet {target}',
  'plan.finding.pending': 'Iakttagelsen för den här bristen är inte skriven ännu.',
  'plan.byRule': 'Förhållande, kriterier och definitionen av klart skrivs enligt regel utifrån den låsta analysen och biblioteket, och ändras aldrig.',
  'plan.condition': 'Förhållande',
  'plan.criteria': 'Kriterier',
  'plan.cause': 'Orsak',
  'plan.effect': 'Effekt',
  'plan.recommendation': 'Rekommendation',
  'plan.notWritten': 'Inte skriven ännu.',
  'plan.writtenBy': 'skriven av {name}',
  'plan.prose.edit': 'Skriv orsak, effekt och rekommendation…',
  'plan.prose.save': 'Spara',
  'plan.prose.saved': 'Sparat.',
  'plan.prose.rootCause': 'En grundorsak är en persons bedömning, eller överenskommen vid valideringsmötet; aldrig en modells.',
  'plan.ownerRole': 'Ägarroll',
  'plan.ownerRole.none': 'inte utsedd',
  'plan.ownerRole.proposed': 'föreslagen av regeln',
  'plan.ownerRole.set': 'ändrad av {name}, eftersom: {reason}',
  'plan.ownerRole.change': 'Ändra ägarrollen…',
  'plan.ownerRole.choose': 'Välj en roll',
  'plan.ownerRole.reason': 'Varför den ändras',
  'plan.ownerRole.save': 'Spara ägarrollen',
  'plan.ownerRole.hint': 'Välj en annan roll och ange ett skäl på minst 3 tecken.',
  'plan.ownerRole.saved': 'Ägarrollen är ändrad.',
  'plan.capOnly.title': 'Nivå 4 kräver en oberoende bedömning',
  'plan.capOnly.intro': 'Byrån utför dessa arbetsmoment, så nivån är bedömd av den som utför funktionen och stannar vid nivå 3. De är ingen brist hos klienten själv.',
  'plan.prepare.title': 'Åtgärdsplanen som dokument',
  'plan.prepare.intro': 'Planen tas fram ur registren som en Word-fil och en PDF: iakttagelserna, åtgärderna som de står och det viktigaste. Inget i den är skrivet av en AI-modell. Den når klienten bara genom sin kontrollpunkt.',
  'plan.prepare': 'Ta fram planen',
  'plan.prepared': 'Planen är framtagen som utkast.',
  'plan.listTitle': 'Planer',
  'plan.none': 'Ingen plan har tagits fram ännu.',
  'plan.standing': 'Kan planen publiceras?',
  'plan.internalWhy': 'Bygger på innehåll som regelbevakningen ännu inte har antagit: planen kan användas inom byrån men kan inte publiceras.',
  'plan.publishable': 'Varje biblioteksfil som planen vilar på är antagen av regelbevakningen: en plan kan publiceras när en granskare har godkänt den.',
  'plan.renderExpires': 'Om planen tas fram igen blir den ett utkast och kontrollpunkten upphör att gälla.',
  'plan.whyNotPublish': 'Du tog fram planen eller bad om granskning, så en annan granskare publicerar den.',
  'ownerRole.board': 'styrelsen (ledningsorganet)',
  'ownerRole.ceo': 'verkställande direktören',
  'ownerRole.aml_compliance_manager': 'regelefterlevnadschefen',
  'ownerRole.appointed_aml_officer': 'den särskilt utsedda befattningshavaren',
  'ownerRole.compliance_officer': 'complianceansvarig',
  'ownerRole.risk_officer': 'riskansvarig',
  'ownerRole.internal_audit_head': 'internrevisionschefen',
  'ownerRole.cfo': 'ekonomichefen',
  'ownerRole.dpo': 'dataskyddsombudet',
  'ownerRole.security_officer': 'informationssäkerhetsansvarig',
  'ownerRole.data_owner': 'dataägaren',
  'ownerRole.firm': 'byrån',
  'ownerRole.other': 'en annan roll hos klienten',
  'rootCauseSource.person': 'en person',
  'rootCauseSource.validation_meeting': 'valideringsmötet',
  'auditField.doneEditedBy': 'definitionen av klart ändrad av',
  'auditField.titleBy': 'rubriken ändrad av',
  'auditField.doneEditedAt': 'definitionen av klart ändrad',
  'auditField.proofItems': 'underlag',
  'auditField.ownerRoleProposed': 'ägarroll föreslagen av regeln',
  'auditField.ownerRole': 'ägarroll',
  'auditField.ownerRoleSetBy': 'ägarrollen ändrad av',
  'auditField.ownerRoleSetAt': 'ägarrollen ändrad',
  'auditField.causeBy': 'orsaken skriven av',
  'auditField.causeAt': 'orsaken skriven',
  'auditField.rootCauseSource': 'grundorsaken från',
  'auditField.effectBy': 'effekten skriven av',
  'auditField.effectAt': 'effekten skriven',
  'auditField.recommendationBy': 'rekommendationen skriven av',
  'auditField.recommendationAt': 'rekommendationen skriven',
  'auditField.writtenBy': 'skriven enligt regel, på uppdrag av',
  'auditField.writtenAt': 'skriven',
  'auditAction.plan.write_findings': 'Skrev bristernas iakttagelser enligt regel',
  'auditAction.plan.finding_prose': 'Skrev orsak, effekt eller rekommendation för en iakttagelse',
  'auditAction.plan.owner_role': 'Ändrade ägarrollen för en iakttagelse',
  'auditAction.plan.edit_done': 'Ändrade definitionen av klart för en åtgärd',
  'auditAction.plan.prepare': 'Tog fram en åtgärdsplan',
  'record.finding': 'Iakttagelse',
  'library.file.finding': 'Regel för iakttagelser och definitionen av klart',
  'errors.PLAN_NOT_FROZEN': 'Iakttagelserna och åtgärdsplanen börjar när analysen är låst.',
  'errors.FINDING_NOT_FOR_GAP': 'En iakttagelse skrivs för en brist i en låst analys.',
  'errors.FINDING_WRITTEN_BY_RULE': 'En iakttagelses förhållande, kriterier, definition av klart och underlag skrivs enligt regel och ändras aldrig.',
  'errors.OWNER_ROLE_REASON_NEEDED': 'Ange varför ägarrollen ändras.',
  'errors.ROOT_CAUSE_NEEDS_MEETING': 'En grundorsak från valideringsmötet registreras med mötet, som kommer senare.',
  'errors.ACTION_NEEDS_FINDING': 'Skriv bristens iakttagelse först: en åtgärd från en brist bär dess definition av klart.',
  'errors.DONE_ONLY_FROM_GAP': 'Bara en åtgärd som skapats från en brist har en definition av klart och underlag.',
  'errors.DONE_REASON_NEEDED': 'Ange varför definitionen av klart ändras.',
  'engine.nav.packs': 'Möte och styrelsepaket',
  'packs.title': 'Valideringsmötet och styrelsepaketet',
  'packs.notFrozen': 'Valideringsmötet och paketen börjar när analysen är låst.',
  'packs.standing': 'Kan paketen publiceras?',
  'packs.internalWhy': 'Bygger på innehåll som regelbevakningen ännu inte har antagit: paketen kan användas inom byrån men kan inte publiceras.',
  'packs.publishable': 'Varje biblioteksfil som paketen bygger på är antagen av regelbevakningen: ett paket kan publiceras när en granskare har godkänt det vid granskningspunkten.',
  'packs.renderExpires': 'Att ta fram det igen gör paketet till ett utkast igen och avslutar granskningspunkten.',
  'packs.whyNotPublish': 'Du tog fram paketet eller begärde granskningen, så en annan granskare publicerar det.',
  'packs.language': 'Språk',
  'packs.validation.title': 'Underlaget för valideringsmötet',
  'packs.validation.intro': 'Utkasten till iakttagelser, bristerna och underlaget de vilar på, som delas med klienten innan rapporten är klar så att inget i den kommer som en överraskning.',
  'packs.validation.prepare': 'Ta fram underlaget för valideringsmötet',
  'packs.validation.prepared': 'Underlaget har tagits fram som ett utkast.',
  'packs.validation.none': 'Inget underlag för valideringsmötet har tagits fram ännu.',
  'packs.log.title': 'Mötets logg: inga överraskningar',
  'packs.log.intro': 'Varje kommentar registreras med talarens roll, aldrig ett namn. En kommentar som rättar en uppgift blir en föreslagen uppgift som en annan medarbetare på byrån beslutar om; den låsta analysen ändras aldrig, och en godtagen rättelse förs in i omprövningen.',
  'packs.log.none': 'Ingen kommentar har registrerats ännu.',
  'packs.log.counts': 'Kommentarer: {n} · rättelser av uppgifter: {fc}, varav {open} ska beslutas · grundorsaker: {rc} · ifrågasatta: {disputed}',
  'packs.log.line': '{code} · {practice} {title} · {kind} · {role}, {date}',
  'packs.log.response': 'Vårt svar: {text}',
  'packs.log.proposed': 'Föreslagen uppgift: {fact} – {value}',
  'packs.log.decided': '{state} av {name}: {reason}',
  'packs.log.recordedBy': 'Registrerad av {name}',
  'packs.log.accept': 'Godta den föreslagna uppgiften',
  'packs.log.decline': 'Avslå den föreslagna uppgiften',
  'packs.log.reason': 'Varför',
  'packs.log.acceptSubmit': 'Godta',
  'packs.log.declineSubmit': 'Avslå',
  'packs.log.decidedDone': 'Den föreslagna uppgiften är beslutad.',
  'packs.log.notYours': 'Du registrerade kommentaren, så en annan medarbetare beslutar om den föreslagna uppgiften.',
  'packs.log.rootCause': 'Gör den till bristens grundorsak',
  'packs.log.rootCauseDone': 'Grundorsaken är registrerad på bristens iakttagelse.',
  'packs.log.isRootCause': 'Det här är bristens grundorsak.',
  'packs.log.needsFinding': 'Skriv bristens iakttagelse först, på sidan för åtgärdsplanen.',
  'proposalStatus.proposed': 'Ska beslutas',
  'proposalStatus.accepted': 'Godtagen för omprövningen',
  'proposalStatus.declined': 'Avslagen',
  'commentKind.factual_correction': 'Rättelse av en uppgift',
  'commentKind.root_cause': 'Grundorsak',
  'commentKind.management_response': 'Ledningens svar',
  'commentKind.disputed': 'Ifrågasatt',
  'packs.record.title': 'Registrera en kommentar från mötet',
  'packs.record.needsPack': 'Ta fram underlaget först: mötet hålls utifrån det.',
  'packs.record.pack': 'Underlag som diskuterades',
  'packs.record.heldOn': 'Mötet hölls den',
  'packs.record.gap': 'Brist',
  'packs.record.role': 'Talarens roll (aldrig ett namn)',
  'packs.record.kind': 'Typ av kommentar',
  'packs.record.comment': 'Vad som sades',
  'packs.record.response': 'Vårt svar (frivilligt)',
  'packs.record.fact': 'Uppgift som rättas',
  'packs.record.value': 'Föreslaget värde',
  'packs.record.document': 'Dokument som visar det (frivilligt)',
  'packs.record.choose': 'Välj…',
  'packs.record.noDocument': 'Inget dokument',
  'packs.record.hint': 'Ange talarens roll, kommentaren och, för en rättelse av en uppgift, uppgiften och dess värde.',
  'packs.record.save': 'Registrera kommentaren',
  'packs.record.done': 'Kommentaren är registrerad.',
  'packs.board.title': 'Styrelsepaketet',
  'packs.board.intro': 'En huvuddel på högst två sidor för klientens styrelse, med bilagan efter. Siffrorna skrivs av kod; inledningen och iakttagelserna skrivs av en person, och textkontrollen läser dem före godkännandet.',
  'packs.board.introduction': 'Inledning',
  'packs.board.observations': 'Iakttagelser',
  'packs.board.prepare': 'Ta fram styrelsepaketet',
  'packs.board.prepared': 'Styrelsepaketet har tagits fram som ett utkast.',
  'packs.board.none': 'Inget styrelsepaket har tagits fram ännu.',
  'packs.board.editProse': 'Ändra inledningen och iakttagelserna',
  'packs.board.saveProse': 'Spara och ta fram igen',
  'packs.board.proseSaved': 'Sparat; paketet har tagits fram igen som ett utkast.',
  'packs.board.proseBy': 'Inledning och iakttagelser skrivna av {name}',
  'prose.title': 'Textkontroll',
  'prose.summary': 'Påståenden om registret som lästes: {statements} · {flags}, {open}',
  'prose.flags.one': '{n} flagga',
  'prose.flags': '{n} flaggor',
  'prose.openCount.one': '{n} öppen',
  'prose.openCount': '{n} öppna',
  'prose.nothingToCheck': 'Ingen text skriven av en person att kontrollera.',
  'prose.clean': 'Texten stämmer med registret: ingen flagga.',
  'prose.nothingRead': 'Kontrollen läste inget påstående om registret i texten.',
  'prose.check.consistency': 'En siffra skiljer sig från registret',
  'prose.check.verdict': 'Ett omdöme utan en matchande slutsats',
  'prose.source.introduction': 'Inledning',
  'prose.source.observations': 'Iakttagelser',
  'prose.source.cause': 'Orsak, {code}',
  'prose.source.effect': 'Effekt, {code}',
  'prose.source.recommendation': 'Rekommendation, {code}',
  'prose.source.title': 'Åtgärdens rubrik, {code}',
  'prose.source.done': 'Definitionen av klart, {code}',
  'prose.source.comment': 'Kommentar från mötet, {code}',
  'prose.source.response': 'Svar på {code}',
  'prose.says': 'Texten säger {stated}; registret har {register}.',
  'prose.value.met': 'uppfyllt',
  'prose.value.partly_met': 'delvis uppfyllt',
  'prose.value.not_met': 'inte uppfyllt',
  'prose.value.none': 'ingen slutsats som personer har beslutat och bekräftat',
  'prose.value.level': 'nivå {n}',
  'prose.answered': 'Står kvar, som {name} registrerade: {answer}',
  'prose.answer': 'Registrera varför den står kvar',
  'prose.answerReason': 'Varför meningen står kvar',
  'prose.answerSubmit': 'Registrera',
  'prose.answerDone': 'Svaret är registrerat.',
  'prose.open': 'Godkännandet väntar tills varje flagga är rättad i texten, som då tas fram igen, eller besvarad av granskaren med ett skäl.',
  'errors.PROSE_FLAGS_OPEN': 'En flagga i textkontrollen är öppen: rätta texten eller registrera varför den står kvar.',
  'errors.PROSE_NOT_CHECKED': 'Texten i den här versionen kontrollerades inte; ta fram den igen.',
  'errors.FLAG_ANSWERED': 'Flaggan är redan besvarad.',
  'errors.FLAG_NOT_AT_CHECKPOINT': 'En flagga besvaras medan versionen väntar vid granskningspunkten.',
  'errors.FOUR_EYES_FLAG': 'En flagga besvaras av en granskare som varken tog fram eller begärde granskningen.',
  'errors.FOUR_EYES_FLAG_AUTHOR': 'Du skrev den flaggade meningen, så en annan granskare registrerar varför den står kvar.',
  'errors.FOUR_EYES_PROSE_AUTHOR': 'Du skrev text som den här versionen innehåller, så en annan granskare godkänner den.',
  'errors.DONE_AFTER_REMEDIATION': 'Åtgärden har markerats som åtgärdad, så dess definition av klart ändras inte längre.',
  'errors.FOUR_EYES_DONE': 'Du ändrade åtgärdens definition av klart, så en annan granskare verifierar den.',
  'errors.FLAG_REASON_NEEDED': 'Ange varför meningen står kvar.',
  'errors.COMMENT_NEEDS_VALIDATION_PACK': 'En kommentar från valideringsmötet registreras på ett underlag för den här analysen.',
  'errors.MEETING_DAY': 'Mötet hålls efter att underlaget togs fram, och senast i dag.',
  'errors.COMMENT_NOT_GAP': 'En kommentar gäller en brist i den här analysen.',
  'errors.COMMENT_DOCUMENT': 'En föreslagen uppgift hänvisar till ett dokument hos klienten som inte är avvecklat.',
  'errors.COMMENT_PROPOSED_FACT': 'En rättelse anger uppgiften och dess värde; en annan kommentar anger inget.',
  'errors.PROPOSAL_DECIDED': 'En kommentar ändras aldrig; en föreslagen uppgift i den beslutas en gång.',
  'errors.PROPOSAL_REASON_NEEDED': 'Ange varför den föreslagna uppgiften godtas eller avslås.',
  'errors.FOUR_EYES_PROPOSAL': 'Du registrerade kommentaren, så en annan medarbetare beslutar om den föreslagna uppgiften.',
  'errors.ROOT_CAUSE_NOT_FROM_MEETING': 'Bara en kommentar om grundorsaken som registrerats på mötet för den här bristen blir dess grundorsak.',
  'errors.ROOT_CAUSE_NEEDS_FINDING': 'Skriv bristens iakttagelse först; grundorsaken registreras på den.',
  'errors.BOARD_PACK_TOO_LONG': 'Styrelsepaketets huvuddel får inte plats på två sidor; korta inledningen eller iakttagelserna.',
  'errors.PROSE_BOARD_PACK_ONLY': 'Bara ett styrelsepaket har en inledning och iakttagelser.',
  'auditField.proseBy': 'Inledning och iakttagelser skrivna av',
  'auditField.proseAt': 'Inledning och iakttagelser skrivna',
  'auditField.packDeliverable': 'Underlag som diskuterades',
  'auditField.commentKind': 'Slag av kommentar',
  'auditField.proposalStatus': 'Föreslagen uppgift',
  'auditField.proseTexts': 'Textstycken som lästes',
  'auditField.proseStatements': 'Påståenden om registret som lästes',
  'auditField.checkedBy': 'Kontrollerad av',
  'auditField.flagSeq': 'Flagga',
  'auditField.flagSource': 'Var meningen står',
  'auditField.checkKind': 'Slag av flagga',
  'auditField.factId': 'Vad registret har',
  'auditField.stated': 'Vad texten säger',
  'auditField.register': 'I registret',
  'auditField.answeredBy': 'Besvarad av',
  'auditField.answeredAt': 'Besvarad',
  'auditField.rootCauseComment': 'Grundorsak från mötet',
  'record.validationComment': 'Kommentar från valideringsmötet',
  'record.proseCheck': 'Textkontroll',
  'record.proseFlag': 'Flagga i textkontrollen',
  'library.file.verdictWords': 'Textkontrollens ord (omdömen och antal)',
  'auditAction.pack.prepare_validation': 'Tog fram ett underlag för valideringsmötet',
  'auditAction.pack.prepare_board': 'Tog fram ett styrelsepaket',
  'auditAction.pack.prose': 'Ändrade ett styrelsepakets inledning eller iakttagelser',
  'auditAction.pack.answer_flag': 'Registrerade varför en flaggad mening står kvar',
  'auditAction.pack.record_comment': 'Registrerade en kommentar från valideringsmötet',
  'auditAction.pack.decide_proposal': 'Beslutade om en föreslagen uppgift',
  'auditAction.pack.root_cause': 'Förde in mötets grundorsak i en iakttagelse',
  // IP6: inkorgen och avvikelsereglerna.
  'auditAction.job.drift.check': 'Kontrollerade det som behöver uppmärksamhet',
  'auditAction.drift.acknowledge': 'Kvitterade ett villkor i inkorgen',
  'auditField.driftRule': 'villkor',
  'auditField.raisedAt': 'upptäckt',
  'auditField.resolvedAt': 'upphörde',
  'auditField.acknowledgedAt': 'kvitterat',
  'nav.inbox': 'Inkorg, alla klienter',
  'nav.inbox.answers': 'Vad behöver uppmärksamhet hos mina klienter?',
  'inbox.intro': 'Det som den dagliga kontrollen hittade hos dina klienter. Varje rad står kvar tills villkoret har upphört; att kvittera den säger att du har sett den och stänger ingenting.',
  'inbox.none': 'Ingenting behöver uppmärksamhet hos någon av dina klienter.',
  'inbox.counts': 'Räknat: de villkor som den dagliga kontrollen finner öppna hos klienter där du är medlem: en nulägesanalys som inte är fryst 90 dagar efter att en tjänst i andra försvarslinjen startade, en startkontroll efter sitt slutdatum, ett meddelande om eskalering som inte skickats en arbetsdag efter att eskaleringen öppnades, en modell att validera, en fryst analys som inte längre räknas om till samma resultat och en acceptans att se över.',
  'inbox.showing': 'visar {n} av {total}',
  'inbox.rule.baseline_due': 'Nulägesanalys väntar',
  'inbox.rule.mandate_check_open': 'Startkontroll öppen',
  'inbox.rule.escalation_notice_unsent': 'Meddelande inte skickat',
  'inbox.rule.model_validation_due': 'Modellvalidering väntar',
  'inbox.rule.baseline_recompute_mismatch': 'Analysen räknas inte om lika',
  'inbox.rule.acceptance_review_due': 'Acceptansen ska ses över',
  'inbox.open': 'Öppna',
  'inbox.raised': 'upptäckt {date}',
  'inbox.acknowledge': 'Kvittera',
  'inbox.acknowledged': 'sedd av {name}, {date}',
  'inbox.acknowledgedNote': 'Kvitterat. Raden står kvar tills villkoret har upphört.',
  'inbox.client': '{name}: {n} öppna',
  'portfolio.q1.firm': 'Byrån',
  'errors.DRIFT_ACKNOWLEDGED': 'Detta är redan kvitterat.',
  'errors.DRIFT_RESOLVED': 'Villkoret har upphört; det finns inget att kvittera.',
  // IP7: omprövning, överföring, före och efter, H5 och scenariot.
  'nav.progress': 'Utveckling',
  'nav.progress.answers': 'Hur har klienten rört sig sedan nulägesanalysen?',
  'nav.portfolioHeat': 'Portföljkarta',
  'nav.portfolioHeat.answers': 'Var ligger mina klienter under målnivå?',
  'progress.beforeAfter': 'Före och efter',
  'progress.none': 'Ingen fryst analys ännu: utvecklingen läses bara ur frysta analyser.',
  'progress.grid': 'Förmågor före och efter',
  'progress.capability': 'Förmåga',
  'progress.target': 'Målnivå',
  'progress.level': 'nivå {n}',
  'progress.noTarget': 'ingen målnivå',
  'progress.partial': 'partiell omprövning: {n} arbetsmoment',
  'progress.partial.one': 'partiell omprövning: {n} arbetsmoment',
  'progress.band.a': 'på eller över målnivå',
  'progress.band.b': '1 nivå under målnivå',
  'progress.band.c': '2 nivåer under målnivå',
  'progress.band.d': '3 eller fler nivåer under målnivå',
  'progress.band.nd': 'ej fastställt: underlag saknas',
  'progress.band.none': 'ej bedömt',
  'progress.band.na': 'ej tillämpligt',
  'progress.change.improved': 'förbättrad',
  'progress.change.worsened': 'försämrad',
  'progress.change.unchanged': 'oförändrad',
  'progress.change.newly_in_scope': 'ny i omfattningen',
  'progress.change.left_scope': 'utanför omfattningen',
  'progress.change.not_comparable': 'inte jämförbar',
  'progress.change.undetermined': 'kan inte jämföras: ej fastställt i den ena',
  'progress.change.not_reassessed': 'inte omprövad: värdet från nulägesanalysen',
  'progress.state.assessed': 'bedömt',
  'progress.state.not_determined': 'ej fastställt',
  'progress.state.not_assessed': 'ej bedömt',
  'progress.state.not_applicable': 'ej tillämpligt',
  'progress.notComparableMeans': 'Inte jämförbar: ett arbetsmoment i förmågan har fått ändrat det som avgör dess nivå (krav, fakta, element eller klausuler) i en ny version av kartan som inte anger vilket arbetsmoment det ersätter. En ny benämning eller översättning gör inte ett arbetsmoment ojämförbart.',
  'progress.counts': 'Varje cell är analysens band mot målnivån; förändringen gäller kolumnen före. Räknat: förmågorna i klientens frysta analyser, i datumordning.',
  'progress.flags': 'Förfallet sedan frysningen',
  'progress.flag.lapsed': 'Underlag förfallet',
  'progress.flag.line': '{practice}: {evidence} gällde till och med {date}. Nivån som frystes i {assessment} står kvar; nästa omprövning prövar den igen.',
  'progress.openIt': 'Öppna analysen',
  'progress.followsLine': 'följer {code}',
  'progress.notComparableList': 'Inte jämförbara med analysen den följer: {list}',
  'progress.carry': 'Överföring',
  'progress.noValue': 'inget värde',
  'progress.retest': 'prövas igen: {reason}',
  'progress.carryNote': 'Ett överfört faktum erbjuds, det registreras inte: registrera det i den nya analysen, enligt samma regler för underlag.',
  'progress.startTitle': 'Starta en omprövning',
  'progress.follows': 'Från den frysta analysen',
  'progress.kind': 'Typ',
  'progress.kind.reassessment': 'Årlig: hela omfattningen',
  'progress.kind.triggered': 'Föranledd: endast valda arbetsmoment',
  'progress.choose': 'Arbetsmomenten som ska omprövas',
  'progress.reason': 'Varför nu',
  'progress.start': 'Starta omprövningen',
  'progress.started': 'Startad. Det som kan föras över och det som måste prövas igen står ovan.',
  'progress.whatIf': 'Tänk om',
  'progress.whatIf.intro': 'Välj ett faktum och ett värde för att se vad regeln skulle räkna fram. Det är ett scenario: inget registreras och inget fryses.',
  'progress.whatIf.on': 'I analysen',
  'progress.whatIf.run': 'Räkna fram scenariot',
  'progress.whatIf.label': 'Scenario: inte ett faktum, fryses aldrig',
  'progress.whatIf.never': 'Framräknat på en kopia av analysens fakta med din ändring. Inget sparades.',
  'progress.whatIf.recorded': 'Som registrerat',
  'progress.whatIf.scenario': 'I scenariot',
  'carry.disposition.carried': 'förs över',
  'carry.disposition.retest': 'prövas igen',
  'carry.reason.corrected_at_validation': 'rättat vid valideringsmötet',
  'carry.reason.never_tested': 'aldrig prövat',
  'carry.reason.remediation_planned': 'åtgärd tagen på det',
  'carry.reason.no_evidence': 'inget underlag',
  'carry.reason.evidence_unverified': 'underlag inte granskat',
  'carry.reason.evidence_lapsed': 'underlag förfallet',
  'h5.intro': 'Endast för planering: en rad per klient där du är medlem, ur dess senaste fullständiga frysta analys. Visas aldrig för en klient och tas aldrig med i en leverans.',
  'h5.none': 'Ingen av dina klienter har en fryst analys ännu.',
  'h5.client': 'Klient',
  'h5.asOf': '{code}, per den {date}',
  'h5.cell': '{k} av {n} förmågor under målnivå',
  'h5.notDetermined': '{n} ej fastställda',
  'h5.notAssessed': 'ej bedömt',
  'h5.counts': 'Räknat: förmågorna som bedömts eller inte kunnat fastställas i varje klients senaste fullständiga frysta analys; under målnivå betyder ett gap över noll. Endast antal, aldrig ett medelvärde, och endast för klienter där du är medlem.',
  'auditField.follows': 'följer',
  'auditField.chosenPractices': 'valda arbetsmoment',
  'auditField.triggerReason': 'varför nu',
  'auditField.retestReason': 'skäl att pröva igen',
  'auditField.evidenceCodes': 'underlag',
  'auditField.correction': 'rättelse',
  'record.carryForward': 'Överföring',
  'auditAction.assessment.reassess': 'Startade en omprövning',
  'errors.REASSESS_NOT_FROZEN': 'En omprövning utgår från en fryst analys.',
  'errors.REASSESS_PRACTICE_UNKNOWN': 'En föranledd omprövning väljer arbetsmoment ur analysen den följer: {practices}.',
  'errors.WHAT_IF_FACT_UNKNOWN': 'Det är inte ett faktum som en person registrerar.',
  'errors.WHAT_IF_EVIDENCE_UNKNOWN': 'Det underlaget finns inte i den här analysen.',
  'inbox.rule.proof_overdue': 'Underlag försenat',
  'inbox.rule.reported_done_unchecked': 'Klart enligt klienten, underlag inte begärt',
  'record.remediationPack': 'Åtgärdspaket',
  'auditField.reportedDoneOn': 'klart enligt klienten den',
  'auditField.reportedDocument': 'det klienten skickade',
  'auditField.reportedRecordedBy': 'klientens besked registrerat av',
  'auditField.reportedAt': 'klientens besked registrerat den',
  'auditField.remediationPack': 'åtgärdspaket',
  'auditField.proofRequest': 'begäran om underlag',
  'auditAction.followup.report_done': 'Registrerade att klienten rapporterar en åtgärd som klar',
  'auditAction.followup.report_pack': 'Registrerade klientens åtgärdspaket som klart för de valda åtgärderna',
  'auditAction.followup.reassess_verified': 'Startade en omprövning av arbetsmomenten vars åtgärder alla är verifierade',
  'auditAction.followup.prepare_report': 'Upprättade en uppföljningsrapport',
  'errors.ACTION_NOT_REPORTABLE': 'Bara en öppen åtgärd, eller en som pågår, kan rapporteras som klar av klienten.',
  'errors.NO_VERIFIED_PRACTICES': 'Inget arbetsmoment i analysen har ännu alla sina åtgärder verifierade.',
  'errors.FOLLOWUP_NEEDS_REASSESSMENT': 'En uppföljningsrapport upprättas ur en fryst omprövning och den frysta analys den följer.',
  'errors.REPORTED_ON_FUTURE': 'Klienten rapporterar en åtgärd som klar på en dag som har kommit.',
  'errors.PACK_REPORTED_ON_FUTURE': 'Klienten rapporterar ett paket som klart på en dag som har kommit.',
  'errors.PACK_DOCUMENT_NOT_LIVE': 'Åtgärdspaketet är ett dokument hos den här klienten som inte är arkiverat.',
  'errors.REPORTED_DOCUMENT_NOT_LIVE': 'Det klienten skickade är ett dokument hos den här klienten som inte är arkiverat.',
  'followup.reportDone': 'Klienten rapporterar detta som klart',
  'followup.reportedNote': 'Registrerat: klienten rapporterar detta som klart. Begäran om underlag är upprättad; skicka den när den är klar.',
  'followup.reportedExplain': 'Klienten säger att detta är klart. Inget ändras förrän underlaget är kontrollerat: registrera åtgärden när underlaget har kommit, och en granskare som inte registrerade den verifierar den.',
  'followup.title': 'Klienten säger att det är klart',
  'followup.line': 'Klart enligt klienten {reported_done} · underlag begärt {proof_requested} · underlag mottaget {proof_received} · verifierade {verified} · underlag försenat {proof_overdue}',
  'followup.intro': 'Räknat över åtgärderna som skapats ur analysens avstånd. Ett besked från klienten ändrar ingen nivå, verifiering eller våg: det gör bara en verifierad åtgärd och nästa omprövning.',
  'followup.none': 'Ingen åtgärd har skapats ur analysens avstånd ännu.',
  'followup.col.reported': 'Klart enligt klienten',
  'followup.col.proof': 'Underlag',
  'followup.state.verified': 'verifierad',
  'followup.state.overdue': 'underlag försenat',
  'followup.state.received': 'underlag mottaget',
  'followup.state.requested': 'underlag begärt',
  'followup.state.drafted': 'begäran upprättad, inte skickad',
  'followup.state.reported': 'klar enligt klienten, inga underlag att begära',
  'followup.state.notReported': 'inte rapporterad som klar',
  'followup.packTitle': 'Klienten rapporterar ett paket som klart',
  'followup.packIntro': 'Ladda upp klientens åtgärdspaket en gång (Dokument) och välj sedan de åtgärder det omfattar. Varje åtgärd får paketet och dagen i en och samma handling, och varje åtgärd med underlag att begära får sin begäran som utkast.',
  'followup.packDocument': 'Åtgärdspaketet',
  'followup.chooseDocument': 'Välj dokumentet',
  'followup.reportedOn': 'Klart enligt klienten den',
  'followup.packActions': 'Åtgärderna som paketet omfattar',
  'followup.packSubmit': 'Registrera paketet som klart för {n} åtgärder',
  'followup.packDone': 'Registrerat: {n} åtgärder klara enligt klienten med paketet.',
  'followup.reassessTitle': 'Varje åtgärd på {n} arbetsmoment är verifierad',
  'followup.reassessSubmit': 'Starta en omprövning av de {n} arbetsmoment vars åtgärder alla är verifierade',
  'followup.standing': 'Kan uppföljningsrapporten publiceras?',
  'followup.internalWhy': 'Bygger på innehåll som regelverksgruppen ännu inte har antagit: rapporten kan användas inom byrån men kan inte publiceras.',
  'followup.publishable': 'Varje biblioteksfil som båda analyserna vilar på är antagen av regelverksgruppen: rapporten kan publiceras när en granskare har godkänt den.',
  'followup.renderExpires': 'Att framställa den på nytt gör rapporten till ett utkast igen och låter granskningspunkten löpa ut.',
  'followup.whyNotPublish': 'Du upprättade rapporten eller begärde granskningen, så en annan granskare publicerar den.',
  'followup.reportsTitle': 'Uppföljningsrapporter för {code}',
  'followup.reportsIntro': 'Framställd ur omprövningens ögonblicksbild och den den följer: färdplanens åtgärder och före och efter, varje siffra med sin nämnare. Ingen modell skriver något av det.',
  'followup.prepareEn': 'Upprätta på engelska',
  'followup.prepareSv': 'Upprätta på svenska',
  'followup.prepared': 'Uppföljningsrapporten är upprättad som utkast.',
  'followup.noReports': 'Ingen uppföljningsrapport ännu.',
  // IP9a: "What went in", the preview, the prompt library and the synthetic class.
  'explain.whatWentIn': "Detta skickades till modellen",
  'explain.howComputed': "Så räknades detta",
  'explain.howComputedText': "Prompten återskapas ur anropets register ({version}): varje mall ur arkivet med sin SHA-256, varje uppgift hos klienten i den version och med de tecken som anropet använde, inhägnad på nytt med en platshållare för hägnadens engångsnycklar, som aldrig sparas. \"Densamma\" sägs bara när båda kontrollsummorna stämmer med det som registrerades när anropet skickades.",
  'explain.check.matches': "Den återskapade prompten är densamma som det som skickades",
  'explain.check.differs': "Det går inte att visa exakt vad som skickades",
  'explain.check.cannot_rebuild': "Kan inte återskapas helt: {n} del raderad",
  'explain.check.not_recorded': "Vad som skickades registrerades inte för detta anrop",
  'explain.chars': "{n} tecken",
  'explain.notRecorded.archive_refused': "Arkivet vägrade en mall i anropet eftersom den är lika med en text hos klienten, så delarna kunde inte registreras. Själva anropet är registrerat.",
  'explain.notRecorded.error': "Delarna i anropet kunde inte registreras på grund av ett fel. Själva anropet är registrerat.",
  'explain.notRecorded.none': "Det gjordes innan Olivia förde detta register, eller vägrades innan någon prompt sattes ihop.",
  'explain.problem.archive_missing': "En mall saknas i arkivet.",
  'explain.problem.archive_altered': "En mall i arkivet stämmer inte längre med sin kontrollsumma.",
  'explain.problem.document_unavailable': "Ett dokument som anropet använde är inte tillgängligt för dig, eller finns inte längre.",
  'explain.problem.document_changed': "Ett dokument innehåller inte längre den text som anropet använde.",
  'explain.problem.record_unavailable': "En uppgift som anropet använde är inte tillgänglig för dig, eller finns inte längre.",
  'explain.problem.record_changed': "En uppgift innehåller inte längre den text som anropet använde.",
  'explain.problem.erased': "En del som anropet använde är raderad och visas därför inte.",
  'explain.part.date_block': "Dagens datum",
  'explain.part.fence_rules': "Hur inhägnad text ska läsas",
  'explain.part.foundation': "Grund",
  'explain.part.task': "Uppgift",
  'explain.part.output_rules': "Regler för svaret",
  'explain.part.practice': "Arbetsmomentet",
  'explain.part.instructions': "Avslutande instruktion",
  'explain.part.nudge': "Svara igen som JSON",
  'explain.part.document_text': "Underlag (inhägnat som data)",
  'explain.part.proposal_request': "En medarbetares begäran (inhägnad som data)",
  'explain.part.module_config': "Modulens indata",
  'explain.range': "tecken {from} till {to}",
  'explain.fencedAsData': "Skickat inom hägnad, som data; visas här bara som hänvisning.",
  'explain.newer': "Det finns en nyare version; körningen använde v{used}.",
  'explain.newerBy': "(Den senaste av {name}, {date}.)",
  'explain.erased': "Raderad; visas inte.",
  'explain.subtitle': "{purpose} · {date} · startat av {name}",
  'explain.engine': "Motor",
  'explain.engine.anthropic': "Anthropics API",
  'explain.engine.openrouter': "OpenRouter (leverantören fast vald i koden, inga uppgifter sparas)",
  'explain.asked': "begärde {model}",
  'explain.served': "fick {model}",
  'explain.notServed': "inget svar",
  'explain.effort': "ansträngning {effort}",
  'explain.dataClass': "Dataklass",
  'explain.class.public': "offentlig",
  'explain.class.synthetic': "syntetisk",
  'explain.class.client': "klientens: endast Claude",
  'explain.class.customer': "kundnivå",
  'explain.status': "Vad som hände",
  'explain.status.sent': "skickat",
  'explain.status.refused': "vägrat",
  'explain.status.failed': "misslyckades",
  'explain.refused': "vägrades innan något skickades ({code}); detta är vad som skulle ha skickats",
  'explain.cost': "Kostnad",
  'explain.template': "Uppgiftsmall",
  'explain.notAdopted': "Inte antagen av två personer på byrån när den kördes: {list}. Tillåtet bara för att data är syntetiska.",
  'explain.layers': "Lagren, i den ordning de skickades",
  'explain.col.layer': "Lager",
  'explain.col.source': "Källa och version",
  'explain.col.hash': "Kontrollsumma",
  'explain.col.size': "Storlek",
  'explain.packs': "Kunskapspaket",
  'explain.noPack': "Inget kunskapspaket användes",
  'explain.injection': "Kontroll av instruktioner i underlaget",
  'explain.injection.none': "inget hittades i de inhägnade delarna",
  'explain.injection.found': "{source} rad {line}: {rule}",
  'explain.injectionRule.ignore_instructions': "ber modellen strunta i sina instruktioner",
  'explain.injectionRule.role_change': "försöker ge modellen en annan roll",
  'explain.injectionRule.system_prompt': "nämner systemprompten",
  'explain.injectionRule.forged_fence': "innehåller en hägnadsmarkering, som togs bort",
  'explain.outcome': "Vad människor gjorde med det",
  'explain.outcome.none': "Inga förslag görs ännu ur ett anrop (det kommer med inkorgen för underlag).",
  'explain.replayNot': "Körs inte om",
  'explain.showSystem': "Visa systemprompten",
  'explain.showFull': "Visa hela prompten som den skickades",
  'synthetic.label': "syntetiska data",
  'synthetic.labelGlm': "syntetiska data, GLM",
  'synthetic.title': "Syntetiska data",
  'synthetic.state.declared': "förklarad syntetisk: väntar på en andra person",
  'synthetic.state.in_force': "syntetisk: bekräftad",
  'synthetic.state.ended': "syntetisk: avslutad; AI för klienten är endast Claude",
  'synthetic.routeOff': "Den här installationen kör inte den syntetiska vägen (OLIVIA_SYNTHETIC_WORKSPACES är avstängd), så klienten hanteras som en verklig klient.",
  'synthetic.explain': "Förklarad när klienten skapades, med intygandet. När en andra person har bekräftat det får AI-steg köras på de låsta OpenRouter-modellerna (Mistral Large 4, GLM eller Kimi). Ett dokument där personuppgifter som identifierar någon hittas skickas aldrig till dem. Stängs det av går varje senare AI-steg endast till Claude, och det kan inte slås på igen.",
  'synthetic.otherConfirms': "Du förklarade det, så en annan arbetande medlem hos klienten bekräftar det.",
  'synthetic.confirm': "Bekräfta: klienten innehåller bara syntetiska data eller testdata",
  'synthetic.declare': "Förklara den syntetisk (bara nu, när den skapas)",
  'synthetic.declareNote': "En andra arbetande medlem bekräftar det. Det går inte att förklara senare. \"Förklarad syntetisk\" vilar på ditt ord: lägg aldrig in verkliga klientuppgifter.",
  'synthetic.attestation': "Den här klienten är fiktiv. Här finns bara syntetiska data eller testdata, inga uppgifter om en verklig kund eller person.",
  'aiRun.subtitle': "Varje modellanrop som gjorts för klienten, med vad som skickades. Körningar startar med inkorgen för underlag; här kan du förhandsgranska vad som skulle skickas.",
  'aiRun.calls': "Modellanrop",
  'aiRun.noCalls': "Inget modellanrop har gjorts för klienten. Räknat: varje anrop, skickat eller vägrat.",
  'aiRun.col.code': "Anrop",
  'aiRun.col.when': "När",
  'aiRun.col.purpose': "Uppgift",
  'aiRun.col.status': "Vad som hände",
  'aiRun.preview': "Förhandsgranska vad som skulle skickas",
  'aiRun.task': "Uppgift",
  'aiRun.taskName.b3.fact': "Faktaförslag",
  'aiRun.taskName.b3.proof_match': "Matchning av bevis",
  'aiRun.taskName.b3.evidence_map': "Karta över underlag",
  'aiRun.practice': "Arbetsmoment",
  'aiRun.practiceHint': "En kod för arbetsmomentet, till exempel AML.A2.1",
  'aiRun.documents': "Dokument",
  'aiRun.documentsHint': "Dokumentkoder, till exempel DOC-001, DOC-002",
  'aiRun.engine': "Motor",
  'aiRun.language': "Svara på",
  'aiRun.language.en': "engelska",
  'aiRun.language.sv': "svenska",
  'aiRun.nothingSent': "Inget skickades: detta är prompten som den skulle sättas ihop nu, med platshållare för hägnadens engångsnycklar.",
  'aiRun.worstCase': "Högsta möjliga kostnad",
  'aiRun.wouldRun': "Om den kördes nu",
  'aiRun.wouldRefuse': "skulle den vägras ({code})",
  'aiRun.wouldGo': "skulle den skickas",
  'prompts.title': "Instruktioner till modeller",
  'prompts.counts': "{n} mallar · {adopted} antagna · {waiting} väntar",
  'prompts.adopted': "antagen",
  'prompts.notAdopted': "inte antagen",
  'prompts.layers': "Lager: {list}",
  'prompts.note': "Varje prompt är en fil i kodförrådet, låst med sin SHA-256. Ett anrop för en verklig klient använder en mall först när en person vid regelverksdesken har kontrollerat den och en andra har antagit just den versionen; för syntetiska data märker en mall som inte är antagen bara svaret. Ingen redigerar en prompt på skärmen.",
  'prompts.drafts.title': 'Utkast som inte skickas',
  'prompts.drafts.note': 'Ett utkast är en nyare version som förvaras bredvid den som används. Det kontrolleras och antas som varje mall, men inget anrop skickar det förrän en granskad ändring av biblioteket erbjuder det i stället för den version det ersätter.',
  'prompts.draft': 'utkast, skickas inte',
  'prompts.replaces': 'Skulle ersätta {path}',
  'library.tabs': "Bibliotekets delar",
  'library.tab.library': "Mallar och regler",
  'library.tab.prompts': "Instruktioner till modeller",
  'errors.SYNTHETIC_ATTESTATION': "En klient förklaras syntetisk bara när den är fiktiv, med det överenskomna intygandet, ord för ord.",
  'errors.SYNTHETIC_SET_AT_CREATION': "En klient förklaras fiktiv bara när den skapas; det går inte att slå på senare.",
  'errors.SYNTHETIC_STATE': "Den syntetiska förklaringen är redan bekräftad, eller har avslutats.",
  'errors.ALLOWANCE_FOUR_EYES': 'Du beslutade om tillåtelsen, så en andra medlem i klientens team bekräftar den.',
  'errors.ALLOWANCE_NOTHING_TO_CONFIRM': 'AI är avstängt för klienten, så det finns inget att bekräfta.',
  'errors.SYNTHETIC_FOUR_EYES': "Du förklarade klienten fiktiv, så en andra person bekräftar det.",
  'errors.AI_DOCUMENT_NO_TEXT': "Dokumentet har ingen utläst text att skicka.",
  'auditAction.proposal_request.add': "Lade till en begäran för en AI-uppgift",
  'auditAction.proposal_request.erase': "Raderade en begäran för en AI-uppgift",
  'auditAction.prompt_text.archive': "Arkiverade en promptmall",
  'auditAction.workspace.synthetic.confirm': "Bekräftade att klienten bara innehåller syntetiska data",
  'record.proposalRequest': "Begäran för en AI-uppgift",
  'record.moduleConfig': "Modulens indata",
  'record.modelCallInput': "Del av ett modellanrop",
  'record.identifierCheck': "Kontroll av identifierande uppgifter",
  'auditField.version': "version",
  'auditField.erasedAt': "raderad",
  'auditField.erasedBy': "raderad av",
  'auditField.module': "modul",
  'auditField.template': "promptmall",
  'auditField.modelServed': "modell som svarade",
  // IP10a: modulerna från ANTON, katalogen, en moduls sida, en körnings utkast, bibliotekets flik Moduler.
  'nav.modules': "Moduler",
  'nav.modules.answers': "Vilka moduler kan hjälpa till för den här klienten, och vad har de skrivit utkast till?",
  'modules.subtitle': "Moduler från ANTON som körs för den här klienten med Olivias regler: ingen webbsökning, varje påstående om klienten citerat, inga poäng, betyg eller siffror som klienten själv ska bestämma. En modul erbjuds när två personer i regelbevakningen har bekräftat den.",
  'modules.forClient': "För den här klienten",
  'modules.aiForClient': "AI för den här klienten:",
  'modules.noServices': "Ingen pågående tjänst är registrerad för klienten, så ingen modul markeras som passande för dess tjänster.",
  'modules.services': "Era tjänster: {list}",
  'modules.catalogue': "Katalogen",
  'modules.filter': "Visa",
  'modules.filter.all': "Alla ({n})",
  'modules.filter.fits': "Era tjänster ({n})",
  'modules.noneFits': "Ingen av de {n} modulerna passar klientens tjänster.",
  'modules.none': "Ingen modul är laddad i biblioteket ännu. Räknat: alla moduler i biblioteket.",
  'modules.col.module': "Modul",
  'modules.col.desk': "Regelbevakningen",
  'modules.col.output': "Resultat",
  'modules.idLine': "{id} · v{version} (ANTON {commit})",
  'modules.webSearchOff': "webbsökning avstängd",
  'modules.catalogueNote': "Varje modul är en kopia av en modul i ANTON i en fast version, och Olivias tillägg skickas efter den. En körning går till Claude via Anthropics API inom klientens AI-tillåtelse, eller, för en klient som har förklarats syntetisk, även till de låsta OpenRouter-modellerna (Mistral Large 4, GLM eller Kimi). Svaret är ett utkast tills en person gör det till en leverans och en granskare godkänner den.",
  'modules.confirmed': "bekräftad av regelbevakningen",
  'modules.waitingDesk': "väntar på regelbevakningen ({n} av 2 steg)",
  'modules.needsSource': "behöver en förankrad källa",
  'modules.needsSourceMissing': "behöver en förankrad källa: inget dokument med text ännu",
  'modules.ai.synthetic': "syntetiska data: Claude eller GLM",
  'modules.ai.on': "på",
  'modules.ai.off': "av",
  'modules.ai.waiting': "väntar på en andra medlem",
  'modules.ai.review_passed': "ska ses över",
  'modules.blocked.ALLOWANCE_OFF': "AI är inte påslaget för den här klienten, så en modul kan inte köras. Klientansvarig slår på det under Medlemmar och AI.",
  'modules.blocked.ALLOWANCE_UNCONFIRMED': "AI för den här klienten väntar på att en andra medlem bekräftar det, så en modul kan inte köras ännu.",
  'modules.blocked.ALLOWANCE_REVIEW_PASSED': "Klientens AI-tillåtelse har passerat sitt datum för översyn, så en modul kan inte köras förrän den har setts över.",
  'modules.blocked.ENGINE_NOT_CONFIGURED': "Den här installationen har ingen modell som en modul får använda för klienten (Claude via Anthropics API kräver sin nyckel), så en modul kan inte köras. Förhandsgranskningen visar ändå vad som skulle skickas.",
  'modules.blocked.NOT_OFFERED': "Modulen erbjuds inte förrän två personer i regelbevakningen har bekräftat den.",
  'modules.texts': "Prompten, tillägget och licensen",
  'modules.readTexts': "Läs prompten ({k}k tecken), tillägget och licensen",
  'modules.readPrompt': "Läs prompten ({path})",
  'modules.promptFrom': "En exakt kopia av {source} i ANTON vid {commit}, SHA-256 {sha}…; den redigeras aldrig.",
  'modules.readOverlay': "Läs tillägget (Olivias regler, som skickas efter modulens text)",
  'modules.licence': "Licens och ändringar (Apache-2.0)",
  'modules.licenceNote': "ANTON är licensierat under Apache License 2.0. Listan nedan anger varje fil som har hämtats från ANTON, de exakta kopiorna och de ändrade filerna, med varje ändring.",
  'modules.provenance': "Ursprunget för de kopierade filerna",
  'modules.notice': "Meddelande",
  'modules.run': "Fyll i, förhandsgranska och kör",
  'modules.required': "(obligatorisk)",
  'modules.choose': "Välj",
  'modules.chooseAnalysis': "Välj en analys",
  'modules.noAnalysis': "Klienten har ingen analys ännu. Räknat: analyser som inte dragits tillbaka.",
  'modules.analysisDerived': "Det som begärs skrivs inte in här: körningen tar posterna i \"Det vi fortfarande behöver\" för analysen, i den listans ordning och med den begäran var och en redan finns i, och modellen formulerar dem.",
  'modules.chooseMany': "Välj alla som gäller",
  'modules.coverage': "Begärt: {n} poster från \"Det vi fortfarande behöver\" ({codes}).",
  'modules.coverageAll': "Utkastet nämner varje post med dess kod.",
  'modules.coverageMissing': "Nämns inte i utkastet:",
  'modules.derivedActions': "Åtgärderna skrivs inte in här: körningen tar varje åtgärd som skapats ur bristerna i den här frysta analysen, med status och underlag så som Olivia registrerat dem. Olivia skriver ut statusen i rapporten; modellen skriver texten runt den.",
  'modules.derivedRegister': "Uppgifterna skrivs inte in här: körningen tar antalen i den frysta analysens register (brister, möjliga överträdelser, styrelsebeslut, åtgärder, eskaleringar). Olivia skriver ut dem som en tabell; modellen skriver bara text, och varje antal den anger kontrolleras.",
  'modules.derivedFinding': "Iakttagelsen skrivs inte in här: körningen tar det Olivias regler skrev för den här bristen i en fryst analys (förhållande, kriterier, vad som visar att den är åtgärdad) och det personer registrerat. Ingen gradering skickas eller efterfrågas.",
  'modules.derivedAgreement': "Avtalets punkter skrivs inte in här: körningen tar punktregistreringarna i avtalskontrollen för den här tjänsten (EBA/GL/2019/02 punkt 75). Välj själva avtalet bland källorna nedan.",
  'modules.noFrozenAnalysis': "Klienten har ingen fryst analys ännu. Räknat: frysta analyser.",
  'modules.chooseGap': "Välj en brist",
  'modules.noGap': "Ingen brist i en fryst analys har en skriven iakttagelse ännu. Räknat: iakttagelser i frysta analyser.",
  'modules.chooseAgreement': "Välj en tjänsts avtal",
  'modules.noAgreement': "Ingen tjänst hos klienten har en avtalskontroll ännu (Uppdraget). Räknat: tjänster med en avtalskontroll.",
  'modules.agreementLine': "{code} · {kind} · {n} av {of} punkter registrerade",
  'modules.codeTableNote': "Utskriven av Olivia ur registret, ovanför utkastets text i leveransen; modellen har inte skrivit den.",
  'modules.proseChecked': "Varje antal och omdöme som utkastet anger har kontrollerats mot Olivias register: inget att flagga.",
  'modules.proseFlags': "Påståenden i utkastet som Olivias register inte stöder:",
  'modules.proseFlag': "\"{excerpt}\": anger {stated}, registret har {register}",
  'modules.valuesEmpty': "Kolumnen som klienten fyller i är tom på varje rad.",
  'modules.valueFilled': "Ifyllt i utkastet där klienten sätter värdet:",
  'modules.coverageActions': "Rapporterar om: {n} åtgärder som skapats ur analysen ({codes}).",
  'modules.coverageActionsAll': "Utkastet nämner varje åtgärd med dess kod.",
  'modules.derivedOffers': "Erbjudandena skrivs inte in här: körningen tar den frysta analysens brister som ligger utanför det klienten har köpt (Olivias regel role.v1) och de tjänster som skulle kunna åtgärda dem, efter oberoendekontrollen. Bara de tjänster som den släpper igenom får föreslås.",
  'modules.derivedAgents': "Antalet ombud skrivs inte in här: körningen tar klientens bekräftade verksamhetsuppgift per den valda dagen. Olivia skriver ut den i rapporten; modellen skriver orden runt den.",
  'modules.chooseFigure': "Välj ett bekräftat antal ombud",
  'modules.noFigure': "Inget bekräftat antal ombud är registrerat för klienten ännu (Verksamhet). Räknat: bekräftade antal ombud.",
  'modules.figureLine': "{value} ombud per {day}",
  'modules.groundingAll': "Utkastet nämner varje källdokument med dess kod.",
  'modules.groundingMissing': "Källdokument som utkastet inte nämner:",
  'modules.independenceNone': "Olivias oberoendekontroll höll inte inne någon tjänst.",
  'modules.independenceWithheld': "Inhållet av Olivias oberoendekontroll: {list}.",
  'modules.withheldNamed': "Nämns i utkastet fast oberoendekontrollen inte låter byrån erbjuda det:",
  'modules.agentsMatch': "Varje antal ombud som utkastet anger är det bekräftade.",
  'modules.agentsStated': "Antal ombud i utkastet som skiljer sig från registreringen ({recorded}):",
  'modules.sources': "Källor: klientens dokument (högst {max})",
  'modules.noDocuments': "Inget dokument från klienten har utläst text ännu.",
  'modules.groundedRule': "Den här modulen använde webbsökning i ANTON. Här är webbsökning avstängd, så den körs bara med en förankrad källa: ett dokument från klienten. Ett kunskapspaket är en sammanfattning, inte en källa, och räknas inte.",
  'modules.boundary.client': "En körning skickar klientens valda dokument och dina uppgifter till Claude via Anthropics API, inom klientens AI-tillåtelse, utan verktyg och utan webbsökning. Resultatet är ett utkast som en person ska läsa.",
  'modules.boundary.synthetic': "Klienten är förklarad syntetisk: en körning kan gå till Claude eller till de låsta OpenRouter-modellerna (Mistral Large 4, GLM eller Kimi). Ett dokument där personuppgifter som identifierar någon hittas skickas aldrig till dem.",
  'modules.runIt': "Kör",
  'modules.aiDraft': "AI-utkast, inte granskat av en person",
  'modules.running': "Kör…",
  'modules.openDraft': "Öppna utkastet {code}",
  'modules.refused.ALLOWANCE_OFF': "Nekad: AI är inte påslaget för den här klienten.",
  'modules.refused.ALLOWANCE_UNCONFIRMED': "Nekad: AI för klienten väntar på att en andra medlem bekräftar det.",
  'modules.refused.ALLOWANCE_REVIEW_PASSED': "Nekad: klientens AI-tillåtelse har passerat sitt datum för översyn.",
  'modules.refused.PROMPT_NOT_ADOPTED': "Nekad: en biblioteksfil som körningen skickar är inte antagen av två personer i regelbevakningen.",
  'modules.refused.CLIENT_TO_PUBLIC_ENGINE': "Nekad: klientens uppgifter går bara till Claude via Anthropics API.",
  'modules.refused.CLIENT_CEILING': "Nekad: den här installationen skickar inga klientuppgifter till någon modell.",
  'modules.refused.AI_OFF': "Nekad: AI är avstängt på servern.",
  'modules.refused.UNPRICED': "Nekad: modellen har inget känt pris medan en daglig AI-budget gäller.",
  'modules.refused.DAILY_SPEND_CAP': "Nekad: dagens AI-budget är förbrukad.",
  'modules.refused.MONTHLY_SPEND_CAP': "Nekad: klientens AI-budget för månaden är förbrukad.",
  'modules.refused.SYNTHETIC_IDENTIFIERS': "Nekad: ett dokument ser ut att innehålla personuppgifter som identifierar någon, så det skickas inte till en OpenRouter-modell.",
  'modules.refused.SYNTHETIC_UNCHECKED': "Nekad: ett dokument har inte kontrollerats för personuppgifter ännu, så det skickas inte till en OpenRouter-modell.",
  'modules.refused.NOT_CONFIGURED': "Nekad: Claude via API:et är inte konfigurerat på servern.",
  'modules.refused.other': "Nekad ({code}).",
  'modules.nothingSent': "Inget skickades.",
  'modules.failed': "Körningen blev inte klar ({code}). Den finns registrerad under AI-utkast.",
  'modules.back': "Alla moduler",
  'modules.card': "Modulen",
  'modules.does': "Vad den gör",
  'modules.never': "Vad den aldrig gör",
  'modules.output': "Resultat",
  'modules.packs': "Kunskapspaket",
  'modules.prompt': "Dess prompt",
  'modules.promptLine': "SHA-256 {sha}…, från ANTON vid {commit}",
  'modules.notOffered': "Erbjuds inte: regelbevakningen har gjort {n} av sina 2 steg för modulen.",
  'modules.runs': "Körningar för den här klienten",
  'modules.noRuns': "Modulen har inte körts för klienten ännu. Räknat: varje körning som gav ett utkast.",
  'modules.col.run': "Körning",
  'modules.col.when': "När och av vem",
  'modules.col.status': "Läge",
  'modules.col.deliverable': "Leverans",
  'modules.runStatus.draft': "utkast",
  'modules.runStatus.made_deliverable': "gjord till leverans",
  'modules.runStatus.discarded': "kasserad",
  'modules.backToModule': "Modulen",
  'modules.draft': "Utkastet",
  'modules.madeBy': "Kördes av",
  'modules.call': "Anropet till modellen",
  'modules.sourcesUsed': "Källor",
  'modules.noSourceUsed': "inget dokument från klienten",
  'modules.discardedWhy': "Kasserat eftersom",
  'modules.deliverableLanguage': "Leverans på",
  'modules.made': "Gjord till leverans. Granskning och godkännande följer på kortet nedan.",
  'modules.makeDeliverable': "Gör en leverans",
  'modules.discard': "Kassera utkastet",
  'modules.discardWhy': "Varför det kasseras",
  'modules.discardSubmit': "Kassera",
  'modules.discarded': "Kasserat.",
  'modules.draftNote': "Ett utkast görs till leverans en gång, eller kasseras; själva texten ändras aldrig. Leveransens sidfot anger att den är upprättad med AI-stöd.",
  'modules.deliverable.standing': "Kan leveransen publiceras?",
  'modules.deliverable.internalWhy': "Bygger på en modul eller en biblioteksfil som regelbevakningen inte har antagit: leveransen kan användas inom byrån men kan inte publiceras.",
  'modules.deliverable.publishable': "Modulen och alla biblioteksfiler som körningen använde är antagna av regelbevakningen: leveransen kan publiceras när en granskare har godkänt den.",
  'modules.deliverable.renderExpires': "Om leveransen framställs på nytt blir den ett utkast igen och granskningspunkten löper ut.",
  'modules.deliverable.whyNotPublish': "Du tog fram leveransen eller begärde granskningen, så en annan granskare publicerar den.",
  'moduleRunStatus.draft': "utkast",
  'moduleRunStatus.made_deliverable': "gjord till leverans",
  'moduleRunStatus.discarded': "kasserad",
  'library.tab.modules': "Moduler",
  'library.tab.packs': "Kunskapspaket",
  'libraryPacks.title': "Kunskapspaket från ANTON",
  'libraryPacks.counts': "{n} paket · {confirmed} bekräftade av regelbevakningen · kopierade från ANTON vid {commit}",
  'libraryPacks.counts.one': "{n} paket · {confirmed} bekräftade av regelbevakningen · kopierat från ANTON vid {commit}",
  'libraryPacks.rule': "Varje post i ett paket är en sammanfattning skriven av ANTONs författare, inte lagtexten. En modulkörning skickar den som sammanhang, märkt så, och aldrig som förankrad källa eller som citatet bakom ett påstående. En körning får använda ett paket först när två personer i regelbevakningen har gjort sina steg på dess kort.",
  'libraryPacks.none': "Inget kunskapspaket är laddat.",
  'libraryPacks.confirmed': "bekräftat av regelbevakningen",
  'libraryPacks.corrected': "rättat av Olivia",
  'libraryPacks.entries': "{n} poster",
  'libraryPacks.entries.one': "{n} post",
  'libraryPacks.deferred': "Uppskjutna: {n} paket, inte kopierade",
  'libraryPacks.deferred.one': "Uppskjutet: {n} paket, inte kopierat",
  'libraryPacks.note': "Ett paket kommer in i Olivia som en kopia av ANTONs filer vid en låst version, via skript (pnpm packs:sync), exakt eller med Olivias registrerade rättelse; ingen redigerar det på skärmen.",
  'packs.label': "Sammanfattning (ANTON-paket), inte en källa",
  'explain.packSummary': "{id} {version}: {n} poster, {label}",
  'explain.packSummary.one': "{id} {version}: {n} post, {label}",
  'explain.part.pack': "Ett kunskapspaket (sammanfattning, inte en källa)",
  'modules.packs.legend': "Kunskapspaket (sammanhang, inte källor)",
  'modules.packs.none': "Den här modulen tar inget kunskapspaket.",
  'modules.packs.rule': "Ett paket lägger till ANTONs sammanfattningar som sammanhang. Det är märkt som en sammanfattning, räknas aldrig som förankrad källa och citeras aldrig som lagtext.",
  'modules.packs.waiting': "väntar på regelbevakningen ({n} av 2 steg)",
  'modules.packsUsed': "Kunskapspaket som användes",
  'modules.packUsedLine': "{title} v{version}: {n} poster, {label}",
  'modules.packUsedLine.one': "{title} v{version}: {n} post, {label}",
  'errors.PACK_NOT_FOR_MODULE': "Den här modulen tar inte det kunskapspaketet. Inget skickades.",
  'errors.PACK_TOO_LARGE': "De valda kunskapspaketen innehåller mer än en körning tar; välj färre. Inget skickades.",
  'libraryModules.title': "Moduler från ANTON",
  'libraryModules.counts': "{n} moduler · {confirmed} bekräftade av regelbevakningen · kopierade från ANTON vid {commit}",
  'libraryModules.screening': "Genomgången omfattar {n} moduler i ANTON: {w1} i första vågen, {w2} i andra vågen, {later} senare och {out} utelämnade, var och en med sitt skäl.",
  'libraryModules.none': "Ingen modul är laddad. Räknat: modulkorten under data/modules.",
  'libraryModules.card': "Kortet ({path}): en person i regelbevakningen kontrollerar det, en andra antar det; sedan erbjuds modulen",
  'libraryModules.parts': "Filerna som en körning skickar med den ({adopted} av {n} antagna)",
  'libraryModules.note': "En modul kommer in i Olivia som en exakt kopia av ANTONs filer vid en låst version, via skript; ingen redigerar den på skärmen. En körning för en verklig klient kräver dessutom att varje fil den skickar är antagen, med exakt den SHA-256.",
  'explain.part.module': "Modulens prompt (ANTON)",
  'explain.part.persona': "Ett expertperspektiv (ANTON)",
  'explain.part.skill': "En färdighet (ANTON)",
  'explain.part.format': "Resultatets format",
  'explain.part.overlay': "Olivias tillägg",
  'errors.NO_GROUNDED_SOURCE': "Den här modulen använde webbsökning i ANTON, som är avstängd här. Välj en förankrad källa: ett dokument från klienten (ett kunskapspaket är en sammanfattning, inte en källa). Inget skickades.",
  'errors.MODULE_RUN_STATE': "Utkastet har redan gjorts till leverans eller kasserats.",
  'errors.MODULE_INPUTS': "Fyll i modulens uppgifter som det står: varje obligatorisk uppgift, och ett alternativ ur listan.",
  'errors.MODULE_DRIFT': "Modulens prompt har ändrats sedan biblioteket laddades. Inget skickades; biblioteket laddas på nytt och regelbevakningen bekräftar modulen igen.",
  'errors.PACK_NOT_CONFIRMED': "Det här kunskapspaketet är inte bekräftat av två personer i regelbevakningen ännu. Inget skickades.",
  'errors.MODULE_TOO_MANY_DOCUMENTS': "Modulen tar högst {max} dokument.",
  'errors.MODULE_NOTHING_TO_REQUEST': "Analysen har inget att be klienten om: den är stängd, eller så besvaras inget av det den fortfarande behöver av ett dokument från klienten. Inget skickades.",
  'errors.MODULE_NOT_FROZEN': "Modulen utgår från en fryst analys; den här är inte fryst. Inget skickades.",
  'errors.MODULE_NOT_A_GAP': "Arbetsmomentet är inte en brist i analysen med en skriven iakttagelse. Skriv iakttagelserna först, eller välj en annan brist. Inget skickades.",
  'errors.MODULE_NOTHING_TO_FOLLOW_UP': "Ingen åtgärd har skapats ur analysens brister, så det finns inget att följa upp. Inget skickades.",
  'errors.MODULE_NO_AGREEMENT_ITEMS': "Ingen punkt i avtalet för den här tjänsten är registrerad ännu (Uppdraget, avtalskontrollen). Inget skickades.",
  'errors.MODULE_NOTHING_OUTSIDE': "Ingen brist i analysen ligger utanför det klienten har köpt, så det finns inget att föreslå. Inget skickades.",
  'errors.MODULE_INDEPENDENCE': "Varje tjänst som skulle kunna åtgärda bristerna utanför det klienten har köpt faller på oberoendekontrollen: byrån skulle granska sitt eget arbete. Inget skickades.",
  'errors.MODULE_NO_AGENTS': "Inget bekräftat antal ombud är registrerat för klienten per den dagen (Verksamhet). Inget skickades.",
  'method.area.modules': "Moduler från ANTON",
  'auditAction.module.compose': "Sparade en moduls uppgifter",
  'auditAction.module.run': "Körde en modul (dess utkast)",
  'auditAction.module.make_deliverable': "Gjorde en leverans av en moduls utkast",
  'auditAction.module.discard': "Kasserade en moduls utkast",
  'record.moduleRun': "Modulutkast",
  'auditField.moduleRun': "modulutkast",
  'auditField.modelCall': "anrop till modellen",
  'auditField.groundedSources': "förankrade källor",
  'auditField.draftChars': "utkastets längd",
  'auditField.discardedBy': "kasserat av",
  'auditField.discardedAt': "kasserat",
  // IP11: the next step, the other experts and hand-offs.
  'nav.handoffs': "Överlämningar",
  'nav.handoffs.answers': "Vad gick till de andra experterna, och vad kom tillbaka?",
  'auditAction.handoff.prepare': "Förberedde en överlämning till en annan expert",
  'auditAction.handoff.request_review': "Begärde granskning av en överlämnings paket",
  'auditAction.handoff.approve': "Godkände en överlämnings paket",
  'auditAction.handoff.reject': "Skickade tillbaka en överlämning",
  'auditAction.handoff.sent': "Registrerade att en överlämning skickades",
  'auditAction.handoff.return': "Registrerade det som kom tillbaka från en expert",
  'auditAction.handoff.evidence': "Registrerade en experts rapport som underlag",
  'auditAction.handoff.withdraw': "Återkallade en överlämning",
  'next.chip': "Nästa steg: {n} ({offered} erbjudna) · {priority}",
  'next.noPriority': "ingen prioritet",
  'next.priority': "Prioritet {priority}",
  'next.title': "Nästa steg för {practice}",
  'next.back': "← Iakttagelserna och planen",
  'next.gapLine': "{title} · nivå {level}, mål {target}",
  'next.rule': "Valt enligt regeln nextstep.v1, version {version}",
  'next.adopted': "antagen av två personer i regelverksgruppen",
  'next.notAdopted': "inte antagen av regelverksgruppen ännu: förslagen är för internt bruk",
  'next.suggestions': "Föreslagna nästa steg",
  'next.noSuggestions': "Ingen regel föreslår ett nästa steg för den här bristen.",
  'next.kind.module': "Modul",
  'next.kind.olivia': "Olivia",
  'next.kind.expert': "Expert",
  'next.state.offered': "erbjuds",
  'next.state.not_yet_offered': "erbjuds inte ännu",
  'next.state.not_yet_available': "inte tillgänglig ännu",
  'next.state.done': "gjort",
  'next.act.raise_action': "Ta upp åtgärden",
  'next.act.apply_series': "Lägg in serien i årshjulet",
  'next.act.create_kri': "Skapa en nyckelriskindikator",
  'next.act.add_request_lines': "Be klienten om det som saknas",
  'next.why': "varför: {reason}",
  'next.openModule': "Öppna, förifylld",
  'next.go': "Gå dit",
  'next.prepare': "Förbered överlämning",
  'next.notSuggested': "Föreslås inte",
  'next.handoffs': "Överlämningar för den här bristen",
  'next.noHandoffs': "Ingen överlämning ännu.",
  'handoff.request.note': "Begäransfilen som Valentin läser in, med klientuppgifter: klienten, aktiviteten den besvarar, byråns tjänster och personerna på dem, bristernas utgångsläge och modellen. En granskare som inte förberett den godkänner exakt dessa byte innan någon skickar den.",
  'handoff.brief.note': "En beskrivning utan klientuppgifter: arbetsmomentet, dess offentliga referenser och uppgiften. Den här experten får bara offentligt material.",
  'handoff.form.title': "Rubrik",
  'handoff.form.occurrence': "Aktiviteten i årshjulet som den besvarar (kod)",
  'handoff.form.occurrenceHint': "Till exempel CY-2027-001: en modellvalidering, en revision eller en granskning som Valentin utför.",
  'handoff.form.validationType': "Typ av validering",
  'handoff.form.lead': "Den ansvariga du ber om hos Valentin (e-post)",
  'handoff.form.periodFrom': "Period från",
  'handoff.form.periodTo': "Period till",
  'handoff.form.objectives': "Mål",
  'handoff.form.scope': "Omfattning",
  'handoff.form.submit': "Förbered paketet",
  'handoff.validationType.initial': "första",
  'handoff.validationType.periodic': "periodisk",
  'handoff.validationType.triggered_by_change': "efter en förändring",
  'handoff.validationType.conditions_review': "en översyn av användningsvillkoren",
  'handoff.task.model_validation': "en oberoende validering av en modell",
  'handoff.task.independent_audit': "ett test av någon som är oberoende av arbetet",
  'handoff.task.bwra': "den allmänna riskbedömningen",
  'handoff.task.article_check': "en kontroll artikel för artikel",
  'handoff.task.watch_rule': "bevakning av en regel som ska ändras",
  'handoff.task.continuous_support': "löpande stöd till teamet",
  'handoff.task.other': "expertstöd",
  'handoff.task.crisis_simulation': "en krisövning",
  'handoff.task.project': "att driva arbetet som ett projekt",
  'handoff.status.draft': "utkast",
  'handoff.status.in_review': "under granskning",
  'handoff.status.approved': "godkänd, inte skickad",
  'handoff.status.sent': "skickad, väntar på svar",
  'handoff.status.answered': "besvarad",
  'handoff.status.withdrawn': "återkallad",
  'handoff.packKind.valentin_request': "olivia.handoff-request.v1 (med klientuppgifter)",
  'handoff.packKind.public_brief': "en beskrivning (inga klientuppgifter)",
  'handoff.independence.computed': "request_id stämmer: oberoende",
  'handoff.independence.cannot_be_matched': "inte oberoende: kvittot kan inte matchas",
  'handoff.match.matched': "kvittot bär den här överlämningens request_id",
  'handoff.match.no_request_id': "kvittot har inget request_id: valideringen startades inte från den här begäran",
  'handoff.match.other_request': "kvittot besvarar en annan begäran",
  'handoff.channelWord.client_data_allowed': "klientuppgifter tillåtna, som fil",
  'handoff.channelWord.public_only': "bara offentligt material",
  'handoff.channelWord.not_available': "inga klientuppgifter: ingen gräns registrerad",
  'handoffs.title': "Överlämningar",
  'handoffs.empty': "Ingen överlämning ännu. En överlämning börjar i en brists nästa steg.",
  'handoffs.col.code': "Överlämning",
  'handoffs.col.expert': "Expert",
  'handoffs.col.gaps': "Brister",
  'handoffs.col.status': "Status",
  'handoffs.col.prepared': "Förberedd",
  'handoff.backToList': "← Överlämningar",
  'handoff.title': "Rubrik",
  'handoff.gaps': "Brister",
  'handoff.analysis': "Analys",
  'handoff.channelLabel': "Expertens datakanal",
  'handoff.prepared': "Förberedd",
  'handoff.pack': "Paketet",
  'handoff.packKindLabel': "Typ",
  'handoff.requestId': "request_id",
  'handoff.occurrence': "Aktivitet i årshjulet",
  'handoff.size': "Storlek",
  'handoff.bytes': "{n} byte",
  'handoff.sha': "SHA-256",
  'handoff.download': "Ladda ned paketet",
  'handoff.checkpoint': "Granskningspunkt",
  'handoff.cp': "{code} · {status} · begärd av {by}",
  'handoff.decided': "beslutad av {by}",
  'handoff.noCheckpoint': "Ingen granskning begärd ännu.",
  'handoff.askReview': "Begär granskning",
  'handoff.approve': "Godkänn paketet",
  'handoff.reject': "Skicka tillbaka",
  'handoff.rejectWhy': "Varför den skickas tillbaka",
  'handoff.reviewerDecides': "En granskare som varken förberett överlämningen eller begärt granskningen beslutar.",
  'handoff.sent': "Skickad",
  'handoff.sentOn': "Dag den skickades",
  'handoff.channel': "Hur den skickades",
  'handoff.markSent': "Registrera att den skickades",
  'handoff.notSent': "Inte skickad: paketet godkänns först vid granskningspunkten.",
  'handoff.readyToSend': "Godkänd: en person skickar filen och registrerar dag och hur.",
  'handoff.sentLine': "{day} av {by} · {channel}",
  'handoff.back': "Det som kom tillbaka",
  'handoff.nothingBack': "Inget har kommit tillbaka ännu.",
  'handoff.returnHint': "Ladda först upp rapporten (och Valentins kvitto) under Dokument och ange dem sedan här.",
  'handoff.report': "Rapport (dokumentkod)",
  'handoff.receipt': "Valentins kvitto (dokumentkod)",
  'handoff.recordReturn': "Registrera det som kom tillbaka",
  'handoff.returned': "Registrerat av",
  'handoff.evidence': "Som underlag",
  'handoff.evidenceAssessment': "Öppen analys",
  'handoff.evidenceTitle': "Rubrik",
  'handoff.evidenceDated': "Daterad",
  'handoff.recordEvidence': "Registrera som underlag",
  'handoff.evidenceNone': "Ingen analys är öppen: rapporten blir underlag vid nästa omprövning.",
  'handoff.evidenceRow': "{code} i {assessment}",
  'handoff.reviewed': "· granskat av {by}",
  'handoff.unreviewed': "· inte granskat ännu: en medlem som inte registrerade svaret granskar det",
  'handoff.withdraw': "Återkalla",
  'handoff.withdrawWhy': "Varför den återkallas",
  'handoff.withdrawn': "Återkallad av {by}: {reason}",
  'modules.prefilled': "Förifylld från {gap}: {facts} fakta saknas vid målet, {references} referenser, underlag {evidence}. Ändra vad du vill innan du kör.",
  'inbox.rule.handoff_unanswered': "Överlämning obesvarad",
  'evidenceKind.expert_report': "En annan experts rapport",
  'record.handoff': "Överlämning",
  'auditField.handoff': "överlämning",
  'auditField.expert': "expert",
  'auditField.task': "uppgift",
  'auditField.gaps': "brister",
  'auditField.packKind': "paket",
  'auditField.clientParts': "med klientuppgifter",
  'auditField.packDocument': "paketfil",
  'auditField.requestId': "begärans id",
  'auditField.returnedBy': "svar registrerat av",
  'auditField.returnedAt': "svar registrerat",
  'auditField.reportDocument': "rapport",
  'auditField.independence': "oberoende",
  'auditField.matchReason': "kvitto",
  'errors.NEXT_NOT_FROZEN': "Nästa steg börjar när analysen är fryst.",
  'errors.HANDOFF_CHANNEL': "Den här experten får bara offentligt material: inget paket med klientuppgifter förbereds för den.",
  'errors.HANDOFF_EXPERTS_NOT_ADOPTED': "Experterna och deras datakanaler väntar på att två personer i regelverksgruppen antar dem; inget paket med klientuppgifter förbereds innan dess.",
  'errors.HANDOFF_EXPERT_UNAVAILABLE': "Den här experten är inte tillgänglig för en överlämning.",
  'errors.HANDOFF_NOT_APPROVED': "En överlämning skickas när en granskare som inte förberett den har godkänt paketet.",
  'errors.FOUR_EYES_HANDOFF': "Du förberedde den här överlämningen eller begärde granskningen, så en annan granskare beslutar.",
  'errors.HANDOFF_SENT_DAY': "Dagen den skickades ligger mellan dagen den förbereddes och i dag.",
  'errors.HANDOFF_IN_REVIEW': "Den här överlämningen väntar först på ett beslut vid granskningspunkten.",
  'errors.HANDOFF_STATE': "Det går inte att göra med överlämningen som den är nu.",
  'errors.HANDOFF_RETURN_DOCUMENTS': "Det som kommer tillbaka är aktuella dokument för den här klienten, inte paketet.",
  'errors.FOUR_EYES_RETURN': "Du registrerade det som kom tillbaka, så en annan medlem granskar det.",
  'errors.HANDOFF_EVIDENCE_MISMATCH': "En returnerad rapport är underlag för sin egen besvarade överlämning, med dess dokument.",
  'errors.HANDOFF_NOT_A_GAP': "Välj brister i den här frysta analysen.",
  'errors.HANDOFF_NEEDS_OCCURRENCE': "En överlämning till Valentin besvarar en aktivitet i årshjulet: välj vilken.",
  'errors.HANDOFF_FINDING_NOT_WRITTEN': "Skriv bristernas iakttagelser först: begäran bär varje brist med sin iakttagelse.",
  'errors.HANDOFF_KIND_NOT_PERFORMED': "Valentin utför inte den här typen av aktivitet.",
  'errors.HANDOFF_JURISDICTION': "Valentin tar klienter i Sverige, Danmark, Norge och Finland.",
  'errors.HANDOFF_TOO_LONG': "Begäran är för lång för Valentin.",
  'errors.HANDOFF_RECEIPT_NEEDED': "Valentins rapport kommer tillbaka med sitt kvitto: välj båda dokumenten.",
  'errors.HANDOFF_NO_RECEIPT': "Bara Valentin skickar tillbaka ett kvitto.",
  'errors.HANDOFF_RECEIPT_INVALID': "Det här dokumentet är inte ett kvitto från Valentin.",
  'errors.HANDOFF_RECEIPT_HASH': "Kvittots egen SHA-256 stämmer inte med innehållet: det ändrades efter att Valentin skrev det.",
  'errors.HANDOFF_REPORT_HASH': "Rapporten är inte den som kvittot anger: dess SHA-256 skiljer sig.",
  // IP12: AI proposals I (the evidence inbox, the proposals, the second opinion, FB15).
  'engine.nav.inbox': "Inkorg för underlag",
  'evinbox.title': "Inkorg för underlag",
  'evinbox.counts': "I inkorgen: {inbox} · arkiverade: {filed} · lagda åt sidan: {set_aside}",
  'evinbox.batch': "Omgång {code}: {n} filer, uppladdade av {who}, {when}",
  'evinbox.note': "Ett dokument väntar här tills en medlem arkiverar det, eller lägger det åt sidan med skälet. Ett AI-förslag kopplar ett dokument till arbetsmomenten; en person godkänner varje koppling efter att ha öppnat den citerade sidan.",
  'evinbox.add': "Ladda upp en omgång",
  'evinbox.files': "Filer",
  'evinbox.filesHint': "Välj många på en gång: PDF, Word, Excel, PowerPoint, e-post och transkriptioner.",
  'evinbox.noCustomerData': "Filerna innehåller inga uppgifter på kundnivå.",
  'evinbox.upload': "Ladda upp {n} filer i en ny omgång",
  'evinbox.uploaded': "{n} filer uppladdade i inkorgen.",
  'evinbox.documents': "Dokument",
  'evinbox.empty': "Inget dokument har laddats upp i den här analysens inkorg ännu.",
  'evinbox.text.extracted': "text utläst ({pages} sidor)",
  'evinbox.text.not_checkable': "ingen text (en skanning eller en bild): citat kan inte kontrolleras",
  'evinbox.text.failed': "texten kunde inte läsas",
  'evinbox.text.pending': "texten är inte läst ännu",
  'evinbox.state.inbox': "i inkorgen",
  'evinbox.state.filed': "arkiverad",
  'evinbox.state.set_aside': "lagd åt sidan",
  'evinbox.proposals': "förslag: {pending} väntar · {accepted} godkända · {refused} avvisade",
  'evinbox.review': "Granska",
  'evinbox.hide': "Dölj",
  'evinbox.notCheckable': "Dokumentet har ingen text att koppla: att läsa en skannad sida väntar på ett beslut av ägaren.",
  'evinbox.file': "Arkivera",
  'evinbox.setAside': "Lägg åt sidan",
  'proposals.allowanceOff': "AI är inte påslaget för den här klienten: inget skickas.",
  'proposals.boundary': "Innan körningen: dokumenten och arbetsmomenten skickas till {model}, klientdata endast via Anthropics API; högst {cost}. Inget skickas förrän du trycker på knappen.",
  'proposals.noPrice': "inget känt pris",
  'proposals.ran': "Klart: {pending} förslag väntar på en person; {refused} avvisades innan någon såg dem.",
  'proposals.refused': "{n} förslag avvisades innan du såg dem.",
  'proposals.refusedReason': "{reason}: {n}",
  'proposals.refusal.QUOTE_NOT_FOUND': "citatet finns inte i dokumentet",
  'proposals.refusal.QUOTE_MISSING': "inget citat",
  'proposals.refusal.QUOTE_TOO_LONG': "citatet är längre än 300 tecken",
  'proposals.refusal.NOT_IN_SCOPE': "arbetsmomentet ingår inte i analysens omfattning",
  'proposals.refusal.UNKNOWN_FACT': "ett konstaterande eller element som arbetsmomentet inte frågar efter",
  'proposals.refusal.UNKNOWN_DOCUMENT': "ett dokument som inte skickades",
  'proposals.refusal.UNKNOWN_ACTION': "en åtgärd som inte finns i paketet",
  'proposals.refusal.UNKNOWN_PROOF_ITEM': "ett bevis som åtgärden inte bad om",
  'proposals.what.link': "{practice}: gäller {on}",
  'proposals.what.fact': "{practice}: {fact} · {value}",
  'proposals.what.proof': "{action}: {item}",
  'proposals.partly': "(delvis funnet)",
  'proposals.value.yes': "ja",
  'proposals.value.no': "nej",
  'proposals.value.unknown': "framgår inte",
  'proposals.status.pending': "väntar på en person",
  'proposals.status.accepted': "godkänt",
  'proposals.status.rejected': "avvisat",
  'proposals.status.stale': "inte längre aktuellt",
  'proposals.whatWentIn': "Detta skickades till modellen ({call})",
  'proposals.quoteAt': "({document}, sidan {page})",
  'proposals.quoteFound': "citatet hittat",
  'proposals.why': "Modellen säger: {why}",
  'proposals.decided': "Beslutat av {who} {when}.",
  'proposals.nobody': "ingen",
  'proposals.because': "Skäl: {reason}",
  'proposals.became': "Det blev {record}.",
  'proposals.openPage': "Öppna sidan {page}",
  'proposals.accept': "Godkänn",
  'proposals.acceptAfterOpen': "Godkänn går att välja när den citerade sidan har öppnats.",
  'proposals.replace': "Godkänn och ersätt det registrerade konstaterandet",
  'proposals.replaceReason': "Varför det registrerade konstaterandet ersätts",
  'proposals.reject': "Avvisa",
  'proposals.none': "Inget förslag väntar.",
  'proposals.noneProof': "Ingen matchning är föreslagen för paketet ännu.",
  'proposals.map': "Föreslå koppling (AI)",
  'proposals.facts': "Föreslå konstateranden (AI)",
  'proposals.factsTitle': "AI-förslag för arbetsmomentet",
  'proposals.showFacts': "Visa AI-förslagen",
  'proposals.secondOpinion': "Be om en andra bedömning",
  'proposals.proof': "Matcha bevis (AI)",
  'proposals.proofCard': "Matchning av bevis",
  'proposals.proofTitle': "Åtgärdspaket {pack}",
  'proposals.showProof': "Matcha bevis i paket {pack} (AI)",
  'secondOpinion.title': "Andra bedömning",
  'secondOpinion.note': "En andra Claude-modell besvarar samma frågor utan att se de första svaren. Varje oenighet löses eller läggs åt sidan med ett skäl; en öppen oenighet stoppar ingenting och räknas före låsningen.",
  'secondOpinion.line': "{code}: {first} och {second} · öppna oenigheter: {open}",
  'secondOpinion.col.fact': "Konstaterande",
  'secondOpinion.col.first': "Första modellen",
  'secondOpinion.col.second': "Andra modellen",
  'secondOpinion.col.agreement': "Överensstämmelse",
  'secondOpinion.col.decision': "Beslut",
  'secondOpinion.answer.yes': "ja",
  'secondOpinion.answer.no': "nej",
  'secondOpinion.answer.unknown': "framgår inte",
  'secondOpinion.answer.none': "inget svar",
  'secondOpinion.agree': "överens",
  'secondOpinion.disagree': "oense",
  'secondOpinion.do.resolved': "Lös",
  'secondOpinion.do.set_aside': "Lägg åt sidan",
  'secondOpinion.resolution.resolved': "Löst av {who}: {reason}",
  'secondOpinion.resolution.set_aside': "Lagd åt sidan av {who}: {reason}",
  'freeze.blocker.ai_unconfirmed': "AI-föreslagna konstateranden ja eller delvis som väntar på en andra medlem",
  'freeze.secondOpinionsOpen': "Oenigheter i andra bedömningar som är öppna: {n}. De stoppar ingenting; läs dem innan du låser.",
  'heat.aiShare': "{n} av {of} konstateranden vilar på AI-förslag.",
  'discover.gather.inbox': "Inkorg för underlag: {inbox} väntar · {filed} arkiverade · {set_aside} lagda åt sidan",
  'discover.analyse.ai': "Konstateranden från AI-förslag: {n} av {of} (ännu inte bekräftade: {u}) · förslag som väntar: {p}",
  'errors.INBOX_CLOSED': "Dokument tas in i inkorgen medan analysen samlar in eller bedömer.",
  'errors.INBOX_STATE': "Dokumentet är redan arkiverat eller lagt åt sidan.",
  'errors.PROPOSAL_STATE': "Förslaget är redan beslutat.",
  'errors.PROPOSAL_NOT_OPENED': "Öppna den citerade sidan innan du godkänner.",
  'errors.FOUR_EYES_PROOF_MATCH': "Du äger åtgärden eller registrerade åtgärdandet, så en annan medlem godkänner matchningen.",
  'errors.SECOND_OPINION_STATE': "Oenigheten är redan löst eller lagd åt sidan, eller så saknas skälet.",
  'errors.FREEZE_AI_UNCONFIRMED': "Några konstateranden registrerade från AI-förslag väntar på att en andra medlem bekräftar dem ({n}).",
  'errors.PROPOSAL_FIGURE': "Svaret innehöll ett värde som kod och människor beslutar, så hela svaret avvisades innan någon såg det. Inget registrerades.",
  'errors.PROPOSAL_SCHEMA': "Svaret hade inte den form som efterfrågades, så hela svaret avvisades innan någon såg det. Inget registrerades.",
  'errors.SECOND_OPINION_ENGINE': "En andra bedömning begärs endast av Claude.",
  'errors.NO_INDEPENDENT_MODEL': "Ingen oberoende modell erbjuds för en andra bedömning av den här körningen.",
  'errors.SECOND_OPINION_INPUT_CHANGED': "Det som den första körningen läste har ändrats sedan dess, så en andra bedömning kan inte läsa detsamma.",
  'errors.AI_INPUT_TOO_LARGE': "Det som körningen skulle skicka är större än ett anrop tar emot. Välj färre dokument.",
  'errors.PROPOSAL_NOTHING_TO_SEND': "Det finns inget för körningen att läsa ännu.",
  'errors.PROPOSAL_ANALYSIS_STATE': "Förslag görs medan analysen samlar in eller bedömer.",
  'auditField.batch': "Omgång",
  'auditField.inboxState': "I inkorgen",
  'auditField.proposalKind': "Slag av förslag",
  'auditField.items': "Poster i svaret",
  'auditField.proposalsPending': "Förslag till en person",
  'auditField.proposalsRefused': "Förslag avvisade av kod",
  'auditField.proofItem': "Bevis",
  'auditField.matchAnswer': "Matchning",
  'auditField.page': "Sida",
  'auditField.openedBy': "Öppnad av",
  'auditField.openedAt': "Öppnad",
  'auditField.firstModel': "Första modellen",
  'auditField.secondModel': "Andra modellen",
  'auditField.firstAnswer': "Första svaret",
  'auditField.secondAnswer': "Andra svaret",
  'auditField.agreement': "Överensstämmelse",
  'auditField.resolution': "Lösning",
  'auditField.resolvedBy': "Löst av",
  'auditField.proposal': "AI-förslag",
  'auditField.action': "Åtgärd",
  'auditField.evidence': "Underlag",
  'proposals.runKind.evidence_map': "karta över underlag",
  'proposals.runKind.fact': "förslag till konstateranden",
  'proposals.runKind.fact_second': "andra bedömning",
  'proposals.runKind.proof_match': "matchning av bevis",
  'proposals.kind.evidence_map': "koppling till underlag",
  'proposals.kind.fact': "konstaterande",
  'proposals.kind.proof_match': "matchning av bevis",
  'proposals.kind.transcript_statement': "utsaga ur en transkription",
  'proposals.kind.interview_summary': "utsaga ur intervjuarens anteckningar",
  'proposals.kind.interview_question': "anpassad fråga",
  'proposals.kind.profile_figure': "verksamhetssiffra",
  'proposals.kind.role_holder': "innehavare av en roll",
  'proposals.kind.inventory_item': "system eller modell",
  'proposals.kind.inherited_item': "övertagen siffra",
  'proposals.kind.mandate': "tjänst eller roll i uppdraget",
  'proposals.runKind.transcript': "utsagor ur en transkription",
  'proposals.runKind.interview_summary': "utsagor ur intervjuarens anteckningar",
  'proposals.runKind.interview_questions': "anpassade frågor",
  'proposals.runKind.profile': "verksamhetssiffror",
  'proposals.runKind.roles': "innehavare av kontrollfunktionerna",
  'proposals.runKind.inventory': "system och modeller",
  'proposals.runKind.inherited': "det övertagna läget",
  'proposals.runKind.mandate': "uppdraget",
  'record.questionRound': "Omgång av anpassade frågor",
  'record.roundQuestion': "Fråga i en omgång",
  'auditField.guideRole': "Intervjuad roll",
  'auditField.chosenBy': "Lagd i omgången av",
  'auditField.chosenAt': "Lagd i omgången",
  'round.role.board_chair': "Styrelsens ordförande",
  'round.role.ceo': "Verkställande direktör",
  'round.role.aml_compliance_officer': "Centralt funktionsansvarig",
  'round.role.first_line_head': "Chef för en enhet i första linjen",
  'round.role.firm_supporter': "Medarbetaren på byrån som stöttar en roll",
  'round.status.open': "öppen",
  'round.status.closed': "stängd",
  'proposals.refusal.UNKNOWN_SPEAKER': "en talare som underlaget inte nämnde",
  'proposals.refusal.UNKNOWN_QUESTION': "en fråga som underlaget inte gav",
  'proposals.refusal.SUSPICION_SENSITIVE': "nära rapporter om misstanke, utan bekräftelsen",
  'proposals.refusal.UNKNOWN_ITEM': "en punkt, ett arbetsmoment eller ett konstaterande som omgången inte frågar om",
  'proposals.refusal.UNKNOWN_FIELD': "ett fält eller en siffra som Olivia inte registrerar",
  'proposals.refusal.BAD_VALUE': "ett värde som dess enhet inte tillåter",
  'proposals.refusal.BAD_PERIOD': "en dag som inte är giltig eller inte har kommit",
  'proposals.refusal.NOT_IN_QUOTE': "citatet säger det inte",
  'proposals.refusal.UNKNOWN_KIND': "ett slag som Olivia inte registrerar",
  'proposals.refusal.UNKNOWN_ROLE': "en roll som Olivia inte registrerar",
  'proposals.refusal.UNKNOWN_SERVICE': "ett tjänsteslag som Olivia inte erbjuder",
  'proposals2.show': "Visa AI-förslagen",
  'proposals2.choose': "Välj …",
  'proposals2.notConfirmed': "Föreslaget av AI, inte bekräftat",
  'proposals2.drafted': "Utkast med AI, godtaget av {name}",
  'proposals2.notTheirWords': "Ännu inte bekräftat av deltagaren: detta är inte deras ord.",
  'proposals2.what.statement': "{speaker} ({kind}): {text}",
  'proposals2.what.question': "{item} · {practice} · {fact}: {question}",
  'proposals2.what.figure': "{field}: {value} per {day}",
  'proposals2.what.holder': "{name}: {role}",
  'proposals2.what.system': "System ({kind}): {name}",
  'proposals2.what.model': "Modell ({kind}): {name}",
  'proposals2.what.count': "{count}: {value} per {day} ({service})",
  'proposals2.what.service': "Tjänst {kind}, {model}, från {from}",
  'proposals2.what.role': "Roll {kind} ({mode}) i {service}; uppgifter: {tasks}",
  'proposals2.tick.allegation': "Den innehåller inget påstående om oegentligheter mot en namngiven person.",
  'proposals2.tick.suspicion': "Den gäller ingen enskild bedömning av misstanke eller rapport.",
  'proposals2.tick.suspicionRun': "Frågorna nära rapporter om misstanke får skickas: intervjun rör ingen enskild bedömning av misstanke eller rapport.",
  'proposals2.startOn': "Tjänstens start",
  'proposals2.firmPerson': "Vem på byrån",
  'proposals2.fromOn': "Från",
  'proposals2.clientHolder': "Vem som har rollen hos klienten",
  'proposals2.putInRound': "Lägg i omgången",
  'proposals2.document': "Dokument",
  'proposals2.letter': "Uppdragsavtal",
  'proposals2.service': "Övertagen tjänst",
  'proposals2.interview.title': "AI-förslag för den här intervjun",
  'proposals2.interview.youAccept': "Du är intervjuaren: du godtar varje utkast till utsaga och lägger frågorna i en omgång.",
  'proposals2.interview.interviewerAccepts': "Intervjuaren godtar varje utkast till utsaga och lägger frågorna i en omgång.",
  'proposals2.runTranscript': "Föreslå utsagor ur transkriptionen (AI)",
  'proposals2.runNotes': "Gör utkast till utsagor ur anteckningarna (AI)",
  'proposals2.runQuestions': "Formulera frågorna (AI)",
  'proposals2.noStatements': "Ingen utsaga är föreslagen för den här intervjun än.",
  'proposals2.noQuestions': "Ingen fråga är formulerad för den här omgången än.",
  'proposals2.rounds.title': "Omgångar av anpassade frågor",
  'proposals2.rounds.none': "Ingen omgång är öppnad för den här intervjun.",
  'proposals2.rounds.role': "Roll att intervjua",
  'proposals2.rounds.open': "Öppna en omgång",
  'proposals2.rounds.close': "Stäng omgången",
  'proposals2.rounds.item': "{ref}: {practice}, konstaterande {fact}",
  'proposals2.rounds.contradiction': "{ref}: {practice}, det som sades stämmer inte med dokumenten: ”{said}”",
  'proposals2.rounds.leftOut': "{n} punkter nära rapporter om misstanke lämnades utanför.",
  'proposals2.picture.title': "Föreslaget av AI ur klientens dokument",
  'proposals2.picture.kind': "Vad som ska föreslås",
  'proposals2.picture.pending': "{n} förslag väntar på en person; inget av dem räknas förrän det är godtaget och, för en siffra, bekräftat av en andra medarbetare.",
  'proposals2.runPicture': "Föreslå ur det här dokumentet (AI)",
  'proposals2.mandate.title': "Uppdraget föreslaget av AI ur uppdragsavtalet",
  'proposals2.mandate.leadActs': "Inget skapas förrän en ansvarig godtar det; reglerna för varje roll gäller då.",
  'proposals2.runMandate': "Föreslå tjänster och roller (AI)",
  'errors.TRANSCRIPT_MISSING': "Den här intervjun har ingen transkription bifogad.",
  'errors.NOTES_MISSING': "Den här intervjun har inga anteckningar från intervjuaren att läsa.",
  'errors.ROUND_OPEN': "Den här intervjun har en öppen omgång av frågor; stäng den först.",
  'errors.ROUND_CLOSED': "Omgången är stängd: den tar inte emot fler frågor.",
  'errors.ROUND_NOTHING_TO_ASK': "Det finns inget kvar att fråga den här rollen: inget konstaterande saknas och ingen motsägelse är öppen för dess arbetsmoment.",
  'errors.PROPOSAL_NOT_INTERVIEWER': "Intervjuaren godtar det som utkastats för intervjun.",
  'errors.FIGURE_RECORDED': "En siffra för det här fältet och den här dagen är redan registrerad; ändra den i verksamhetsprofilen om den ska ändras.",
  'errors.ROLE_HOLDER_RECORDED': "Någon hos klienten är redan registrerad i den här rollen; ändra det på klientsidan om det har ändrats.",
  'errors.INHERITED_AS_OF': "Det övertagna läget är registrerat per en annan dag; ändra det för hand.",
  'errors.MANDATE_NO_SERVICE': "Klienten har ingen pågående tjänst av det här slaget för rollen: godta eller lägg till tjänsten först.",
  'proposals.answer.found': "funnet",
  'proposals.answer.partly_found': "delvis funnet",
  'proposals.status.refused': "avvisat av kod",
  'proposals.quoteCheck.found': "hittat",
  'proposals.quoteCheck.not_found': "inte hittat",
  'proposals.quoteCheck.not_checkable': "kan inte kontrolleras",
  'proposals.quoteCheck.too_long': "för långt",
  'record.intakeBatch': "Omgång till inkorgen",
  'record.proposalRun': "Körning av AI-förslag",
  'record.proposal': "AI-förslag",
  'record.proposalQuote': "Citat i ett AI-förslag",
  'record.evidenceOpening': "Öppning av en citerad sida",
  'record.secondOpinion': "Andra bedömning",
  'record.secondOpinionItem': "Överensstämmelse i en andra bedömning",
  'auditAction.inbox.batch': "Öppnade en omgång till inkorgen",
  'auditAction.inbox.file': "Arkiverade ett dokument i inkorgen",
  'auditAction.inbox.set_aside': "Lade ett dokument i inkorgen åt sidan",
  'auditAction.proposal.evidence_map': "Registrerade en AI-koppling av ett dokument",
  'auditAction.proposal.facts': "Registrerade AI-förslag till konstateranden",
  'auditAction.proposal.brief': "Skrev underlaget för en körning av AI-förslag",
  'auditAction.proposal.transcript': "Registrerade AI-förslag till utsagor ur en transkription",
  'auditAction.proposal.interview_summary': "Registrerade AI-förslag till utsagor ur intervjuarens anteckningar",
  'auditAction.proposal.interview_questions': "Registrerade intervjufrågor formulerade av AI",
  'auditAction.proposal.profile': "Registrerade verksamhetssiffror föreslagna av AI",
  'auditAction.proposal.roles': "Registrerade innehavare av kontrollfunktioner föreslagna av AI",
  'auditAction.proposal.inventory': "Registrerade system och modeller föreslagna av AI",
  'auditAction.proposal.inherited': "Registrerade siffror i det övertagna läget föreslagna av AI",
  'auditAction.proposal.mandate': "Registrerade tjänster och roller föreslagna av AI",
  'auditAction.round.open': "Öppnade en omgång av anpassade frågor",
  'auditAction.round.close': "Stängde en omgång av anpassade frågor",
  'auditAction.proposal.second_opinion': "Registrerade en andra bedömning",
  'auditAction.proposal.proof_match_instruction': "Sparade vad en matchning av bevis frågar efter",
  'auditAction.proposal.proof_match': "Registrerade en AI-matchning av bevis",
  'auditAction.proposal.open': "Öppnade den citerade sidan i ett AI-förslag",
  'auditAction.proposal.accept': "Godkände ett AI-förslag",
  'auditAction.proposal.reject': "Avvisade ett AI-förslag",
  'auditAction.second_opinion.decide': "Löste eller lade åt sidan en oenighet i en andra bedömning",  // IP13b: AI proposals III (wording, prose, the second take, modules for a need).
  'proposals3.show': "Visa AI-utkasten",
  'proposals3.text': "Text",
  'proposals3.none': "Inget utkast ännu.",
  'proposals3.accept': "Godta som det är skrivet",
  'proposals3.notes': "Anteckningar för läsaren (de stoppar inget; granskningspunkten kontrollerar texten igen):",
  'proposals3.numbers': "Tal som utkastet anger men som underlaget inte anger: {list}",
  'proposals3.askSecondTake': "Begär en andra läsning (AI)",
  'proposals3.secondTakeRan': "Klart: {flags} flaggor skrivna; {refused} avvisades eftersom deras ord inte finns i utkastet.",
  'proposals3.secondTake': "Andra läsning",
  'proposals3.secondTakeLine': "{code} av {model} på ett utkast av {draft}: {flags} flaggor, {refused} avvisade",
  'proposals3.neverEdits': "En andra läsning flaggar bara: den ändrar aldrig utkastet.",
  'proposals3.noFlags': "Ingen flagga.",
  'proposals3.acceptFlag': "Godta flaggan",
  'proposals3.rejectFlag': "Avvisa flaggan",
  'proposals3.flagDecided': "Flaggan är avgjord.",
  'proposals3.flagDecidedBy': "Avgjord av {who} {when}: {reason}",
  'proposals3.count': "Andra läsning: {flags} flaggor, {open} öppna",
  'proposals3.count.noTake': "Innehåller {drafts} AI-utkast; ingen andra läsning har begärts.",
  'proposals3.count.open': "Avgör varje flagga i den andra läsningen innan granskningspunkten godkänns.",
  'proposals3.finding.title': "AI-utkast till konsekvens och rekommendation",
  'proposals3.finding.never': "Modellen skriver utkast till konsekvens och rekommendation utifrån iakttagelsen som koden skrev den, aldrig orsaken. Ett utkast förs in i en iakttagelse som ännu saknar konsekvens och rekommendation, precis som det är skrivet.",
  'proposals3.finding.run': "Skriv utkast till konsekvens och rekommendation (AI)",
  'proposals3.board.title': "AI-utkast till inledning och iakttagelser",
  'proposals3.board.checked': "Modellen skriver utifrån registret i den frysta analysen. Ett godtaget utkast återges och kontrolleras som en persons ord, och dess flaggor hindrar godkännandet.",
  'proposals3.board.run': "Skriv utkast till inledning och iakttagelser (AI)",
  'proposals3.request.title': "Följebrevet och radernas formulering",
  'proposals3.request.intro': "AI-utkast till följebrevet och till varje rad",
  'proposals3.request.letter': "Följebrev",
  'proposals3.request.runLetter': "Skriv utkast till följebrevet (AI)",
  'proposals3.request.runWording': "Formulera raderna (AI)",
  'proposals3.request.sent': "Begäran är skickad: dess ord ändras inte längre.",
  'proposals3.module.title': "Andra läsning",
  'proposals3.module.intro': "Claudes andra modell läser utkastet och underlaget och flaggar bara. Varje flagga avgörs innan leveransens granskningspunkt godkänns.",
  'proposals3.suggest.title': "Beskriv vad du behöver",
  'proposals3.suggest.intro': "Modellen ordnar de bekräftade moduler som passar ett behov du skriver. Den ger bara en ordning; inget sparas utom anropet.",
  'proposals3.suggest.need': "Vad behöver du?",
  'proposals3.suggest.prepare': "Förbered förslaget",
  'proposals3.suggest.run': "Föreslå moduler (AI)",
  'proposals3.suggest.ran': "{n} moduler föreslagna; {dropped} namn utelämnade eftersom de inte är bekräftade moduler.",
  'proposals3.suggest.orderOnly': "Ordningen är modellens förslag; den säger inget om prioritet.",
  'secondTake.kind.unsupported_claim': "Påstående utan stöd",
  'secondTake.kind.missing_quote': "Citat saknas",
  'secondTake.kind.verdict_word': "Omdömesord utan slutsats",
  'secondTake.kind.regulator_objection': "En tillsynsmyndighet skulle invända",
  'secondTake.status.open': "öppen",
  'secondTake.status.accepted': "godtagen",
  'secondTake.status.rejected': "avvisad",
  'proposals.kind.request_wording': "formulering av en rad i en begäran",
  'proposals.kind.letter': "följebrev",
  'proposals.kind.finding_text': "konsekvens och rekommendation för en iakttagelse",
  'proposals.kind.report_prose': "inledning och iakttagelser i ett styrelseunderlag",
  'proposals.runKind.request_wording': "radernas formulering",
  'proposals.runKind.letter': "följebrevet",
  'proposals.runKind.finding_text': "en iakttagelses konsekvens och rekommendation",
  'proposals.runKind.report_prose': "ett styrelseunderlags text",
  'record.secondTake': "Andra läsning",
  'record.secondTakeFlag': "Flagga i en andra läsning",
  'auditField.code': "kod",
  'auditField.createdAt': "skapad",
  'auditField.coverLetterBy': "följebrev av",
  'auditField.coverLetterAt': "följebrev den",
  'auditField.wordingBy': "formulering av",
  'auditField.wordingAt': "formulering den",
  'auditField.draftModel': "utkastets modell",
  'auditField.secondTakeFlags': "flaggor skrivna",
  'auditField.flagsRefused': "flaggor som koden avvisade",
  'auditField.secondTakeBy': "andra läsning begärd av",
  'auditField.flagKind': "typ av flagga",
  'auditAction.proposal.finding_text': "Registrerade ett AI-utkast till en iakttagelses konsekvens och rekommendation",
  'auditAction.proposal.report_prose': "Registrerade ett AI-utkast till ett styrelseunderlags text",
  'auditAction.proposal.letter': "Registrerade ett AI-utkast till ett följebrev",
  'auditAction.proposal.request_wording': "Registrerade AI-utkast till en begärans rader",
  'auditAction.second_take.brief': "Skrev underlaget till en andra läsning",
  'auditAction.second_take.run': "Registrerade en andra läsning och dess flaggor",
  'auditAction.second_take.decide': "Avgjorde en flagga i en andra läsning",
  'auditAction.request.cover_letter': "Skrev eller tog bort en begärans följebrev",
  'auditAction.request.line_wording': "Skrev eller tog bort en rads formulering",
  'auditAction.nextstep.rank_brief': "Skrev underlaget till ett modulförslag",
  'errors.AI_ROOT_CAUSE': "En orsak är en persons ord: text som AI har skrivit utkast till är aldrig en grundorsak.",
  'errors.REQUEST_NOT_DRAFT': "Bara en begäran som inte är skickad får ett följebrev eller en rads formulering.",
  'errors.DRAFT_CHANGED': "Utkastet ändrades medan den andra läsningen gjordes; inget registrerades. Begär igen.",
  'errors.SECOND_TAKE_FLAG_DECIDED': "Flaggan är redan avgjord.",
  'errors.SECOND_TAKE_FOUR_EYES': "Du körde eller godtog utkastet, så en annan ansvarig eller granskare avgör dess flaggor.",
  'errors.SECOND_TAKE_FLAGS_OPEN': "En flagga i en andra läsning av ett utkast som leveransen innehåller är öppen: avgör den först.",
  'errors.SECOND_TAKE_NOT_A_DRAFT': "En andra läsning läser ett AI-utkast som står kvar: en iakttagelses text, ett styrelseunderlags text eller ett modulutkast.",
  'errors.FINDING_TEXT_WRITTEN': "Iakttagelsen har redan en konsekvens eller en rekommendation; ta bort dem först om utkastet ska ersätta dem.",
  'errors.BOARD_PROSE_WRITTEN': "Styrelseunderlaget har redan en inledning eller iakttagelser; ta bort dem först om utkastet ska ersätta dem.",
  'errors.COVER_LETTER_WRITTEN': "Begäran har redan ett följebrev; ta bort det först om utkastet ska ersätta det.",
  'errors.LINE_WORDING_WRITTEN': "Raden har redan en formulering; ta bort den först om utkastet ska ersätta den.",
  // AI-C (2026-10-11): a client's data through OpenRouter, the client's permission, the routes, the statement v2.
  'aic.title': "AI på klientens uppgifter via OpenRouter",
  'aic.intro': "Utöver Claude får klientens uppgifter gå till en modell via OpenRouter bara där klientens senaste undertecknade tillstånd nämner modellen, en klientansvarig har slagit på vägen och en andra person, en klientansvarig eller en granskare, har bekräftat den. AI-medgivandet ovan behövs fortfarande: en väg slår aldrig på AI. Uppgifter om klientens kunder går aldrig den här vägen.",
  'aic.switchOff': "Den här installationen skickar inte en klients uppgifter till OpenRouter (OLIVIA_CLIENT_DATA_OPENROUTER är avstängd). Vägarna nedan förblir avstängda.",
  'aic.routes': "Vägar, en per modell",
  'aic.state.off': "Av",
  'aic.state.waiting': "Väntar på att en andra person bekräftar",
  'aic.state.in_force': "Gäller",
  'aic.state.not_named': "Gäller inte: klientens senaste tillstånd nämner inte modellen",
  'aic.state.ended': "Avstängd",
  'aic.provider': "Leverantör: {provider}, fast vald; inga uppgifter sparas; ingen reserv hos en annan leverantör.",
  'aic.proposedBy': "{code}: påslagen av {name} den {date}.",
  'aic.confirmedBy': "Bekräftad av {name} den {date}.",
  'aic.endedBy': "Avstängd av {name} den {date}.",
  'aic.propose': "Slå på vägen",
  'aic.confirm': "Bekräfta vägen",
  'aic.end': "Stäng av vägen",
  'aic.endReason': "Varför den stängs av (minst 20 tecken)",
  'aic.block.not_named': "Klientens senaste tillstånd nämner inte modellen.",
  'aic.block.own_proposal': "Du slog på detta, så en andra person bekräftar det.",
  'aic.block.not_lead_or_reviewer': "En klientansvarig eller en granskare för klienten bekräftar.",
  'aic.block.switch_off': "Installationen skickar inte en klients uppgifter till OpenRouter, så detta kan inte bekräftas nu.",
  'aic.receipt.title': "Klientens tillstånd",
  'aic.receipt.latest': "{code}, undertecknat den {date} av {name} ({role}); dokumentet {document}.",
  'aic.receipt.models': "Modeller som klienten tillåter: {models}.",
  'aic.receipt.noModels': "Det tillåter ingen modell via OpenRouter.",
  'aic.receipt.none': "Inget tillstånd registrerat ännu.",
  'aic.receipt.record': "Registrera tillståndet",
  'aic.receipt.signedOn': "Undertecknat den",
  'aic.receipt.signer': "Undertecknat av, hos klienten",
  'aic.receipt.signerRole': "Undertecknat i egenskap av",
  'aic.receipt.document': "Det undertecknade dokumentet",
  'aic.receipt.modelsLegend': "Modeller som klienten tillåter",
  'aic.receipt.modelBox': "Vi tillåter {model} via OpenRouter, EU ({provider}), inga uppgifter sparas",
  'aic.receipt.noneNote': "Ett tillstånd som inte kryssar i någon modell drar tillbaka varje väg: det senaste tillståndet avgör.",
  'aic.processors': "OpenRouter, Inc. (USA) är mellanhand och modellens leverantör bakom den underbiträde, båda enligt personuppgiftsbiträdesavtalet (dataskyddsförordningen artikel 28); klienten för upp arrangemanget i sitt informationsregister (DORA artikel 28.3).",
  'aiRun.model.openrouter': "{model} via OpenRouter ({provider}, inga uppgifter sparas)",
  'modules.boundary.routes': "Klientens tillstånd och dess vägar som gäller låter också en körning gå till {models} via OpenRouter, var och en fast vald hos sin leverantör och utan att några uppgifter sparas.",
  'explain.provider.h': "Leverantör",
  'explain.provider': "Leverantör: {provider}; inga uppgifter sparas; ingen datainsamling; ingen reserv hos en annan leverantör.",
  'explain.provider.other': "Leverantör: {provider}.",
  'errors.CLIENT_ROUTE_PERMISSION': "Klientens senaste tillstånd nämner inte modellen, så vägen kan inte slås på eller bekräftas.",
  'errors.CLIENT_ROUTE_STATE': "Vägen är redan påslagen, bekräftad eller avstängd.",
  'errors.CLIENT_ROUTE_FOUR_EYES': "Du slog på vägen, så en andra person bekräftar den.",
  'errors.CLIENT_ROUTES_OFF': "Den här installationen skickar inte en klients uppgifter till OpenRouter (OLIVIA_CLIENT_DATA_OPENROUTER är avstängd).",
  'errors.CLIENT_ROUTE_NOT_IN_FORCE': "Modellen får inte läsa klientens uppgifter: ingen väg för den gäller här. Inget skickades.",
  'errors.PERMISSION_SIGNED_LATER': "Dagen då tillståndet undertecknades ligger efter i dag.",
  'errors.REASONING_ONLY': "Modellen använde hela svaret till resonemang och gav inget svar. Inget sparades; försök igen eller välj en annan modell.",
  'auditAction.ai_permission.record': "Registrerade klientens tillstånd till AI via OpenRouter",
  'auditAction.ai_route.propose': "Slog på en väg till en AI-modell via OpenRouter",
  'auditAction.ai_route.confirm': "Bekräftade en väg till en AI-modell via OpenRouter",
  'auditAction.ai_route.end': "Stängde av en väg till en AI-modell via OpenRouter",
  'record.aiPermission': "AI-tillstånd",
  'record.aiRoute': "AI-väg",
  'auditField.signedOn': "undertecknat den",
  'auditField.signer': "undertecknat av, hos klienten",
  'auditField.signerRole': "undertecknat i egenskap av",
  'auditField.permittedModels': "modeller som klienten tillåter",
  'auditField.permission': "klientens tillstånd",
  'auditField.provider': "leverantör",
  'auditField.switchedOnBy': "påslagen av",
  'auditField.switchedOffBy': "avstängd av",
  'auditField.switchedOffAt': "avstängd den",
  'privacy.ai.v2': "En klients uppgifter, även dess dokument, går till Anthropic (Claude, via dess API) bara där klienten har tillåtit det. De kan också gå till en av tre modeller via OpenRouter (Mistral Large 4, GLM 5.3 eller GLM 5.3 Flash), men bara där den här installationen tillåter det, klientens undertecknade tillstånd nämner modellen och två personer på byrån har slagit på och bekräftat vägen. Uppgifter om klientens kunder går bara till Claude, och bara där klientens dokumenterade underlag dessutom tillåter det. Inget som avslöjar en misstanke går till någon modell.",
  'privacy.processors.h': "Biträden bakom AI:n",
  'privacy.processors': "För en modell via OpenRouter är OpenRouter, Inc. i USA mellanhand; bakom den körs modellen av Mistral AI:s EU-tjänst (Mistral Large 4) eller av Inceptron (GLM 5.3 och GLM 5.3 Flash), var och en fast vald i koden, där inga uppgifter sparas, ingen datainsamling sker och ingen reserv hos en annan leverantör används. De är underbiträden, godkända av klienten och bundna av samma skyldigheter enligt ett personuppgiftsbiträdesavtal (dataskyddsförordningen artikel 28.2 och 28.4); överföringen till USA vilar på avtalets skyddsåtgärder (dataskyddsförordningen kapitel V).",
  'privacy.outsourcing.h': "Utkontraktering och tredjepartsleverantörer av IKT-tjänster",
  'privacy.outsourcing': "För en klient i finanssektorn ingår användningen av dessa tjänster i dess utkontraktering och avtal med tredjepartsleverantörer av IKT-tjänster (EBA:s riktlinjer om utkontraktering, EBA/GL/2019/02, och Finansinspektionens regler); klienten för upp den i informationsregistret över avtal med tredjepartsleverantörer av IKT-tjänster (DORA, förordning (EU) 2022/2554, artikel 28.3). Vi ger klienten det som behövs för registret.",
  // ── H3b-6: Fråga Olivia, guiden som är inbyggd i Olivia ──────────────────────────────────────────────────────
  'help.bar': 'Fråga Olivia…',
  'help.bar.input': 'Din fråga till Olivia',
  'help.title': 'Fråga Olivia',
  'help.subtitle': 'Guiden som är inbyggd i Olivia',
  'help.tabs': 'Fråga Olivia: avsnitt',
  'help.tab.ask': 'Fråga',
  'help.tab.how': 'Så fungerar det',
  'help.tab.where': 'Var hittar jag…',
  'help.size.toCompact': 'Mindre panel',
  'help.size.toLarge': 'Större panel',
  'help.intro': 'Jag är guiden som är inbyggd i Olivia. Jag svarar utifrån Olivias guide och säger vilka delar jag använde. Jag gör ingenting åt dig: jag ändrar, godkänner och bekräftar ingenting, och jag läser inga klienters uppgifter.',
  'help.question': 'Din fråga',
  'help.questionHint': 'Till exempel: ”Varför kan jag inte godkänna punkten jag förberett?” Skriv inte personuppgifter eller uppgifter om en klient.',
  'help.send': 'Fråga',
  'help.asking': 'Letar i guiden…',
  'help.notice.ai': 'Svaren skrivs av {model} utifrån Olivias guide. Din fråga skickas inte till modellen och sparas inte: jag matchar den mot guiden här, och bara de delar av guiden som passar skickas.',
  'help.notice.openrouter': 'OpenRouter är ett företag i USA; det skickar texten bara vidare till Inceptron, i EU, utan att data sparas.',
  'help.notice.anthropic': 'Det går via Anthropics API, samma väg som en klients uppgifter.',
  'help.notice.none': 'AI-svar är inte inställda på den här servern, så jag svarar med guidens egna ord, hittade genom matchning av ord. Din fråga sparas inte.',
  'help.answer.ai': 'Svaret är skrivet av {model}',
  'help.answer.keywords': 'Guidens egna ord',
  'help.from': 'Från:',
  'help.seeAlso': 'Se också:',
  'help.openScreen': 'Öppna {name}',
  'help.reason.not_configured': 'AI-svar är inte inställda på den här servern, så detta är vad guiden säger, hittat genom matchning av ord.',
  'help.reason.rate_limited': 'Du har nått timmens gräns för AI-svar, så detta är vad guiden säger, hittat genom matchning av ord.',
  'help.reason.unavailable': 'AI-svaret kunde inte tas fram just nu, så detta är vad guiden säger, hittat genom matchning av ord.',
  'help.reason.spend_cap': 'Kostnadstaket för AI på den här servern är nått, så detta är vad guiden säger, hittat genom matchning av ord.',
  'help.reason.uncited': 'Jag lade AI-svaret åt sidan eftersom det inte angav vilka delar av guiden det vilar på, så detta är vad guiden säger.',
  'help.reason.figure': 'Jag lade AI-svaret åt sidan eftersom det angav en siffra, något som bara en vy får ange, så detta är vad guiden säger.',
  'help.reason.overstated': 'Jag lade AI-svaret åt sidan eftersom det påstod mer än guiden stöder, så detta är vad guiden säger.',
  'help.none': 'Jag kunde inte matcha din fråga mot guiden. Pröva andra ord, eller öppna Så fungerar det.',
  'help.refused.personal_data': 'Din fråga ser ut att innehålla personuppgifter, till exempel ett personnummer, ett kontonummer, en e-postadress eller ett telefonnummer. Jag har inte skickat eller sparat den. Ställ frågan igen utan dem.',
  'help.refused.credentials': 'Din fråga ser ut att innehålla ett lösenord, en nyckel eller en token. Jag har inte skickat eller sparat den. Ställ frågan igen utan den, och byt hemligheten om den är äkta.',
  'help.cite.step': 'steg {n}, {title}',
  'help.cite.faq': 'fråga: {title}',
  'help.cite.glossary': 'ordlista: {title}',
  'help.cite.screen': 'vy: {title}',
  'help.cite.ai': 'vad AI gör och aldrig gör',
  'help.cite.brief': 'Olivias beskrivning',
  'help.how.current': 'Du är här',
  'help.how.step': 'Steg {n}: {title}',
  'help.how.youDo': 'Du gör',
  'help.how.itDoes': 'Olivia gör',
  'help.how.youDecide': 'Du beslutar',
  'help.how.aiDoes': 'Vad AI gör här',
  'help.how.aiNever': 'Vad AI aldrig gör här',
  'help.how.pick': 'Diagram',
  'help.how.diagramsIntro': 'Så fungerar Olivia, ritat. Välj en ruta för att se vad den är, var den finns i Olivia och vilket regelkort som gäller.',
  'help.how.asText': 'Olivia med guidens ord',
  'help.how.diagramsUnavailable': 'Diagrammen kan inte läsas på den här servern just nu, så här är guiden som text.',
  'help.draft': 'Den här guiden är ett utkast, version {version}, för granskning.',
  'help.diagram.start': 'Gå igenom stegen',
  'help.diagram.prev': 'Föregående steg',
  'help.diagram.next': 'Nästa steg',
  'help.diagram.position': 'Steg {n} av {total}: {label}',
  'help.diagram.count': '{n} steg, i ordning',
  'help.diagram.count.one': 'Ett steg',
  'help.diagram.choose': 'Välj en ruta i ritningen för att se vad den är, var den finns i Olivia och vilket regelkort som gäller.',
  'help.diagram.where': 'Var den finns i Olivia',
  'help.diagram.rules': 'Regelkort som gäller',
  'help.diagram.noRule': 'Inget regelkort gäller för den här rutan: personer beslutar här, eller så anger vyn vad som gäller.',
  'help.diagram.noWhere': 'Den här rutan är inte en enskild vy: den gäller överallt där den hör hemma.',
  'help.diagram.key': 'Förklaring till formerna',
  'help.diagram.list': 'Diagrammet som en lista',
  'help.diagram.badge': 'steg {n}',
  'help.diagram.kind.step': 'Ett steg',
  'help.diagram.kind.decision': 'En person beslutar',
  'help.diagram.kind.person': 'En person eller en roll',
  'help.diagram.kind.ai': 'AI föreslår',
  'help.diagram.kind.rule': 'Kod eller en regel avgör',
  'help.diagram.kind.data': 'Uppgifter',
  'help.diagram.kind.external': 'Utanför Olivia',
  'help.diagram.kind.output': 'Det som lämnar Olivia',
  'help.needs.client': 'Öppna en klient först.',
  'help.needs.assessment': 'Öppna någon av klientens analyser först.',
  'help.needs.practice': 'Öppna ett arbetsmoment i en analys först.',
  'help.needs.record': 'Öppna posten från dess lista först.',
  'help.screen.newClient': 'Ny klient',
  'help.screen.onboarding': 'Uppsättning av klienten',
  'help.screen.assessment': 'Analysen',
  'help.screen.practice': 'Ett arbetsmoment i analysen',
  'help.screen.nextSteps': 'Nästa steg för en brist',
  'help.screen.interview': 'En intervju',
  'help.screen.request': 'En begäran till klienten',
  'help.screen.action': 'En åtgärd',
  'help.screen.handoff': 'En överlämning',
  'help.where.intro': 'Varje vy du kan öppna, som menyn visar dem, med frågan var och en svarar på.',
  'help.where.noClient': 'Du är inte i en klient nu: en klients vyer öppnas när du väljer en klient.',
  'help.where.portfolio': 'Över dina klienter',
  'help.where.clientWork': 'En klient, i Arbete',
  'help.where.clientExpert': 'En klient, i Expertläge',
  'help.fault.missing': 'en fil i guiden saknas',
  'help.fault.sums': 'en fil i guiden stämmer inte med sin låsta kontrollsumma',
  'help.fault.not_json': 'en fil i guiden kan inte läsas',
  'help.fault.format': 'en fil i guiden följer inte sitt format',
  'help.fault.topics': 'Olivias egna avsnitt passar inte guiden',
  'help.fault.rule_card': 'ett diagram nämner ett regelkort som inte finns',
  'errors.HELP_TOO_MANY': 'Du har ställt många frågor den här timmen. Försök igen senare.',
  'errors.HELP_GUIDE_UNAVAILABLE': 'Guiden kan inte läsas på den här servern just nu: {reason}.',
  'errors.HELP_DIAGRAMS_UNAVAILABLE': 'Diagrammen kan inte läsas på den här servern just nu: {reason}.',
  // IP15f: questionnaires with self-ratings, and the perception gap.
  'engine.nav.questionnaires': 'Frågeformulär',
  'questionnaires.title': 'Frågeformulär och självskattningar',
  'questionnaires.intro': 'Ett frågeformulär frågar klientens personal hur väl varje arbetsmoment i omfattningen är på plats, enligt de fem nivåerna. Det som kommer tillbaka registreras endast som självskattningar: det visas bredvid de belagda nivåerna och ändrar aldrig en nivå, ett faktum eller ett avstånd.',
  'questionnaires.maps': 'Kartor i analysen',
  'questionnaires.noMaps': 'Ingen karta ingår ännu i analysens omfattning.',
  'questionnaires.inScope': '{n} påståenden för arbetsmomenten i omfattningen',
  'questionnaires.adopted': 'Frågeformuläret är antaget av regelbevakningen',
  'questionnaires.notAdopted': 'Frågeformuläret är ännu inte antaget av regelbevakningen: filen säger att den är ett utkast',
  'questionnaires.noneForMap': 'Inget frågeformulär för den här versionen av kartan',
  'questionnaires.issueTitle': 'Skapa ett frågeformulär',
  'questionnaires.issueIntro': 'Ett påstående per arbetsmoment i omfattningen, på det språk du väljer. Filen är en Excel-arbetsbok som klienten fyller i och skickar tillbaka.',
  'questionnaires.language': 'Filens språk',
  'questionnaires.issue': 'Skapa frågeformuläret',
  'questionnaires.issued': 'Frågeformulär {code} är skapat med {n} påståenden. Ladda ned det nedan.',
  'questionnaires.issueClosed': 'Ett frågeformulär skapas medan analysen är i insamling eller bedömning, av en ansvarig, en analytiker eller en granskare.',
  'questionnaires.noIssues': 'Inget frågeformulär är ännu skapat för analysen.',
  'questionnaires.issuesLabel': 'Skapade frågeformulär',
  'questionnaires.col.issue': 'Frågeformulär',
  'questionnaires.col.language': 'Språk',
  'questionnaires.col.statements': 'Påståenden',
  'questionnaires.col.issuedBy': 'Skapat av',
  'questionnaires.col.file': 'Fil',
  'questionnaires.download': 'Ladda ned arbetsboken',
  'questionnaires.draftMarked': 'Märkt som utkast i filen',
  'questionnaires.returnsTitle': 'Besvarade filer',
  'questionnaires.uploadIntro': 'Ladda upp arbetsboken som den kom tillbaka. Den läses strikt: ett ändrat påstående, en annan version, en formel eller ett svar utanför skalan gör att hela filen avvisas, och inget av den sparas. Själva filen lagras inte; svaren gör det.',
  'questionnaires.file': 'Besvarad arbetsbok (.xlsx, högst 2 MB)',
  'questionnaires.statementNone': 'Filen innehåller inga uppgifter på kundnivå.',
  'questionnaires.upload': 'Ladda upp svaren',
  'questionnaires.recorded': 'Registrerat som {code}: {n} svar sparade som självskattningar, {m} påståenden lämnade tomma.',
  'questionnaires.noReturns': 'Ingen besvarad fil är uppladdad ännu.',
  'questionnaires.returnsLabel': 'Besvarade filer',
  'questionnaires.col.return': 'Besvarad fil',
  'questionnaires.col.answeredBy': 'Besvarat av, enligt filen',
  'questionnaires.col.answered': 'Svar',
  'questionnaires.col.uploadedBy': 'Uppladdat av',
  'questionnaires.col.state': 'Läge',
  'questionnaires.answeredOn': 'besvarat {day}',
  'questionnaires.noDay': 'inget datum angivet',
  'questionnaires.answeredCount': '{n} självskattningar',
  'questionnaires.retired': 'Återkallat: {reason}',
  'questionnaires.retire': 'Återkalla filens svar',
  'questionnaires.retireReason': 'Varför återkallas de?',
  'questionnaires.current': 'Gällande',
  'perception.title': 'Självskattningar bredvid underlaget',
  'perception.intro': 'Varje självskattning bredvid den nivå som underlaget stöder. Där det ännu inte finns någon belagd nivå sägs det: en självskattning jämförs aldrig med ett tomrum, räknas aldrig ihop till ett medelvärde och ändrar aldrig en nivå.',
  'perception.provisional': 'Nivåerna beräknas på det som är registrerat nu och är preliminära tills analysen är låst.',
  'perception.frozen': 'Nivåerna är den låsta analysens registrering.',
  'perception.empty': 'Ingen självskattning är registrerad ännu.',
  'perception.col.practice': 'Arbetsmoment',
  'perception.col.evidenced': 'Belagd nivå',
  'perception.col.selfRated': 'Självskattningar',
  'perception.reading.no_evidence': 'Inget underlag ännu',
  'perception.reading.not_in_scope': 'Inte i analysen nu',
  'perception.reading.above': 'Skattad {n} över underlaget',
  'perception.reading.same': 'Samma som underlaget',
  'perception.reading.below': 'Skattad {n} under underlaget',
  'errors.QUESTIONNAIRE_FILE_TOO_LARGE': 'En fil med ett frågeformulär får vara högst {mb} MB.',
  'errors.QUESTIONNAIRE_NOT_XLSX': 'Filen är inte en Excel-arbetsbok (.xlsx). Ladda upp frågeformuläret som det skickades, sparat som .xlsx.',
  'errors.QUESTIONNAIRE_ZIP_REFUSED': 'Arbetsboken innehåller något som ett frågeformulär aldrig gör (makron, länkar till andra filer, inbäddade objekt), eller är byggd för att bli mycket större än den ser ut. Den lästes inte.',
  'errors.QUESTIONNAIRE_UNKNOWN': 'Filen är inte ett frågeformulär som skapats för den här klienten. Ladda upp filen som skickades härifrån.',
  'errors.QUESTIONNAIRE_OTHER_ANALYSIS': 'Frågeformuläret skapades för en annan analys av den här klienten. Ladda upp det där.',
  'errors.QUESTIONNAIRE_VERSION': 'Filen är en annan version av frågeformuläret än den som skapades. Skicka den skapade filen till klienten igen.',
  'errors.QUESTIONNAIRE_EDITED': 'Frågeformuläret har ändrats: ett påstående, ett blad eller en rubrik är inte som det skapades ({where}). Bara svaren, kommentarerna och vem som svarat får fyllas i.',
  'errors.QUESTIONNAIRE_FORMULA': 'Filen innehåller en formel (blad {sheet}, cell {cell}). Svaren skrivs in eller väljs i listan, de beräknas aldrig: ta bort formeln och ladda upp igen.',
  'errors.QUESTIONNAIRE_OFF_SCALE': 'Svaret på {practice} ligger inte på skalan. Välj 0 till 4 i listan, eller lämna det tomt.',
  'errors.QUESTIONNAIRE_COMMENT': 'Kommentaren till {practice} kan inte sparas: en kommentar är text på högst 1 000 tecken, bredvid ett svar.',
  'errors.QUESTIONNAIRE_TEXT_UNSAFE': 'En kommentar eller uppgiften om vem som svarat börjar med =, +, -, @ eller en tabb, som ett kalkylblad kan köra som en formel. Börja texten med en bokstav eller en siffra.',
  'errors.QUESTIONNAIRE_RESPONDENT': 'Vem som svarat är inte ifyllt som det ska: en roll behövs, namn och roll är högst 200 tecken, och datumet är en dag mellan skapandet och i dag.',
  'errors.QUESTIONNAIRE_EMPTY': 'Inget påstående är besvarat i filen.',
  'errors.QUESTIONNAIRE_RETURNED_ALREADY': 'Filen är redan registrerad, som {code}.',
  'errors.QUESTIONNAIRE_NOTHING_TO_ASK': 'Inget arbetsmoment i analysens omfattning har ännu ett frågeformulär.',
  'errors.QUESTIONNAIRE_RETURN_RETIRED': 'Svaren är redan återkallade.',
  'auditField.questionnaires': 'frågeformulär',
  'auditField.questionnaireAdopted': 'antaget av regelbevakningen',
  'auditField.issuedBy': 'skapat av',
  'auditField.issuedAt': 'skapat',
  'auditField.respondentRole': 'roll för den som svarat',
  'auditField.answeredOn': 'besvarat den',
  'exportKind.questionnaire': 'ett frågeformulär till klienten',
  'auditAction.export.questionnaire': 'Laddade ned ett frågeformulär till klienten',
  'auditAction.questionnaire.issue': 'Skapade ett frågeformulär',
  'auditAction.questionnaire.return': 'Registrerade svaren på ett frågeformulär som självskattningar',
  'auditAction.questionnaire.retire_return': 'Återkallade svaren på ett frågeformulär',
  'record.questionnaireIssue': 'Skapat frågeformulär',
  'record.questionnaireReturn': 'Besvarat frågeformulär',
  'record.selfRating': 'Självskattning',
}
